import React, { useState, useEffect } from 'react';
import { Home, Printer, Users, FileText, Wrench, Plus, Menu, Edit, Trash2, Phone, AlertCircle, TrendingUp, LogOut, User, X, Upload, Download, Settings, Paperclip, Eye, Loader, Calendar, MoreVertical, Mail, Package, Receipt, BookOpen, ClipboardList, BarChart3, RefreshCw, ShoppingCart } from 'lucide-react';
import { initializeApp, getApps } from 'firebase/app';
import { auth, db, storage } from './firebase';
import { mastersXml, standardMastersXml, vouchersXml, voucherMessage, pullDebtorsXml, pullReceiptsXml, pullLedgerListXml, mergeTallySettings, stateFromGstin, isGstin, gstTotals } from './tally/tallyXml';
import { clientToLedger, quotationToVoucher, computeBilling, findClientByName, invoiceToVoucher, invoiceTotals, INVOICE_LEDGER_KINDS, suggestIncomeLedger, proformaToVoucher, challanToVoucher } from './tally/tallyData';
import { buildInvoicePDFDoc, buildChallanPDFDoc, buildTallyInvoicePDFDoc, buildPurchaseOrderPDFDoc, DEFAULT_DECLARATION } from './docs/businessPdf';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, linkWithPopup, reauthenticateWithPopup, getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, writeBatch, onSnapshot, runTransaction, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RB_INFOTECH_LOGO } from './assets/rbInfotechLogo';
import { RB_SEAL } from './assets/rbSeal';
import { CANOPUS_SEAL } from './assets/canopusSeal';

const PrinterRentalApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [linkedClientId, setLinkedClientId] = useState('');   // client logins: the client record they belong to
  const [userPermissions, setUserPermissions] = useState({});
  const [userModuleAccess, setUserModuleAccess] = useState({});   // per-module 'none' | 'r' | 'rw' set in User Management (overrides role defaults)
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authSetupError, setAuthSetupError] = useState('');
  // The open page survives a refresh (kept per browser tab)
  const [currentView, setCurrentView] = useState(() => { try { return sessionStorage.getItem('copiq_view') || 'dashboard'; } catch { return 'dashboard'; } });
  useEffect(() => { try { sessionStorage.setItem('copiq_view', currentView); } catch {} }, [currentView]);
  const [refreshing, setRefreshing] = useState(false);
  // In-app refresh (for the installed app, which has no browser reload button): drop cached files, then reload on the same page
  const refreshApp = async () => {
    setRefreshing(true);
    try { if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))); } } catch {}
    window.location.reload();
  };
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [printersRaw, setPrinters] = useState([]);
  const [clientsRaw, setClients] = useState([]);
  const [rentalsRaw, setRentals] = useState([]);
  const [serviceCallsRaw, setServiceCalls] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [meterReadingsRaw, setMeterReadings] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [printerMovementsRaw, setPrinterMovements] = useState([]);
  const [quotationsRaw, setQuotations] = useState([]);
  const [marketingHighlights, setMarketingHighlights] = useState([]);
  const [serviceHistorySerial, setServiceHistorySerial] = useState(null);
  const [backupLogs, setBackupLogs] = useState([]);
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [currentCompany, setCurrentCompanyState] = useState(() => {
    try {
      return localStorage.getItem('activeCompany') || 'rb_infotech';
    } catch {
      return 'rb_infotech';
    }
  });
  const setCurrentCompany = (companyId) => {
    setCurrentCompanyState(companyId);
    try { localStorage.setItem('activeCompany', companyId); } catch {}
  };
  const [clientAssetsRaw, setClientAssets] = useState([]); // Client-owned printers for service
  const [leadsRaw, setLeads] = useState([]);
  const [invoicesRaw, setInvoicesRaw] = useState([]);
  const [challansRaw, setChallansRaw] = useState([]);
  const [proformasRaw, setProformasRaw] = useState([]);       // proforma invoices (not posted to Tally)
  const [invCfgRaw, setInvCfgRaw] = useState(null);             // config/invoice  { companies: { <companyId>: { bankDetails, dueDays, terms } } }
  const [invTab, setInvTab] = useState('invoices');
  const [invSearch, setInvSearch] = useState('');
  const [invFilter, setInvFilter] = useState('all');
  const [invForm, setInvForm] = useState(null);
  const [challanForm, setChallanForm] = useState(null);
  const [invSettingsForm, setInvSettingsForm] = useState(null);
  const [invSaving, setInvSaving] = useState(false);
  const [tallyCfgRaw, setTallyCfgRaw] = useState(null);        // config/tally  { companies: { <companyId>: settings } }
  const [tallyAgent, setTallyAgent] = useState(null);          // config/tallyAgent  (heartbeat from the helper app)
  const [tallyQueueRaw, setTallyQueueRaw] = useState([]);
  const [tallyOutstandingRaw, setTallyOutstandingRaw] = useState([]);
  const [tallyReceiptsRaw, setTallyReceiptsRaw] = useState([]);
  const [tallyInvoicesRaw, setTallyInvoicesRaw] = useState([]);   // Sales vouchers read back from Tally
  const [tallyLedgersRaw, setTallyLedgersRaw] = useState([]);   // real ledger names fetched from Tally (Duties & Taxes, Sales Accounts)
  const [tallyTab, setTallyTab] = useState('status');
  const [tallyInvMonth, setTallyInvMonth] = useState(() => new Date().toISOString().slice(0, 7));   // month shown on the 'From Tally' tab (this month)
  const [tallyMonth, setTallyMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const [tallySel, setTallySel] = useState({});
  const [tallySearch, setTallySearch] = useState('');
  const [tallyForm, setTallyForm] = useState(null);
  const [tallyBusy, setTallyBusy] = useState(false);
  const [assetCounters, setAssetCounters] = useState({});   // config/assetCounters: highest number EVER issued per prefix - numbers are never reused
  const [remindersRaw, setRemindersRaw] = useState([]);       // manual reminders (collection 'reminders')
  const [reminderFilter, setReminderFilter] = useState('all');
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderForm, setReminderForm] = useState({ title: '', note: '', dueDate: '', clientName: '' });
  const [emailRulesForm, setEmailRulesForm] = useState({});   // Settings > Email rules (config/emailRules)
  const [emailRulesSaving, setEmailRulesSaving] = useState(false);
  const [inboxType, setInboxType] = useState('all');
  const [inboxStatus, setInboxStatus] = useState('new');
  const [inboxSearch, setInboxSearch] = useState('');
  const [inboxOpenId, setInboxOpenId] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailRunStatus, setEmailRunStatus] = useState(null);
  const [inboxMailsRaw, setInboxMails] = useState([]); // enquiry/order/service mails read by the emailLeadsSync function
  const [clientActivitiesRaw, setClientActivities] = useState([]);
  const [activityClientId, setActivityClientId] = useState(null);
  const [stockItemsRaw, setStockItemsRaw] = useState([]);
  const [stockMovesRaw, setStockMovesRaw] = useState([]);
  const [purchaseOrdersRaw, setPurchaseOrdersRaw] = useState([]);
  const [vendorsRaw, setVendorsRaw] = useState([]);
  const [poUi, setPoUi] = useState({ tab: 'orders', search: '', status: '', form: null, vendor: null, recv: null, saving: false });
  const [stockUi, setStockUi] = useState({ tab: 'items', search: '', fEng: '', item: null, move: null });
  const [opsTab, setOpsTab] = useState('dashboard');          // Daily Operations: 'dashboard' | 'report'
  const [opsFrom, setOpsFrom] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
  const [opsTo, setOpsTo] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());

  // Every business-data collection except Engineers (shared staff across both
  // companies) is scoped to whichever company is currently active. Records
  // saved before multi-company support existed have no companyId at all -
  // those are treated as RB Infotech's, so nothing needs to be migrated.
  // Inlining the company-check directly in each filter (instead of a shared
  // helper function) avoids an exhaustive-deps warning, since a shared
  // function reference would need to be in every dependency array despite
  // being recreated fresh on every render.
  const printers = React.useMemo(() => printersRaw.filter(p => (p.companyId || 'rb_infotech') === currentCompany), [printersRaw, currentCompany]);
  const clients = React.useMemo(() => clientsRaw.filter(c => (c.companyId || 'rb_infotech') === currentCompany), [clientsRaw, currentCompany]);
  const rentals = React.useMemo(() => rentalsRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany), [rentalsRaw, currentCompany]);
  const serviceCalls = React.useMemo(() => serviceCallsRaw.filter(s => (s.companyId || 'rb_infotech') === currentCompany), [serviceCallsRaw, currentCompany]);
  const stockItems = React.useMemo(() => stockItemsRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany), [stockItemsRaw, currentCompany]);
  const purchaseOrders = React.useMemo(() => purchaseOrdersRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany).sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.poNumber).localeCompare(String(a.poNumber), undefined, { numeric: true })), [purchaseOrdersRaw, currentCompany]);
  const vendors = React.useMemo(() => vendorsRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany).sort((a, b) => String(a.name).localeCompare(String(b.name))), [vendorsRaw, currentCompany]);
  const stockMoves = React.useMemo(() => stockMovesRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany), [stockMovesRaw, currentCompany]);
  const meterReadings = React.useMemo(() => meterReadingsRaw.filter(m => (m.companyId || 'rb_infotech') === currentCompany), [meterReadingsRaw, currentCompany]);
  const printerMovements = React.useMemo(() => printerMovementsRaw.filter(pm => (pm.companyId || 'rb_infotech') === currentCompany), [printerMovementsRaw, currentCompany]);
  const quotations = React.useMemo(() => quotationsRaw.filter(q => (q.companyId || 'rb_infotech') === currentCompany), [quotationsRaw, currentCompany]);
  const clientAssets = React.useMemo(() => clientAssetsRaw.filter(ca => (ca.companyId || 'rb_infotech') === currentCompany), [clientAssetsRaw, currentCompany]);
  const leads = React.useMemo(() => leadsRaw.filter(l => (l.companyId || 'rb_infotech') === currentCompany), [leadsRaw, currentCompany]);
  const invoices = React.useMemo(() => invoicesRaw.filter(i => (i.companyId || 'rb_infotech') === currentCompany), [invoicesRaw, currentCompany]);
  const proformas = React.useMemo(() => proformasRaw.filter(p => (p.companyId || 'rb_infotech') === currentCompany), [proformasRaw, currentCompany]);
  const challans = React.useMemo(() => challansRaw.filter(c => (c.companyId || 'rb_infotech') === currentCompany), [challansRaw, currentCompany]);
  const invCfg = React.useMemo(() => ({ invoiceStyle: 'tally', declaration: DEFAULT_DECLARATION, bankDetails: '', dueDays: 15, requireApproval: true, terms: 'Payment is due within 15 days from the date of invoice; overdue payments attract penalty as per the MSME Act.\nSubject to Bangalore jurisdiction only.', ...(((invCfgRaw || {}).companies || {})[currentCompany] || {}) }), [invCfgRaw, currentCompany]);
  const tallyCfg = React.useMemo(() => mergeTallySettings(((tallyCfgRaw || {}).companies || {})[currentCompany]), [tallyCfgRaw, currentCompany]);
  const tallyQueue = React.useMemo(() => tallyQueueRaw.filter(q => (q.companyId || 'rb_infotech') === currentCompany), [tallyQueueRaw, currentCompany]);
  const tallyOutstanding = React.useMemo(() => tallyOutstandingRaw.filter(o => (o.companyId || 'rb_infotech') === currentCompany), [tallyOutstandingRaw, currentCompany]);
  const tallyLedgerLists = React.useMemo(() => tallyLedgersRaw.find(x => (x.companyId || 'rb_infotech') === currentCompany) || null, [tallyLedgersRaw, currentCompany]);
  const tallyInvoices = React.useMemo(() => tallyInvoicesRaw.filter(o => (o.companyId || 'rb_infotech') === currentCompany), [tallyInvoicesRaw, currentCompany]);
  const tallyReceipts = React.useMemo(() => tallyReceiptsRaw.filter(o => (o.companyId || 'rb_infotech') === currentCompany), [tallyReceiptsRaw, currentCompany]);
  // ref ("client:ID", "quotation:ID", "bill:...") -> latest queue status, so rows can show "In Tally" / "Queued" / "Failed"
  const tallyRefStatus = React.useMemo(() => {
    const m = {};
    [...tallyQueue].sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt))).forEach(q => (q.refs || []).forEach(r => { m[r] = q.status; }));
    return m;
  }, [tallyQueue]);
  const reminders = React.useMemo(() => remindersRaw.filter(r => (r.companyId || 'rb_infotech') === currentCompany), [remindersRaw, currentCompany]);
  const inboxMails = React.useMemo(() => inboxMailsRaw.filter(m => (m.companyId || 'rb_infotech') === currentCompany), [inboxMailsRaw, currentCompany]);
  // Keep the "highest asset number ever issued" marks (config/assetCounters) up to date: from every asset that exists now
  // and from the audit history (so a number whose asset was later deleted/relabeled is still remembered). Only ever raises a mark.
  useEffect(() => {
    if (!currentUser || printersRaw.length === 0) return;
    const highest = {};
    const note = (str) => {
      for (const m of String(str || '').matchAll(/\b([A-Z]{2,6})(\d{4,5})\b/g)) {
        if (!/^(RB|CN)(PRN|DESK|LAP|SCN)$/.test(m[1])) continue;
        highest[m[1]] = Math.max(highest[m[1]] || 0, parseInt(m[2], 10));
      }
    };
    printersRaw.forEach(p => note(p.assetNumber));
    auditLogs.filter(l => l.entityType === 'Asset').forEach(l => note(l.entityName));
    const raise = Object.entries(highest).filter(([prefix, n]) => n > (Number(assetCounters[prefix]) || 0));
    if (raise.length === 0) return;
    runTransaction(db, async (tx) => {
      const ref = doc(db, 'config', 'assetCounters');
      const snap = await tx.get(ref);
      const cur = snap.exists() ? snap.data() : {};
      const next = {};
      raise.forEach(([prefix, n]) => { if (n > (Number(cur[prefix]) || 0)) next[prefix] = n; });
      if (Object.keys(next).length) tx.set(ref, next, { merge: true });
    }).catch((e) => console.error('[assetCounters] could not save high-water marks:', e.message));
  }, [printersRaw, auditLogs, assetCounters, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'config', 'emailRules')).then(snap => { if (snap.exists()) setEmailRulesForm(snap.data()); }).catch(() => {});
  }, [currentUser?.uid]);
  const clientActivities = React.useMemo(() => clientActivitiesRaw.filter(a => (a.companyId || 'rb_infotech') === currentCompany), [clientActivitiesRaw, currentCompany]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importType, setImportType] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // One-shot value used only to seed the Assets page search box when a QR
  // code is scanned (see the URL-param handler below). NOT used for typing -
  // each view manages its own live search state locally, so keystrokes don't
  // trigger a full app re-render (which would otherwise remount the page and
  // lose input focus after every character).
  const [qrScannedAssetSearch, setQrScannedAssetSearch] = useState('');

  // Equipment/Service categories offered - used on Service Calls and Quotations
  // Multi-company support: RB Infotech Solutions and Canopus Enterprises
  // share this one portal/login, but keep fully separate data. Every
  // business record gets tagged with a companyId, and every view filters
  // by whichever company is currently active. Records without a companyId
  // (all your existing data) are treated as RB Infotech's, so nothing
  // needs to be migrated - old data just keeps working.
  const COMPANIES = {
    rb_infotech: {
      id: 'rb_infotech',
      name: 'RB Infotech Solutions',
      address: '#5, 1st Floor, 13th Cross, Hoskerehalli Road, R R Nagar, Bangalore - 560098',
      phone: '88845 69705 / 88800 06033',
      email: 'rbinfotech2014@gmail.com',
      gstin: '29ABDFR3460K1ZV',
      msme: 'UDYAM-KR-03-0099926',
      quotePrefix: 'RBQT'
    },
    canopus: {
      id: 'canopus',
      name: 'Canopus Enterprises',
      address: '1st, 5, Hoskerehalli Main Road, Rajarajeshwari Nagar, Bengaluru - 560098',
      phone: '88845 69705 / 88800 06033',
      email: 'canopusenterprises18@gmail.com',
      gstin: '29BMCPR2433M1ZO',
      msme: '',
      quotePrefix: 'CNQT'
    }
  };

  // Logo shown on the quotation PDF letterhead, per company. Canopus has no
  // logo file yet, so it's omitted here and buildQuotationPDFDoc falls back
  // to the colored company-name text for it.
  const COMPANY_LOGOS = {
    rb_infotech: RB_INFOTECH_LOGO
  };

  // Company seal for the quotation signature block, per company. Canopus
  // has no scanned original seal, so CANOPUS_SEAL was designed to match the
  // same circular-stamp convention as RB Infotech's (see canopusSeal.js).
  // Canopus still has no logo file, so COMPANY_LOGOS below stays RB-only.
  const COMPANY_SEALS = {
    rb_infotech: RB_SEAL,
    canopus: CANOPUS_SEAL
  };

  const EQUIPMENT_TYPES = [
    { value: 'laserjet_printer', label: '🖨️ LaserJet Printer' },
    { value: 'inktank_printer', label: '💧 Ink Tank Printer' },
    { value: 'scanner', label: '📠 Scanner' },
    { value: 'laptop', label: '💻 Laptop' },
    { value: 'desktop', label: '🖥️ Desktop' },
    { value: 'barcode_printer', label: '🏷️ Barcode Printer' }
  ];

  // Quotations can also be for consumables, which aren't equipment we track
  // anywhere else (service calls, client assets) - so cartridge types live
  // only in this quotation-specific list, not in EQUIPMENT_TYPES itself.
  const CARTRIDGE_EQUIPMENT_TYPES = [
    { value: 'toner_cartridge', label: '🖨️ Toner Cartridge' },
    { value: 'toner_cartridge_parts', label: '🔩 Toner Cartridge Parts' }
  ];
  // Server / email service / software licence quotations - sold items, not equipment we service, so (like
  // cartridges) they live only in the quotation list.
  const IT_SALE_EQUIPMENT_TYPES = [
    { value: 'server', label: '🗄️ Server' },
    { value: 'email_service', label: '✉️ Email Service' },
    { value: 'software_license', label: '🔑 Software License' }
  ];
  const QUOTATION_EQUIPMENT_TYPES = [...EQUIPMENT_TYPES, ...CARTRIDGE_EQUIPMENT_TYPES, ...IT_SALE_EQUIPMENT_TYPES];
  // Anything that is a supply / sale rather than a repair: picking it while on the default "Service / Repair"
  // quotation type switches to the matching sale type (and its terms).
  const SUPPLY_QUOTE_TYPE_FOR_EQUIPMENT = {
    toner_cartridge: 'cartridge', toner_cartridge_parts: 'cartridge',
    server: 'server_sale', email_service: 'email_service', software_license: 'license_sale'
  };
  const EQUIPMENT_FOR_SUPPLY_QUOTE_TYPE = { server_sale: 'server', email_service: 'email_service', license_sale: 'software_license' };
  const isSupplyType = (t) => !!SUPPLY_QUOTE_TYPE_FOR_EQUIPMENT[t];

  // Known Make/Model catalog - used for autocomplete when adding assets
  const KNOWN_PRINTER_MODELS = [
    { make: 'HP', model: 'LJ MFP E42540' },
    { make: 'HP', model: 'LJ 1020 PLUS' },
    { make: 'HP', model: 'LJ P1606DN' },
    { make: 'HP', model: 'LJ M501' },
    { make: 'HP', model: 'LJ M506' },
    { make: 'HP', model: 'LJ PRO M1136 MFP' },
    { make: 'HP', model: 'LJ 128FW' },
    { make: 'HP', model: 'LJ 1005' },
    { make: 'HP', model: 'LJ 1108' },
    { make: 'HP', model: 'LJ 202DW' },
    { make: 'HP', model: '202DW' },
    { make: 'HP', model: 'LJ 1020' },
    { make: 'HP', model: 'LJ 128FN' },
    { make: 'HP', model: 'LJ 4104DW' },
    { make: 'HP', model: 'LJ 4104' },
    { make: 'HP', model: 'LJ 226DW' },
    { make: 'HP', model: 'LJ 136NW' },
    { make: 'Canon', model: '244' },
    { make: 'Kyocera', model: '3200i' },
    { make: 'Kyocera', model: 'Taskalfa MZ3200i' },
    { make: 'Kyocera', model: 'Taskalfa 3212i' },
    { make: 'Kyocera', model: 'Taskalfa 3252ci' },
    { make: 'Kyocera', model: 'ECOSYS M4125idn' },
    { make: 'Kyocera', model: 'FS 6525 MFP' },
    { make: 'Kyocera', model: 'Taskalfa 4012i' },
    { make: 'Kyocera', model: 'TA-3200 A3 MFP' }
  ];

  // Role definitions
  const ROLES = {
    super_admin: { label: 'Super Admin', level: 5 },
    admin: { label: 'Admin', level: 4 },
    manager: { label: 'Manager', level: 3 },
    field_engineer: { label: 'Field Engineer', level: 2 },
    user: { label: 'User', level: 2 },
    client: { label: 'Client', level: 1 }
  };

  // Default permission set per role - used whenever a new login is created
  // (User Management, Grant Portal Access, Google Sign-In, etc.). Defined at
  // the top level so every component can use it via closure, not just the
  // one view that originally needed it.
  const getPermissionsByRole = (role) => {
    const perms = {
      super_admin: { canManageUsers: true, canEditAssets: true, canEditClients: true, canEditRentals: true, canEditReadings: true, canDelete: true, canImport: true, canExport: true, canCreateQuotations: true, canViewTally: true, canManageInvoices: true },
      admin: { canManageUsers: false, canEditAssets: true, canEditClients: true, canEditRentals: true, canEditReadings: true, canDelete: true, canImport: true, canExport: true, canCreateQuotations: true, canViewTally: false, canManageInvoices: true },
      manager: { canManageUsers: false, canEditAssets: false, canEditClients: false, canEditRentals: true, canEditReadings: true, canDelete: false, canImport: false, canExport: true, canCreateQuotations: true, canViewTally: false, canManageInvoices: true },
      field_engineer: { canManageUsers: false, canEditAssets: false, canEditClients: false, canEditRentals: false, canDelete: false, canImport: false, canExport: false, canViewServiceCalls: true, canEditServiceCalls: true, canViewClients: true, canViewClientAssets: true, canViewReadings: true, canEditReadings: true, canCreateQuotations: true, canViewTally: false, canManageInvoices: false },
      user: { canManageUsers: false, canEditAssets: false, canEditClients: false, canEditRentals: false, canEditReadings: false, canDelete: false, canImport: false, canExport: true, canCreateQuotations: false },
      client: { canManageUsers: false, canEditAssets: false, canEditClients: false, canEditRentals: false, canEditReadings: false, canDelete: false, canImport: false, canExport: false, canCreateQuotations: false }
    };
    return perms[role] || perms.user;
  };

  // Every known permission key with a human label, used to render the
  // granular per-user checkboxes in User Management (Edit User). Keeping
  // this list in one place so a newly-added permission key only needs to be
  // added here to show up in the editor.
  const PERMISSION_LABELS = {
    canManageUsers: 'Manage Users',
    canEditAssets: 'Edit Assets',
    canEditClients: 'Edit Clients',
    canEditRentals: 'Edit Rentals',
    canEditReadings: 'Edit Meter Readings',
    canDelete: 'Delete Records',
    canImport: 'Import Data',
    canExport: 'Export Data',
    canCreateQuotations: 'Create Quotations',
    canViewTally: 'See the Tally tab (accounting sync)',
    canManageInvoices: 'Invoices & Delivery Challans',
    canApproveInvoices: 'Approve / reject invoices',
    canManagePurchase: 'Purchase orders & vendors',
    canManagePriceLocks: 'Set client price locks & locked rental rates',
    canViewServiceCalls: 'View Service Calls',
    canEditServiceCalls: 'Edit Service Calls',
    canViewClients: 'View Clients',
    canViewClientAssets: 'View Client-Owned Assets',
    canViewReadings: 'View Meter Readings'
  };

  // Search helper: matches if EVERY word in the query appears somewhere across
  // the given fields (in any order, in any field) - not just a single exact
  // substring. So "kauvery" or "sri kauvery" or "kauvery electronic" all match
  // "SRI KAUVERY MEDICAL CARE (I)LTD -ELECTRONIC CITY" even though the words
  // aren't adjacent in the original text.
  const matchesSearch = (query, ...fields) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = fields.filter(Boolean).join(' ').toLowerCase();
    return q.split(/\s+/).every(word => haystack.includes(word));
  };

  // Asset number prefixes per company/category - e.g. RBPRN0042 for an RB
  // Infotech printer, CNPRN0001 for a Canopus one. RB Infotech's prefixes
  // (and their existing global, cross-company numbering scope in every call
  // site below) are left exactly as they were: some existing Canopus assets
  // already have RB-style numbers printed on physical stickers, so those
  // must never be renumbered. Only NEW assets created while Canopus is the
  // active company get the CN prefix, with their own count starting fresh.
  const getAssetPrefixMap = (companyId) => {
    const code = companyId === 'canopus' ? 'CN' : 'RB';
    return { 'Printer': `${code}PRN`, 'Desktop': `${code}DESK`, 'Laptop': `${code}LAP`, 'Scanner': `${code}SCN` };
  };

  // Next asset number for a prefix. Numbers are NEVER reused: the next one is always one past the highest number
  // ever issued for that prefix - whether or not that asset still exists (deleted, relabeled, transferred...).
  // A number that was once printed on a physical sticker must not turn up on a different machine later.
  // "Ever issued" = the highest number among current assets and the saved high-water mark in
  // config/assetCounters (kept up to date by the effect in the app root).
  const assetFloor = (prefix) => Number((assetCounters || {})[prefix]) || 0;
  const getNextAssetNumberForPrefix = (prefix, existingPrinters) => {
    const used = existingPrinters
      .filter(p => p.assetNumber && p.assetNumber.startsWith(prefix))
      .map(p => parseInt(p.assetNumber.replace(prefix, ''), 10))
      .filter(n => !isNaN(n));
    const n = Math.max(0, assetFloor(prefix), ...used) + 1;
    return `${prefix}${String(n).padStart(4, '0')}`;
  };

  // Converts a rupee amount into words, Indian numbering style (Lakh/Crore),
  // e.g. 6608 -> "Six Thousand Six Hundred Eight Only" - used on the printed
  // quotation, matching the "Total In Words" line on the sample format.
  const numberToWordsINR = (num) => {
    num = Math.round(num);
    if (num === 0) return 'Zero Only';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const twoDigits = (n) => {
      if (n < 20) return ones[n];
      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    };
    const threeDigits = (n) => {
      if (n < 100) return twoDigits(n);
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + twoDigits(n % 100) : '');
    };
    let result = '';
    const crore = Math.floor(num / 10000000); num %= 10000000;
    const lakh = Math.floor(num / 100000); num %= 100000;
    const thousand = Math.floor(num / 1000); num %= 1000;
    const hundred = num;
    if (crore) result += threeDigits(crore) + ' Crore ';
    if (lakh) result += threeDigits(lakh) + ' Lakh ';
    if (thousand) result += threeDigits(thousand) + ' Thousand ';
    if (hundred) result += threeDigits(hundred) + ' ';
    return result.trim() + ' Only';
  };

  // Generates the next quote number following each company's own prefix
  // pattern, e.g. RBQT2026-27\194 for RB Infotech or CNQT2026-27\12 for
  // Canopus. Indian financial year runs April to March.
  //
  // Reads a FRESH snapshot from Firestore rather than trusting local
  // `quotations` state (same reasoning as the printer asset-number
  // generator) - two quotations created shortly apart, before local state
  // had refreshed to include the first one, previously computed the SAME
  // "next" number independently and both saved successfully (there was no
  // uniqueness check at save time, unlike printers). That produced quote
  // numbers shared across completely different clients. The while loop below
  // is a second layer of defense: even against a fresh snapshot, two saves
  // racing within the same read-then-write window could still collide, so
  // skip past any number that's already taken rather than trusting the count
  // blindly.
  // ONE allocator for every numbered document (quotations, invoices, proformas, challans, purchase orders), so the series
  // stay in step everywhere: next number = highest number already used in that series + 1 - counting documents in the
  // portal AND vouchers raised directly in Tally (read back by the helper) - and the number is reserved atomically
  // (docNumbers/...) so two people saving at the same moment can never be given the same one.
  const allocateNumber = async (collName, field, prefix, extraUsed = []) => {
    const snap = await getDocs(collection(db, collName));
    const mine = snap.docs.map(d => d.data()).filter(x => (x.companyId || 'rb_infotech') === currentCompany);
    const used = new Set([...mine.map(x => x[field]), ...extraUsed].map(s => String(s || '').trim()).filter(Boolean));
    const suffixOf = (s) => { const m = /^(.*)\\(\d+)$/.exec(s); return m && m[1] === prefix ? parseInt(m[2], 10) : 0; };
    let n = Math.max(0, ...[...used].map(suffixOf)) + 1;
    for (let tries = 0; tries < 50; tries++, n++) {
      const cand = `${prefix}\\${n}`;
      if (used.has(cand)) continue;
      const ref = doc(db, 'docNumbers', `${currentCompany}__${collName}__${prefix}__${n}`.replace(/[^A-Za-z0-9_-]/g, '_'));
      const ok = await runTransaction(db, async (tx) => {
        const s = await tx.get(ref);
        if (s.exists() && Date.now() - (s.data().at || 0) < 10 * 60 * 1000) return false;   // someone else holds it (a reservation expires after 10 minutes)
        tx.set(ref, { at: Date.now(), by: (currentUser && currentUser.email) || '', companyId: currentCompany, coll: collName, number: cand });
        return true;
      });
      if (ok) return cand;
    }
    throw new Error('Could not reserve a document number - please try again.');
  };

  const generateQuoteNumber = async () => {
    const prefix = COMPANIES[currentCompany]?.quotePrefix || 'QT';
    const today = new Date();
    const y = today.getFullYear();
    const fyStart = today.getMonth() >= 3 ? y : y - 1; // April = month index 3
    const fyEndShort = String((fyStart + 1) % 100).padStart(2, '0');
    const fyLabel = `${fyStart}-${fyEndShort}`;
    const fyPrefix = `${prefix}${fyLabel}`;

    return allocateNumber('quotations', 'quoteNumber', fyPrefix);
  };

  // Common service line items suggested per printer type, so the engineer
  // doesn't have to type these from scratch every time - matches the kind of
  // work shown in your sample quotations (motor/roller for laser, ink pump/
  // printhead for inkjet), extended to cover all equipment types serviced.
  const QUOTATION_ITEM_PRESETS = {
    laserjet_printer: [
      { description: 'Replacement of Main motor and Paper pickup roller set assembly', hsn: '998713', qty: 1, rate: 2400 },
      { description: 'Replacement of Drum Unit', hsn: '998713', qty: 1, rate: 1800 },
      { description: 'Replacement of Fuser Assembly', hsn: '998713', qty: 1, rate: 3500 },
      { description: 'General Service & Cleaning', hsn: '998713', qty: 1, rate: 500 }
    ],
    inktank_printer: [
      { description: 'Replacement of Printhead', hsn: '998713', qty: 1, rate: 3800 },
      { description: 'Replacement of Ink Pumping Station', hsn: '998713', qty: 1, rate: 1800 },
      { description: 'Ink Tank / Cartridge Cleaning Service', hsn: '998713', qty: 1, rate: 500 },
      { description: 'General Service & Cleaning', hsn: '998713', qty: 1, rate: 400 }
    ],
    scanner: [
      { description: 'Scanner Glass / Sensor Cleaning', hsn: '998713', qty: 1, rate: 500 },
      { description: 'Replacement of Scanner Roller/ADF Assembly', hsn: '998713', qty: 1, rate: 2000 },
      { description: 'General Service & Calibration', hsn: '998713', qty: 1, rate: 600 }
    ],
    laptop: [
      { description: 'Software Formatting & OS Reinstallation', hsn: '998714', qty: 1, rate: 800 },
      { description: 'Keyboard Replacement', hsn: '998714', qty: 1, rate: 1500 },
      { description: 'Screen Replacement', hsn: '998714', qty: 1, rate: 4500 },
      { description: 'General Service & Cleaning', hsn: '998714', qty: 1, rate: 500 }
    ],
    desktop: [
      { description: 'Software Formatting & OS Reinstallation', hsn: '998714', qty: 1, rate: 700 },
      { description: 'RAM/SMPS Replacement', hsn: '998714', qty: 1, rate: 1500 },
      { description: 'CPU Cabinet Cleaning & Service', hsn: '998714', qty: 1, rate: 400 }
    ],
    toner_cartridge: [
      { description: 'Toner Cartridge - Black', hsn: '8443', qty: 1, rate: '' },
      { description: 'Toner Cartridge - Cyan / Magenta / Yellow', hsn: '8443', qty: 1, rate: '' }
    ],
    toner_cartridge_parts: [
      { description: 'Cartridge Chip', hsn: '8443', qty: 1, rate: '' },
      { description: 'OPC Drum', hsn: '8443', qty: 1, rate: '' },
      { description: 'Primary Charge Roller (PCR)', hsn: '8443', qty: 1, rate: '' },
      { description: 'Magnetic Roller', hsn: '8443', qty: 1, rate: '' },
      { description: 'Doctor Blade', hsn: '8443', qty: 1, rate: '' },
      { description: 'Wiper / Cleaning Blade', hsn: '8443', qty: 1, rate: '' },
      { description: 'Toner Powder Refill', hsn: '3215', qty: 1, rate: '' }
    ],
    server: [
      { description: 'Server - make / model, processor, RAM, storage (specify)', hsn: '8471', qty: 1, rate: '' },
      { description: 'Server Operating System License', hsn: '997331', qty: 1, rate: '' },
      { description: 'Additional RAM / Hard Disk / SSD', hsn: '8473', qty: 1, rate: '' },
      { description: 'Installation, Configuration & Commissioning', hsn: '998713', qty: 1, rate: '' }
    ],
    email_service: [
      { description: 'Business Email - per mailbox per year (storage as per plan)', hsn: '998315', qty: 1, rate: '' },
      { description: 'Domain Name Registration / Renewal', hsn: '998315', qty: 1, rate: '' },
      { description: 'Email Setup & Migration (one time)', hsn: '998315', qty: 1, rate: '' },
      { description: 'Additional Mailbox Storage', hsn: '998315', qty: 1, rate: '' }
    ],
    software_license: [
      { description: 'Operating System License', hsn: '997331', qty: 1, rate: '' },
      { description: 'Office Suite License (per user)', hsn: '997331', qty: 1, rate: '' },
      { description: 'Antivirus / Endpoint Security License (per device, 1 year)', hsn: '997331', qty: 1, rate: '' },
      { description: 'Other Software License (specify)', hsn: '997331', qty: 1, rate: '' }
    ],
    barcode_printer: [
      { description: 'Replacement of Print Head', hsn: '998713', qty: 1, rate: 2800 },
      { description: 'Replacement of Platen Roller', hsn: '998713', qty: 1, rate: 900 },
      { description: 'General Service & Calibration', hsn: '998713', qty: 1, rate: 500 }
    ]
  };

  // The kinds of quotations issued - each has its own Terms & Conditions,
  // since "service/repair" terms don't fit a cartridge sale, a printer sale,
  // or a rental agreement. Shared across both companies (RB Infotech and
  // Canopus) - only the letterhead (name/address/phone/GSTIN) differs
  // per-company, via COMPANIES[quotation.companyId] in buildQuotationPDFDoc.
  const QUOTATION_TYPES = [
    { value: 'service', label: 'Service / Repair' },
    { value: 'sale', label: 'Equipment Sale' },
    { value: 'cartridge', label: 'Cartridge / Consumable Sale' },
    { value: 'rental', label: 'Equipment Rental' },
    { value: 'laptop_rental', label: 'Laptop Rental' },
    { value: 'server_sale', label: 'Sale of Server' },
    { value: 'email_service', label: 'Email Service' },
    { value: 'license_sale', label: 'License Sale' }
  ];

  // Phrases the PDF subject line for each quotation type - "Quotation for
  // Printer Service" reads fine for a repair, but is wrong on a sale,
  // rental, or cartridge quote (matches the wording style of the sample
  // quotation PDFs used to build QUOTATION_TERMS_PRESETS above).
  const QUOTATION_SUBJECT_PHRASE = {
    service: (label) => `${label} Service`,
    sale: (label) => `${label}`,
    cartridge: () => 'Cartridge / Consumables',
    rental: (label) => `${label} on Rental Basis`,
    laptop_rental: (label) => `${label} on Rental Basis`,
    server_sale: () => 'Supply of Server',
    email_service: () => 'Email Service',
    license_sale: () => 'Software License'
  };

  const QUOTATION_TERMS_PRESETS = {
    service: [
      'Pricing: All charges are exclusive of GST. GST will be charged at the applicable rate (currently 18%) at the time of invoicing.',
      'Quotation Validity: This quotation is valid for 5 days from the date of issue. Charges are subject to revision thereafter.',
      'Spare Parts: Spare parts supplied or replaced during service are non-returnable. Any manufacturing defects will be addressed under the applicable manufacturer\'s warranty.',
      'Payment Terms: Payment is due within 15 days from the date of invoice.',
      'Service Confirmation: A written confirmation by email or a Purchase Order is required from your end to proceed with the service.'
    ].join('\n'),
    sale: [
      'Pricing: Quoted prices are exclusive of GST. GST will be charged at the applicable rate (currently 18%) at the time of invoicing.',
      'Quotation Validity: This quotation is valid for 3 days from the date of issue. Prices are subject to revision thereafter.',
      'Warranty: Covered under the applicable manufacturer\'s warranty.',
      'Payment Terms: 100% advance payment required before dispatch.',
      'Confirmation: A written confirmation by email or a Purchase Order is required from your end to proceed with the order.'
    ].join('\n'),
    cartridge: [
      'Pricing: Quoted prices are inclusive of GST @ 18% (as applicable).',
      'Quotation Validity: This quotation is valid for 5 days from the date of issue.',
      'Payment Terms: 100% immediate payment.',
      'Confirmation: A written confirmation by email or a Purchase Order is required from your end to proceed with the order.'
    ].join('\n'),
    // "Equipment Rental" isn't one-size-fits-all: the page-yield/free-pages/
    // cartridge-compatibility clauses only make sense for page-metered
    // printers (laser/ink tank). A barcode printer rents on ribbons, not
    // toner, and a scanner/desktop has no printing concept at all - reusing
    // the printer clauses verbatim on those reads oddly. Split by equipment
    // type instead of one flat string; getRentalTerms() below picks the
    // right one. Each still fits the general "Minimum tenure / damage /
    // installation / jurisdiction / payment / GST" shape of the original.
    rental: {
      printer: [
        'Declared yield values are in accordance with ISO/IEC 24711 under continuous printing; actual yield varies considerably based on the content of printed pages and other factors.',
        'Minimum tenure period shall be 5 years from the date of the agreement.',
        'Damage found to be caused physically or by short circuit in the printer (power supply, formatter & engine controller board) will be billed at actual cost.',
        'If major repair or rectification of a breakdown is required, free service including spare parts will be provided; 1 day\'s time is required for printer service, depending on distance.',
        'Any physical damage or short circuit to the machine will be the client\'s responsibility.',
        'Our cartridges should not be used in any other printer, and no other cartridges should be used in our printer machines.',
        'Once delivered, one-time installation & configuration is free of cost; any further visits will be chargeable at applicable service charges.',
        'The customer is responsible for losses resulting from improper handling. Not covered under the contract: defects from using paper other than the recommended 75-90 GSM copier paper (bond, plain, or letterhead) - actual repair charges apply; and damage from physical handling, power variations, external objects (staples, pins, rubber bands), improper earthing, rodent damage, or any other foreign material found inside the machine.',
        'Subject to Bangalore jurisdiction only.',
        'Unconsumed free pages will not carry forward to the next month.',
        'Payment is due within 15 days from the date of billing; overdue payments attract penalty as per the MSME Act.',
        'This agreement is effective from the date of signing for a period of 5 years, renewable thereafter by mutual consent.',
        'TAX: GST @ 18% extra.'
      ].join('\n'),
      barcode_printer: [
        'Minimum tenure period shall be 5 years from the date of the agreement.',
        'Damage found to be caused physically or by short circuit in the printer (power supply, printhead, or control board) will be billed at actual cost.',
        'If major repair or rectification of a breakdown is required, free service including spare parts will be provided; 1 day\'s time is required for service, depending on distance.',
        'Any physical damage or short circuit to the machine will be the client\'s responsibility.',
        'Only ribbons and labels recommended by us should be used; damage caused by non-recommended consumables will be billed at actual cost.',
        'Once delivered, one-time installation & configuration is free of cost; any further visits will be chargeable at applicable service charges.',
        'The customer is responsible for losses resulting from improper handling, power variations, external objects, improper earthing, rodent damage, or any other foreign material found inside the machine.',
        'Subject to Bangalore jurisdiction only.',
        'Payment is due within 15 days from the date of billing; overdue payments attract penalty as per the MSME Act.',
        'This agreement is effective from the date of signing for a period of 5 years, renewable thereafter by mutual consent.',
        'TAX: GST @ 18% extra.'
      ].join('\n'),
      general: [
        'Minimum tenure period shall be 5 years from the date of the agreement.',
        'Damage found to be caused physically or by short circuit in the machine will be billed at actual cost.',
        'If major repair or rectification of a breakdown is required, free service including spare parts will be provided; 1 day\'s time is required for service, depending on distance.',
        'Any physical damage or short circuit to the machine will be the client\'s responsibility.',
        'Once delivered, one-time installation & configuration is free of cost; any further visits will be chargeable at applicable service charges.',
        'The customer is responsible for losses resulting from improper handling, power variations, external objects, improper earthing, rodent damage, or any other foreign material found inside the machine.',
        'Subject to Bangalore jurisdiction only.',
        'Payment is due within 15 days from the date of billing; overdue payments attract penalty as per the MSME Act.',
        'This agreement is effective from the date of signing for a period of 5 years, renewable thereafter by mutual consent.',
        'TAX: GST @ 18% extra.'
      ].join('\n')
    },
    server_sale: [
      'TAX: GST @ 18% extra.',
      'Prices are valid for 15 days from the date of this quotation; hardware prices are subject to change with the manufacturer\'s pricing.',
      'Delivery within the agreed lead time after receipt of the Purchase Order and advance, subject to stock availability.',
      'Warranty is as per the manufacturer\'s standard warranty; it does not cover physical damage, power surge / short circuit, improper handling, liquid or rodent damage, or unauthorised modification.',
      'One-time installation & configuration at the client\'s site is included; any further visits will be chargeable at applicable service charges.',
      'Operating system and software licences, if included, are supplied under the respective vendor\'s licence terms.',
      'Goods once sold will not be taken back or exchanged except for manufacturing defects covered under warranty.',
      'Data backup and data safety remain the client\'s responsibility.',
      'Payment is due within 15 days from the date of billing (or as agreed in the Purchase Order); overdue payments attract penalty as per the MSME Act.',
      'Subject to Bangalore jurisdiction only.'
    ].join('\n'),
    email_service: [
      'TAX: GST @ 18% extra.',
      'The service is provided on an annual subscription basis, from the date of activation; renewal charges are payable in advance before the expiry date.',
      'Charges are per mailbox per year, with storage as per the selected plan; additional mailboxes or storage are billed extra.',
      'The client must provide access to the domain / DNS records required for setup, and is responsible for the domain\'s registration and renewal unless included in this quotation.',
      'The service is subject to the uptime and policies of the underlying email service provider; planned maintenance and provider outages are outside our control.',
      'Misuse of the service (spam, bulk unsolicited mail, illegal content) will lead to suspension without refund.',
      'Regular backup of important mail is advisable; we are not liable for data loss caused by user deletion or provider failure.',
      'Subscription fees are non-refundable once the service is activated.',
      'Payment is due in advance (or within 15 days from billing); overdue payments attract penalty as per the MSME Act.',
      'Subject to Bangalore jurisdiction only.'
    ].join('\n'),
    license_sale: [
      'TAX: GST @ 18% extra.',
      'Licences are supplied under the respective software vendor\'s licence agreement (EULA); they are non-transferable and for the licensed number of users / devices only.',
      'Licence keys / download links are delivered electronically after receipt of payment.',
      'Validity, updates and support are as per the vendor\'s licence term; renewals must be done before expiry to avoid interruption.',
      'Once a licence key is issued or activated, the order cannot be cancelled, refunded or exchanged.',
      'Prices are valid for 7 days from the date of this quotation and may change with the vendor\'s pricing.',
      'Installation / activation support, if required, is chargeable unless included in this quotation.',
      'Payment is due within 15 days from the date of billing (or in advance as agreed); overdue payments attract penalty as per the MSME Act.',
      'Subject to Bangalore jurisdiction only.'
    ].join('\n'),
    laptop_rental: [
      'TAX: GST @ 18% extra.',
      'Rental period: Minimum 2 months.',
      'Physical damage and short circuit will be the client\'s responsibility.',
      'Laptops are supplied for rental purposes only.',
      'Laptops will be dispatched once the Purchase Order is received.',
      'Rental charges are billed monthly; the minimum 2-month period applies irrespective of whether the laptop is used.',
      'Rental duration is calculated month-wise, not day-wise.'
    ].join('\n')
  };

  // Picks the right "Equipment Rental" T&C variant for the selected
  // equipment type (see QUOTATION_TERMS_PRESETS.rental above).
  const getRentalTerms = (equipmentType) => {
    if (equipmentType === 'laserjet_printer' || equipmentType === 'inktank_printer') return QUOTATION_TERMS_PRESETS.rental.printer;
    if (equipmentType === 'barcode_printer') return QUOTATION_TERMS_PRESETS.rental.barcode_printer;
    return QUOTATION_TERMS_PRESETS.rental.general;
  };

  // Single source of truth for "what terms text goes with this combination" -
  // used by both the Quotation Type and Equipment Type dropdowns, since
  // changing either one can change which terms are correct (only "rental"
  // varies by equipment type; the rest are fixed per quotation type).
  const getTermsForQuotation = (quotationType, equipmentType) => {
    if (quotationType === 'rental') return getRentalTerms(equipmentType);
    return QUOTATION_TERMS_PRESETS[quotationType] || QUOTATION_TERMS_PRESETS.service;
  };

  // Builds the quotation PDF document (jsPDF object) matching the RB
  // Infotech Solutions letterhead format (company header, Bill To, itemized
  // GST table, totals, terms & conditions, signature). Returns the doc
  // object so callers can either download it or attach it to an email.
  const buildQuotationPDFDoc = (quotation) => {
    // Use the company that actually issued THIS quotation (not necessarily
    // whichever company is currently active in the switcher) - so opening an
    // old quotation later always shows the correct original letterhead, even
    // after switching companies in the meantime.
    const company = COMPANIES[quotation.companyId] || COMPANIES.rb_infotech;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    // Header - the real logo if this company has one on file, otherwise the
    // colored company-name text as before.
    const logo = COMPANY_LOGOS[company.id];
    if (logo) {
      const logoH = 12;
      const logoW = logoH * (900 / 184); // source logo is 900x184px
      doc.addImage(logo, 'PNG', 14, 8, logoW, logoH);
      y = 26;
    } else {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text(company.name, 14, y);
      doc.setTextColor(0, 0, 0);
      y += 6;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(company.address, 14, y);
    y += 4.5;
    doc.text(`Ph: ${company.phone}  |  Email: ${company.email}`, 14, y);
    y += 4.5;
    doc.text(`GSTIN: ${company.gstin}${company.msme ? `  |  MSME/Udyam No: ${company.msme}` : ''}`, 14, y);

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTE', pageWidth - 14, 20, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`# ${quotation.quoteNumber}`, pageWidth - 14, 27, { align: 'right' });
    const displayDate = quotation.date ? new Date(quotation.date + 'T00:00:00').toLocaleDateString('en-GB').replace(/\//g, '-') : '';
    doc.text(`Date: ${displayDate}`, pageWidth - 14, 32, { align: 'right' });
    doc.text('Place of Supply: Karnataka (29)', pageWidth - 14, 37, { align: 'right' });

    y += 8;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, y, pageWidth - 14, y);
    y += 7;

    // Bill To
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Bill To:', 14, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text(quotation.clientName || '', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (quotation.clientAddress) {
      const addrLines = doc.splitTextToSize(quotation.clientAddress, 100);
      addrLines.forEach(line => { doc.text(line, 14, y); y += 4.5; });
    }
    if (quotation.clientGSTIN) {
      doc.text(`GSTIN: ${quotation.clientGSTIN}`, 14, y);
      y += 4.5;
    }
    y += 3;

    // Printer type / subject line
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const equipmentLabel = (QUOTATION_EQUIPMENT_TYPES.find(t => t.value === quotation.printerType)?.label || 'Printer').replace(/^\S+\s/, ''); // strip leading emoji
    const subjectPhrase = (QUOTATION_SUBJECT_PHRASE[quotation.quotationType] || QUOTATION_SUBJECT_PHRASE.service)(equipmentLabel);
    doc.text(`Subject: Quotation for ${subjectPhrase}${quotation.printerModel ? ' - ' + quotation.printerModel : ''}${quotation.printerSerial ? ' (S/N: ' + quotation.printerSerial + ')' : ''}`, 14, y);
    y += 6;

    // Items table
    const tableRows = quotation.items.map((item, idx) => {
      const amount = (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
      const cgst = amount * 0.09;
      const sgst = amount * 0.09;
      return [
        idx + 1,
        item.description,
        item.hsn || '998713',
        item.qty,
        (parseFloat(item.rate) || 0).toFixed(2),
        '9%', cgst.toFixed(2),
        '9%', sgst.toFixed(2),
        amount.toFixed(2)
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'CGST%', 'CGST Amt', 'SGST%', 'SGST Amt', 'Amount']],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235], fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 45 } }
    });

    let finalY = doc.lastAutoTable.finalY + 6;

    // Totals box (right-aligned)
    const subTotal = quotation.items.reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)), 0);
    const totalCGST = subTotal * 0.09;
    const totalSGST = subTotal * 0.09;
    const grandTotal = subTotal + totalCGST + totalSGST;

    const totalsX = pageWidth - 70;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Sub Total', totalsX, finalY);
    doc.text(subTotal.toFixed(2), pageWidth - 14, finalY, { align: 'right' });
    finalY += 5;
    doc.text('CGST (9%)', totalsX, finalY);
    doc.text(totalCGST.toFixed(2), pageWidth - 14, finalY, { align: 'right' });
    finalY += 5;
    doc.text('SGST (9%)', totalsX, finalY);
    doc.text(totalSGST.toFixed(2), pageWidth - 14, finalY, { align: 'right' });
    finalY += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total', totalsX, finalY);
    doc.text(`Rs. ${grandTotal.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });
    finalY += 10;

    // Total in words
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Total In Words:', 14, finalY);
    doc.setFont('helvetica', 'italic');
    doc.text(`Indian Rupee ${numberToWordsINR(grandTotal)}`, 14, finalY + 5);
    finalY += 14;

    // Long Terms & Conditions (e.g. the rental agreement's 13 clauses) can
    // run past the bottom of the page - plain doc.text() never auto-paginates
    // like autoTable does, so without this check the overflow lines would be
    // drawn below the visible page edge and silently disappear from the PDF.
    const pageHeight = doc.internal.pageSize.getHeight();
    const ensureSpace = (neededHeight = 4) => {
      if (finalY + neededHeight > pageHeight - 20) {
        doc.addPage();
        finalY = 20;
      }
    };

    // Notes
    if (quotation.notes) {
      ensureSpace(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes', 14, finalY);
      finalY += 5;
      doc.setFont('helvetica', 'normal');
      const noteLines = doc.splitTextToSize(quotation.notes, pageWidth - 28);
      noteLines.forEach(line => { ensureSpace(); doc.text(line, 14, finalY); finalY += 4.5; });
      finalY += 3;
    }

    // Terms & Conditions
    ensureSpace(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', 14, finalY);
    finalY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const terms = (quotation.terms || '').split('\n').filter(Boolean);
    terms.forEach(term => {
      const lines = doc.splitTextToSize(term, pageWidth - 28);
      lines.forEach(line => { ensureSpace(); doc.text(line, 14, finalY); finalY += 4; });
    });

    // Signature - leaves extra room at the bottom when a seal image will be
    // drawn between "For <company>" and "Authorized Signature", matching the
    // layout of the original sample quotation PDFs. With long T&C (e.g. the
    // rental terms), content can already end close to the page bottom - if
    // the signature+seal block wouldn't fully fit below it, push the whole
    // block onto a fresh page instead of letting it silently run off the edge.
    const seal = COMPANY_SEALS[company.id];
    const sigBlockHeight = seal ? 45 : 35;
    if (finalY + 15 + sigBlockHeight > pageHeight) {
      doc.addPage();
      finalY = 20;
    } else {
      finalY = Math.max(finalY + 15, pageHeight - sigBlockHeight);
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Yours faithfully,', pageWidth - 14, finalY, { align: 'right' });
    doc.text(`For ${company.name}`, pageWidth - 14, finalY + 5, { align: 'right' });
    if (seal) {
      const sealSize = 20;
      doc.addImage(seal, 'PNG', pageWidth - 14 - sealSize, finalY + 8, sealSize, sealSize);
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signature', pageWidth - 14, finalY + (seal ? 32 : 20), { align: 'right' });

    return doc;
  };

  const getQuotationFilename = (quotation) =>
    `${quotation.quoteNumber.replace(/\\/g, '-')}_${(quotation.clientName || 'quotation').replace(/[^a-z0-9]/gi, '_')}.pdf`;

  const generateQuotationPDF = (quotation) => {
    const doc = buildQuotationPDFDoc(quotation);
    doc.save(getQuotationFilename(quotation));
  };

  // Base64url-encodes a string (Gmail API requires URL-safe base64, not
  // standard base64, for the raw message field).
  const base64UrlEncode = (str) => {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  // Sends the quotation PDF by email, as the currently logged-in user's own
  // Gmail account, via the Gmail API. Requires the user to have granted the
  // gmail.send permission (captured in googleAccessToken on Google
  // login/connect). Falls back to downloading the PDF + opening a pre-filled
  // mailto: link if no valid token is available, so the feature never just
  // silently fails - the user can still send it manually with one extra step.
  const sendQuotationEmail = async (quotation, recipientEmail) => {
    const emailCompany = COMPANIES[quotation.companyId] || COMPANIES.rb_infotech;
    const subject = `Quotation ${quotation.quoteNumber} from ${emailCompany.name}`;
    const bodyText = `Dear ${quotation.clientName},\n\nPlease find attached our quotation ${quotation.quoteNumber} as requested.\n\n${quotation.notes || ''}\n\nThanking you,\n${emailCompany.name}`;

    const sendViaMailtoFallback = (reason) => {
      generateQuotationPDF(quotation);
      const mailtoUrl = `mailto:${encodeURIComponent(recipientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
      window.open(mailtoUrl, '_blank');
      alert(`⚠️ ${reason}\n\nThe PDF has been downloaded, and your email app should open with the message pre-filled - please attach the downloaded PDF manually before sending.`);
    };

    // IMPORTANT: use this local variable for the token throughout this
    // function, never the `googleAccessToken` state directly - React state
    // setters don't take effect until the next render, so reading state
    // right after calling connectGmailForSending() would see the OLD value
    // and every send would fail with a bogus auth error.
    let token = googleAccessToken;
    if (!token) {
      token = await connectGmailForSending();
      if (!token) {
        sendViaMailtoFallback('Gmail sending isn\'t connected yet.');
        return;
      }
    }

    const attemptSend = async (authToken) => {
      const doc = buildQuotationPDFDoc(quotation);
      const pdfBase64 = doc.output('datauristring').split(',')[1]; // strip the data: prefix

      const boundary = 'boundary_' + Date.now();
      const mimeMessage =
        `To: ${recipientEmail}\r\n` +
        `Subject: ${subject}\r\n` +
        `MIME-Version: 1.0\r\n` +
        `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n` +
        `--${boundary}\r\n` +
        `Content-Type: text/plain; charset="UTF-8"\r\n\r\n` +
        `${bodyText}\r\n\r\n` +
        `--${boundary}\r\n` +
        `Content-Type: application/pdf; name="${getQuotationFilename(quotation)}"\r\n` +
        `Content-Disposition: attachment; filename="${getQuotationFilename(quotation)}"\r\n` +
        `Content-Transfer-Encoding: base64\r\n\r\n` +
        `${pdfBase64}\r\n` +
        `--${boundary}--`;

      return fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ raw: base64UrlEncode(mimeMessage) })
      });
    };

    try {
      let response = await attemptSend(token);

      if (!response.ok && response.status === 401) {
        // Token expired or invalid - get a fresh one and retry ONCE with it
        // directly (not via state, same reasoning as above).
        const freshToken = await connectGmailForSending();
        if (!freshToken) {
          throw new Error('Could not refresh Gmail permission.');
        }
        token = freshToken;
        response = await attemptSend(token);
      }

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error?.message || `Gmail API error (${response.status})`);
      }

      alert(`✅ Quotation ${quotation.quoteNumber} emailed to ${recipientEmail} from ${currentUser?.email}.`);
      await logAudit('UPDATE', 'Quotation', quotation.id, `Emailed ${quotation.quoteNumber} to ${recipientEmail}`, {});
    } catch (error) {
      sendViaMailtoFallback(`Couldn't send via Gmail: ${error.message}`);
    }
  };

  // Permission helper
  // ---- Module access (User Management > Module access). For each module a user can be given No access / Read only / Read & write.
  // Nothing set = the role defaults below behave exactly as before. Like the other permissions, this is enforced in the app (screens and
  // buttons), not in the database rules.
  const ACCESS_MODULES = [
    { key: 'leads', label: 'Leads', nav: 'leads', editFlag: 'canEditLeads', icon: TrendingUp },
    { key: 'inbox', label: 'Email Inbox', nav: 'inbox', editFlag: 'canEditInbox', icon: Mail },
    { key: 'reminders', label: 'Reminders', nav: 'reminders', editFlag: 'canEditReminders', icon: Calendar },
    { key: 'assets', label: 'Assets', nav: 'printers', editFlag: 'canEditAssets', icon: Printer },
    { key: 'clients', label: 'Clients', nav: 'clients', editFlag: 'canEditClients', icon: Users },
    { key: 'rentals', label: 'Rentals', nav: 'rentals', editFlag: 'canEditRentals', icon: FileText },
    { key: 'service', label: 'Service', nav: 'service', editFlag: 'canEditServiceCalls', icon: Wrench },
    { key: 'quotations', label: 'Quotations', nav: 'quotations', editFlag: 'canCreateQuotations', icon: FileText },
    { key: 'invoices', label: 'Invoices / DC / Proforma', nav: 'invoices', editFlag: 'canManageInvoices', createOnly: true, icon: FileText },
    { key: 'tally', label: 'Tally', nav: 'tally', editFlag: 'canViewTally', icon: FileText },
    { key: 'engineers', label: 'Engineers', nav: 'engineers', editFlag: 'canEditEngineers', icon: Users },
    { key: 'readings', label: 'Meter Readings', nav: 'readings', editFlag: 'canEditReadings', icon: TrendingUp },
    { key: 'reports', label: 'Reports', nav: 'reports', readOnly: true, icon: FileText },
    { key: 'operations', label: 'Daily Operations', nav: 'operations', readOnly: true, icon: Calendar },
    { key: 'stock', label: 'Stock / Stores', nav: 'stock', icon: FileText },
    { key: 'purchase', label: 'Purchase Orders', nav: 'purchase', editFlag: 'canManagePurchase', createOnly: true, icon: ShoppingCart }
  ];

  // Named job roles. Picking one in User Management > Edit User sets the base role, the permission ticks and every module's access in one go.
  // Built from how each login is actually used (see Audit Logs). Anything not listed for a module is "No access". Still editable per user afterwards.
  const ROLE_PRESETS = {
    accounts: { label: 'Accounts', base: 'admin', note: 'Invoices, approvals, Tally, read-only on the business data. Cannot edit assets, clients or service.',
      perms: { canManageInvoices: true, canApproveInvoices: true, canViewTally: true, canDelete: false, canImport: false, canExport: true, canEditAssets: false, canEditClients: false, canEditRentals: false, canEditReadings: false, canCreateQuotations: false },
      mods: { invoices: 'rw', tally: 'rw', clients: 'r', rentals: 'r', assets: 'r', readings: 'r', quotations: 'r', reports: 'r' } },
    backoffice: { label: 'Back office / Data entry', base: 'manager', note: 'Enters assets, rentals, clients, readings and quotations. Can import; cannot delete or touch invoices and Tally.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: true, canExport: true, canEditAssets: true, canEditClients: true, canEditRentals: true, canEditReadings: true, canCreateQuotations: true },
      mods: { assets: 'rw', clients: 'rw', rentals: 'rw', readings: 'rw', quotations: 'rw', service: 'r', reminders: 'rw', leads: 'r' } },
    sales: { label: 'Sales & Quotations', base: 'manager', note: 'Leads, clients, quotations and new rentals. No delete, no invoices, no service.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: true, canEditAssets: true, canEditClients: true, canEditRentals: true, canEditReadings: false, canCreateQuotations: true },
      mods: { leads: 'rw', inbox: 'rw', reminders: 'rw', clients: 'rw', quotations: 'rw', rentals: 'rw', assets: 'rw', service: 'r', readings: 'r' } },
    opshead: { label: 'Daily Operations Head', base: 'manager', note: 'Runs the day: Daily Operations, client allocation, call assignment, engineers, stores, reports.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: true, canEditAssets: false, canEditClients: true, canEditRentals: false, canEditReadings: true, canCreateQuotations: true },
      mods: { operations: 'r', service: 'rw', engineers: 'rw', clients: 'rw', readings: 'rw', stock: 'rw', assets: 'r', rentals: 'r', quotations: 'r', reminders: 'rw', reports: 'r' } },
    coordinator: { label: 'Service Coordinator', base: 'manager', note: 'Books and updates service calls, client-owned assets and service quotations.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: true, canEditAssets: true, canEditClients: true, canEditRentals: false, canEditReadings: true, canCreateQuotations: true },
      mods: { service: 'rw', clients: 'rw', assets: 'rw', quotations: 'rw', readings: 'rw', engineers: 'r', stock: 'r', rentals: 'r', reminders: 'rw' } },
    storekeeper: { label: 'Store Keeper', base: 'user', note: 'Stock / Stores only: receive, issue, take back. Can see assets and service calls but not edit.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: true, canEditAssets: false, canEditClients: false, canEditRentals: false, canEditReadings: false, canCreateQuotations: false },
      mods: { stock: 'rw', assets: 'r', service: 'r' } },
    engineer: { label: 'Field Engineer', base: 'field_engineer', note: 'Sees only their own calls, completes them, records readings and quotations. Sees stock but cannot change it.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: false },
      mods: { service: 'rw', clients: 'r', assets: 'r', readings: 'rw', quotations: 'rw', stock: 'r' } },
    viewer: { label: 'View only', base: 'user', note: 'Can look at the business data and export; cannot change anything. No invoices or Tally.',
      perms: { canManageInvoices: false, canApproveInvoices: false, canViewTally: false, canDelete: false, canImport: false, canExport: true, canEditAssets: false, canEditClients: false, canEditRentals: false, canEditReadings: false, canCreateQuotations: false },
      mods: { leads: 'r', assets: 'r', clients: 'r', rentals: 'r', service: 'r', readings: 'r', quotations: 'r', reports: 'r' } }
  };
  const moduleMapFor = (preset) => Object.fromEntries(ACCESS_MODULES.map(m => [m.key, preset.mods[m.key] || 'none']));
  const VIEW_FLAG_MODULE = { canViewServiceCalls: 'service', canViewClientAssets: 'service', canViewClients: 'clients', canViewReadings: 'readings' };
  // New per-module edit flags that used to be covered by a broader flag
  const LEGACY_EDIT_FLAG = { canEditLeads: 'canEditClients', canEditInbox: 'canEditClients', canEditReminders: 'canEditClients', canEditEngineers: 'canEditAssets' };
  const moduleOverride = (moduleKey) => (userRole === 'super_admin' ? 'rw' : (userModuleAccess[moduleKey] || ''));
  const moduleOfNav = (navId) => (ACCESS_MODULES.find(m => m.nav === navId) || {}).key;

  const hasPermission = (permission) => {
    if (userRole === 'super_admin') return true;
    const editMod = ACCESS_MODULES.find(m => m.editFlag === permission);
    const modKey = editMod ? editMod.key : VIEW_FLAG_MODULE[permission];
    const level = modKey ? userModuleAccess[modKey] : '';
    if (level) return editMod ? level === 'rw' : level !== 'none';     // editing needs Read & write; viewing needs at least Read
    if (LEGACY_EDIT_FLAG[permission]) return hasPermission(LEGACY_EDIT_FLAG[permission]);
    if (userPermissions[permission] !== undefined) return userPermissions[permission];
    // Fallback for users created before a given permission flag existed on
    // their stored record (e.g. canCreateQuotations added later) - infer a
    // sensible default from their role instead of silently denying access.
    if (permission === 'canCreateQuotations') {
      return ['admin', 'manager', 'field_engineer'].includes(userRole);
    }
    if (permission === 'canManageInvoices' || permission === 'canManagePurchase') {
      return ['admin', 'manager'].includes(userRole);
    }
    if (permission === 'canApproveInvoices' || permission === 'canManagePriceLocks') {
      return false;     // only people explicitly given it (User Management > Edit user) - the super admin always has it
    }
    return false;
  };
  // Can the user open Tally / Invoices at all (Read or Read & write)?
  const canSeeTally = () => (moduleOverride('tally') ? moduleOverride('tally') !== 'none' : hasPermission('canViewTally'));
  const canSeeInvoices = () => (moduleOverride('invoices') ? moduleOverride('invoices') !== 'none' : hasPermission('canManageInvoices'));
  // Daily Operations: admin / manager / super admin by default; others only when given Read access in User Management
  const canApproveInvoices = () => hasPermission('canApproveInvoices');
  // ---- Stock <-> service calls / clients
  const normStockName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const lowStockItems = () => stockItems.filter(i => i.active !== false && Number(i.minQty) > 0 && stockOnHand(i.id) <= Number(i.minQty));
  const stockVendors = () => [...new Set([...stockItems.map(i => i.vendor), ...stockMoves.map(m => m.vendor)].map(v => String(v || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const stockMovesForCall = (callId) => stockMoves.filter(m => m.callId === callId && (m.type === 'issue' || m.type === 'use'));
  // supplied to a client = issued/used for them and no longer carried by an engineer
  const stockSuppliedTo = (clientName) => stockMoves.filter(m => (m.type === 'issue' || m.type === 'use') && m.clientName && normStockName(m.clientName) === normStockName(clientName) && (m.consumed || !m.engineer));
  const engineerHolding = (engineer, itemId) => stockMoves.reduce((n, m) => (m.engineer === engineer && m.itemId === itemId ? n + (m.type === 'issue' && !m.consumed ? m.qty : m.type === 'return' || m.type === 'use' ? -m.qty : 0) : n), 0);
  const stockMoveLines = (clientName) => stockSuppliedTo(clientName).filter(m => !m.billedTo).map(m => { const it = stockItems.find(i => i.id === m.itemId); return { move: m, line: { description: `${m.itemName}${m.callId ? ' (service call)' : ''}`, hsn: '', qty: m.qty, rate: it && it.rate ? it.rate : '', stockMoveId: m.id } }; });
  const canSeeStock = () => (moduleOverride('stock') ? moduleOverride('stock') !== 'none' : ['admin', 'manager'].includes(userRole));
  const canSeePurchase = () => (moduleOverride('purchase') ? moduleOverride('purchase') !== 'none' : ['admin', 'manager'].includes(userRole));
  const canSeeOperations = () => (moduleOverride('operations') ? moduleOverride('operations') !== 'none' : !['user', 'field_engineer', 'client'].includes(userRole));
  // Can the user CREATE documents in a module? ('Create only' and 'Read & write' can; with no override the role's own edit flag decides.)
  const canCreateIn = (moduleKey) => {
    if (userRole === 'super_admin') return true;
    const level = userModuleAccess[moduleKey] || '';
    if (level) return level === 'create' || level === 'rw';
    const m = ACCESS_MODULES.find(x => x.key === moduleKey);
    return !!m && hasPermission(m.editFlag);
  };
  // ---- Price lock per customer: a client with priceLock on has a fixed price list (client.priceList = [{ item, rate }]).
  // Everyone except the super admin must use those items at those rates in quotations, invoices and proformas.
  const normItem = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const priceLockOf = (clientId, clientName) => {
    const c = clients.find(x => x.id === clientId) || (clientName ? findClientByName(clients, clientName) : null);
    return c && c.priceLock && (c.priceList || []).some(e => normItem(e.item)) ? c : null;
  };
  const priceMatch = (c, description) => {
    const d = normItem(description); let best = null;
    (c.priceList || []).forEach(e => { const k = normItem(e.item); if (k && d.startsWith(k) && (!best || k.length > normItem(best.item).length)) best = e; });
    return best;
  };
  const priceLockIssues = (clientId, clientName, items) => {
    if (userRole === 'super_admin') return [];
    const c = priceLockOf(clientId, clientName);
    if (!c) return [];
    return (items || []).filter(i => i.description || i.rate).map(i => {
      const m = priceMatch(c, i.description);
      if (!m) return `"${String(i.description || '').slice(0, 50)}" is not in ${c.name}'s locked price list.`;
      if (Number(i.rate) !== Number(m.rate)) return `"${m.item}" is locked at ₹${m.rate} for ${c.name} (you entered ₹${i.rate}).`;
      return '';
    }).filter(Boolean);
  };
  // ---- Rental rate lock: client.rentalRates = [{ typeKey, rentalType, monthlyRate, a4FreePages, a4PageCost, ... }] (typeKey = A4/A3/MFP/MFP_Colour/Colour printer type, or Desktop/Laptop/Scanner)
  const RENTAL_RATE_FIELDS = ['monthlyRate', 'a4FreePages', 'a4PageCost', 'a3FreePages', 'a3PageCost', 'colourA4FreePages', 'colourA4PageCost', 'colourA3FreePages', 'colourA3PageCost', 'cartridgeCost'];
  const RENTAL_TYPE_KEYS = [['A4', 'A4 printer'], ['A3', 'A3 printer'], ['MFP', 'MFP B/W'], ['MFP_Colour', 'MFP Colour'], ['Colour', 'Colour printer'], ['Desktop', 'Desktop'], ['Laptop', 'Laptop'], ['Scanner', 'Scanner']];
  const rentalTypeKeyOf = (r) => ((r.category || 'Printer') === 'Printer' ? (r.printerType || r.type || 'A4') : r.category);
  const rentalLockClient = (clientName) => {
    const c = clientName ? findClientByName(clients, clientName) : null;
    return c && c.priceLock && (c.rentalRates || []).length ? c : null;
  };
  const rentalLockEntry = (r) => {
    const c = rentalLockClient(r.clientName);
    if (!c) return null;
    const key = rentalTypeKeyOf(r), rt = r.rentalType || 'regular';
    return (c.rentalRates || []).find(e => e.typeKey === key && (e.rentalType || 'regular') === rt) || null;
  };
  const rentalLockIssues = (r) => {
    if (userRole === 'super_admin') return [];
    const c = rentalLockClient(r.clientName);
    if (!c) return [];
    const e = rentalLockEntry(r);
    const label = (RENTAL_TYPE_KEYS.find(x => x[0] === rentalTypeKeyOf(r)) || [0, rentalTypeKeyOf(r)])[1];
    if (!e) return [`${c.name} has no locked rental rate for "${label}" (${r.rentalType || 'regular'}).`];
    return RENTAL_RATE_FIELDS.filter(f => e[f] !== undefined && e[f] !== '' && e[f] !== null && Number(r[f] || 0) !== Number(e[f])).map(f => `${f} is locked at ${e[f]} for ${c.name} (you entered ${r[f] || 0}).`);
  };
  // Who may edit a customer's locked price list / rental rates: super admin, or anyone given "Set client price locks"
  const canSetLocks = () => userRole === 'super_admin' || hasPermission('canManagePriceLocks');
  // Approval rule: an invoice needs an approver (accounts) unless every price on it is locked for that customer
  // (client price list for sales invoices, locked rental rates for monthly rental billing).
  const invoiceFullyLocked = (inv) => {
    const c = clients.find(x => x.id === inv.clientId) || findClientByName(clients, inv.clientName);
    if (!c || !c.priceLock) return false;
    if (inv.source === 'billing') return (c.rentalRates || []).length > 0;
    const list = c.priceList || [];
    if (!list.some(e => normItem(e.item))) return false;
    const lines = (inv.items || []).filter(i => i.description || i.rate);
    return lines.length > 0 && lines.every(i => { const m = priceMatch(c, i.description); return m && Number(m.rate) === Number(i.rate); });
  };
  const invoiceNeedsApproval = (inv) => invCfg.requireApproval === true && !invoiceFullyLocked(inv);
  const canWriteTally = () => userRole === 'super_admin' || (moduleOverride('tally') ? moduleOverride('tally') === 'rw' : hasPermission('canViewTally'));

  // Check auth and load user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth State Changed:', user ? user.email : 'No user');
      setCurrentUser(user);
      if (user) {
        try {
          console.log('📖 Fetching user doc for UID:', user.uid);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          console.log('📄 User doc exists?', userDoc.exists());
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('✅ User data loaded:', userData);
            console.log('👤 Role:', userData.role);
            console.log('🔑 Permissions:', userData.permissions);
            
            setUserRole(userData.role);
            setLinkedClientId(userData.linkedClientId || '');
            setUserPermissions(userData.permissions || {});
            setUserModuleAccess(userData.moduleAccess || {});
            console.log('✨ State set - Role:', userData.role);
            
            // Check URL for asset QR scan
            const urlParams = new URLSearchParams(window.location.search);
            const scannedAsset = urlParams.get('asset');
            if (scannedAsset) {
              setCurrentView('printers');
              setQrScannedAssetSearch(scannedAsset);
              // Clear URL parameter
              window.history.replaceState({}, '', window.location.pathname);
            }
          } else {
            console.log('⚠️ No user document found, creating for:', user.email);
            // First time super admin
            if (user.email.includes('basavaraj') || user.email === 'admin@printerpro.com') {
              const defaultUser = {
                email: user.email,
                role: 'super_admin',
                permissions: {
                  canManageUsers: true,
                  canEditAssets: true,
                  canEditClients: true,
                  canEditRentals: true,
                  canDelete: true,
                  canImport: true,
                  canExport: true
                },
                createdAt: new Date()
              };
              console.log('🚀 Creating super admin doc...');
              await setDoc(doc(db, 'users', user.uid), defaultUser);
              setUserRole('super_admin');
              setUserPermissions(defaultUser.permissions);
              console.log('✅ Super admin created!');
            } else {
              // IMPORTANT: never silently fall back to a low-privilege role here.
              // If this account genuinely has no user record, show a clear error
              // instead of quietly granting "client" access - that hides real
              // problems (wrong UID, deleted record, etc.) behind a confusing,
              // silently-wrong permission level.
              console.error('⛔ No user record found for', user.email, '- refusing to guess a role.');
              setAuthSetupError(`No account record found for ${user.email}.\n\nThis can happen if:\n• You signed in with Google for the first time, and it created a different login identity than your existing email/password account\n• Your account record was deleted from the Users list\n\nPlease sign in with your usual email/password, or contact your Super Admin to have your access set up.`);
              await signOut(auth);
            }
          }
        } catch (error) {
          console.error('❌ Error loading user data:', error);
          console.error('Error details:', error.message, error.code);
          // Same principle: an error reading the account record should never
          // silently become "client" access - show what actually went wrong.
          setAuthSetupError(`Error loading your account: ${error.message}\n\nPlease try logging in again. If this keeps happening, contact your Super Admin.`);
          await signOut(auth);
        }
      } else {
        setUserRole(null);
        setUserModuleAccess({});
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Live-sync the operational collections from Firestore via onSnapshot,
  // instead of a one-time getDocs() at login. Previously, once a tab loaded
  // its data it never saw writes from any OTHER tab/user again unless some
  // action handler happened to manually refetch - which was applied
  // inconsistently across the app, and was the direct cause of the
  // duplicate-quote-number bug (two saves computed the "next number" from
  // local state that hadn't caught up with each other yet). onSnapshot fixes
  // that at the source: every connected client now sees every write, from
  // anyone, immediately - no per-action refetching needed anymore.
  //
  // auditLogs is deliberately kept as a one-time fetch below, not live: it
  // gets written on nearly every action across the whole team, and nobody
  // needs the Audit Logs page to update in real time - making it live would
  // mean paying for a constantly-churning listener for no real benefit.
  useEffect(() => {
    if (!currentUser || !userRole) return;

    // A client login only ever reads its own client record and the records carrying its client name (the
    // database rules enforce the same), so it never subscribes to whole collections.
    if (userRole === 'client') {
      if (!linkedClientId) return;
      let cancelled = false;
      const unsubs = [];
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'clients', linkedClientId));
          if (cancelled || !snap.exists()) return;
          const c = { id: snap.id, ...snap.data() };
          setClients([c]);
          const scoped = (name, setter) => onSnapshot(
            query(collection(db, name), where('clientName', '==', c.name)),
            s => setter(s.docs.map(d => ({ id: d.id, ...d.data() }))),
            err => console.error(`Error syncing ${name}:`, err)
          );
          unsubs.push(scoped('rentals', setRentals), scoped('serviceCalls', setServiceCalls), scoped('quotations', setQuotations),
            scoped('meterReadings', setMeterReadings), scoped('clientAssets', setClientAssets));
        } catch (err) { console.error('Could not load the client portal data:', err); }
      })();
      return () => { cancelled = true; unsubs.forEach(u => u()); };
    }

    const subscribeCollection = (name, setter, sortFn) => onSnapshot(
      collection(db, name),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setter(sortFn ? docs.sort(sortFn) : docs);
      },
      err => console.error(`Error syncing ${name}:`, err)
    );

    const unsubscribers = [
      subscribeCollection('printers', setPrinters),
      subscribeCollection('clients', setClients),
      subscribeCollection('rentals', setRentals),
      subscribeCollection('serviceCalls', setServiceCalls),
      subscribeCollection('engineers', setEngineers),
      subscribeCollection('meterReadings', setMeterReadings),
      subscribeCollection('stockItems', setStockItemsRaw),
      subscribeCollection('stockMoves', setStockMovesRaw),
      subscribeCollection('purchaseOrders', setPurchaseOrdersRaw),
      subscribeCollection('vendors', setVendorsRaw),
      subscribeCollection('printerMovements', setPrinterMovements, (a, b) => new Date(b.date) - new Date(a.date)),
      subscribeCollection('clientAssets', setClientAssets),
      subscribeCollection('quotations', setQuotations, (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
      subscribeCollection('leads', setLeads, (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
      onSnapshot(doc(db, 'config', 'emailInbox'), snap => setEmailRunStatus(snap.exists() ? snap.data() : null), () => {}),
      subscribeCollection('reminders', setRemindersRaw),
      subscribeCollection('invoices', setInvoicesRaw),
      subscribeCollection('challans', setChallansRaw),
      subscribeCollection('proformas', setProformasRaw),
      onSnapshot(doc(db, 'config', 'invoice'), snap => setInvCfgRaw(snap.exists() ? snap.data() : null), () => {}),
      onSnapshot(doc(db, 'config', 'tally'), snap => setTallyCfgRaw(snap.exists() ? snap.data() : null), () => {}),
      onSnapshot(doc(db, 'config', 'tallyAgent'), snap => setTallyAgent(snap.exists() ? snap.data() : null), () => {}),
      subscribeCollection('tallyQueue', setTallyQueueRaw, (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))),
      subscribeCollection('tallyOutstanding', setTallyOutstandingRaw),
      subscribeCollection('tallyReceipts', setTallyReceiptsRaw),
      subscribeCollection('tallyInvoices', setTallyInvoicesRaw),
      subscribeCollection('tallyLedgers', setTallyLedgersRaw),
      onSnapshot(doc(db, 'config', 'assetCounters'), snap => { setAssetCounters(snap.exists() ? snap.data() : {}); }, () => {}),
      subscribeCollection('inboxMails', setInboxMails, (a, b) => new Date(b.lastReceivedAt || 0) - new Date(a.lastReceivedAt || 0)),
      subscribeCollection('clientActivities', setClientActivities, (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
      subscribeCollection('marketingHighlights', setMarketingHighlights)
    ];

    (async () => {
      try {
        const auditSnap = await getDocs(collection(db, 'auditLogs'));
        setAuditLogs(auditSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      } catch (error) {
        console.error('Error loading audit logs:', error);
      }
    })();

    return () => unsubscribers.forEach(unsub => unsub());
  }, [currentUser, userRole, linkedClientId]);

  // Load system users
  useEffect(() => {
    const loadUsers = async () => {
      if (currentUser && userRole === 'super_admin') {
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setSystemUsers(users);

          const backupSnapshot = await getDocs(collection(db, 'backupLogs'));
          setBackupLogs(backupSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)));
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }
    };
    loadUsers();
  }, [currentUser, userRole]);

  // Firestore collection name map
  const FS_COLLECTIONS = {
    printers: 'printers',
    clients: 'clients',
    rentals: 'rentals',
    serviceCalls: 'serviceCalls',
    engineers: 'engineers',
    readings: 'meterReadings',
    auditLogs: 'auditLogs',
    quotations: 'quotations'
  };

  // Delete single doc from Firestore
  const deleteFirestoreDoc = async (collKey, id) => {
    const collName = FS_COLLECTIONS[collKey] || collKey;
    await deleteDoc(doc(db, collName, id.toString()));
  };

  // Batch save array of docs to Firestore (for imports)
  const batchSaveDocs = async (collKey, items) => {
    const collName = FS_COLLECTIONS[collKey] || collKey;
    const batch = writeBatch(db);
    items.forEach(item => {
      const id = item.id ? item.id.toString() : Date.now().toString();
      batch.set(doc(db, collName, id), { ...item, id });
    });
    await batch.commit();
  };

  // Audit logging function
  const logAudit = async (action, entityType, entityId, entityName, changes = {}) => {
    const auditEntry = {
      id: `AUD${Date.now()}`,
      timestamp: new Date().toISOString(),
      userEmail: currentUser?.email || 'Unknown',
      userId: currentUser?.uid || 'Unknown',
      companyId: currentCompany,
      action,
      entityType,
      entityId,
      entityName,
      changes,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString()
    };
    try {
      await setDoc(doc(db, 'auditLogs', auditEntry.id), auditEntry);
      setAuditLogs(prev => [auditEntry, ...prev]);
    } catch (err) {
      console.error('Audit log error:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setAuthSetupError('');
    try {
      const result = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      
      // Log login
      try {
        const loginEntry = {
          id: `AUD${Date.now()}`,
          timestamp: new Date().toISOString(),
          userEmail: result.user.email,
          userId: result.user.uid,
          companyId: currentCompany,
          action: 'LOGIN',
          entityType: 'Auth',
          entityId: result.user.uid,
          entityName: result.user.email,
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString()
        };
        await setDoc(doc(db, 'auditLogs', loginEntry.id), loginEntry);
      } catch (e) { console.log('Login audit error:', e); }

      // Track last-login per user, surfaced as a column in User Management -
      // separate from the auditLogs trail above (that's the full history,
      // this is just "when did this account last actually sign in").
      try {
        await setDoc(doc(db, 'users', result.user.uid), { lastLogin: new Date().toISOString() }, { merge: true });
      } catch (e) { console.log('lastLogin update error:', e); }

      // Auto-setup super admin on first login
      if (result.user.email === 'basavaraj@rbinfo.in' || result.user.email.includes('basavaraj')) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          name: 'Basavaraj',
          role: 'super_admin',
          // Was a hardcoded, incomplete permission list (missing
          // canEditReadings/canCreateQuotations etc., added to other roles
          // since) - use the same source of truth as every other role grant.
          permissions: getPermissionsByRole('super_admin'),
          createdAt: new Date(),
          isActive: true
        }, { merge: true });
        
        console.log('✅ Super admin setup complete!');
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (error) {
      setLoginError('Invalid email or password');
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setAuthSetupError('');
    try {
      const provider = new GoogleAuthProvider();
      // Request permission to send email as this user's own Gmail account,
      // used by the "Email Quotation" feature. Google will show a consent
      // screen the first time asking to allow this - that's expected.
      provider.addScope('https://www.googleapis.com/auth/gmail.send');
      const result = await signInWithPopup(auth, provider);

      // Capture the OAuth access token so quotations can be emailed directly
      // from the logged-in user's own Gmail. This token is short-lived
      // (~1 hour) and kept only in memory - never persisted to storage.
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        setGoogleAccessToken(credential.accessToken);
      }
      
      // Check if user exists in Firestore under this Google-Auth UID
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        // NOTE: Firebase treats email/password and Google sign-in as separate
        // accounts (different UIDs) even for the same email address. We'd
        // previously tried to detect and warn about this by scanning the
        // whole `users` collection for a matching email - but that requires
        // broad read access that Firestore security rules correctly deny to
        // a brand-new user (who has no `users/{uid}` doc yet), which caused
        // EVERY first-time Google sign-in to fail with a permissions error.
        // Removed - if someone accidentally uses Google instead of their
        // usual email/password login, a Super Admin can spot and correct it
        // via User Management rather than the client trying to self-detect it.
        const googleSsoUser = {
          email: result.user.email,
          name: result.user.displayName || 'User',
          role: 'field_engineer', // Default role for Google SSO
          permissions: {
            canViewServiceCalls: true,
            canEditServiceCalls: true,
            canViewClients: true,
            canViewClientAssets: true,
            canViewReadings: true,
            canEditReadings: true
          },
          createdAt: new Date(),
          isActive: true,
          loginMethod: 'google'
        };
        await setDoc(doc(db, 'users', result.user.uid), googleSsoUser);
        // A new account with real access just self-provisioned - a Super
        // Admin should be able to see that in the trail, same as one they
        // create by hand in User Management.
        await logAudit('CREATE', 'User', result.user.uid, `${googleSsoUser.email} (self-registered via Google)`, googleSsoUser);
      }

      // Google sign-in previously wrote neither a LOGIN audit entry nor
      // lastLogin - meaning Google-only users were invisible in the "Logins
      // Only" audit filter and in User Management's Last Login column.
      try {
        const loginEntry = {
          id: `AUD${Date.now()}`,
          timestamp: new Date().toISOString(),
          userEmail: result.user.email,
          userId: result.user.uid,
          companyId: currentCompany,
          action: 'LOGIN',
          entityType: 'Auth',
          entityId: result.user.uid,
          entityName: result.user.email,
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString()
        };
        await setDoc(doc(db, 'auditLogs', loginEntry.id), loginEntry);
        await setDoc(doc(db, 'users', result.user.uid), { lastLogin: new Date().toISOString() }, { merge: true });
      } catch (e) { console.log('Google login audit/lastLogin error:', e); }
    } catch (error) {
      setAuthSetupError(`Google login failed: ${error.message} (${error.code || 'unknown error'})`);
    }
  };

  // Lets someone who's already logged in (whether via Google or email/
  // password) grant Gmail-sending permission without having to log out and
  // back in - used by the "Email Quotation" feature the first time it's
  // used, or whenever the ~1 hour token has expired.
  //
  // IMPORTANT: this uses linkWithPopup/reauthenticateWithPopup, NOT
  // signInWithPopup - the latter would treat this as a fresh sign-in and
  // could switch the active session to a completely different Firebase user
  // if the Google account isn't already linked to this one, silently
  // changing the logged-in person's role/permissions mid-session (the exact
  // class of auth bug this app hit once before).
  const connectGmailForSending = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/gmail.send');
      provider.setCustomParameters({ login_hint: currentUser?.email || '' });

      let result;
      const alreadyLinkedGoogle = currentUser?.providerData?.some(p => p.providerId === 'google.com');
      if (alreadyLinkedGoogle) {
        // Already signed in via Google - just re-auth to refresh the token
        // and grant the new scope, staying on the exact same account.
        result = await reauthenticateWithPopup(currentUser, provider);
      } else {
        // Signed in via email/password - LINK a Google credential to this
        // same account instead of starting a new sign-in, so the session
        // never changes identity.
        result = await linkWithPopup(currentUser, provider);
      }

      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        setGoogleAccessToken(credential.accessToken);
        // Return the token directly (not just true) - setGoogleAccessToken()
        // won't be reflected in the `googleAccessToken` state variable until
        // the next render, so any caller that needs to use the token RIGHT
        // AWAY in this same function call must use this return value, not
        // the state variable, or it'll silently use the old (often empty)
        // value and every send will fail.
        return credential.accessToken;
      }
      return null;
    } catch (error) {
      if (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use') {
        alert('❌ This Google account is already linked to a different login in this system. Please contact your Super Admin.');
      } else {
        alert(`❌ Could not connect Gmail: ${error.message}`);
      }
      return null;
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setCurrentView('dashboard');
  };

  // Excel Export
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // Delete item from any collection
  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    // Find full item record (needed for audit log + potential restore) and a display name
    let itemName = id;
    let fullRecord = null;
    let entityTypeLabel = type;
    if (type === 'printers') { fullRecord = printers.find(p => p.id === id); itemName = fullRecord?.assetNumber || id; entityTypeLabel = 'Asset'; }
    else if (type === 'clients') { fullRecord = clients.find(c => c.id === id); itemName = fullRecord?.name || id; entityTypeLabel = 'Client'; }
    else if (type === 'engineers') { fullRecord = engineers.find(e => e.id === id); itemName = fullRecord?.name || id; entityTypeLabel = 'Engineer'; }
    else if (type === 'rentals') { fullRecord = rentals.find(r => r.id === id); itemName = fullRecord?.clientName || id; entityTypeLabel = 'Rental'; }
    else if (type === 'readings') { fullRecord = meterReadings.find(r => r.id === id); itemName = fullRecord?.clientName || id; entityTypeLabel = 'MeterReading'; }
    else if (type === 'serviceCalls') { fullRecord = serviceCalls.find(s => s.id === id); itemName = fullRecord?.clientName || id; entityTypeLabel = 'ServiceCall'; }
    
    try {
      await deleteFirestoreDoc(type, id);
      // Log the full record (not just a name) so a Super Admin can restore it later from Audit Logs
      await logAudit('DELETE', entityTypeLabel, id, itemName, fullRecord || {});
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting item. Please try again.');
      return;
    }
    if (type === 'printers') {
    } else if (type === 'clients') {
    } else if (type === 'engineers') {
    } else if (type === 'readings') {
    } else if (type === 'rentals') {
    } else if (type === 'serviceCalls') {
    }
  };

  // Excel Import Handler
  const handleExcelImport = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      try {
        if (type === 'printers') {
          const prefixMap = getAssetPrefixMap(currentCompany);
          const counters = {Printer: 0, Desktop: 0, Laptop: 0, Scanner: 0};
          printers.forEach(p => {
            if (p.assetNumber) {
              Object.entries(prefixMap).forEach(([cat, prefix]) => {
                if (p.assetNumber.startsWith(prefix)) {
                  const num = parseInt(p.assetNumber.replace(prefix, '')) || 0;
                  counters[cat] = Math.max(counters[cat], num);
                }
              });
            }
          });
          Object.entries(prefixMap).forEach(([cat, pfx]) => { counters[cat] = Math.max(counters[cat] || 0, assetFloor(pfx)); });   // never reuse an issued number
          const skippedRows = [];
          const skippedDuplicateSerials = [];
          // Every physical printer must have a unique serial - check both
          // against what's already saved AND against earlier rows in this
          // same import file, so a duplicated spreadsheet row (or re-running
          // the same import twice) can't silently create duplicate assets.
          const existingSerials = new Set(printers.map(p => p.serialNumber?.trim().toLowerCase()).filter(Boolean));
          const seenInThisImport = new Set();
          const importBase = Date.now();
          const imported = jsonData.map((row, idx) => {
            const serialNumber = row.SerialNumber || row.serialNumber;
            const make = row.Make || row.make;
            const model = row.Model || row.model;
            if (!serialNumber || !make || !model) {
              skippedRows.push(idx + 2); // +2 = account for header row + 1-index
              return null;
            }
            const normalizedSerial = serialNumber.trim().toLowerCase();
            if (existingSerials.has(normalizedSerial) || seenInThisImport.has(normalizedSerial)) {
              skippedDuplicateSerials.push({ row: idx + 2, serialNumber, make, model });
              return null;
            }
            seenInThisImport.add(normalizedSerial);
            const category = row.Category || row.category || 'Printer';
            const prefix = prefixMap[category] || 'RBPRN';
            let assetNumber = row.AssetNumber || row.assetNumber;
            if (!assetNumber) {
              counters[category] = (counters[category] || 0) + 1;
              assetNumber = `${prefix}${String(counters[category]).padStart(4, '0')}`;
            }
            return {
              id: `PRT${importBase}_${idx}`, // idx guarantees uniqueness within this import batch
              assetNumber,
              serialNumber,
              category,
              make,
              model,
              type: row.Type || row.type || 'A4',
              status: row.Status || row.status || 'available'
            };
          }).filter(Boolean);

          if (imported.length === 0) {
            if (skippedDuplicateSerials.length > 0 && skippedRows.length === 0) {
              alert(`⚠️ No printers imported — all ${skippedDuplicateSerials.length} row(s) have a Serial Number that's already in Assets (or repeated within this file).`);
            } else {
              alert('❌ No valid rows found. Make sure SerialNumber, Make, and Model columns are filled for every row.');
            }
          } else {
            await batchSaveDocs('printers', imported);
            await logAudit('IMPORT', 'Asset', 'bulk-import', `Imported ${imported.length} printers`, {});
            let msg = `✅ Imported ${imported.length} printers successfully!`;
            if (skippedRows.length > 0) {
              msg += `\n\n⚠️ Skipped ${skippedRows.length} row(s) missing SerialNumber/Make/Model (spreadsheet row ${skippedRows.join(', ')}).`;
            }
            if (skippedDuplicateSerials.length > 0) {
              msg += `\n\n⚠️ Skipped ${skippedDuplicateSerials.length} row(s) — Serial Number already exists (prevents duplicates):\n${skippedDuplicateSerials.slice(0, 8).map(s => `Row ${s.row}: ${s.serialNumber}`).join(', ')}${skippedDuplicateSerials.length > 8 ? '...' : ''}`;
            }
            alert(msg);
          }
        } else if (type === 'clients') {
          const imported = jsonData.map((row, idx) => ({
            id: `CLI${Date.now()}_${idx}`,
            name: row.Name || row.name,
            company: row.Company || row.company || '',
            phone: row.Phone || row.phone,
            email: row.Email || row.email
          }));
          await batchSaveDocs('clients', imported);
          await logAudit('IMPORT', 'Client', 'bulk-import', `Imported ${imported.length} clients`, {});
          alert(`Imported ${imported.length} clients successfully!`);
        } else if (type === 'rentals') {
          const skippedRentalRows = [];
          const skippedDuplicateSerials = [];
          const skippedUnknownSerials = [];

          // Fetch a fresh snapshot to check for existing ACTIVE rentals per printer,
          // so re-running an import (or an import file with repeated rows) doesn't
          // create duplicate active rental records for the same printer.
          const existingRentalsSnapshot = await getDocs(collection(db, 'rentals'));
          const existingActiveSerials = new Set(
            existingRentalsSnapshot.docs
              .map(d => d.data())
              .filter(r => r.status !== 'returned')
              .map(r => r.serialNumber)
          );
          const seenInThisImport = new Set();

          // Fetch a fresh printers snapshot so every imported rental's SerialNumber
          // can be checked against REAL assets before creating anything. A typo'd
          // or non-existent serial is now rejected outright, instead of silently
          // creating a "ghost" rental and only warning about it after the fact.
          const importPrintersSnapshot = await getDocs(collection(db, 'printers'));
          const knownSerials = new Set(importPrintersSnapshot.docs.map(d => d.data().serialNumber).filter(Boolean));

          const imported = jsonData.map((row, idx) => {
            const clientName = row.ClientName || row.clientName;
            const serialNumber = row.SerialNumber || row.serialNumber;
            if (!clientName || !serialNumber) {
              skippedRentalRows.push(idx + 2);
              return null;
            }
            if (!knownSerials.has(serialNumber)) {
              skippedUnknownSerials.push({ row: idx + 2, serialNumber, clientName });
              return null;
            }
            // Skip if this printer already has an active rental (either already in the
            // database, or earlier in this same import file - duplicate row)
            if (existingActiveSerials.has(serialNumber) || seenInThisImport.has(serialNumber)) {
              skippedDuplicateSerials.push({ row: idx + 2, serialNumber, clientName });
              return null;
            }
            seenInThisImport.add(serialNumber);

            const rentalType = row.RentalType || row.rentalType || 'regular';
            const isStandby = rentalType === 'standby';
            const isPerPage = rentalType === 'per_page';
            return {
              id: `RNT${Date.now()}_${idx}`,
              clientName,
              department: row.Department || row.department || '',
              serialNumber,
              printerModel: row.PrinterModel || row.printerModel || '',
              printerType: row.PrinterType || row.printerType || 'A4',
              rentalType,
              startDate: row.StartDate || row.startDate || new Date().toISOString().split('T')[0],
              endDate: row.EndDate || row.endDate || '',
              poNumber: row.PONumber || row.poNumber || '',
              monthlyRate: (isStandby || isPerPage) ? 0 : parseFloat(row.MonthlyRate || row.monthlyRate) || 0,
              a4FreePages: (isStandby || isPerPage) ? 0 : parseInt(row.A4FreePages || row.a4FreePages || row.FreePages || row.freePages) || 0,
              a4PageCost: isStandby ? 0 : parseFloat(row.A4PageCost || row.a4PageCost || row.PageCost || row.pageCost) || 0,
              a3FreePages: (isStandby || isPerPage) ? 0 : parseInt(row.A3FreePages || row.a3FreePages) || 0,
              a3PageCost: isStandby ? 0 : parseFloat(row.A3PageCost || row.a3PageCost) || 0,
              cartridgeCost: isStandby ? parseFloat(row.CartridgeCost || row.cartridgeCost) || 0 : 0,
              status: row.Status || row.status || 'active'
            };
          }).filter(Boolean);

          if (imported.length === 0) {
            if (skippedUnknownSerials.length > 0 && skippedDuplicateSerials.length === 0 && skippedRentalRows.length === 0) {
              alert(`❌ No rentals imported — all ${skippedUnknownSerials.length} row(s) reference a Serial Number that doesn't match any existing Asset. Double-check for typos, or import the Printers sheet first if these are genuinely new assets.`);
            } else if (skippedDuplicateSerials.length > 0 && skippedRentalRows.length === 0) {
              alert(`⚠️ No rentals imported — all ${skippedDuplicateSerials.length} row(s) reference printers that already have an active rental. If you meant to update these, check in the old rental first or edit it directly instead of re-importing.`);
            } else {
              alert('❌ No valid rows found. Make sure ClientName and SerialNumber columns are filled for every row.');
            }
          } else {
          await batchSaveDocs('rentals', imported);

          // Assign each rented printer: flip its status to 'rented' so Asset Management stays in sync
          const activeImports = imported.filter(r => r.status === 'active');
          const printersSnapshot = await getDocs(collection(db, 'printers'));
          const allPrinters = printersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          let statusBatch = writeBatch(db);
          let statusOpCount = 0;
          let assignedCount = 0;

          for (const rental of activeImports) {
            const matchedPrinter = allPrinters.find(p => p.serialNumber === rental.serialNumber);
            if (matchedPrinter) {
              statusBatch.update(doc(db, 'printers', matchedPrinter.id), { status: 'rented' });
              statusOpCount++;
              assignedCount++;
              if (statusOpCount === 450) { await statusBatch.commit(); statusBatch = writeBatch(db); statusOpCount = 0; }
            }
          }
          if (statusOpCount > 0) await statusBatch.commit();

          await logAudit('IMPORT', 'Rental', 'bulk-import', `Imported ${imported.length} rentals, assigned ${assignedCount} printers`, {});

          let msg = `✅ Imported ${imported.length} rentals successfully!\n✅ ${assignedCount} printers auto-assigned (status set to "rented").`;
          if (skippedRentalRows.length > 0) {
            msg += `\n\n⚠️ Skipped ${skippedRentalRows.length} row(s) missing ClientName/SerialNumber (spreadsheet row ${skippedRentalRows.join(', ')}).`;
          }
          if (skippedUnknownSerials.length > 0) {
            msg += `\n\n❌ Rejected ${skippedUnknownSerials.length} row(s) — Serial Number doesn't match any existing Asset (check for typos):\n${skippedUnknownSerials.slice(0, 8).map(s => `Row ${s.row}: "${s.serialNumber}" (${s.clientName})`).join('\n')}${skippedUnknownSerials.length > 8 ? '...' : ''}`;
          }
          if (skippedDuplicateSerials.length > 0) {
            msg += `\n\n⚠️ Skipped ${skippedDuplicateSerials.length} row(s) — printer already has an active rental (prevents duplicates):\n${skippedDuplicateSerials.slice(0, 8).map(s => `Row ${s.row}: ${s.serialNumber}`).join(', ')}${skippedDuplicateSerials.length > 8 ? '...' : ''}`;
          }
          alert(msg);
          }
        } else if (type === 'engineers') {
          const imported = jsonData.map((row, idx) => ({
            id: `ENG${Date.now()}_${idx}`,
            name: row.Name || row.name,
            specialization: row.Specialization || row.specialization || '',
            phone: row.Phone || row.phone,
            email: row.Email || row.email || '',
            status: row.Status || row.status || 'available'
          }));
          await batchSaveDocs('engineers', imported);
          alert(`Imported ${imported.length} engineers successfully!`);
        } else if (type === 'serviceCalls') {
          const imported = jsonData.map((row, idx) => ({
            id: `SC${Date.now()}_${idx}`,
            date: row.Date || row.date,
            clientName: row.ClientName || row.clientName,
            serialNumber: row.SerialNumber || row.serialNumber,
            issue: row.Issue || row.issue || '',
            engineer: row.Engineer || row.engineer || '',
            status: row.Status || row.status || 'pending'
          }));
          await batchSaveDocs('serviceCalls', imported);
          alert(`Imported ${imported.length} service calls successfully!`);
        } else if (type === 'readings') {
          const skippedReadingRows = [];
          const imported = jsonData.map((row, idx) => {
            const serialNumber = row.SerialNumber || row.serialNumber;
            const date = row.Date || row.date;
            if (!serialNumber || !date) {
              skippedReadingRows.push(idx + 2);
              return null;
            }
            return {
              id: `RDG${Date.now()}_${idx}`,
              date,
              clientName: row.ClientName || row.clientName || '',
              serialNumber,
              printerModel: row.PrinterModel || row.printerModel || '',
              a4Reading: parseInt(row.A4Reading || row.a4Reading || 0) || 0,
              previousA4: parseInt(row.PreviousA4 || row.previousA4 || 0) || 0,
              a3Reading: parseInt(row.A3Reading || row.a3Reading || 0) || 0,
              previousA3: parseInt(row.PreviousA3 || row.previousA3 || 0) || 0,
              scannerReading: parseInt(row.ScannerReading || row.scannerReading || 0) || 0,
              previousScanner: parseInt(row.PreviousScanner || row.previousScanner || 0) || 0,
              cartridgesUsed: parseInt(row.CartridgesUsed || row.cartridgesUsed || 0) || 0,
              recordedBy: row.RecordedBy || row.recordedBy || currentUser?.email || ''
            };
          }).filter(Boolean);

          if (imported.length === 0) {
            alert('❌ No valid rows found. Make sure Date and SerialNumber columns are filled for every row.');
          } else {
            await batchSaveDocs('readings', imported);
            await logAudit('IMPORT', 'MeterReading', 'bulk-import', `Imported ${imported.length} meter readings`, {});
            let msg = `✅ Imported ${imported.length} meter readings successfully!`;
            if (skippedReadingRows.length > 0) {
              msg += `\n\n⚠️ Skipped ${skippedReadingRows.length} row(s) missing Date/SerialNumber (spreadsheet row ${skippedReadingRows.join(', ')}).`;
            }
            alert(msg);
          }
        }
      } catch (err) {
        console.error('Import error:', err);
        alert(`❌ Error saving imported data to cloud.\n\nDetails: ${err.message || err}\n\nCheck that your column headers match exactly (SerialNumber, Make, Model, etc.) and try again.`);
      }
      setShowImportDialog(false);
    };
    reader.readAsArrayBuffer(file);
  };

  // Download Excel Template
  const downloadTemplate = (type) => {
    let template = [];
    if (type === 'printers') {
      template = [{ SerialNumber: 'HP001', Category: 'Printer', Make: 'HP', Model: 'LaserJet Pro', Type: 'A4', Status: 'available' }];
    } else if (type === 'clients') {
      template = [{ Name: 'John Doe', Company: 'ABC Corp', Phone: '1234567890', Email: 'john@abc.com' }];
    } else if (type === 'rentals') {
      template = [{ ClientName: 'John Doe', Department: 'IT', SerialNumber: 'HP001', PrinterModel: 'HP LaserJet Pro', PrinterType: 'A4', RentalType: 'regular', MonthlyRate: 5000, A4FreePages: 1000, A4PageCost: 0.5, A3FreePages: 0, A3PageCost: 0, StartDate: '2024-01-01', PONumber: '', Status: 'active' }];
    } else if (type === 'engineers') {
      template = [{ Name: 'Mike Smith', Specialization: 'Printer Maintenance', Phone: '9876543210', Email: 'mike@company.com', Status: 'available' }];
    } else if (type === 'serviceCalls') {
      template = [{ Date: '2024-01-15', ClientName: 'John Doe', SerialNumber: 'HP001', Issue: 'Paper jam', Engineer: 'Mike Smith', Status: 'pending' }];
    } else if (type === 'readings') {
      template = [{ Date: '2024-01-15', ClientName: 'John Doe', SerialNumber: 'HP001', PrinterModel: 'HP LaserJet Pro', A4Reading: 5789, PreviousA4: 5234, A3Reading: 0, PreviousA3: 0, ScannerReading: 0, PreviousScanner: 0, RecordedBy: 'admin@company.com' }];
    }
    exportToExcel(template, `${type}_template`);
  };

  // Get client info
  const getClientByEmail = () => {
    if (userRole === 'client' && currentUser) {
      return clients.find(c => c.email === currentUser.email) || clients[0] || null;
    }
    return null;
  };

  const getClientRentals = () => {
    const client = getClientByEmail();
    return client ? rentals.filter(r => r.clientName === client.name && r.status === 'active') : [];
  };

  const getClientReadings = () => {
    const clientRentalSerials = getClientRentals().map(r => r.serialNumber);
    return meterReadings.filter(r => clientRentalSerials.includes(r.serialNumber));
  };

  const getClientServiceCalls = () => {
    const client = getClientByEmail();
    return client ? serviceCalls.filter(c => c.clientName === client.name) : [];
  };

  const getClientQuotations = () => {
    const client = getClientByEmail();
    return client ? quotations.filter(q => q.clientName === client.name) : [];
  };

  // USER MANAGEMENT VIEW (Super Admin Only)
  const UserManagementView = () => {
    const [newUser, setNewUser] = useState({ email: '', password: '', role: 'user', name: '' });
    // Edit User modal - a local draft copy of the user being edited, so
    // changes aren't saved until "Save Changes" is clicked. null = closed.
    const [editingUser, setEditingUser] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    const createUser = async () => {
      if (!newUser.email || !newUser.password) {
        alert('Email and password required');
        return;
      }

      try {
        // IMPORTANT: creating a user with the primary `auth` instance would
        // automatically sign in AS that new user, kicking out the admin who's
        // currently logged in. To avoid that, we create the account on a
        // temporary secondary Firebase app instance, then immediately sign
        // out of that secondary instance - the admin's real session (on the
        // primary `auth`) is never touched.
        const secondaryApp = getApps().find(a => a.name === 'Secondary') || initializeApp(auth.app.options, 'Secondary');
        const secondaryAuth = getAuth(secondaryApp);

        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newUser.email, newUser.password);
        const permissions = getPermissionsByRole(newUser.role);
        const createdUser = {
          email: newUser.email,
          name: newUser.name,
          role: newUser.role,
          permissions,
          createdAt: new Date(),
          createdBy: currentUser.email,
          isActive: true
        };
        await setDoc(doc(db, 'users', userCredential.user.uid), createdUser);
        await logAudit('CREATE', 'User', userCredential.user.uid, `${createdUser.email} (${createdUser.role})`, createdUser);

        await signOut(secondaryAuth);

        alert('User created successfully!');
        setNewUser({ email: '', password: '', role: 'user', name: '' });
        
        // Reload users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        setSystemUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        alert('Error creating user: ' + error.message);
      }
    };

    const deleteUser = async (userId, userEmail) => {
      if (!window.confirm(`Delete user: ${userEmail}?\n\nThis will:\n✓ Delete from Firebase Auth\n✓ Delete from Firestore\n\nThis cannot be undone!`)) return;
      try {
        // Removing someone's login/access is exactly the kind of action that
        // needs a trail - who deleted whom, and when. Log the full record
        // (role, permissions) before it's gone, same as every other delete
        // in the app, so it's restorable from Audit Logs if this was a mistake.
        const fullRecord = systemUsers.find(u => u.id === userId) || {};
        await deleteDoc(doc(db, 'users', userId));
        await logAudit('DELETE', 'User', userId, userEmail, fullRecord);

        // Delete from Firebase Auth
        // Note: This requires admin SDK in production
        // For now, manual deletion via Firebase Console is needed
        // Or use Cloud Function with Admin SDK

        setSystemUsers(systemUsers.filter(u => u.id !== userId));
        alert('✅ User deleted from Firestore!\n\n⚠️ To fully remove, also delete from Firebase Console:\nAuthentication → Users → Delete');
      } catch (error) {
        alert('Error deleting user: ' + error.message);
      }
    };

    const openEditUser = (user) => {
      const modAccess = { ...(user.moduleAccess || {}) };
      const perms = { ...(user.permissions || {}) };
      if (perms.canManageInvoices === undefined) perms.canManageInvoices = ['admin', 'manager'].includes(user.role);   // what they effectively have today
      if (perms.canViewTally === undefined) perms.canViewTally = user.role === 'super_admin';
      setEditingUser({ ...user, permissions: perms, moduleAccess: modAccess });
    };

    const toggleEditPermission = (key) => {
      setEditingUser(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: !prev.permissions?.[key] } }));
    };

    const applyRolePreset = (key) => {
      const pr = ROLE_PRESETS[key];
      if (!pr) return;
      setEditingUser(prev => ({ ...prev, role: pr.base, jobRole: key, permissions: { ...getPermissionsByRole(pr.base), ...pr.perms }, moduleAccess: moduleMapFor(pr) }));
    };

    const applyRoleDefaultsToEdit = () => {
      setEditingUser(prev => ({ ...prev, permissions: getPermissionsByRole(prev.role), moduleAccess: {} }));
    };

    const saveUserEdit = async () => {
      if (!editingUser) return;
      setSavingEdit(true);
      try {
        const updated = {
          name: editingUser.name || '',
          role: editingUser.role,
          jobRole: editingUser.jobRole || '',
          permissions: editingUser.permissions || {},
          moduleAccess: Object.fromEntries(Object.entries(editingUser.moduleAccess || {}).filter(([, v]) => ['none', 'r', 'create', 'rw'].includes(v)))
        };
        await setDoc(doc(db, 'users', editingUser.id), updated, { merge: true });
        await logAudit('UPDATE', 'User', editingUser.id, `${editingUser.email} (${updated.role})`, updated);
        setSystemUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...updated } : u));
        setEditingUser(null);
      } catch (error) {
        alert('Error updating user: ' + error.message);
      }
      setSavingEdit(false);
    };

    // Firebase's client SDK has no way for an admin to directly set another
    // user's password without signing in as them - the standard, secure
    // approach is to email them a reset link instead, same as "Forgot
    // Password" would, rather than building custom backend/Admin-SDK code
    // just to move a password between two people.
    const sendPasswordReset = async (email) => {
      setSendingReset(true);
      try {
        await sendPasswordResetEmail(auth, email);
        alert(`✅ Password reset email sent to ${email}.`);
      } catch (error) {
        alert('❌ Error sending reset email: ' + error.message);
      }
      setSendingReset(false);
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-6">
          <h3 className="text-lg font-bold mb-4">Create New User</h3>
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            <input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            <input type="password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150">
              {Object.keys(ROLES).map(roleKey => (
                <option key={roleKey} value={roleKey}>{ROLES[roleKey].label}</option>
              ))}
            </select>
          </div>
          <button onClick={createUser} className="mt-4 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-6 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium">Create User</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {systemUsers.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4">{user.name || '-'}</td>
                  <td className="px-6 py-4">{user.email}</td>
                  <td className="px-6 py-4"><span className="px-2.5 py-1 bg-brand-50 text-brand-700 border border-brand-200 rounded-full text-xs font-medium">{ROLES[user.role]?.label || user.role}</span></td>
                  <td className="px-6 py-4 text-sm text-slate-500">{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : <span className="text-slate-300 italic">Never</span>}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditUser(user)} className="p-1.5 rounded-md hover:bg-brand-50 text-brand-600 transition-colors duration-150" title="Edit"><Edit size={18} /></button>
                      {user.id !== currentUser.uid && <button onClick={() => deleteUser(user.id, user.email)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors duration-150" title="Delete"><Trash2 size={18} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editingUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditingUser(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Edit User</h3>
                  <p className="text-sm text-slate-500">{editingUser.email}</p>
                </div>
                <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-colors duration-150"><X size={20} /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-600">Name</label>
                  <input type="text" value={editingUser.name || ''} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} className="w-full border rounded-lg px-3 py-2 mt-1" />
                </div>

                <div>
                  <label className="text-sm text-slate-600">Role</label>
                  <select value={editingUser.role} onChange={(e) => setEditingUser({...editingUser, role: e.target.value})} className="w-full border rounded-lg px-3 py-2 mt-1">
                    {Object.keys(ROLES).map(roleKey => (
                      <option key={roleKey} value={roleKey}>{ROLES[roleKey].label}</option>
                    ))}
                  </select>
                </div>

                <div className="border border-brand-200 rounded-lg p-3 bg-brand-50/40 space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Job role (quick setup)</label>
                  <select value={editingUser.jobRole || ''} onChange={(e) => applyRolePreset(e.target.value)} className="w-full border rounded-lg px-3 py-2 bg-white">
                    <option value="">Choose a job role to fill in the permissions and module access…</option>
                    {Object.entries(ROLE_PRESETS).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                  </select>
                  {editingUser.jobRole && ROLE_PRESETS[editingUser.jobRole] && <p className="text-xs text-slate-500">{ROLE_PRESETS[editingUser.jobRole].note} Review the ticks below, then Save.</p>}
                </div>

                <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Permissions</p>
                    <button type="button" onClick={applyRoleDefaultsToEdit} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2 py-1 rounded-lg font-medium transition-colors duration-150">Reset to role defaults</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-sm bg-white rounded-lg border px-3 py-2 cursor-pointer">
                        <input type="checkbox" checked={!!editingUser.permissions?.[key]} onChange={() => toggleEditPermission(key)} className="rounded" />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Module access</p>
                    <p className="text-xs text-slate-400">Per module: <strong>No access</strong> hides it, <strong>Read only</strong> shows it without add/edit/delete, <strong>Create only</strong> (invoices) allows new documents but no edit/delete afterwards, <strong>Read &amp; write</strong> allows changes. "Role default" keeps what the role and the permissions above give. Super admin always has full access.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {ACCESS_MODULES.map(m => (
                      <label key={m.key} className="flex items-center justify-between gap-2 text-sm bg-white rounded-lg border px-3 py-1.5">
                        <span className="truncate">{m.label}</span>
                        <select
                          value={(editingUser.moduleAccess || {})[m.key] || ''}
                          onChange={(e) => setEditingUser(prev => ({ ...prev, moduleAccess: { ...(prev.moduleAccess || {}), [m.key]: e.target.value } }))}
                          className={`border rounded px-2 py-1 text-xs ${(editingUser.moduleAccess || {})[m.key] === 'none' ? 'border-red-300 text-red-700' : (editingUser.moduleAccess || {})[m.key] ? 'border-brand-300 text-brand-700' : 'border-slate-300'}`}
                        >
                          <option value="">Role default</option>
                          <option value="none">No access</option>
                          <option value="r">Read only</option>
                          {m.createOnly && <option value="create">Create only (no edit / delete)</option>}
                          {!m.readOnly && <option value="rw">Read &amp; write</option>}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-3 bg-slate-50 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Password</p>
                    <p className="text-xs text-slate-400">Sends a reset link to their email - there's no way to set it directly.</p>
                  </div>
                  <button
                    type="button"
                    disabled={sendingReset}
                    onClick={() => sendPasswordReset(editingUser.email)}
                    className="text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150 disabled:opacity-50 shrink-0"
                  >
                    {sendingReset ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </div>

                {editingUser.lastLogin && (
                  <p className="text-xs text-slate-400">Last login: {new Date(editingUser.lastLogin).toLocaleString()}</p>
                )}
              </div>

              <div className="flex gap-3 pt-5 mt-2 border-t border-slate-100">
                <button onClick={saveUserEdit} disabled={savingEdit} className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white py-2.5 rounded-lg shadow-sm hover:shadow-md font-medium transition-all duration-150 disabled:opacity-50">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditingUser(null)} className="flex-1 bg-white text-slate-700 border border-slate-300 py-2.5 rounded-lg hover:bg-slate-50 font-medium transition-all duration-150">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Import Dialog Component
  // Service History - combined timeline of Service Calls + Inward/Outward
  // movements for a specific printer serial, accessible from Assets and
  // Client Service pages via setServiceHistorySerial(serial).
  const ServiceHistoryModal = () => {
    if (!serviceHistorySerial) return null;

    const printer = printers.find(p => p.serialNumber === serviceHistorySerial);
    const clientAsset = clientAssets.find(a => a.serialNumber === serviceHistorySerial);
    const asset = printer || clientAsset;

    const relevantCalls = serviceCalls
      .filter(c => c.serialNumber === serviceHistorySerial)
      .map(c => ({ kind: 'call', date: c.date, data: c }));
    const relevantMovements = printerMovements
      .filter(m => m.printerSerial === serviceHistorySerial)
      .map(m => ({ kind: 'movement', date: m.date, data: m }));
    const relevantQuotations = quotations
      .filter(q => q.printerSerial === serviceHistorySerial)
      .map(q => ({ kind: 'quotation', date: q.date, data: q }));
    const relevantRentals = rentals
      .filter(r => r.serialNumber === serviceHistorySerial)
      .map(r => ({ kind: 'rental', date: r.startDate || r.date || r.createdAt, data: r }));
    const relevantReadings = meterReadings
      .filter(m => m.serialNumber === serviceHistorySerial)
      .map(m => ({ kind: 'reading', date: m.date, data: m }));
    const assetNo = asset?.assetNumber;
    const relevantChanges = auditLogs
      .filter(l => l.entityType === 'Asset' && l.action !== 'IMPORT' && ((asset?.id && l.entityId === asset.id) || (assetNo && String(l.entityName || '').includes(assetNo))))
      .map(l => ({ kind: 'change', date: l.timestamp || l.date, data: l }));
    const timeline = [...relevantCalls, ...relevantMovements, ...relevantQuotations, ...relevantRentals, ...relevantReadings, ...relevantChanges]
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    const fmtWhen = (d) => { const x = new Date(d); return isNaN(x) ? (d || '') : x.toLocaleDateString('en-IN'); };

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setServiceHistorySerial(null)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">🕓 Asset History</h3>
              <p className="text-sm text-slate-500">
                {asset?.assetNumber ? `${asset.assetNumber} • ` : ''}{asset?.make} {asset?.model} • S/N: {serviceHistorySerial}
              </p>
            </div>
            <button onClick={() => setServiceHistorySerial(null)} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-colors duration-150"><X size={20} /></button>
          </div>

          {timeline.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-8">Nothing recorded for this asset yet.</p>
          ) : (
            <div className="space-y-3">
              {timeline.map((entry, idx) => (
                <div key={idx} className={`rounded-lg p-4 border ${
                  entry.kind === 'call' ? 'bg-orange-50 border-orange-200' :
                  entry.kind === 'quotation' ? 'bg-indigo-50 border-indigo-200' :
                  ['rental', 'reading', 'change'].includes(entry.kind) ? 'bg-slate-50 border-slate-200' : 'bg-brand-50 border-brand-200'
                }`}>
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      entry.kind === 'call' ? 'bg-orange-100 text-orange-800' :
                      entry.kind === 'quotation' ? 'bg-indigo-100 text-indigo-800' : 'bg-brand-100 text-brand-800'
                    }`}>
                      {entry.kind === 'call' ? '🔧 Service Call' : entry.kind === 'quotation' ? '📝 Quotation' : entry.kind === 'rental' ? '📄 Rental' : entry.kind === 'reading' ? '🔢 Meter reading' : entry.kind === 'change' ? '✏️ Record change' : entry.data.type === 'IN' ? '📥 Received (IN)' : '📤 Returned (OUT)'}
                    </span>
                    <span className="text-xs text-slate-500">{entry.kind === 'change' ? fmtWhen(entry.date) : entry.date}</span>
                  </div>
                  {entry.kind === 'rental' ? (
                    <>
                      <p className="text-sm font-medium text-slate-800">{entry.data.clientName}</p>
                      <p className="text-xs text-slate-600 mt-1">{entry.data.rentalType === 'standby' ? 'Standby' : `₹${entry.data.monthlyRate || 0}/mo`} • status: {entry.data.status || 'active'}{entry.data.endDate ? ` • ended ${entry.data.endDate}` : ''}</p>
                    </>
                  ) : entry.kind === 'reading' ? (
                    <>
                      <p className="text-sm text-slate-800">{entry.data.clientName || ''}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        {[['A4', entry.data.a4Reading], ['A3', entry.data.a3Reading], ['Colour A4', entry.data.colourA4Reading], ['Colour A3', entry.data.colourA3Reading], ['Scan', entry.data.scannerReading]]
                          .filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${k}: ${v}`).join(' • ') || 'Reading recorded'}
                      </p>
                    </>
                  ) : entry.kind === 'change' ? (
                    <p className="text-xs text-slate-600">{entry.data.action} by {entry.data.userEmail || 'Unknown'}{entry.data.entityName ? ` — ${entry.data.entityName}` : ''}</p>
                  ) : entry.kind === 'call' ? (
                    <>
                      <p className="text-sm font-medium text-slate-800">{entry.data.issue}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Engineer: {entry.data.engineer || 'Unassigned'}
                        {entry.data.pickedUpBy && ` • Picked up by: ${entry.data.pickedUpBy === '__other__' ? entry.data.pickedUpByOther : entry.data.pickedUpBy}`}
                        {entry.data.pickupDate && ` on ${entry.data.pickupDate}`}
                      </p>
                      {entry.data.returnedBy && (
                        <p className="text-xs text-slate-600">
                          Returned by: {entry.data.returnedBy === '__other__' ? entry.data.returnedByOther : entry.data.returnedBy}
                          {entry.data.returnDate && ` on ${entry.data.returnDate}`}
                        </p>
                      )}
                      {(entry.data.assignmentHistory || []).length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">Assigned: {(entry.data.assignmentHistory || []).map(h => `${h.engineer || 'Unassigned'} (${fmtWhen(h.at)})`).join(' → ')}</p>
                      )}
                      {entry.data.status === 'completed' && (entry.data.workDone || entry.data.partsUsed || entry.data.completedAt) && (
                        <div className="text-xs text-slate-600 mt-1 bg-white/70 rounded p-2 border border-green-100">
                          {entry.data.workDone && <p><b>Work done:</b> {entry.data.workDone}</p>}
                          {entry.data.partsUsed && <p><b>Parts used:</b> {entry.data.partsUsed}</p>}
                          {(entry.data.stockUsed || []).length > 0 && <p><b>Stock used:</b> {(entry.data.stockUsed || []).map(r => `${r.itemName} ×${r.qty}`).join(', ')}</p>}
                          {entry.data.timeSpentMins ? <p><b>Time spent:</b> {entry.data.timeSpentMins} min</p> : null}
                          {entry.data.signedOffBy && <p><b>Signed off by:</b> {entry.data.signedOffBy}</p>}
                          {entry.data.completedAt && <p>Completed {fmtWhen(entry.data.completedAt)}{entry.data.completedBy ? ` by ${entry.data.completedBy}` : ''}</p>}
                        </div>
                      )}
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                        entry.data.status === 'completed' ? 'bg-green-100 text-green-800' :
                        entry.data.status === 'in-progress' ? 'bg-brand-100 text-brand-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{entry.data.status}</span>
                    </>
                  ) : entry.kind === 'quotation' ? (() => {
                    const subTotal = (entry.data.items || []).reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)), 0);
                    const grandTotal = subTotal * 1.18;
                    return (
                      <>
                        <p className="text-sm font-medium text-slate-800 font-mono">{entry.data.quoteNumber}</p>
                        <p className="text-xs text-slate-600 mt-1">₹{grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} • {(entry.data.items || []).length} item(s)</p>
                        <button onClick={() => generateQuotationPDF(entry.data)} className="text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-2 py-1 rounded-lg font-medium mt-2 transition-colors duration-150">
                          ⬇️ Download PDF
                        </button>
                      </>
                    );
                  })() : (
                    <>
                      <p className="text-sm text-slate-700">{entry.data.serviceNotes || 'No notes recorded'}</p>
                      <p className="text-xs text-slate-600 mt-1">Technician: {entry.data.technician}{entry.data.clientName ? ` • ${entry.data.clientName}` : ''}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Client Activity Timeline - a relationship log (calls, emails, meetings,
  // notes) per client, distinct from Audit Logs which tracks system CRUD
  // actions, not conversations. Opened from the Clients page.
  const ClientActivityModal = () => {
    const [newType, setNewType] = useState('note');
    const [newText, setNewText] = useState('');
    const [saving, setSaving] = useState(false);

    if (!activityClientId) return null;
    const client = clients.find(c => c.id === activityClientId);
    if (!client) return null;

    const timeline = clientActivities
      .filter(a => a.clientId === activityClientId)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    const TYPE_META = {
      call: { label: '📞 Call', color: 'bg-brand-50 border-brand-200 text-brand-800' },
      email: { label: '✉️ Email', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
      meeting: { label: '🤝 Meeting', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
      note: { label: '📝 Note', color: 'bg-slate-50 border-slate-200 text-slate-800' }
    };

    const addActivity = async () => {
      if (!newText.trim()) return;
      setSaving(true);
      try {
        const activity = {
          id: `ACT${Date.now()}`,
          clientId: activityClientId,
          companyId: client.companyId || currentCompany,
          type: newType,
          text: newText.trim(),
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'clientActivities', activity.id), activity);
        setNewText('');
      } catch (err) {
        alert('Error adding activity: ' + err.message);
      }
      setSaving(false);
    };

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setActivityClientId(null)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">📋 Activity Timeline</h3>
              <p className="text-sm text-slate-500">{client.name}</p>
            </div>
            <button onClick={() => setActivityClientId(null)} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-colors duration-150"><X size={20} /></button>
          </div>

          <div className="flex gap-2 mb-5">
            <select value={newType} onChange={(e) => setNewType(e.target.value)} className="border rounded-lg px-3 py-2 text-sm shrink-0">
              <option value="note">📝 Note</option>
              <option value="call">📞 Call</option>
              <option value="email">✉️ Email</option>
              <option value="meeting">🤝 Meeting</option>
            </select>
            <input
              type="text"
              placeholder="What happened?"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addActivity(); }}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none"
            />
            <button
              onClick={addActivity}
              disabled={saving || !newText.trim()}
              className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              Add
            </button>
          </div>

          {timeline.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-8">No activity logged for this client yet.</p>
          ) : (
            <div className="space-y-2">
              {timeline.map(entry => (
                <div key={entry.id} className={`rounded-lg p-3 border ${TYPE_META[entry.type]?.color || TYPE_META.note.color}`}>
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <span className="text-xs font-semibold">{TYPE_META[entry.type]?.label || TYPE_META.note.label}</span>
                    <span className="text-xs text-slate-500 shrink-0">{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-sm text-slate-800">{entry.text}</p>
                  <p className="text-xs text-slate-400 mt-1">{entry.createdBy}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };


  const ImportDialog = () => {
    if (!showImportDialog) return null;

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowImportDialog(false)}>
        <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
            <h3 className="text-xl font-semibold text-slate-900">Import {importType}</h3>
            <button onClick={() => setShowImportDialog(false)} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-colors duration-150"><X size={20} /></button>
          </div>

            <div className="space-y-4">
              <p className="text-sm text-slate-600">Upload Excel file to import {importType}</p>
              <button onClick={() => downloadTemplate(importType)} className="w-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center justify-center gap-2">
                <Download size={18} /> Download Template
              </button>
              <input type="file" accept=".xlsx,.xls" onChange={(e) => handleExcelImport(e, importType)} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            </div>
        </div>
      </div>
    );
  };

  // CLIENT VIEWS (simplified)
  const ClientDashboard = () => {
    const clientRentals = getClientRentals();
    const client = getClientByEmail();
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-6">
          <h2 className="text-2xl font-bold mb-2">Welcome, {client?.name || 'Client'}!</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientRentals.map(rental => (
              <div key={rental.id} className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-100 p-3 rounded-lg"><Printer className="text-brand-600" size={24} /></div>
                  <div>
                    <h3 className="font-semibold">{rental.printerModel}</h3>
                    {rental.assetNumber && <p className="text-sm text-slate-600 font-medium">Asset: {rental.assetNumber}</p>}
                    <p className="text-sm text-slate-500">Serial: {rental.serialNumber} <button onClick={() => setServiceHistorySerial(rental.serialNumber)} className="ml-1 text-orange-600 hover:underline" title="Asset history">🕓 History</button></p>
                    {rental.rentalType === 'standby' ? (
                      <p className="text-sm mt-2">Cartridge Cost: ₹{rental.cartridgeCost || 0}</p>
                    ) : (
                      <p className="text-sm mt-2">Rate: ₹{rental.monthlyRate || 0}/mo</p>
                    )}
                    {rental.category !== 'Desktop' && rental.category !== 'Laptop' && (
                      <>
                        <p className="text-sm">A4 Free Pages: {rental.a4FreePages || 0}</p>
                        {(rental.printerType === 'A3' || rental.printerType === 'MFP' || rental.printerType === 'MFP_Colour') && (
                          <p className="text-sm">A3 Free Pages: {rental.a3FreePages || 0}</p>
                        )}
                        {rental.printerType === 'MFP_Colour' && (
                          <>
                            <p className="text-sm text-pink-700">Colour A4 Free Pages: {rental.colourA4FreePages || 0}</p>
                            <p className="text-sm text-pink-700">Colour A3 Free Pages: {rental.colourA3FreePages || 0}</p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  const ClientMeterReadings = () => {
    const readings = getClientReadings();
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">My Meter Readings</h2>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Printer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">A4 Pages</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">A3 Pages</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {readings.map(r => {
                const a4Used = (r.a4Reading || 0) - (r.previousA4 || 0);
                const a3Used = (r.a3Reading || 0) - (r.previousA3 || 0);
                const matchedPrinter = getClientRentals().find(x => x.serialNumber === r.serialNumber);   // clients see their own rentals, not the asset register
                return (
                  <tr key={r.id}>
                    <td className="px-6 py-4">{r.date}</td>
                    <td className="px-6 py-4">{matchedPrinter?.assetNumber ? `${matchedPrinter.assetNumber} • ` : ''}{r.serialNumber}</td>
                    <td className="px-6 py-4 font-medium text-brand-600">{a4Used.toLocaleString()}</td>
                    <td className="px-6 py-4 font-medium text-purple-600">{a3Used > 0 ? a3Used.toLocaleString() : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const ClientServiceHistory = () => {
    const calls = getClientServiceCalls().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">My Service Requests</h2>
        {calls.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 flex flex-col items-center justify-center">
            <Wrench size={48} className="text-slate-300 mb-4" />
            <p className="text-slate-500">No service requests on file yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {calls.map(call => (
              <div key={call.id} className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-slate-900">{call.printerModel || call.serialNumber}</p>
                    <p className="text-xs text-slate-500">{call.date}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    call.status === 'completed' ? 'bg-green-100 text-green-800' :
                    call.status === 'in-progress' ? 'bg-brand-100 text-brand-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {call.status === 'completed' ? '🟢 Completed' : call.status === 'in-progress' ? '🔵 In Progress' : '🟡 Open'}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{call.issue}</p>
                {call.engineer && <p className="text-xs text-slate-500 mt-2">Engineer assigned: {call.engineer}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ClientQuotationsView = () => {
    const myQuotations = getClientQuotations().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">My Quotations</h2>
        {myQuotations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 flex flex-col items-center justify-center">
            <FileText size={48} className="text-slate-300 mb-4" />
            <p className="text-slate-500">No quotations on file yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myQuotations.map(q => {
              const subTotal = (q.items || []).reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)), 0);
              const grandTotal = subTotal * 1.18;
              return (
                <div key={q.id} className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
                  <p className="font-mono text-sm font-semibold text-brand-700">{q.quoteNumber}</p>
                  <p className="text-xs text-slate-500 mb-2">{q.date} {q.printerModel ? `• ${q.printerModel}` : ''}</p>
                  <p className="text-lg font-bold text-slate-900 mb-3">₹{grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <button onClick={() => generateQuotationPDF(q)} className="text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150 flex items-center gap-1">
                    <Download size={13} /> Download PDF
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ADMIN DASHBOARD
  const Dashboard = () => {
    const [activityDate, setActivityDate] = useState(new Date().toISOString().split('T')[0]);
    const [showActivityReport, setShowActivityReport] = useState(true);

    const stats = [
      { label: 'Total Printers', value: printers.length, icon: Printer, tint: 'bg-brand-50 text-brand-600' },
      { label: 'Active Rentals', value: rentals.filter(r => r.status === 'active').length, icon: FileText, tint: 'bg-emerald-50 text-emerald-600' },
      { label: 'Pending Calls', value: serviceCalls.filter(c => c.status !== 'completed').length, icon: AlertCircle, tint: 'bg-orange-50 text-orange-600' },
      { label: 'Total Clients', value: clients.length, icon: Users, tint: 'bg-purple-50 text-purple-600' }
    ];

    // Daily Activity Report: tallies CREATE/UPDATE/DELETE actions per entity
    // type for the selected date, scoped to whichever company is currently
    // active - using the Audit Log as the single source of truth for "what
    // happened today" for THIS company, not both combined. Entries logged
    // before company-tagging existed have no companyId, so (per the same
    // convention used everywhere else in the app) they're attributed to
    // RB Infotech rather than silently disappearing from both views.
    const dayLogs = auditLogs.filter(l => (l.timestamp || '').split('T')[0] === activityDate && (l.companyId || 'rb_infotech') === currentCompany);
    const activityCounts = {};
    dayLogs.forEach(l => {
      const key = `${l.entityType}__${l.action}`;
      activityCounts[key] = (activityCounts[key] || 0) + 1;
    });
    const activityEntityLabels = {
      Asset: { label: 'Assets', icon: '🖨️' },
      Client: { label: 'Clients', icon: '👥' },
      Rental: { label: 'Rentals', icon: '📄' },
      ServiceCall: { label: 'Service Calls', icon: '🔧' },
      MeterReading: { label: 'Meter Readings', icon: '📊' },
      Engineer: { label: 'Engineers', icon: '👷' },
      Quotation: { label: 'Quotations', icon: '📝' },
      ClientAsset: { label: 'Client Devices', icon: '📱' }
    };
    const activitySummary = Object.entries(activityEntityLabels).map(([type, meta]) => ({
      type,
      ...meta,
      created: activityCounts[`${type}__CREATE`] || 0,
      updated: activityCounts[`${type}__UPDATE`] || 0,
      deleted: activityCounts[`${type}__DELETE`] || 0
    })).filter(row => row.created + row.updated + row.deleted > 0);
    const totalActivityToday = dayLogs.length;

    return (
      <div className="space-y-6">
        {/* Daily Activity Report */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <button onClick={() => setShowActivityReport(!showActivityReport)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors duration-150">
            <div className="flex items-center gap-3">
              <span className="text-xl">📅</span>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Daily Activity Report — {COMPANIES[currentCompany]?.name}</p>
                <p className="text-xs text-slate-500">{totalActivityToday} action{totalActivityToday !== 1 ? 's' : ''} logged on {activityDate}</p>
              </div>
            </div>
            <span className="text-slate-400 text-sm">{showActivityReport ? '▲ Hide' : '▼ Show'}</span>
          </button>
          {showActivityReport && (
            <div className="px-5 pb-5 border-t border-slate-100 pt-4">
              <div className="flex items-center gap-3 mb-4">
                <label className="text-sm text-slate-600">Date:</label>
                <input
                  type="date"
                  value={activityDate}
                  onChange={(e) => setActivityDate(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none"
                />
                <button onClick={() => setActivityDate(new Date().toISOString().split('T')[0])} className="text-xs text-brand-600 hover:underline">Today</button>
              </div>

              {activitySummary.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No activity recorded for this date.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activitySummary.map(row => (
                    <div key={row.type} className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">{row.icon} {row.label}</p>
                      <div className="flex gap-3 flex-wrap">
                        {row.created > 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">+{row.created} created</span>}
                        {row.updated > 0 && <span className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded-full font-medium">{row.updated} updated</span>}
                        {row.deleted > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">{row.deleted} deleted</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {canSeeInvoices() && canApproveInvoices() && invoices.some(i => invApproval(i) === 'pending') && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-amber-900">🧾 <b>{invoices.filter(i => invApproval(i) === 'pending').length}</b> invoice(s) waiting for your approval</p>
            <button onClick={() => { setInvFilter('approval'); setInvTab('invoices'); setCurrentView('invoices'); }} className="text-sm font-medium text-amber-900 underline">Review →</button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold mt-2 text-slate-900">{stat.value}</p>
                </div>
                <div className={`${stat.tint} p-3 rounded-xl`}><stat.icon size={24} /></div>
              </div>
            </div>
          ))}
        </div>

        {userRole !== 'user' && userRole !== 'client' && userRole !== 'field_engineer' && inboxMails.some(m => (m.status || 'new') === 'new') && (() => {
          const open = inboxMails.filter(m => (m.status || 'new') === 'new');
          const top = [...open].sort((a, b) => (a.type === 'order' ? 0 : 1) - (b.type === 'order' ? 0 : 1) || String(a.followUpDate || '9999').localeCompare(String(b.followUpDate || '9999'))).slice(0, 5);
          return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-semibold text-slate-900">📧 Email inbox - waiting for action</h3>
                <button onClick={() => { setInboxType('all'); setInboxStatus('new'); setCurrentView('inbox'); }} className="text-sm text-brand-600 hover:text-brand-700 font-medium">Open inbox →</button>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                🛒 {open.filter(m => m.type === 'order').length} orders · 📧 {open.filter(m => m.type === 'enquiry').length} enquiries · 🔧 {open.filter(m => m.type === 'service').length} service requests
              </p>
              <ul className="divide-y divide-slate-100">
                {top.map(m => (
                  <li key={m.id} className="py-1.5 text-sm flex gap-2 items-baseline">
                    <span>{m.type === 'order' ? '🛒' : m.type === 'service' ? '🔧' : '📧'}</span>
                    <span className="font-medium text-slate-800 shrink-0 max-w-[40%] truncate">{String(m.name || m.email || '').replace(/^["'\s]+|["'\s]+$/g, '')}</span>
                    <span className="text-slate-500 truncate">{m.subject}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
      </div>
    );
  };

  // PRINTERS VIEW
  const PrintersView = () => {
    const canEdit = hasPermission('canEditAssets');
    const canDelete = hasPermission('canDelete');
    const canImport = hasPermission('canImport');
    const canExport = hasPermission('canExport');
    const [assetDupePreview, setAssetDupePreview] = useState(null);
    const [relabelPreview, setRelabelPreview] = useState(null);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [showAssetTools, setShowAssetTools] = useState(false);
    const [serialDupeCheck, setSerialDupeCheck] = useState(null);
    const [exactDupeDeletePreview, setExactDupeDeletePreview] = useState(null);
    const [integrityCheck, setIntegrityCheck] = useState(null);
    // Lazy-initialized from the one-shot QR-scan value (if the page was opened
    // via a scanned asset QR code); typing afterwards only updates this local
    // state, so it doesn't trigger a full app re-render on every keystroke.
    const [searchPrinters, setSearchPrinters] = useState(qrScannedAssetSearch || '');
    const [filterClient, setFilterClient] = useState('');
    const [filterModel, setFilterModel] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');   // 'cat:Desktop' (asset category) or 'type:A3' (printer type)

    // Category of an asset (older records may have none -> work it out from the asset-number prefix)
    const assetCategoryOf = (p) => p.category || (/DESK/i.test(p.assetNumber || '') ? 'Desktop' : /LAP/i.test(p.assetNumber || '') ? 'Laptop' : /SCN/i.test(p.assetNumber || '') ? 'Scanner' : 'Printer');
    const PRINTER_TYPE_LABELS = { A4: 'A4 Printer', A3: 'A3 Printer', MFP: 'MFP B/W', MFP_Colour: 'MFP Colour', Colour: 'Colour Printer' };
    // What the Type column shows: the print type for printers, the category for desktops/laptops/scanners (they never had a print size)
    const assetTypeLabel = (p) => assetCategoryOf(p) === 'Printer' ? (PRINTER_TYPE_LABELS[p.type] || p.type || 'Printer') : assetCategoryOf(p);

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setModalType('printer');
      setEditingItem(item);
      setShowModal(true);
    };

    const handleDelete = async (id) => {
      if (!canDelete) return;
      const printer = printers.find(p => p.id === id);
      if (window.confirm('Delete this printer?')) {
        try {
          await deleteFirestoreDoc('printers', id);
          await logAudit('DELETE', 'Asset', id, printer?.assetNumber || id, printer || {});
        } catch (err) {
          alert('Error deleting printer: ' + err.message);
        }
      }
    };

    const getClientForPrinter = (serialNumber) => {
      const rental = rentals.find(r => r.serialNumber === serialNumber && r.status === 'active');
      return rental ? rental.clientName : '-';
    };

    const marketingHighlightIds = new Set(marketingHighlights.map(h => h.id));

    // Filter printers based on search
    const filteredPrinters = printers
      .filter(printer => 
        matchesSearch(searchPrinters, printer.serialNumber, printer.assetNumber, printer.make, printer.model, getClientForPrinter(printer.serialNumber))
      )
      .filter(printer => !filterClient || getClientForPrinter(printer.serialNumber) === filterClient)
      .filter(printer => !filterModel || `${printer.make} ${printer.model}` === filterModel)
      .filter(printer => !filterStatus || printer.status === filterStatus)
      .filter(printer => !filterType || (filterType.startsWith('cat:') ? assetCategoryOf(printer) === filterType.slice(4) : assetCategoryOf(printer) === 'Printer' && (printer.type || 'A4') === filterType.slice(5)))
      .sort((a, b) => {
        // Sort by asset number prefix (RBPRN, RBDESK, RBLAP, RBSCN), then numerically
        const parse = (assetNum) => {
          if (!assetNum) return { prefix: 'ZZZZ', num: 999999 }; // no asset number sorts last
          const match = assetNum.match(/^([A-Za-z]+)(\d+)$/);
          return match ? { prefix: match[1], num: parseInt(match[2], 10) } : { prefix: assetNum, num: 0 };
        };
        const pa = parse(a.assetNumber);
        const pb = parse(b.assetNumber);
        if (pa.prefix !== pb.prefix) return pa.prefix.localeCompare(pb.prefix);
        return pa.num - pb.num;
      });

    // Sticker header = the company the asset belongs to (Canopus assets used to print "RB INFOTECH SOLUTIONS")
    const stickerCompanyOf = (printer) => String((COMPANIES[printer.companyId || 'rb_infotech'] || COMPANIES.rb_infotech).name).toUpperCase();

    const showQRCode = (printer) => {
      const stickerCompany = stickerCompanyOf(printer);
      const portalUrl = `https://printer-rental-app.web.app/?asset=${encodeURIComponent(printer.assetNumber || printer.serialNumber)}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(portalUrl)}`;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Asset Sticker - ${printer.assetNumber}</title>
            <style>
              @page { size: A4; margin: 10mm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .controls { text-align: center; margin-bottom: 20px; }
              .controls button { padding: 12px 30px; margin: 5px; background: #2563eb; color: white; border: none; border-radius: 5px; font-size: 16px; cursor: pointer; }
              .sticker-container { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
              
              /* Standard Sticker - 4 per row on A4 */
              .sticker {
                width: 90mm;
                height: 50mm;
                border: 2px dashed #ccc;
                padding: 8px;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                gap: 10px;
                page-break-inside: avoid;
                background: white;
              }
              .sticker-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: center;
              }
              .company-name { font-size: 11px; color: #666; font-weight: bold; margin-bottom: 4px; }
              .asset-number { font-size: 22px; font-weight: bold; color: #2563eb; margin-bottom: 4px; letter-spacing: 1px; }
              .make-model { font-size: 13px; font-weight: 600; margin-bottom: 2px; }
              .serial { font-size: 11px; color: #666; }
              .qr-section {
                width: 80px;
                height: 80px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-section img { width: 80px; height: 80px; }
              
              /* Small Label - 12 per row */
              .small-sticker {
                width: 60mm;
                height: 30mm;
                border: 1px dashed #ccc;
                padding: 5px;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                gap: 5px;
                page-break-inside: avoid;
                background: white;
              }
              .small-sticker .asset-number { font-size: 14px; }
              .small-sticker .make-model { font-size: 10px; }
              .small-sticker .qr-section img { width: 50px; height: 50px; }
              
              /* Large Sticker - Single */
              .large-sticker {
                width: 180mm;
                height: 100mm;
                border: 2px solid #2563eb;
                padding: 20px;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                gap: 20px;
                page-break-inside: avoid;
                background: white;
                border-radius: 10px;
              }
              .large-sticker .asset-number { font-size: 36px; }
              .large-sticker .make-model { font-size: 18px; }
              .large-sticker .serial { font-size: 14px; }
              .large-sticker .qr-section { width: 150px; height: 150px; }
              .large-sticker .qr-section img { width: 150px; height: 150px; }
              
              @media print {
                .controls { display: none; }
                .sticker, .small-sticker, .large-sticker { border: 1px dashed #999 !important; }
              }
              
              .size-selector { text-align: center; margin: 20px 0; }
              .size-selector button { background: #6b7280; }
              .size-selector button.active { background: #2563eb; }
            </style>
          </head>
          <body>
            <div class="controls">
              <h1 style="text-align:center;">Asset Sticker Print</h1>
              <div class="size-selector">
                <button onclick="showSize('large')" id="btn-large" class="active">Large (1 per page)</button>
                <button onclick="showSize('medium')" id="btn-medium">Medium (4 per page)</button>
                <button onclick="showSize('small')" id="btn-small">Small (12 per page)</button>
              </div>
              <button onclick="window.print()">🖨️ Print Sticker</button>
              <button onclick="window.close()" style="background:#ef4444;">✕ Close</button>
            </div>
            
            <!-- Large Sticker -->
            <div id="large-view" class="sticker-container">
              <div class="large-sticker">
                <div class="sticker-info">
                  <div class="company-name">${stickerCompany} - ASSET TAG</div>
                  <div class="asset-number">${printer.assetNumber || 'NO-ID'}</div>
                  <div class="make-model">${printer.make} ${printer.model}</div>
                  <div class="serial">S/N: ${printer.serialNumber}</div>
                  <div class="serial">Type: ${printer.type || printer.category}</div>
                </div>
                <div class="qr-section">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(portalUrl)}" alt="QR"/>
                </div>
              </div>
            </div>
            
            <!-- Medium Stickers (4 per page) -->
            <div id="medium-view" class="sticker-container" style="display:none;">
              ${Array(4).fill().map(() => `
                <div class="sticker">
                  <div class="sticker-info">
                    <div class="company-name">${stickerCompany}</div>
                    <div class="asset-number">${printer.assetNumber || 'NO-ID'}</div>
                    <div class="make-model">${printer.make} ${printer.model}</div>
                    <div class="serial">S/N: ${printer.serialNumber}</div>
                  </div>
                  <div class="qr-section">
                    <img src="${qrUrl}" alt="QR"/>
                  </div>
                </div>
              `).join('')}
            </div>
            
            <!-- Small Labels (12 per page) -->
            <div id="small-view" class="sticker-container" style="display:none;">
              ${Array(12).fill().map(() => `
                <div class="small-sticker">
                  <div class="sticker-info">
                    <div class="asset-number">${printer.assetNumber || 'NO-ID'}</div>
                    <div class="make-model">${printer.make}</div>
                  </div>
                  <div class="qr-section">
                    <img src="${qrUrl}" alt="QR"/>
                  </div>
                </div>
              `).join('')}
            </div>
            
            <script>
              function showSize(size) {
                document.getElementById('large-view').style.display = size === 'large' ? 'flex' : 'none';
                document.getElementById('medium-view').style.display = size === 'medium' ? 'flex' : 'none';
                document.getElementById('small-view').style.display = size === 'small' ? 'flex' : 'none';
                document.querySelectorAll('.size-selector button').forEach(b => b.classList.remove('active'));
                document.getElementById('btn-' + size).classList.add('active');
              }
            </script>
          </body>
        </html>
      `);
    };

    const printAllStickers = () => {
      const printersWithAssets = printers.filter(p => p.assetNumber);
      if (printersWithAssets.length === 0) {
        alert('No printers with asset numbers. Generate asset numbers first!');
        return;
      }
      
      const printWindow = window.open('', '_blank');
      const stickersHTML = printersWithAssets.map(printer => {
        const portalUrl = `https://printer-rental-app.web.app/?asset=${encodeURIComponent(printer.assetNumber || printer.serialNumber)}`;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(portalUrl)}`;
        return `
          <div class="sticker">
            <div class="sticker-info">
              <div class="company-name">${stickerCompanyOf(printer)}</div>
              <div class="asset-number">${printer.assetNumber}</div>
              <div class="make-model">${printer.make}</div>
              <div class="model">${printer.model}</div>
              <div class="serial">S/N: ${printer.serialNumber}</div>
            </div>
            <div class="qr-section">
              <img src="${qrUrl}" alt="QR"/>
            </div>
          </div>
        `;
      }).join('');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>All Asset Stickers (${printersWithAssets.length})</title>
            <style>
              @page { size: A4; margin: 5mm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 5px; }
              .controls { text-align: center; margin-bottom: 15px; }
              .controls button { padding: 12px 30px; margin: 5px; background: #2563eb; color: white; border: none; border-radius: 5px; font-size: 16px; cursor: pointer; }
              .sticker-grid { 
                display: grid; 
                grid-template-columns: repeat(4, 1fr); 
                gap: 2mm; 
              }
              .sticker {
                width: 100%;
                height: 35mm;
                border: 1px dashed #999;
                padding: 2mm;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                gap: 2mm;
                page-break-inside: avoid;
                background: white;
              }
              .sticker-info { flex: 1; overflow: hidden; }
              .company-name { font-size: 7px; color: #666; font-weight: bold; line-height: 1.1; }
              .asset-number { font-size: 14px; font-weight: bold; color: #2563eb; margin: 2px 0; letter-spacing: 0.5px; }
              .make-model { font-size: 10px; font-weight: 600; }
              .model { font-size: 9px; color: #333; }
              .serial { font-size: 8px; color: #666; }
              .qr-section { width: 45px; height: 45px; flex-shrink: 0; }
              .qr-section img { width: 45px; height: 45px; }
              @media print {
                .controls { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="controls">
              <h1>Asset Stickers (${printersWithAssets.length} items)</h1>
              <p>4 stickers per row • Cut along dashed lines</p>
              <button onclick="window.print()">🖨️ Print All</button>
              <button onclick="window.close()" style="background:#ef4444;">✕ Close</button>
            </div>
            <div class="sticker-grid">${stickersHTML}</div>
          </body>
        </html>
      `);
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-bold">Asset Management</h2>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <button
                onClick={() => setShowAssetTools(v => !v)}
                className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"
              >
                <Wrench size={18} /> Tools
              </button>
              {showAssetTools && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAssetTools(false)} />
                  <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 z-20 max-h-[75vh] overflow-y-auto">
                    {canEdit && printers.some(p => !p.assetNumber) && (
                      <button
                        onClick={async () => {
                          setShowAssetTools(false);
                          const withoutAsset = printers.filter(p => !p.assetNumber);
                          if (!window.confirm(`Generate asset numbers for ${withoutAsset.length} printers?`)) return;

                          const prefixMap = getAssetPrefixMap(currentCompany);
                          const counters = {Printer: 0, Desktop: 0, Laptop: 0, Scanner: 0};

                          printers.forEach(p => {
                            if (p.assetNumber) {
                              Object.entries(prefixMap).forEach(([cat, prefix]) => {
                                if (p.assetNumber.startsWith(prefix)) {
                                  const num = parseInt(p.assetNumber.replace(prefix, '')) || 0;
                                  counters[cat] = Math.max(counters[cat], num);
                                }
                              });
                            }
                          });

                          Object.entries(prefixMap).forEach(([cat, pfx]) => { counters[cat] = Math.max(counters[cat] || 0, assetFloor(pfx)); });   // never reuse an issued number
                          let updated = 0;
                          for (const printer of withoutAsset) {
                            const category = printer.category || 'Printer';
                            const prefix = prefixMap[category];
                            counters[category]++;
                            const assetNumber = `${prefix}${String(counters[category]).padStart(4, '0')}`;
                            const updatedPrinter = { ...printer, assetNumber, category };

                            try {
                              await setDoc(doc(db, 'printers', printer.id), updatedPrinter);
                              await logAudit('UPDATE', 'Asset', printer.id, `Assigned asset number ${assetNumber} (bulk generate)`, updatedPrinter);
                              updated++;
                            } catch (e) {
                              console.error('Error updating', printer.id, e);
                            }
                          }

                          alert(`✅ Generated asset numbers for ${updated} printers!`);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-purple-700"
                      >
                        <span className="w-4 text-center">🏷️</span> Generate Numbers ({printers.filter(p => !p.assetNumber).length})
                      </button>
                    )}
                    <button
                      onClick={() => { setShowAssetTools(false); printAllStickers(); }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-indigo-700"
                    >
                      <span className="w-4 text-center">📄</span> Print All Stickers
                    </button>
                    {(() => {
                      const seen = {};
                      let dupeCount = 0;
                      printers.forEach(p => {
                        if (!p.assetNumber) return;
                        seen[p.assetNumber] = (seen[p.assetNumber] || 0) + 1;
                      });
                      dupeCount = Object.values(seen).filter(c => c > 1).reduce((sum, c) => sum + (c - 1), 0);
                      if (dupeCount === 0 || userRole !== 'super_admin') return null;
                      return (
                        <button
                          onClick={() => {
                            setShowAssetTools(false);
                            // Build a preview of exactly what will be kept vs renumbered, from current data,
                            // so the Super Admin can review before anything changes.
                            const groups = {};
                            printers.forEach(p => {
                              if (!p.assetNumber) return;
                              if (!groups[p.assetNumber]) groups[p.assetNumber] = [];
                              groups[p.assetNumber].push(p);
                            });
                            const preview = Object.entries(groups)
                              .filter(([, group]) => group.length > 1)
                              .map(([assetNum, group]) => ({ assetNum, keep: group[0], toRenumber: group.slice(1) }));
                            setAssetDupePreview(preview);
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-amber-700"
                        >
                          <span className="w-4 text-center">⚠️</span> Fix Duplicates ({dupeCount})
                        </button>
                      );
                    })()}
                    {userRole === 'super_admin' && (
                      <button
                        onClick={() => {
                          setShowAssetTools(false);
                          const groups = {};
                          printers.forEach(p => {
                            if (!p.serialNumber) return;
                            const key = p.serialNumber.trim().toLowerCase();
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(p);
                          });
                          const dupeSerials = Object.values(groups).filter(g => g.length > 1);
                          if (dupeSerials.length === 0) {
                            alert('✅ No duplicate serial numbers found across your Assets.');
                            return;
                          }
                          setSerialDupeCheck(dupeSerials);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-rose-700"
                      >
                        <span className="w-4 text-center">🔍</span> Check Duplicate Serials
                      </button>
                    )}
                    {userRole === 'super_admin' && (
                      <button
                        onClick={() => {
                          setShowAssetTools(false);
                          // Only groups records sharing BOTH Asset Number AND Serial
                          // Number - these are true copies of the same row (e.g. from
                          // an import run twice), not two different real printers
                          // that happen to share a number - so deleting is safe here,
                          // unlike the "Fix Duplicates" (renumber) tool above.
                          const groups = {};
                          printers.forEach(p => {
                            if (!p.assetNumber || !p.serialNumber) return;
                            const key = `${p.assetNumber.trim().toLowerCase()}__${p.serialNumber.trim().toLowerCase()}`;
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(p);
                          });
                          const preview = Object.values(groups)
                            .filter(group => group.length > 1)
                            .map(group => {
                              // Prefer keeping the one currently "rented" (it's the
                              // one actually referenced elsewhere), else the first.
                              const sorted = [...group].sort((a, b) => (b.status === 'rented' ? 1 : 0) - (a.status === 'rented' ? 1 : 0));
                              return { assetNumber: sorted[0].assetNumber, serialNumber: sorted[0].serialNumber, keep: sorted[0], toDelete: sorted.slice(1) };
                            });
                          if (preview.length === 0) {
                            alert('✅ No exact duplicate assets found (same Asset Number and Serial Number).');
                            return;
                          }
                          setExactDupeDeletePreview(preview);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-red-700"
                      >
                        <span className="w-4 text-center">🗑️</span> Delete Exact Duplicates
                      </button>
                    )}
                    {userRole === 'super_admin' && (
                      <button
                        onClick={() => {
                          setShowAssetTools(false);
                          // 1. Printers marked "rented" with ZERO active rentals actually pointing to them
                          const orphanedRented = printers.filter(p => {
                            if (p.status !== 'rented') return false;
                            return !rentals.some(r => r.serialNumber === p.serialNumber && r.status !== 'returned');
                          });

                          // 2. Printers with 2+ ACTIVE rentals pointing to the same serial (over-linked)
                          const serialToActiveRentals = {};
                          rentals.filter(r => r.status !== 'returned').forEach(r => {
                            if (!r.serialNumber) return;
                            if (!serialToActiveRentals[r.serialNumber]) serialToActiveRentals[r.serialNumber] = [];
                            serialToActiveRentals[r.serialNumber].push(r);
                          });
                          const overLinked = Object.entries(serialToActiveRentals)
                            .filter(([, list]) => list.length > 1)
                            .map(([serial, list]) => ({ serial, rentals: list, printer: printers.find(p => p.serialNumber === serial) }));

                          // 3. Rentals whose serial doesn't match ANY printer record at all (ghost rentals)
                          const ghostRentals = rentals.filter(r => r.status !== 'returned' && r.serialNumber && !printers.some(p => p.serialNumber === r.serialNumber));

                          if (orphanedRented.length === 0 && overLinked.length === 0 && ghostRentals.length === 0) {
                            alert('✅ No mismatches found — every "rented" printer has exactly one active rental, and every active rental points to a real printer.');
                            return;
                          }
                          setIntegrityCheck({ orphanedRented, overLinked, ghostRentals });
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-indigo-700"
                      >
                        <span className="w-4 text-center">🩺</span> Data Integrity Check
                      </button>
                    )}
                    {userRole === 'super_admin' && currentCompany === 'canopus' && (() => {
                      const rbPrefixToCn = { RBPRN: 'CNPRN', RBDESK: 'CNDESK', RBLAP: 'CNLAP', RBSCN: 'CNSCN' };
                      const legacyPrinters = printers.filter(p => p.assetNumber && Object.keys(rbPrefixToCn).some(rb => p.assetNumber.startsWith(rb)));
                      if (legacyPrinters.length === 0) return null;
                      return (
                        <button
                          onClick={() => {
                            setShowAssetTools(false);
                            // One-time historical cleanup: relabel this company's assets
                            // that still carry the old shared RB-prefix numbering (from
                            // before per-company prefixes existed) with a fresh CN
                            // sequence starting at 0001 per category - Canopus gets its
                            // own count, not a copy of RB Infotech's old digits.
                            const existingNumbers = new Set(printers.map(p => p.assetNumber).filter(Boolean));
                            const byPrefix = {};
                            legacyPrinters.forEach(p => {
                              const rbPrefix = Object.keys(rbPrefixToCn).find(rb => p.assetNumber.startsWith(rb));
                              const cnPrefix = rbPrefixToCn[rbPrefix];
                              (byPrefix[cnPrefix] = byPrefix[cnPrefix] || []).push(p);
                            });
                            const preview = [];
                            Object.entries(byPrefix).forEach(([cnPrefix, group]) => {
                              // Stable order: by current asset number, so re-running this
                              // (or reviewing the preview) is predictable.
                              const sorted = [...group].sort((a, b) => a.assetNumber.localeCompare(b.assetNumber));
                              let n = assetFloor(cnPrefix) + 1;   // numbers are never reused
                              sorted.forEach(p => {
                                let newNumber = `${cnPrefix}${String(n).padStart(4, '0')}`;
                                while (existingNumbers.has(newNumber)) {
                                  n++;
                                  newNumber = `${cnPrefix}${String(n).padStart(4, '0')}`;
                                }
                                existingNumbers.add(newNumber);
                                preview.push({ printer: p, oldNumber: p.assetNumber, newNumber });
                                n++;
                              });
                            });
                            setRelabelPreview(preview);
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-teal-700"
                        >
                          <span className="w-4 text-center">🏷️</span> Relabel to CN Prefix ({legacyPrinters.length})
                        </button>
                      );
                    })()}
                    {userRole === 'super_admin' && printers.length > 0 && (
                      <>
                        <div className="border-t border-slate-100 my-1" />
                        <button
                          onClick={async () => {
                            setShowAssetTools(false);
                            const typed = window.prompt(`⚠️ This will permanently delete ALL ${printers.length} assets and their ${rentals.length} linked rentals.\n\nClients and Users will NOT be affected.\n\nType CLEAR ASSETS to confirm:`);
                            if (typed !== 'CLEAR ASSETS') {
                              if (typed !== null) alert('Text did not match. Cancelled — nothing was deleted.');
                              return;
                            }

                            try {
                              // Delete all printers
                              let batch = writeBatch(db);
                              let opCount = 0;
                              for (const p of printers) {
                                batch.delete(doc(db, 'printers', p.id));
                                opCount++;
                                if (opCount === 450) { await batch.commit(); batch = writeBatch(db); opCount = 0; }
                              }
                              if (opCount > 0) await batch.commit();

                              // Delete all rentals (linked to those printers)
                              batch = writeBatch(db);
                              opCount = 0;
                              for (const r of rentals) {
                                batch.delete(doc(db, 'rentals', r.id));
                                opCount++;
                                if (opCount === 450) { await batch.commit(); batch = writeBatch(db); opCount = 0; }
                              }
                              if (opCount > 0) await batch.commit();

                              await logAudit('DELETE', 'Database', 'clear-assets', `Cleared ${printers.length} assets and ${rentals.length} rentals`, {});
                              alert(`✅ Cleared ${printers.length} assets and ${rentals.length} rentals. Clients and Users untouched.`);
                            } catch (error) {
                              alert('❌ Error clearing assets: ' + error.message);
                            }
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 flex items-center gap-2.5 text-red-700 font-medium"
                        >
                          <span className="w-4 text-center">🗑️</span> Clear Assets & Rentals
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            {canImport && <button onClick={() => { setImportType('printers'); setShowImportDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Upload size={18} /> Import</button>}
            {canExport && <button onClick={() => exportToExcel(filteredPrinters.map(p => ({
              AssetNumber: p.assetNumber || '',
              SerialNumber: p.serialNumber || '',
              Category: p.category || '',
              Make: p.make || '',
              Model: p.model || '',
              Type: p.type || '',
              Status: p.status || '',
              RentedTo: getClientForPrinter(p.serialNumber) || '',
              PurchaseDate: p.purchaseDate || '',
              WarrantyDate: p.warrantyDate || '',
              Processor: p.processor || '',
              RAM: p.ram || '',
              Storage: p.storage || '',
              OS: p.os || '',
              BIOSSerial: p.biosSerial || ''
            })), 'printers')} className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"><Download size={18} /> Export</button>}
            {canEdit && <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Plus size={18} /> Add</button>}
          </div>
        </div>

        {/* Search Box */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-4 space-y-3">
          <input
            type="text"
            placeholder="🔍 Search by asset no, serial, make, model, or client..."
            value={searchPrinters}
            onChange={(e) => setSearchPrinters(e.target.value)}
            className="w-full px-4 py-2.5 md:py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
          />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-sm"
            >
              <option value="">Filter by Type (all)</option>
              <optgroup label="Asset type">
                {['Printer', 'Desktop', 'Laptop', 'Scanner'].map(c => <option key={c} value={`cat:${c}`}>{c} ({printers.filter(p => assetCategoryOf(p) === c).length})</option>)}
              </optgroup>
              <optgroup label="Printer type">
                {Object.entries(PRINTER_TYPE_LABELS).map(([k, l]) => <option key={k} value={`type:${k}`}>{l} ({printers.filter(p => assetCategoryOf(p) === 'Printer' && (p.type || 'A4') === k).length})</option>)}
              </optgroup>
            </select>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-sm"
            >
              <option value="">Filter by Client (all)</option>
              {[...new Set(printers.map(p => getClientForPrinter(p.serialNumber)).filter(c => c && c !== '-'))].sort().map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-sm"
            >
              <option value="">Filter by Model (all)</option>
              {[...new Set(printers.filter(p => p.make && p.model).map(p => `${p.make} ${p.model}`))].sort().map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-sm"
            >
              <option value="">Filter by Status (all)</option>
              <option value="available">Available</option>
              <option value="rented">Rented</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          {(filterClient || filterModel || filterStatus || filterType) && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {filteredPrinters.length} of {printers.length} assets</span>
              <button onClick={() => { setFilterClient(''); setFilterModel(''); setFilterStatus(''); setFilterType(''); }} className="text-brand-600 hover:underline font-medium">Clear filters</button>
            </div>
          )}
        </div>

        {serialDupeCheck && (
          <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-rose-900 text-lg">🔍 Duplicate Serial Numbers Found</h3>
                <p className="text-sm text-rose-700">These are DIFFERENT asset records that share the same Serial Number. This is a data problem, not something to auto-fix - please check which one has the correct serial, and correct the wrong one via Edit.</p>
              </div>
              <button onClick={() => setSerialDupeCheck(null)} className="text-rose-600 hover:text-rose-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3">
              {serialDupeCheck.map((group, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-rose-200 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Shared Serial: {group[0].serialNumber}</p>
                  {group.map(printer => {
                    const linkedRentals = rentals.filter(r => r.serialNumber === printer.serialNumber);
                    return (
                      <div key={printer.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2.5 mb-2 last:mb-0">
                        <div>
                          <p className="text-sm font-medium">{printer.assetNumber || 'No asset number'} — {printer.make} {printer.model}</p>
                          <p className="text-xs text-slate-500">
                            {linkedRentals.length > 0
                              ? linkedRentals.map(r => `${r.clientName} (${r.status})`).join(', ')
                              : 'No rentals linked'}
                          </p>
                        </div>
                        <button
                          onClick={() => { openModal(printer); setSerialDupeCheck(null); }}
                          className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg font-medium transition-colors duration-150 shrink-0"
                        >
                          Edit this one
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {exactDupeDeletePreview && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-red-900 text-lg">🗑️ Exact Duplicate Assets Found</h3>
                <p className="text-sm text-red-700">These records share the same Asset Number AND Serial Number - true copies of the same row. Review before confirming deletion.</p>
              </div>
              <button onClick={() => setExactDupeDeletePreview(null)} className="text-red-600 hover:text-red-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3">
              {exactDupeDeletePreview.map((group, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-red-200 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">{group.assetNumber} • S/N: {group.serialNumber}</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ KEEP</span>
                      <span className="text-sm">{group.keep.make} {group.keep.model} — {group.keep.status}</span>
                    </div>
                  </div>
                  {group.toDelete.map(printer => (
                    <div key={printer.id} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 last:mb-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">✕ DELETE</span>
                        <span className="text-sm">{printer.make} {printer.model} — {printer.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  let deletedCount = 0;
                  for (const group of exactDupeDeletePreview) {
                    for (const printer of group.toDelete) {
                      try {
                        await logAudit('DELETE', 'Asset', printer.id, `Exact duplicate deleted (${group.assetNumber} / ${group.serialNumber})`, printer);
                        await deleteDoc(doc(db, 'printers', printer.id));
                        deletedCount++;
                      } catch (e) {
                        console.error('Error deleting exact duplicate printer', printer.id, e);
                      }
                    }
                  }
                  setExactDupeDeletePreview(null);
                  alert(`✅ Deleted ${deletedCount} exact duplicate asset(s). Full details of what was removed are in Audit Logs.`);
                }}
                className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium"
              >
                Confirm & Delete Duplicates
              </button>
              <button
                onClick={() => setExactDupeDeletePreview(null)}
                className="bg-white text-slate-700 border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {integrityCheck && (
          <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-5 space-y-5">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-indigo-900 text-lg">🩺 Data Integrity Check Results</h3>
                <p className="text-sm text-indigo-700">Mismatches between what a printer's status says and what the Rentals data actually shows. Fix each one via Edit.</p>
              </div>
              <button onClick={() => setIntegrityCheck(null)} className="text-indigo-600 hover:text-indigo-800 p-1"><X size={20} /></button>
            </div>

            {integrityCheck.orphanedRented.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">⚠️ Marked "rented" but no active rental found ({integrityCheck.orphanedRented.length})</p>
                <div className="space-y-2">
                  {integrityCheck.orphanedRented.map(printer => (
                    <div key={printer.id} className="flex items-center justify-between bg-white rounded-lg border border-indigo-200 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium">{printer.assetNumber || 'No asset number'} — {printer.make} {printer.model}</p>
                        <p className="text-xs text-slate-500">S/N: {printer.serialNumber}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => { openModal(printer); setIntegrityCheck(null); }} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg font-medium transition-colors duration-150">Edit Printer</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {integrityCheck.overLinked.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">⚠️ Multiple active rentals pointing to the same printer ({integrityCheck.overLinked.length})</p>
                <div className="space-y-2">
                  {integrityCheck.overLinked.map(group => (
                    <div key={group.serial} className="bg-white rounded-lg border border-indigo-200 p-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">S/N: {group.serial} {group.printer ? `(${group.printer.assetNumber})` : '(no matching printer record)'}</p>
                      {group.rentals.map(r => (
                        <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 mb-1 last:mb-0">
                          <p className="text-sm">{r.clientName} <span className="text-xs text-slate-400">({r.status})</span></p>
                          <button onClick={() => { setEditingItem(r); setModalType('rental'); setShowModal(true); setIntegrityCheck(null); setCurrentView('rentals'); }} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2.5 py-1 rounded-lg font-medium transition-colors duration-150">Edit Rental</button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {integrityCheck.ghostRentals.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">❌ Active rentals whose Serial Number matches no printer at all ({integrityCheck.ghostRentals.length})</p>
                <div className="space-y-2">
                  {integrityCheck.ghostRentals.map(r => (
                    <div key={r.id} className="flex items-center justify-between bg-white rounded-lg border border-indigo-200 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium">{r.clientName}</p>
                        <p className="text-xs text-slate-500">S/N: {r.serialNumber} (not found in Assets)</p>
                      </div>
                      <button onClick={() => { setEditingItem(r); setModalType('rental'); setShowModal(true); setIntegrityCheck(null); setCurrentView('rentals'); }} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg font-medium transition-colors duration-150 shrink-0">Edit Rental</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {assetDupePreview && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-amber-900 text-lg">⚠️ Duplicate Asset Numbers Found</h3>
                <p className="text-sm text-amber-700">Both printers below are real assets and will be kept — only their Asset Number changes. Review before confirming.</p>
              </div>
              <button onClick={() => setAssetDupePreview(null)} className="text-amber-600 hover:text-amber-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3">
              {assetDupePreview.map(group => (
                <div key={group.assetNum} className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Duplicated Number: {group.assetNum}</p>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ KEEPS {group.assetNum}</span>
                      <span className="font-medium text-sm">{group.keep.make} {group.keep.model}</span>
                    </div>
                    <p className="text-xs text-slate-500">Serial: {group.keep.serialNumber}</p>
                  </div>

                  {group.toRenumber.map(printer => (
                    <div key={printer.id} className="bg-brand-50 border border-brand-200 rounded-lg p-3 mb-2 last:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full">↻ WILL BE RENUMBERED</span>
                        <span className="font-medium text-sm">{printer.make} {printer.model}</span>
                      </div>
                      <p className="text-xs text-slate-500">Serial: {printer.serialNumber}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  const prefixMap = getAssetPrefixMap(currentCompany);
                  const freshSnapshot = await getDocs(collection(db, 'printers'));
                  const freshPrinters = freshSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

                  // Track highest number used per category so renumbered assets don't collide further
                  const counters = { Printer: 0, Desktop: 0, Laptop: 0, Scanner: 0 };
                  freshPrinters.forEach(p => {
                    if (p.assetNumber) {
                      Object.entries(prefixMap).forEach(([cat, prefix]) => {
                        if (p.assetNumber.startsWith(prefix)) {
                          const num = parseInt(p.assetNumber.replace(prefix, '')) || 0;
                          counters[cat] = Math.max(counters[cat], num);
                        }
                      });
                    }
                  });

                  Object.entries(prefixMap).forEach(([cat, pfx]) => { counters[cat] = Math.max(counters[cat] || 0, assetFloor(pfx)); });   // never reuse an issued number
                  let fixedCount = 0;
                  let syncedRentalCount = 0;
                  for (const group of assetDupePreview) {
                    for (const printer of group.toRenumber) {
                      const category = printer.category || 'Printer';
                      const prefix = prefixMap[category] || 'RBPRN';
                      counters[category]++;
                      const newAssetNumber = `${prefix}${String(counters[category]).padStart(4, '0')}`;
                      const updatedPrinter = { ...printer, assetNumber: newAssetNumber };
                      try {
                        await setDoc(doc(db, 'printers', printer.id), updatedPrinter);
                        await logAudit('UPDATE', 'Asset', printer.id, `Renumbered ${group.assetNum} → ${newAssetNumber} (duplicate fix)`, updatedPrinter);
                        fixedCount++;

                        // Keep any rental(s) referencing this printer's serial number
                        // in sync, so the Rentals page doesn't keep showing the old,
                        // now-incorrect asset number after this renumber.
                        const matchingRentals = rentals.filter(r => r.serialNumber === printer.serialNumber);
                        for (const r of matchingRentals) {
                          const updatedRental = { ...r, assetNumber: newAssetNumber };
                          await setDoc(doc(db, 'rentals', r.id), updatedRental);
                          syncedRentalCount++;
                        }
                      } catch (e) {
                        console.error('Error renumbering', printer.id, e);
                      }
                    }
                  }

                  setAssetDupePreview(null);
                  alert(`✅ Fixed ${fixedCount} duplicate asset number(s)! Each duplicate was renumbered to the next available number in its category.${syncedRentalCount > 0 ? ` Also synced ${syncedRentalCount} linked rental record(s) to match.` : ''}`);
                }}
                className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium"
              >
                Confirm & Renumber Duplicates
              </button>
              <button
                onClick={() => setAssetDupePreview(null)}
                className="bg-white text-slate-700 border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {relabelPreview && (
          <div className="bg-teal-50 border-2 border-teal-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-teal-900 text-lg">🏷️ Relabel Legacy Asset Numbers</h3>
                <p className="text-sm text-teal-700">These Canopus assets are still numbered with the old shared RB-prefix scheme. Each one gets a fresh CN-prefixed number below, starting at 0001 per category - Canopus's own sequence, independent of RB Infotech's numbers.</p>
              </div>
              <button onClick={() => setRelabelPreview(null)} className="text-teal-600 hover:text-teal-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-2">
              {relabelPreview.map(item => (
                <div key={item.printer.id} className="flex items-center justify-between rounded-lg border p-3 bg-white border-teal-200">
                  <div>
                    <p className="text-sm font-medium">{item.printer.make} {item.printer.model} <span className="text-xs text-slate-400">S/N {item.printer.serialNumber}</span></p>
                    <p className="text-xs">
                      <span className="font-mono text-slate-500">{item.oldNumber}</span>
                      {' → '}
                      <span className="font-mono font-semibold text-teal-700">{item.newNumber}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  let relabeledCount = 0;
                  let syncedRentalCount = 0;
                  for (const item of relabelPreview) {
                    try {
                      const updated = { ...item.printer, assetNumber: item.newNumber };
                      await setDoc(doc(db, 'printers', item.printer.id), updated);
                      await logAudit('UPDATE', 'Asset', item.printer.id, `Relabeled ${item.oldNumber} → ${item.newNumber} (legacy RB→CN prefix cleanup)`, updated);
                      relabeledCount++;

                      const matchingRentals = rentals.filter(r => r.serialNumber === item.printer.serialNumber);
                      for (const r of matchingRentals) {
                        const updatedRental = { ...r, assetNumber: item.newNumber };
                        await setDoc(doc(db, 'rentals', r.id), updatedRental);
                        syncedRentalCount++;
                      }
                    } catch (e) {
                      console.error('Error relabeling', item.printer.id, e);
                    }
                  }
                  setRelabelPreview(null);
                  alert(`✅ Relabeled ${relabeledCount} asset(s) to the CN prefix.${syncedRentalCount > 0 ? ` Also synced ${syncedRentalCount} linked rental record(s) to match.` : ''}`);
                }}
                className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium"
              >
                Confirm & Relabel
              </button>
              <button
                onClick={() => setRelabelPreview(null)}
                className="bg-white text-slate-700 border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Asset No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Serial</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Make/Model</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rented To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Warranty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPrinters.map(printer => (
                <tr key={printer.id}>
                  <td className="px-6 py-4 font-bold text-brand-700">{printer.assetNumber || '-'}</td>
                  <td className="px-6 py-4">{printer.serialNumber}</td>
                  <td className="px-6 py-4">
                    <p>{printer.make} {printer.model}</p>
                    {(printer.category === 'Desktop' || printer.category === 'Laptop') && (printer.processor || printer.ram || printer.storage || printer.os || printer.biosSerial) && (
                      <p className="text-xs text-slate-400">{[printer.processor, printer.ram, printer.storage, printer.os, printer.biosSerial && `BIOS: ${printer.biosSerial}`].filter(Boolean).join(' · ')}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">{assetTypeLabel(printer)}</td>
                  <td className="px-6 py-4">
                    {userRole === 'super_admin' ? (
                      <select
                        value={printer.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          if (newStatus === printer.status) return;

                          // Safety check: warn if forcing a "rented" printer to "available"
                          // while an active rental record still references it - that would be
                          // a data mismatch (rental thinks it's still assigned).
                          const activeRental = rentals.find(r => r.serialNumber === printer.serialNumber && r.status !== 'returned');
                          if (newStatus === 'available' && activeRental) {
                            if (!window.confirm(`⚠️ This printer still has an active rental:\n\n${activeRental.clientName}\n\nForcing status to "Available" will NOT close that rental automatically - it'll still show as active there, creating a mismatch.\n\nUse "Check In / Return" on the Rentals page instead if this printer was genuinely returned.\n\nForce status change anyway?`)) {
                              return;
                            }
                          }

                          try {
                            const updated = { ...printer, status: newStatus };
                            await setDoc(doc(db, 'printers', printer.id), updated);
                            await logAudit('UPDATE', 'Asset', printer.id, `${printer.assetNumber || printer.serialNumber} status: ${printer.status} → ${newStatus}`, updated);
                          } catch (err) {
                            alert('Error updating status: ' + err.message);
                          }
                        }}
                        className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 outline-none cursor-pointer ${printer.status === 'available' ? 'bg-green-100 text-green-800' : printer.status === 'rented' ? 'bg-brand-100 text-brand-800' : 'bg-amber-100 text-amber-800'}`}
                      >
                        <option value="available">available</option>
                        <option value="rented">rented</option>
                        <option value="maintenance">maintenance</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs ${printer.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-brand-100 text-brand-800'}`}>{printer.status}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">{getClientForPrinter(printer.serialNumber)}</td>
                  <td className="px-6 py-4">
                    {printer.warrantyDate ? (() => {
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const daysLeft = Math.ceil((new Date(printer.warrantyDate) - today) / (1000 * 60 * 60 * 24));
                      const badgeColor = daysLeft < 0 ? 'bg-red-100 text-red-700' : daysLeft <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${badgeColor}`}>
                          {printer.warrantyDate}{daysLeft < 0 ? ' (expired)' : ''}
                        </span>
                      );
                    })() : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    {canEdit && <button onClick={() => openModal(printer)} className="p-1.5 rounded-full hover:bg-brand-50 text-brand-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Edit"><Edit size={18} /></button>}
                    {canDelete && <button onClick={() => handleDelete(printer.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Delete"><Trash2 size={18} /></button>}
                    <div className="relative">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === printer.id ? null : printer.id)}
                        className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:scale-110 active:scale-95 transition-all duration-150"
                        title="More actions"
                      >
                        <MoreVertical size={18} />
                      </button>
                      {openActionMenu === printer.id && (
                        <>
                          {/* Invisible full-screen backdrop, click-to-close - simpler and
                              more reliable across browsers than a document click listener */}
                          <div className="fixed inset-0 z-10" onClick={() => setOpenActionMenu(null)} />
                          <div className="absolute right-0 top-full mt-1 w-60 bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 z-20">
                            <button onClick={() => { showQRCode(printer); setOpenActionMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-slate-700">
                              <span className="w-4 text-center">📱</span> View QR Code
                            </button>
                            <button onClick={() => { setServiceHistorySerial(printer.serialNumber); setOpenActionMenu(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-slate-700">
                              <span className="w-4 text-center">🕓</span> Service History
                            </button>
                            {userRole === 'super_admin' && printer.assetNumber && (
                              <button
                                onClick={async () => {
                                  setOpenActionMenu(null);
                                  try {
                                    const oldNumber = printer.assetNumber;
                                    const prefix = getAssetPrefixMap(printer.companyId || currentCompany)[printer.category || 'Printer'];
                                    const freshSnap = await getDocs(collection(db, 'printers'));
                                    const fresh = freshSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                                    const newNumber = getNextAssetNumberForPrefix(prefix, fresh);   // never a previously issued number
                                    if (!window.confirm(`Give ${printer.make} ${printer.model} (S/N ${printer.serialNumber}) a NEW asset number?\n\n${oldNumber}  →  ${newNumber}\n\nLinked rentals are updated too. ${oldNumber} is retired and will not be reused. Reprint the sticker afterwards.`)) return;
                                    await setDoc(doc(db, 'printers', printer.id), { assetNumber: newNumber }, { merge: true });
                                    let synced = 0;
                                    for (const r of rentals.filter(x => x.serialNumber && x.serialNumber === printer.serialNumber)) {
                                      await setDoc(doc(db, 'rentals', r.id), { assetNumber: newNumber }, { merge: true });
                                      synced++;
                                    }
                                    await logAudit('UPDATE', 'Asset', printer.id, `Renumbered ${oldNumber} → ${newNumber} (new number; old number retired)`, { assetNumber: newNumber });
                                    alert(`✅ ${oldNumber} → ${newNumber}${synced ? ` (${synced} linked rental${synced > 1 ? 's' : ''} updated)` : ''}.\n\nPlease print a new sticker.`);
                                  } catch (err) { alert('Could not renumber: ' + err.message); }
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-slate-700"
                              >
                                <span className="w-4 text-center">🔢</span> Assign new asset number
                              </button>
                            )}
                            {userRole === 'super_admin' && (() => {
                              const otherCompanyId = Object.keys(COMPANIES).find(id => id !== currentCompany);
                              const otherCompanyName = COMPANIES[otherCompanyId]?.name || otherCompanyId;
                              return (
                                <button
                                  onClick={async () => {
                                    setOpenActionMenu(null);
                            // Transferring a printer that's still actively rented would leave
                            // the rental pointing at a printer that now belongs to a different
                            // company's fleet - the same data-mismatch class the status-change
                            // guard above already protects against, so block it here too.
                            const activeRental = rentals.find(r => r.serialNumber === printer.serialNumber && r.status !== 'returned');
                            if (activeRental) {
                              alert(`❌ Can't transfer - this printer still has an active rental:\n\n${activeRental.clientName}\n\nCheck in / return it first on the Rentals page, then transfer.`);
                              return;
                            }
                            // Assign the destination company's OWN prefix and next
                            // available number, same as brand-new assets and the
                            // Relabel tool - a transfer is exactly the situation
                            // that causes mismatched-label problems if the number
                            // is left as-is (the whole reason the Relabel tool
                            // exists), so keep it consistent going forward instead
                            // of accumulating more of the same mismatch each time.
                            const oldNumber = printer.assetNumber || '';
                            let newNumber = oldNumber;
                            if (oldNumber) {
                              const prefixMap = getAssetPrefixMap(otherCompanyId);
                              const newPrefix = prefixMap[printer.category || 'Printer'];
                              const existingNumbers = new Set(printersRaw.map(p => p.assetNumber).filter(Boolean));
                              let n = Math.max(assetFloor(newPrefix), ...printersRaw.map(p => String(p.assetNumber || '').startsWith(newPrefix) ? parseInt(String(p.assetNumber).replace(newPrefix, ''), 10) || 0 : 0)) + 1;   // never reuse
                              newNumber = `${newPrefix}${String(n).padStart(4, '0')}`;
                              while (existingNumbers.has(newNumber)) {
                                n++;
                                newNumber = `${newPrefix}${String(n).padStart(4, '0')}`;
                              }
                            }
                            const renumberNote = oldNumber && newNumber !== oldNumber ? `\n\nIt will be renumbered: ${oldNumber} → ${newNumber}\n(reprint the sticker after transferring)` : '';
                            const reason = window.prompt(`Transfer ${oldNumber || printer.serialNumber} from ${COMPANIES[currentCompany]?.name} to ${otherCompanyName}?${renumberNote}\n\nOptional reason (shown in Audit Logs):`, '');
                            if (reason === null) return;
                            try {
                              const updated = { ...printer, companyId: otherCompanyId, assetNumber: newNumber };
                              await setDoc(doc(db, 'printers', printer.id), updated);
                              await logAudit('UPDATE', 'Asset', printer.id, `Transferred ${COMPANIES[currentCompany]?.name} → ${otherCompanyName}${newNumber !== oldNumber ? `, renumbered ${oldNumber} → ${newNumber}` : ''}${reason ? ` (${reason})` : ''}`, updated);
                              alert(`✅ Transferred to ${otherCompanyName}${newNumber !== oldNumber ? ` as ${newNumber}` : ''}. Switch companies (top right) to see it there.${newNumber !== oldNumber ? ' Remember to print a new sticker.' : ''}`);
                            } catch (err) {
                              alert('❌ Error transferring asset: ' + err.message);
                            }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-teal-700"
                                >
                                  <span className="w-4 text-center">🔄</span> Transfer to {otherCompanyName}
                                </button>
                              );
                            })()}
                            {userRole === 'super_admin' && (() => {
                              const isFeatured = !!marketingHighlightIds.has(printer.id);
                              return (
                                <button
                                  onClick={async () => {
                                    setOpenActionMenu(null);
                                    if (isFeatured) {
                                      try {
                                        await deleteDoc(doc(db, 'marketingHighlights', printer.id));
                                      } catch (err) {
                                        alert('❌ Error unfeaturing: ' + err.message);
                                      }
                                      return;
                                    }
                                    // Only the fields below ever leave the app for this - no
                                    // client name, no serial number, nothing rental-specific.
                                    // Prefilled from this printer's own active rental if it has
                                    // one, but editable - this is marketing copy (a rate you're
                                    // willing to advertise to attract new business), not a
                                    // guaranteed quote, so it doesn't have to match any one
                                    // client's actual negotiated contract exactly.
                                    const linkedRental = rentals.find(r => r.serialNumber === printer.serialNumber && r.status !== 'returned');
                                    const monthlyRate = window.prompt('Monthly rate to advertise (₹):', linkedRental?.monthlyRate || '');
                                    if (monthlyRate === null) return;
                                    const a4PageCost = window.prompt('Cost per A4 page (₹):', linkedRental?.a4PageCost || '');
                                    if (a4PageCost === null) return;
                                    const cartridgeCost = window.prompt('Cost per cartridge (₹, leave blank if not applicable):', linkedRental?.cartridgeCost || '');
                                    if (cartridgeCost === null) return;
                                    try {
                                      const highlight = {
                                        id: printer.id,
                                        make: printer.make || '',
                                        model: printer.model || '',
                                        category: printer.category || 'Printer',
                                        type: printer.type || '',
                                        companyId: currentCompany,
                                        companyName: COMPANIES[currentCompany]?.name || '',
                                        monthlyRate: Number(monthlyRate) || 0,
                                        a4PageCost: Number(a4PageCost) || 0,
                                        cartridgeCost: Number(cartridgeCost) || 0,
                                        featuredAt: new Date().toISOString(),
                                        featuredBy: currentUser?.email || 'Unknown'
                                      };
                                      await setDoc(doc(db, 'marketingHighlights', printer.id), highlight);
                                      await logAudit('UPDATE', 'Asset', printer.id, `${printer.assetNumber || printer.serialNumber} featured for marketing (₹${monthlyRate}/mo, ₹${a4PageCost}/page)`, highlight);
                                      alert('✅ Featured! It\'ll show up on the daily marketing post page.');
                                    } catch (err) {
                                      alert('❌ Error featuring: ' + err.message);
                                    }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2.5 text-amber-700"
                                >
                                  <span className="w-4 text-center">{isFeatured ? '★' : '☆'}</span> {isFeatured ? 'Unfeature' : 'Feature for Marketing'}
                                </button>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // CLIENTS VIEW  
  const ClientsView = () => {
    const canEdit = hasPermission('canEditClients');
    const canDelete = hasPermission('canDelete');
    const canImport = hasPermission('canImport');
    const canExport = hasPermission('canExport');
    const [searchClients, setSearchClients] = useState('');
    const [showUnassignedClients, setShowUnassignedClients] = useState(true);
    const [engineerFilter, setEngineerFilter] = useState('');
    const [bulkEngineer, setBulkEngineer] = useState('');
    // Which client's rented-assets list is expanded inline on their card
    // (toggled by the "N assets provided" badge) - one at a time.
    const [expandedAssetsClientId, setExpandedAssetsClientId] = useState(null);

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setModalType('client');
      setEditingItem(item);
      setShowModal(true);
    };

    const handleDelete = async (id) => {
      if (!canDelete) return;
      if (window.confirm('Delete this client?')) {
        try {
          await deleteFirestoreDoc('clients', id);
        } catch (err) {
          alert('Error deleting client: ' + err.message);
        }
      }
    };

    // Creates a portal login for this specific client, pre-filled and
    // guaranteed to match their actual email - avoids the risk of a staff
    // member manually typing a mismatched email in User Management, which
    // would create a client-role account that can never see any of their
    // own data (the portal matches by exact email).
    const grantPortalAccess = async (client) => {
      if (!client.email) {
        alert('❌ This client has no email on file. Add one first (Edit → Email) before granting portal access.');
        return;
      }
      const password = window.prompt(`Set a temporary password for ${client.name} (${client.email}).\n\nShare this with them separately - they should change it after first login.`, '');
      if (!password) return;
      if (password.length < 6) {
        alert('❌ Password must be at least 6 characters (Firebase requirement).');
        return;
      }
      try {
        const secondaryApp = getApps().find(a => a.name === 'Secondary') || initializeApp(auth.app.options, 'Secondary');
        const secondaryAuth = getAuth(secondaryApp);
        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, client.email, password);
        const permissions = getPermissionsByRole('client');
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: client.email,
          name: client.name,
          role: 'client',
          permissions,
          createdAt: new Date(),
          isActive: true,
          loginMethod: 'email',
          linkedClientId: client.id
        });
        await secondaryAuth.signOut();

        const updatedClient = { ...client, portalAccess: true };
        await setDoc(doc(db, 'clients', client.id), updatedClient);
        await logAudit('CREATE', 'Client', client.id, `Granted portal access to ${client.name}`, {});

        alert(`✅ Portal access created for ${client.name}.\n\nLogin: ${client.email}\nPassword: ${password}\n\nShare these credentials with them securely.`);
      } catch (err) {
        if (err.code === 'auth/email-already-in-use') {
          alert(`❌ An account already exists for ${client.email}. They may already have portal access, or this email is used for a different login.`);
        } else {
          alert('❌ Error creating portal access: ' + err.message);
        }
      }
    };

    // Filter clients based on search - previousNames means a search for the
    // client's old (pre-rename) name still finds them.
    const filteredClients = clients.filter(client =>
      matchesSearch(searchClients, client.name, client.company, client.email, client.phone, client.allocatedEngineer, ...(client.previousNames || []))
    ).filter(client => !engineerFilter || (engineerFilter === '__none__' ? !client.allocatedEngineer : client.allocatedEngineer === engineerFilter));

    // Engineer-wise allocation in bulk: sets the allocated engineer on every
    // client currently shown (so filter/search first, then allocate).
    const bulkAllocate = async () => {
      if (!canEdit || !bulkEngineer) return;
      const target = bulkEngineer === '__clear__' ? '' : bulkEngineer;
      const label = target || 'nobody (clear allocation)';
      if (!window.confirm(`Allocate ${filteredClients.length} client${filteredClients.length !== 1 ? 's' : ''} to ${label}?`)) return;
      try {
        for (let i = 0; i < filteredClients.length; i += 400) {
          const batch = writeBatch(db);
          filteredClients.slice(i, i + 400).forEach(c => batch.set(doc(db, 'clients', c.id), { allocatedEngineer: target }, { merge: true }));
          await batch.commit();
        }
        await logAudit('UPDATE', 'Client', 'bulk-allocation', `${filteredClients.length} client(s) allocated to ${label}`, { count: filteredClients.length, engineer: target });
        setBulkEngineer('');
        alert(`✅ ${filteredClients.length} client${filteredClients.length !== 1 ? 's' : ''} allocated to ${label}.`);
      } catch (err) {
        alert(`❌ Could not allocate: ${err.message}`);
      }
    };

    // Consistent, deterministic avatar color per client (based on name hash)
    const avatarColors = ['bg-brand-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500'];
    const getAvatarColor = (name) => {
      const hash = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      return avatarColors[hash % avatarColors.length];
    };
    const getInitials = (name) => {
      if (!name) return '?';
      const words = name.trim().split(/\s+/).filter(Boolean);
      return words.length === 1 ? words[0].slice(0, 2).toUpperCase() : (words[0][0] + words[1][0]).toUpperCase();
    };
    // Only show company as a separate line if it's meaningfully different from the name
    const showCompany = (client) => {
      if (!client.company) return false;
      const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
      return normalize(client.company) !== normalize(client.name || '');
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-bold">Client Management</h2>
          <div className="flex gap-2 flex-wrap">
            {canImport && <button onClick={() => { setImportType('clients'); setShowImportDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Upload size={18} /> Import</button>}
            {canExport && <button onClick={() => exportToExcel(filteredClients.map(c => ({
              Name: c.name || '',
              Company: c.company || '',
              Phone: c.phone || '',
              Email: c.email || '',
              Address: c.address || '',
              GSTIN: c.gstin || '',
              AllocatedEngineer: c.allocatedEngineer || '',
              AssetsProvided: rentals.filter(r => r.clientName === c.name && r.status !== 'returned').length
            })), 'clients')} className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"><Download size={18} /> Export</button>}
            {canEdit && <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Plus size={18} /> Add</button>}
          </div>
        </div>

        {/* Search Box */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-4">
          <input
            type="text"
            placeholder="🔍 Search by name, company, email, or phone..."
            value={searchClients}
            onChange={(e) => setSearchClients(e.target.value)}
            className="w-full px-4 py-2.5 md:py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
          />
          <div className="flex gap-2 flex-wrap items-center mt-3">
            <select value={engineerFilter} onChange={(e) => setEngineerFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500">
              <option value="">👷 All engineers</option>
              <option value="__none__">Not allocated</option>
              {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name} ({clients.filter(c => c.allocatedEngineer === eng.name).length})</option>)}
            </select>
            {canEdit && (
              <>
                <select value={bulkEngineer} onChange={(e) => setBulkEngineer(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500">
                  <option value="">Allocate shown clients to…</option>
                  {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name}</option>)}
                  <option value="__clear__">Clear allocation</option>
                </select>
                {bulkEngineer && <button onClick={bulkAllocate} className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-2 rounded-lg text-sm font-medium">Allocate {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}</button>}
              </>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-2">
            {filteredClients.length.toLocaleString()} client{filteredClients.length !== 1 ? 's' : ''}
            {searchClients ? ` matching "${searchClients}"` : ' total'}
            {engineerFilter ? (engineerFilter === '__none__' ? ' · not allocated' : ` · ${engineerFilter}`) : ''}
          </p>
        </div>

        {/* Client Grid or Empty State */}
        {filteredClients.length > 0 ? (
          <>
            {(() => {
              const withAssets = [];
              const withoutAssets = [];
              filteredClients.forEach(client => {
                const assetCount = rentals.filter(r => r.clientName === client.name && r.status !== 'returned').length;
                (assetCount > 0 ? withAssets : withoutAssets).push({ ...client, __assetCount: assetCount });
              });

              const renderClientCard = (client) => (
                <div key={client.id} className="group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-brand-200 transition-all p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`${getAvatarColor(client.name)} text-white rounded-full w-11 h-11 flex items-center justify-center font-bold text-sm shrink-0`}>
                        {getInitials(client.name)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2" title={client.name}>
                          {client.name}
                        </h3>
                        {showCompany(client) && (
                          <p className="text-sm text-slate-500 line-clamp-1 mt-0.5" title={client.company}>{client.company}</p>
                        )}
                        {client.previousNames?.length > 0 && (
                          <p className="text-xs text-slate-400 italic mt-0.5" title={client.previousNames.join(', ')}>
                            formerly {client.previousNames[client.previousNames.length - 1]}
                          </p>
                        )}
                        {client.allocatedEngineer && <p className="text-xs text-brand-700 mt-1">👷 {client.allocatedEngineer}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                      <button onClick={() => setActivityClientId(client.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Activity Timeline"><FileText size={16} /></button>
                      {canEdit && <button onClick={() => openModal(client)} className="p-1.5 rounded hover:bg-brand-50 text-brand-600"><Edit size={16} /></button>}
                      {canDelete && <button onClick={() => handleDelete(client.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={16} /></button>}
                    </div>
                  </div>
                  {client.__assetCount > 0 ? (
                    <button
                      onClick={() => setExpandedAssetsClientId(prev => prev === client.id ? null : client.id)}
                      className={`inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-xs font-medium mb-2 transition-colors duration-150 ${expandedAssetsClientId === client.id ? 'bg-brand-600 text-white' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}
                    >
                      🖨️ {client.__assetCount} asset{client.__assetCount !== 1 ? 's' : ''} provided {expandedAssetsClientId === client.id ? '▲' : '▼'}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-xs font-medium mb-2 bg-slate-50 text-slate-400">
                      🖨️ {client.__assetCount} assets provided
                    </span>
                  )}
                  {expandedAssetsClientId === client.id && (() => {
                    const clientRentals = rentals
                      .filter(r => r.clientName === client.name && r.status !== 'returned')
                      .sort((a, b) => (a.assetNumber || '').localeCompare(b.assetNumber || ''));
                    return (
                      <div className="mb-2 space-y-1.5 bg-slate-50 rounded-lg p-2 border border-slate-100">
                        {clientRentals.map(rental => {
                          const liveAssetNumber = printers.find(p => p.serialNumber === rental.serialNumber)?.assetNumber || rental.assetNumber;
                          return (
                            <div key={rental.id} className="bg-white rounded-lg border border-slate-200 p-2 flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {liveAssetNumber && <span>{liveAssetNumber} · </span>}
                                  {rental.category || rental.printerType || 'Asset'}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  S/N {rental.serialNumber}{rental.printerModel ? ` · ${rental.printerModel}` : ''}
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                  Since {rental.startDate}{rental.monthlyRate ? ` · ₹${rental.monthlyRate}/month` : ''}
                                </p>
                              </div>
                              <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-700 text-white whitespace-nowrap">
                                {rental.rentalType === 'standby' ? '⚡ Standby' : '🔥 Active'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {client.portalAccess ? (
                    <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-xs font-medium mb-2 bg-emerald-50 text-emerald-700">
                      🔑 Portal Access Granted
                    </span>
                  ) : (
                    canEdit && (
                      <button
                        onClick={() => grantPortalAccess(client)}
                        className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-xs font-medium mb-2 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors duration-150"
                      >
                        🔑 Grant Portal Access
                      </button>
                    )
                  )}
                  <div className="space-y-1.5 text-sm mt-auto pt-2 border-t border-slate-100">
                    {client.phone ? (
                      <p className="flex items-center gap-2 text-slate-700"><Phone size={14} className="text-slate-400 shrink-0" /><span className="truncate">{client.phone}</span></p>
                    ) : (
                      <p className="flex items-center gap-2 text-slate-300 italic text-xs"><Phone size={14} className="shrink-0" />No phone on file</p>
                    )}
                    {client.email && (
                      <p className="text-slate-500 truncate" title={client.email}>{client.email}</p>
                    )}
                    {canSeeStock() && (() => {
                      const sup = stockSuppliedTo(client.name);
                      return sup.length > 0 ? (
                        <p className="text-xs text-slate-500" title={sup.map(m => `${m.date}: ${m.itemName} ×${m.qty}${m.billedTo ? ' (billed ' + m.billedTo + ')' : ''}`).join('\n')}>📦 Stock supplied: {sup.length} line(s) · last {sup.slice().sort((a, b) => String(b.date).localeCompare(String(a.date)))[0].itemName}{sup.filter(m => !m.billedTo).length ? ` · ${sup.filter(m => !m.billedTo).length} not billed` : ''}</p>
                      ) : null;
                    })()}
                    {canSeeTally() && (() => {
                      const o = tallyOutstanding.find(x => String(x.name || '').trim().toLowerCase() === String(client.name || '').trim().toLowerCase());
                      return o && o.outstanding !== 0 ? (
                        <p className={`text-xs font-semibold ${o.outstanding > 0 ? 'text-red-600' : 'text-emerald-700'}`}>💰 Tally outstanding: ₹{Math.abs(o.outstanding).toLocaleString('en-IN')}{o.outstanding < 0 ? ' Cr' : ''}</p>
                      ) : null;
                    })()}
                    {client.contacts && client.contacts.length > 0 && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Users size={12} className="shrink-0" /> +{client.contacts.length} more contact{client.contacts.length !== 1 ? 's' : ''}
                      </p>
                    )}
                    {client.followUpDate && (() => {
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const daysLeft = Math.ceil((new Date(client.followUpDate) - today) / 86400000);
                      const overdue = daysLeft < 0;
                      return (
                        <p className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : daysLeft <= 2 ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                          ⏰ {overdue ? `Overdue ${Math.abs(daysLeft)}d` : daysLeft === 0 ? 'Follow up today' : `Follow up in ${daysLeft}d`}
                          {client.followUpNote ? ` — ${client.followUpNote}` : ''}
                        </p>
                      );
                    })()}
                  </div>
                </div>
              );

              return (
                <>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-lg font-semibold text-slate-900">📦 Clients with Assets Assigned</h3>
                      <span className="bg-brand-100 text-brand-700 text-xs font-bold px-2 py-0.5 rounded-full">{withAssets.length}</span>
                    </div>
                    {withAssets.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {withAssets.map(renderClientCard)}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic bg-white rounded-xl border border-slate-100 p-6 text-center">No clients currently have assets assigned.</p>
                    )}
                  </div>

                  <div>
                    <button onClick={() => setShowUnassignedClients(!showUnassignedClients)} className="w-full flex items-center justify-between bg-white rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors duration-150 px-4 py-3 mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">📭 Clients without Assets Assigned</h3>
                        <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">{withoutAssets.length}</span>
                      </div>
                      <span className="text-slate-400 text-sm">{showUnassignedClients ? '▲ Hide' : '▼ Show'}</span>
                    </button>
                    {showUnassignedClients && (
                      withoutAssets.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {withoutAssets.map(renderClientCard)}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic bg-white rounded-xl border border-slate-100 p-6 text-center">Every client currently has at least one asset assigned.</p>
                      )
                    )}
                  </div>
                </>
              );
            })()}
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-12 flex flex-col items-center justify-center">
            <div className="text-slate-400 mb-4">
              <Users size={64} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchClients ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-slate-500 text-center max-w-md">
              {searchClients 
                ? `No clients match "${searchClients}". Try a different search term.`
                : 'Click "Add" to create your first client or "Import" to bring them in from Excel.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Simplified other views for space
  // RENTALS VIEW
  const RentalsView = () => {
    const canEdit = hasPermission('canEditRentals');
    const canDelete = hasPermission('canDelete');
    const canImport = hasPermission('canImport');
    const canExport = hasPermission('canExport');
    const [showReturned, setShowReturned] = useState(false);
    const [dupePreview, setDupePreview] = useState(null);
    // Which rental's detail row (readings breakdown, agreement doc, PO, etc.)
    // is expanded - the table shows one compact row per rental by default,
    // with the full per-type detail available on demand instead of always
    // taking up space, per feedback that the old card grid was too busy and
    // forced too much scrolling to scan many rentals.
    const [expandedRentalId, setExpandedRentalId] = useState(null);
    // Column sort for the table - '' means no explicit sort (keep natural
    // Firestore order). Clicking a sortable header sets the field, or flips
    // direction if it's already the active sort field.
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState('asc');
    const toggleSort = (field) => {
      if (sortBy === field) {
        setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortDir('asc');
      }
    };
    const SortHeader = ({ field, children, align }) => (
      <th
        onClick={() => toggleSort(field)}
        className={`px-3 py-3 cursor-pointer select-none hover:text-slate-800 transition-colors duration-150 ${align === 'right' ? 'text-right' : ''}`}
      >
        {children} {sortBy === field ? (sortDir === 'asc' ? '▲' : '▼') : <span className="text-slate-300">⇅</span>}
      </th>
    );
    const [searchRentals, setSearchRentals] = useState('');

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setEditingItem(item);
      setModalType('rental');
      setShowModal(true);
    };

    // Same deterministic avatar-color/initials approach as the Clients page,
    // reused here so a client "reads" the same way across both pages.
    const avatarColors = ['bg-brand-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500'];
    const getAvatarColor = (name) => {
      const hash = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      return avatarColors[hash % avatarColors.length];
    };
    const getInitials = (name) => {
      if (!name) return '?';
      const words = name.trim().split(/\s+/).filter(Boolean);
      return words.length === 1 ? words[0].slice(0, 2).toUpperCase() : (words[0][0] + words[1][0]).toUpperCase();
    };

    const rentalTypeLabel = {
      regular: { text: 'Regular', color: 'bg-brand-100 text-brand-700' },
      per_page: { text: 'Pay Per Page', color: 'bg-orange-100 text-orange-700' },
      standby: { text: '⚡ Standby', color: 'bg-yellow-100 text-yellow-700' }
    };

    const checkInRental = async (rental) => {
      if (!window.confirm(`Check in / Return this printer?\n\nClient: ${rental.clientName}\nPrinter: ${rental.printerModel || rental.serialNumber}\n\nThis will:\n✓ Mark rental as "Returned"\n✓ Set printer status back to "Available"`)) return;
      
      try {
        // 1. Update rental status
        const updatedRental = { ...rental, status: 'returned', endDate: new Date().toISOString().split('T')[0] };
        await setDoc(doc(db, 'rentals', rental.id), updatedRental);

        // 2. Set printer back to available
        const affectedPrinter = printers.find(p => p.serialNumber === rental.serialNumber);
        if (affectedPrinter) {
          const updatedPrinter = { ...affectedPrinter, status: 'available' };
          await setDoc(doc(db, 'printers', affectedPrinter.id), updatedPrinter);
        }

        await logAudit('CHECK_IN', 'Rental', rental.id, `${rental.clientName} - ${rental.serialNumber} (Returned)`, updatedRental);
        alert('✅ Printer checked in successfully! Status set to Available.');
      } catch (error) {
        alert('❌ Error checking in printer: ' + error.message);
      }
    };

    // The reverse of checkInRental - there was previously no way to undo a
    // Check-In / Return through the UI at all. Needed whenever a rental was
    // marked "returned" by mistake (or its printer got corrected via Edit)
    // and the record actually needs to go back to being active.
    const reactivateRental = async (rental) => {
      // Same safety check used when creating/editing a rental: don't allow
      // reactivating onto a printer that already has a different active rental.
      const freshRentalsSnapshot = await getDocs(collection(db, 'rentals'));
      const conflicting = freshRentalsSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .find(r => r.serialNumber === rental.serialNumber && r.status !== 'returned' && r.id !== rental.id);
      if (conflicting) {
        alert(`❌ Can't reactivate — this printer already has an active rental:\n\n${conflicting.clientName}\n\nCheck in that rental first, or edit this one to point to a different printer.`);
        return;
      }

      if (!window.confirm(`Reactivate this rental?\n\nClient: ${rental.clientName}\nPrinter: ${rental.printerModel || rental.serialNumber}\n\nThis will:\n✓ Mark rental as "Active" again\n✓ Set printer status back to "Rented"`)) return;

      try {
        const updatedRental = { ...rental, status: 'active', endDate: '' };
        await setDoc(doc(db, 'rentals', rental.id), updatedRental);

        const affectedPrinter = printers.find(p => p.serialNumber === rental.serialNumber);
        if (affectedPrinter) {
          const updatedPrinter = { ...affectedPrinter, status: 'rented' };
          await setDoc(doc(db, 'printers', affectedPrinter.id), updatedPrinter);
        }

        await logAudit('UPDATE', 'Rental', rental.id, `${rental.clientName} - ${rental.serialNumber} (Reactivated)`, updatedRental);
        alert('✅ Rental reactivated! Status set back to Active.');
      } catch (error) {
        alert('❌ Error reactivating rental: ' + error.message);
      }
    };

    // Filter rentals based on search + returned/active toggle
    const returnedCount = rentals.filter(r => r.status === 'returned').length;
    const filteredRentalsUnsorted = rentals.filter(rental => {
      // Use the printer's CURRENT asset number for search too (not the rental's
      // possibly-stale cached copy), so search results match what's displayed.
      const liveAssetNum = printers.find(p => p.serialNumber === rental.serialNumber)?.assetNumber || rental.assetNumber;
      const searchMatch = matchesSearch(searchRentals, rental.clientName, rental.serialNumber, liveAssetNum, rental.printerModel);
      const statusMatch = showReturned || rental.status !== 'returned';
      return searchMatch && statusMatch;
    });

    const filteredRentals = !sortBy ? filteredRentalsUnsorted : [...filteredRentalsUnsorted].sort((a, b) => {
      let va, vb;
      if (sortBy === 'client') { va = a.clientName || ''; vb = b.clientName || ''; }
      else if (sortBy === 'model') { va = a.printerModel || ''; vb = b.printerModel || ''; }
      else if (sortBy === 'status') { va = a.status === 'returned' ? 1 : 0; vb = b.status === 'returned' ? 1 : 0; }
      else if (sortBy === 'period') { va = a.startDate || ''; vb = b.startDate || ''; }
      else if (sortBy === 'rate') { va = a.rentalType === 'standby' ? (a.cartridgeCost || 0) : (a.monthlyRate || 0); vb = b.rentalType === 'standby' ? (b.cartridgeCost || 0) : (b.monthlyRate || 0); }
      else { va = ''; vb = ''; }
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Rentals</h2>
          <div className="flex gap-2">
            {(() => {
              const seen = {};
              rentals.forEach(r => {
                if (!r.serialNumber || r.status === 'returned') return;
                seen[r.serialNumber] = (seen[r.serialNumber] || 0) + 1;
              });
              const dupeCount = Object.values(seen).filter(c => c > 1).reduce((sum, c) => sum + (c - 1), 0);
              if (dupeCount === 0 || userRole !== 'super_admin') return null;
              return (
                <button
                  onClick={() => {
                    // Build a preview of exactly what will be kept vs deleted, from current data,
                    // so the Super Admin can review before anything is actually removed.
                    const groups = {};
                    rentals.forEach(r => {
                      if (!r.serialNumber || r.status === 'returned') return;
                      if (!groups[r.serialNumber]) groups[r.serialNumber] = [];
                      groups[r.serialNumber].push(r);
                    });
                    const preview = Object.entries(groups)
                      .filter(([, group]) => group.length > 1)
                      .map(([serial, group]) => {
                        const sorted = [...group].sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0));
                        return { serial, keep: sorted[0], toDelete: sorted.slice(1) };
                      });
                    setDupePreview(preview);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"
                >
                  ⚠️ Fix Duplicates ({dupeCount})
                </button>
              );
            })()}
            {(() => {
              const legacyArtifactCount = rentals.filter(r => r.status === 'returned' && r.duplicateFixNote).length;
              if (legacyArtifactCount === 0 || userRole !== 'super_admin') return null;
              return (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Found ${legacyArtifactCount} leftover record(s) from an earlier duplicate-fix run (marked "returned" instead of deleted, from before this was changed).\n\nPermanently delete these now? A copy is saved to Audit Logs first.`)) return;

                    const freshSnapshot = await getDocs(collection(db, 'rentals'));
                    const legacyArtifacts = freshSnapshot.docs
                      .map(d => ({ id: d.id, ...d.data() }))
                      .filter(r => r.status === 'returned' && r.duplicateFixNote);

                    let cleanedCount = 0;
                    for (const artifact of legacyArtifacts) {
                      try {
                        await logAudit('DELETE', 'Rental', artifact.id, `Legacy duplicate-fix artifact deleted (${artifact.clientName} - ${artifact.serialNumber})`, artifact);
                        await deleteDoc(doc(db, 'rentals', artifact.id));
                        cleanedCount++;
                      } catch (e) {
                        console.error('Error deleting legacy artifact', artifact.id, e);
                      }
                    }

                    alert(`✅ Cleaned up ${cleanedCount} leftover record(s).`);
                  }}
                  className="bg-slate-700 hover:bg-slate-800 active:bg-slate-900 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"
                >
                  🧹 Clean Up Old Artifacts ({legacyArtifactCount})
                </button>
              );
            })()}
            {(() => {
              const staleCount = rentals.filter(r => {
                const printer = printers.find(p => p.serialNumber === r.serialNumber);
                return printer && printer.assetNumber && r.assetNumber !== printer.assetNumber;
              }).length;
              if (staleCount === 0 || userRole !== 'super_admin') return null;
              return (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Found ${staleCount} rental(s) with an out-of-date asset number (their printer was renumbered since, e.g. by a duplicate fix, but the rental's saved copy never updated).\n\nSync them to match the printer's current asset number now?`)) return;

                    const freshRentalsSnapshot = await getDocs(collection(db, 'rentals'));
                    const freshRentals = freshRentalsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    const freshPrintersSnapshot = await getDocs(collection(db, 'printers'));
                    const freshPrinters = freshPrintersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

                    let syncedCount = 0;
                    for (const r of freshRentals) {
                      const printer = freshPrinters.find(p => p.serialNumber === r.serialNumber);
                      if (printer && printer.assetNumber && r.assetNumber !== printer.assetNumber) {
                        try {
                          await setDoc(doc(db, 'rentals', r.id), { ...r, assetNumber: printer.assetNumber });
                          await logAudit('UPDATE', 'Rental', r.id, `Synced asset number ${r.assetNumber || '(none)'} → ${printer.assetNumber} (${r.clientName})`, { ...r, assetNumber: printer.assetNumber });
                          syncedCount++;
                        } catch (e) {
                          console.error('Error syncing rental asset number', r.id, e);
                        }
                      }
                    }

                    alert(`✅ Synced ${syncedCount} rental record(s) to match their printer's current asset number.`);
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"
                >
                  🔄 Sync Asset Numbers ({staleCount})
                </button>
              );
            })()}
            {canImport && <button onClick={() => { setImportType('rentals'); setShowImportDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Upload size={18} /> Import</button>}
            {canExport && <button onClick={() => exportToExcel(filteredRentals, 'rentals')} className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"><Download size={18} /> Export</button>}
            {canEdit && <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Plus size={18} /> Add</button>}
          </div>
        </div>

        {/* Search Box */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              placeholder="🔍 Search by client name, serial number, or printer model..."
              value={searchRentals}
              onChange={(e) => setSearchRentals(e.target.value)}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150"
            />
            <button
              onClick={() => setShowReturned(!showReturned)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 border ${
                showReturned
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {showReturned ? '✓ Showing Returned' : `Show Returned (${returnedCount})`}
            </button>
          </div>
        </div>
        {!showReturned && returnedCount > 0 && (
          <p className="text-xs text-slate-400 -mt-2">{returnedCount} returned rental{returnedCount !== 1 ? 's' : ''} hidden — click "Show Returned" above to view history.</p>
        )}

        {dupePreview && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-amber-900 text-lg">⚠️ Duplicate Rentals Found</h3>
                <p className="text-sm text-amber-700">Review below before confirming. The record to KEEP is shown in green; records to DELETE are shown in red.</p>
              </div>
              <button onClick={() => setDupePreview(null)} className="text-amber-600 hover:text-amber-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3">
              {dupePreview.map(group => (
                <div key={group.serial} className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Serial: {group.serial}{group.keep.assetNumber ? ` • Asset: ${group.keep.assetNumber}` : ''}</p>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ KEEP</span>
                      <span className="font-medium text-sm">{group.keep.clientName}</span>
                    </div>
                    <p className="text-xs text-slate-500">Start: {group.keep.startDate || 'N/A'} • {group.keep.printerModel || ''}</p>
                  </div>

                  {group.toDelete.map(dupe => (
                    <div key={dupe.id} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 last:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">✕ DELETE</span>
                        <span className="font-medium text-sm">{dupe.clientName}</span>
                      </div>
                      <p className="text-xs text-slate-500">Start: {dupe.startDate || 'N/A'} • {dupe.printerModel || ''}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  let fixedCount = 0;
                  for (const group of dupePreview) {
                    for (const dupe of group.toDelete) {
                      try {
                        // Save the full duplicate record to Audit Logs BEFORE deleting, so there's
                        // a permanent trace of exactly what was removed and why.
                        await logAudit('DELETE', 'Rental', dupe.id, `Duplicate deleted (${dupe.clientName} - ${group.serial})`, dupe);
                        await deleteDoc(doc(db, 'rentals', dupe.id));
                        fixedCount++;
                      } catch (e) {
                        console.error('Error deleting duplicate rental', dupe.id, e);
                      }
                    }
                  }
                  setDupePreview(null);
                  alert(`✅ Deleted ${fixedCount} duplicate rental(s)! The earliest rental for each printer was kept. Full details of what was removed are in Audit Logs.`);
                }}
                className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium"
              >
                Confirm & Delete Duplicates
              </button>
              <button
                onClick={() => setDupePreview(null)}
                className="bg-white text-slate-700 border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-2 py-3 w-8"></th>
                <SortHeader field="client">Client</SortHeader>
                <SortHeader field="model">Asset</SortHeader>
                <th className="px-3 py-3">Type</th>
                <SortHeader field="status">Status</SortHeader>
                <SortHeader field="period">Period</SortHeader>
                <SortHeader field="rate">Rate</SortHeader>
                <th className="px-3 py-3">Last Reading</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRentals.map(rental => {
                const typeInfo = rentalTypeLabel[rental.rentalType || 'regular'];
                const isActive = rental.status !== 'returned';
                let expiryBadge = null;
                if (isActive && rental.agreementEndDate) {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const daysLeft = Math.ceil((new Date(rental.agreementEndDate) - today) / (1000 * 60 * 60 * 24));
                  if (daysLeft <= 30) {
                    expiryBadge = daysLeft < 0
                      ? { text: `⚠️ Overdue ${Math.abs(daysLeft)}d`, color: 'bg-red-100 text-red-700' }
                      : { text: daysLeft === 0 ? 'Expires today' : `Renews in ${daysLeft}d`, color: daysLeft <= 7 ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700' };
                  }
                }
                // Always look up the printer's CURRENT asset number by serial - don't
                // trust rental.assetNumber alone, since it's a snapshot taken when the
                // rental was created and can go stale if the printer is later renumbered
                // (e.g. by the duplicate-fix tool). Falls back to the cached value only
                // if the printer record itself can no longer be found.
                const liveAssetNumber = printers.find(p => p.serialNumber === rental.serialNumber)?.assetNumber || rental.assetNumber;
                const isDeskOrLaptop = rental.category === 'Desktop' || rental.category === 'Laptop';
                const isMFPColour = rental.printerType === 'MFP_Colour';
                const readingsForThis = isDeskOrLaptop ? [] : meterReadings
                  .filter(mr => mr.serialNumber === rental.serialNumber)
                  .sort((a, b) => new Date(b.date) - new Date(a.date));
                const lastReading = readingsForThis[0];
                const baselineA4 = lastReading?.a4Reading ?? rental.openingA4Reading ?? 0;
                const baselineA3 = lastReading?.a3Reading ?? rental.openingA3Reading ?? 0;
                const baselineColourA4 = lastReading?.colourA4Reading ?? rental.openingColourA4Reading ?? 0;
                const baselineColourA3 = lastReading?.colourA3Reading ?? rental.openingColourA3Reading ?? 0;
                const openAddReading = () => {
                  setEditingItem({
                    clientName: rental.clientName,
                    serialNumber: rental.serialNumber,
                    assetNumber: rental.assetNumber,
                    printerModel: rental.printerModel,
                    printerType: rental.printerType,
                    rentalType: rental.rentalType,
                    cartridgeCost: rental.cartridgeCost,
                    previousA4: baselineA4,
                    previousA3: baselineA3,
                    previousColourA4: baselineColourA4,
                    previousColourA3: baselineColourA3,
                    previousScanner: lastReading?.scannerReading || 0,
                    a4Reading: baselineA4,
                    a3Reading: baselineA3,
                    colourA4Reading: baselineColourA4,
                    colourA3Reading: baselineColourA3,
                    scannerReading: lastReading?.scannerReading || 0
                  });
                  setModalType('reading');
                  setShowModal(true);
                };
                const isExpanded = expandedRentalId === rental.id;
                const rowAccent = !isActive
                  ? ''
                  : expiryBadge?.text.includes('Overdue')
                    ? 'bg-red-50/40'
                    : expiryBadge
                      ? 'bg-amber-50/40'
                      : '';

                return (
                  <React.Fragment key={rental.id}>
                    <tr className={`border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150 ${rowAccent} ${!isActive ? 'opacity-70' : ''}`}>
                      <td className="px-2 py-2.5 align-top">
                        <button onClick={() => setExpandedRentalId(isExpanded ? null : rental.id)} className="p-1 text-slate-400 hover:text-brand-600" title={isExpanded ? 'Collapse' : 'Expand details'}>
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 align-top min-w-0">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className={`shrink-0 w-8 h-8 rounded-full ${getAvatarColor(rental.clientName)} text-white flex items-center justify-center font-bold text-xs`}>
                            {getInitials(rental.clientName)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate max-w-[200px]" title={rental.clientName}>{rental.clientName}</p>
                            {rental.department && <p className="text-xs text-slate-400 truncate max-w-[200px]">{rental.department}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-top min-w-0">
                        <p className="font-semibold text-slate-800 truncate max-w-[200px]" title={rental.printerModel || '—'}>{rental.printerModel || '—'}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]" title={`${liveAssetNumber ? liveAssetNumber + ' • ' : ''}S/N ${rental.serialNumber}`}>
                          {liveAssetNumber && <span className="font-medium text-slate-700">{liveAssetNumber}</span>}
                          {liveAssetNumber && ' • '}
                          S/N {rental.serialNumber}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${typeInfo.color}`}>{typeInfo.text}</span>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${isActive ? 'bg-emerald-700 text-white' : 'bg-slate-200 text-slate-600'}`}>
                            {isActive ? '🔥 Active' : 'Returned'}
                          </span>
                          {expiryBadge && <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${expiryBadge.text.includes('Overdue') ? 'bg-red-700 text-white' : 'bg-amber-400 text-amber-950'}`}>⏰ {expiryBadge.text}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-top text-xs text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-1"><Calendar size={12} /> {rental.startDate}{rental.endDate ? ` – ${rental.endDate}` : ''}</div>
                        {rental.agreementEndDate && <p className="text-slate-400 mt-0.5">Agreement ends {rental.agreementEndDate}</p>}
                      </td>
                      <td className="px-3 py-2.5 align-top text-sm font-semibold text-slate-800 whitespace-nowrap">
                        {rental.rentalType === 'standby'
                          ? `₹${rental.cartridgeCost || 0} / cartridge`
                          : `₹${rental.monthlyRate || 0}/mo`}
                      </td>
                      <td className="px-3 py-2.5 align-top text-xs whitespace-nowrap">
                        {isDeskOrLaptop ? (
                          <span className="text-slate-300">—</span>
                        ) : lastReading ? (
                          <span className="text-slate-500">{lastReading.date}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> No readings
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {!isDeskOrLaptop && canEdit && (
                            <button onClick={openAddReading} className="p-1.5 rounded-full hover:bg-brand-50 text-brand-600 transition-colors duration-150" title="Add Reading"><TrendingUp size={16} /></button>
                          )}
                          {isActive && canEdit && (
                            <button onClick={() => checkInRental(rental)} className="p-1.5 rounded-full hover:bg-orange-50 text-orange-600 transition-colors duration-150" title="Check In / Return"><Download size={16} /></button>
                          )}
                          {!isActive && canEdit && (
                            <button onClick={() => reactivateRental(rental)} className="p-1.5 rounded-full hover:bg-emerald-50 text-emerald-600 transition-colors duration-150" title="Reactivate"><Upload size={16} /></button>
                          )}
                          {canEdit && <button onClick={() => openModal(rental)} className="p-1.5 rounded-full hover:bg-brand-50 text-brand-600 transition-colors duration-150" title="Edit"><Edit size={16} /></button>}
                          {canDelete && <button onClick={() => deleteItem('rentals', rental.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600 transition-colors duration-150" title="Delete"><Trash2 size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <td></td>
                        <td colSpan={8} className="px-3 py-3">
                          <div className="space-y-3 text-sm">
                            {rental.department && <p className="text-slate-500">Department: <span className="text-slate-800">{rental.department}</span></p>}
                            {rental.poNumber && <p className="text-slate-500">PO Number: <span className="text-slate-800">{rental.poNumber}</span></p>}
                            {rental.createdBy && <p className="text-slate-500">Created by: <span className="text-slate-800">{rental.createdBy}</span></p>}
                            {rental.agreementDocUrl && (
                              <a href={rental.agreementDocUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-600 hover:text-brand-800 text-xs bg-brand-50 rounded-full px-3 py-1.5 w-fit font-semibold">
                                <Paperclip size={13} /> {rental.agreementDocName || 'View Agreement'}
                              </a>
                            )}

                            {!isDeskOrLaptop && (
                              <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1.5">
                                {rental.monthlyRate > 0 && rental.rentalType !== 'standby' && (
                                  <div className="flex justify-between"><span className="text-slate-600">Monthly Rate:</span><span className="font-semibold">₹{rental.monthlyRate}</span></div>
                                )}
                                <div className="flex justify-between">
                                  <span className="text-slate-600">{isMFPColour ? 'B/W A4: Free / Cost:' : 'A4: Free / Cost:'}</span>
                                  <span>{rental.a4FreePages || 0} pages / ₹{rental.a4PageCost || 0}</span>
                                </div>
                                {(rental.printerType === 'A3' || rental.printerType === 'MFP' || isMFPColour) && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">{isMFPColour ? 'B/W A3: Free / Cost:' : 'A3: Free / Cost:'}</span>
                                    <span>{rental.a3FreePages || 0} pages / ₹{rental.a3PageCost || 0}</span>
                                  </div>
                                )}
                                {isMFPColour && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-pink-700">🎨 Colour A4: Free / Cost:</span>
                                      <span>{rental.colourA4FreePages || 0} pages / ₹{rental.colourA4PageCost || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-pink-700">🎨 Colour A3: Free / Cost:</span>
                                      <span>{rental.colourA3FreePages || 0} pages / ₹{rental.colourA3PageCost || 0}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}

                            {!isDeskOrLaptop && (
                              <div className="bg-white rounded-xl border border-slate-200 p-3">
                                {lastReading ? (
                                  <>
                                    <p className="text-slate-600 text-xs mb-1.5">Last Reading ({lastReading.date}):</p>
                                    <div className="flex gap-3 flex-wrap">
                                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{isMFPColour ? 'B/W A4' : 'A4'}: {(lastReading.a4Reading || 0).toLocaleString()}</span>
                                      {(rental.printerType === 'A3' || rental.printerType === 'MFP' || isMFPColour) && (
                                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">{isMFPColour ? 'B/W A3' : 'A3'}: {(lastReading.a3Reading || 0).toLocaleString()}</span>
                                      )}
                                      {isMFPColour && (
                                        <>
                                          <span className="text-xs bg-pink-50 text-pink-700 px-2 py-0.5 rounded">🎨 Colour A4: {(lastReading.colourA4Reading || 0).toLocaleString()}</span>
                                          <span className="text-xs bg-pink-50 text-pink-700 px-2 py-0.5 rounded">🎨 Colour A3: {(lastReading.colourA3Reading || 0).toLocaleString()}</span>
                                        </>
                                      )}
                                      {(rental.printerType === 'MFP' || isMFPColour) && (
                                        <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded">Scan: {(lastReading.scannerReading || 0).toLocaleString()}</span>
                                      )}
                                    </div>
                                  </>
                                ) : rental.rentalType !== 'standby' && (rental.openingA4Reading || rental.openingA4Reading === 0) ? (
                                  <>
                                    <p className="text-slate-500 text-xs mb-1.5">No readings logged yet — opening baseline set</p>
                                    <div className="flex gap-3 flex-wrap">
                                      <span className="text-xs bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded">{isMFPColour ? 'Opening B/W A4' : 'Opening A4'}: {(rental.openingA4Reading || 0).toLocaleString()}</span>
                                      {(rental.printerType === 'A3' || rental.printerType === 'MFP' || isMFPColour) && (
                                        <span className="text-xs bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded">{isMFPColour ? 'Opening B/W A3' : 'Opening A3'}: {(rental.openingA3Reading || 0).toLocaleString()}</span>
                                      )}
                                      {isMFPColour && (
                                        <>
                                          <span className="text-xs bg-pink-50 text-pink-700 px-2 py-0.5 rounded">Opening Colour A4: {(rental.openingColourA4Reading || 0).toLocaleString()}</span>
                                          <span className="text-xs bg-pink-50 text-pink-700 px-2 py-0.5 rounded">Opening Colour A3: {(rental.openingColourA3Reading || 0).toLocaleString()}</span>
                                        </>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-slate-400 text-xs">No readings logged yet.</p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filteredRentals.length === 0 && (
            <p className="text-sm text-slate-400 italic text-center py-8">No rentals match your search.</p>
          )}
        </div>
      </div>
    );
  };

  // SERVICE CALLS VIEW
  const ServiceCallsView = () => {
    const canEdit = moduleOverride('service') ? moduleOverride('service') === 'rw' : (hasPermission('canEditRentals') || hasPermission('canEditServiceCalls'));
    const canDelete = hasPermission('canDelete');
    const canImport = hasPermission('canImport');
    const canExport = hasPermission('canExport');
    const [mainTab, setMainTab] = useState('calls'); // 'calls' | 'inventory' | 'movements'
    const [searchCalls, setSearchCalls] = useState('');
    const [filterCallStatus, setFilterCallStatus] = useState('');
    const [filterCallClient, setFilterCallClient] = useState('');
    // A field engineer who is logged in sees their own calls first
    const myEngineer = engineers.find(e => e.email && currentUser?.email && e.email.toLowerCase() === currentUser.email.toLowerCase());
    const [filterCallEngineer, setFilterCallEngineer] = useState(userRole === 'field_engineer' && myEngineer ? myEngineer.name : '');

    const filteredCalls = serviceCalls
      .filter(call => matchesSearch(searchCalls, call.clientName, call.serialNumber, call.issue, call.engineer))
      .filter(call => !filterCallStatus || call.status === filterCallStatus)
      .filter(call => !filterCallClient || call.clientName === filterCallClient)
      .filter(call => !filterCallEngineer || (filterCallEngineer === '__none__' ? !call.engineer : call.engineer === filterCallEngineer));

    // Quick (re)assignment straight from the list
    const reassignCall = async (call, engineerName) => {
      try {
        const now = new Date().toISOString();
        const history = [...(call.assignmentHistory || []), { engineer: engineerName, at: now, by: currentUser?.email || 'Unknown' }];
        await setDoc(doc(db, 'serviceCalls', call.id), { engineer: engineerName, assignedAt: now, assignmentHistory: history }, { merge: true });
        await logAudit('UPDATE', 'ServiceCall', call.id, `${call.clientName} - ${call.serialNumber} assigned to ${engineerName || 'nobody'}`, { ...call, engineer: engineerName, assignedAt: now, assignmentHistory: history });
      } catch (err) {
        alert(`❌ Could not assign: ${err.message}`);
      }
    };

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setEditingItem(item);
      setModalType('serviceCall');
      setShowModal(true);
    };

    // Generate Quotation - opens the Quotation form pre-filled with this
    // service call's client, equipment type, and printer details, and links
    // the resulting quotation back to this call so the connection between
    // "issue reported" -> "quote sent" -> "job done" is traceable both ways.
    const generateQuotationFromCall = (call) => {
      if (!hasPermission('canCreateQuotations')) {
        alert('❌ You do not have permission to create quotations.');
        return;
      }
      if (call.assetSource === 'company') {
        if (!window.confirm('⚠️ This service call is for one of our own rental printers, not a client-owned device.\n\nQuotations are meant for client-owned equipment - servicing our rental units is normally covered under the rental agreement itself.\n\nContinue anyway?')) {
          return;
        }
      }
      const client = clients.find(c => c.name === call.clientName);
      setEditingItem({
        clientId: client?.id || '',
        clientName: call.clientName,
        clientAddress: client?.address || '',
        clientGSTIN: client?.gstin || '',
        printerType: call.equipmentType || 'laserjet_printer',
        printerModel: call.printerModel || '',
        printerSerial: call.serialNumber || '',
        serviceCallId: call.id,
        date: new Date().toISOString().split('T')[0],
        items: [{ description: call.issue || '', hsn: '998713', qty: 1, rate: '' }],
        notes: 'Looking forward for your business.',
        quotationType: 'service',
        terms: QUOTATION_TERMS_PRESETS.service
      });
      setModalType('quotation');
      setShowModal(true);
    };

    // --- Client Asset Register logic (merged in from the old Client Service page) ---
    const [assetSearch, setAssetSearch] = useState('');
    const openAssetModal = (item = null) => {
      setEditingItem(item);
      setModalType('clientAsset');
      setShowModal(true);
    };
    const handleDeleteAsset = async (id) => {
      if (!window.confirm('Delete this client asset?')) return;
      try {
        const fullRecord = clientAssets.find(a => a.id === id) || {};
        await deleteDoc(doc(db, 'clientAssets', id));
        await logAudit('DELETE', 'ClientAsset', id, `${fullRecord.serialNumber || id} - ${fullRecord.clientName || 'Unknown client'}`, fullRecord);
        alert('✅ Asset deleted');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    };
    const filteredAssets = clientAssets.filter(a =>
      matchesSearch(assetSearch, a.serialNumber, a.make, a.model, a.clientName)
    );
    const getServiceStatus = (serial) => {
      const lastMovement = printerMovements.find(m => m.printerSerial === serial);
      if (!lastMovement || lastMovement.type === 'OUT') {
        return { label: 'At Client', color: 'bg-green-500 text-white' };
      }
      return { label: 'In Service', color: 'bg-orange-500 text-white' };
    };

    // --- Inward/Outward Register logic (merged in from the old Client Service page) ---
    const [movementType, setMovementType] = useState('');
    const [movementAssetSource, setMovementAssetSource] = useState('client');
    const [selectedPrinter, setSelectedPrinter] = useState('');
    const [serviceNotes, setServiceNotes] = useState('');
    const [technicianName, setTechnicianName] = useState('');
    const [movementDate, setMovementDate] = useState(new Date().toISOString().split('T')[0]);
    const [movementA4Reading, setMovementA4Reading] = useState('');
    const [movementA3Reading, setMovementA3Reading] = useState('');
    const [movementScannerReading, setMovementScannerReading] = useState('');

    // Determine which reading fields are relevant for the currently selected
    // printer. Company assets have a known type (A4/A3/MFP/MFP_Colour) we can
    // check directly; client-owned devices don't have a tracked type, so A3
    // is offered but not forced (we can't reliably know their capability).
    const getAssetReadingRequirements = (serial, isCompany) => {
      if (!serial) return { hasA3: false, requireA3: false, hasScanner: false };
      if (isCompany) {
        const printer = printers.find(p => p.serialNumber === serial);
        const type = printer?.type;
        const hasA3 = type === 'A3' || type === 'MFP' || type === 'MFP_Colour';
        const hasScanner = type === 'MFP' || type === 'MFP_Colour';
        return { hasA3, requireA3: hasA3, hasScanner };
      }
      // Client-owned: type unknown, so offer A3 as optional rather than required.
      return { hasA3: true, requireA3: false, hasScanner: false };
    };

    // Every printer movement - IN or OUT, our own asset or a client-owned
    // device - requires a meter reading. This is how billing/usage tracking
    // stays accurate: without a reading at every handover point, there's no
    // reliable way to know how many pages were run during that visit.
    const getLastReadingFor = (serial) => meterReadings
      .filter(r => r.serialNumber === serial)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    const handleServiceMovement = async () => {
      if (!selectedPrinter || !movementType) {
        alert('⚠️ Please fill all required fields');
        return;
      }
      if (!movementA4Reading) {
        alert('⚠️ A meter reading (A4) is required for every printer movement - this is how we track usage accurately. Please enter the current A4 count.');
        return;
      }
      const isCompanyAsset = movementAssetSource === 'company';
      const readingReq = getAssetReadingRequirements(selectedPrinter, isCompanyAsset);
      if (readingReq.requireA3 && !movementA3Reading) {
        alert('⚠️ This printer supports A3 - an A3 meter reading is required too.');
        return;
      }
      if (readingReq.hasScanner && !movementScannerReading) {
        alert('⚠️ This is an MFP with scanning capability - a Scanner count is required too.');
        return;
      }
      const asset = isCompanyAsset
        ? printers.find(p => p.serialNumber === selectedPrinter)
        : clientAssets.find(a => a.serialNumber === selectedPrinter);
      if (!asset) {
        alert('❌ Asset not found');
        return;
      }
      const activeRental = isCompanyAsset ? rentals.find(r => r.serialNumber === selectedPrinter && r.status !== 'returned') : null;
      const partyName = isCompanyAsset ? (activeRental?.clientName || 'Not currently rented') : asset.clientName;
      const movement = {
        id: `SVC${Date.now()}`,
        printerSerial: selectedPrinter,
        printerModel: `${asset.make} ${asset.model}`,
        assetSource: movementAssetSource,
        assetNumber: isCompanyAsset ? (asset.assetNumber || '') : '',
        companyId: currentCompany,
        clientName: partyName,
        type: movementType,
        date: movementDate,
        serviceNotes,
        technician: technicianName || currentUser.email,
        recordedBy: currentUser.email,
        timestamp: new Date().toISOString()
      };
      try {
        await setDoc(doc(db, 'printerMovements', movement.id), movement);

        // Record the mandatory meter reading tied to this movement.
        const lastReading = getLastReadingFor(selectedPrinter);
        const readingDoc = {
          id: `RDG${Date.now()}`,
          date: movementDate,
          clientName: partyName,
          serialNumber: selectedPrinter,
          assetNumber: isCompanyAsset ? (asset.assetNumber || '') : '',
          printerModel: `${asset.make} ${asset.model}`,
          a4Reading: parseInt(movementA4Reading) || 0,
          previousA4: lastReading?.a4Reading ?? 0,
          a3Reading: movementA3Reading ? parseInt(movementA3Reading) : (lastReading?.a3Reading ?? 0),
          previousA3: lastReading?.a3Reading ?? 0,
          scannerReading: movementScannerReading ? parseInt(movementScannerReading) : (lastReading?.scannerReading ?? 0),
          previousScanner: lastReading?.scannerReading ?? 0,
          recordedBy: currentUser?.email || 'Unknown',
          movementId: movement.id,
          notes: `Auto-captured on ${movementType === 'IN' ? 'receiving' : 'returning'} printer for service`
        };
        await setDoc(doc(db, 'meterReadings', readingDoc.id), readingDoc);

        if (isCompanyAsset) {
          // IN = printer arrives for service -> maintenance (busy/unavailable).
          // OUT = printer leaves after service -> back to rented (if still under
          // an active rental) or available. This matches the same logic used
          // for the automatic movements triggered from Service Calls.
          let newStatus;
          if (movementType === 'IN') {
            newStatus = 'maintenance';
          } else {
            const stillRented = rentals.some(r => r.serialNumber === asset.serialNumber && r.status !== 'returned');
            newStatus = stillRented ? 'rented' : 'available';
          }
          const updatedPrinter = { ...asset, status: newStatus };
          await setDoc(doc(db, 'printers', asset.id), updatedPrinter);
        }
        const actionText = movementType === 'IN' ? `received for service` : `sent out / returned after service`;
        alert(`✅ ${asset.serialNumber} ${actionText}. Meter reading (A4: ${movementA4Reading}) recorded.`);
        setSelectedPrinter('');
        setServiceNotes('');
        setTechnicianName('');
        setMovementType('');
        setMovementA4Reading('');
        setMovementA3Reading('');
        setMovementScannerReading('');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    };

    const pmScheduledCount = printers.filter(p => p.pmIntervalDays > 0).length;
    const TABS = [
      { id: 'calls', label: `🔧 Service Calls (${serviceCalls.length})` },
      { id: 'inventory', label: `📋 Client Asset Register (${clientAssets.length})` },
      { id: 'movements', label: `📦 Inward/Outward Register (${printerMovements.length})` },
      { id: 'maintenance', label: `🛠️ Preventive Maintenance (${pmScheduledCount})` }
    ];

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold">Service</h2>
          <p className="text-sm text-slate-500">Service calls, client-owned asset register, and the inward/outward movement log - all in one place.</p>
        </div>

        {/* Tab bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="flex border-b overflow-x-auto">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setMainTab(t.id)}
                className={`flex-1 min-w-max px-4 py-3 md:py-4 font-medium text-sm whitespace-nowrap ${
                  mainTab === t.id ? 'bg-brand-50 text-brand-600 border-b-2 border-brand-600' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* TAB: Service Calls */}
        {mainTab === 'calls' && (
          <div className="space-y-4">
            <div className="flex justify-end gap-2">
              {canImport && <button onClick={() => { setImportType('serviceCalls'); setShowImportDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Upload size={18} /> Import</button>}
              {canExport && <button onClick={() => exportToExcel(filteredCalls, 'serviceCalls')} className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"><Download size={18} /> Export</button>}
              {canEdit && <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Plus size={18} /> Add</button>}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 space-y-3">
              <input
                type="text"
                placeholder="🔍 Search by client, serial, issue, or engineer..."
                value={searchCalls}
                onChange={(e) => setSearchCalls(e.target.value)}
                className="w-full px-4 py-2.5 md:py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select value={filterCallEngineer} onChange={(e) => setFilterCallEngineer(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none">
                  <option value="">👷 All engineers</option>
                  <option value="__none__">Unassigned</option>
                  {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name} ({serviceCalls.filter(c => c.engineer === eng.name && c.status !== 'completed').length} open)</option>)}
                </select>
                <select value={filterCallStatus} onChange={(e) => setFilterCallStatus(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none">
                  <option value="">Filter by Status (all)</option>
                  <option value="open">🟡 Open</option>
                  <option value="in-progress">🔵 In Progress</option>
                  <option value="completed">🟢 Completed</option>
                </select>
                <select value={filterCallClient} onChange={(e) => setFilterCallClient(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none">
                  <option value="">Filter by Client (all)</option>
                  {[...new Set(serviceCalls.map(c => c.clientName).filter(Boolean))].sort().map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {(filterCallStatus || filterCallClient || filterCallEngineer) && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Showing {filteredCalls.length} of {serviceCalls.length} calls</span>
                  <button onClick={() => { setFilterCallStatus(''); setFilterCallClient(''); setFilterCallEngineer(''); }} className="text-brand-600 hover:underline font-medium">Clear filters</button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Printer</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Issue</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Engineer</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Picked Up By</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Pickup Date</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Returned By</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Return Date</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCalls.map(call => {
                    const matchedPrinter = printers.find(p => p.serialNumber === call.serialNumber);
                    const statusDot = call.status === 'completed' ? 'bg-green-500' : call.status === 'in-progress' ? 'bg-brand-500' : 'bg-amber-500';
                    return (
                      <tr key={call.id} className="hover:bg-slate-50 transition-colors duration-150">
                        <td className="px-6 py-4 text-slate-600">{call.date}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${statusDot}`} />
                            <span className="font-medium text-slate-900">{call.clientName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {matchedPrinter?.assetNumber && <span className="font-medium text-slate-700">{matchedPrinter.assetNumber} • </span>}
                          {call.serialNumber}
                        </td>
                        <td className="px-6 py-4">{call.issue}{canSeeStock() && stockMovesForCall(call.id).length > 0 && <p className="text-xs text-slate-400 mt-1" title="Stock issued or used on this call">📦 {stockMovesForCall(call.id).map(m => `${m.itemName} ×${m.qty}`).join(', ')}</p>}</td>
                        <td className="px-6 py-4">
                          {canEdit && call.status !== 'completed' ? (
                            <select value={call.engineer || ''} onChange={(e) => reassignCall(call, e.target.value)} className={`border rounded-lg px-2 py-1 text-sm ${call.engineer ? 'border-slate-300' : 'border-amber-400 bg-amber-50'}`} title="Assign / reassign engineer">
                              <option value="">Unassigned</option>
                              {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name}</option>)}
                            </select>
                          ) : (call.engineer || '-')}
                        </td>
                        <td className="px-6 py-4">{call.pickedUpBy === '__other__' ? call.pickedUpByOther : call.pickedUpBy || '-'}</td>
                        <td className="px-6 py-4">{call.pickupDate || '-'}</td>
                        <td className="px-6 py-4">{call.returnedBy === '__other__' ? call.returnedByOther : call.returnedBy || '-'}</td>
                        <td className="px-6 py-4">{call.returnDate || '-'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${
                            call.status === 'completed' ? 'bg-green-500 text-white' :
                            call.status === 'in-progress' ? 'bg-brand-500 text-white' :
                            'bg-amber-400 text-amber-950'
                          }`}>
                            {call.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            <button onClick={() => setServiceHistorySerial(call.serialNumber)} className="p-1.5 rounded-full hover:bg-orange-50 text-orange-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Service History">🕓</button>
                            {hasPermission('canCreateQuotations') && <button onClick={() => generateQuotationFromCall(call)} className="p-1.5 rounded-full hover:bg-indigo-50 text-indigo-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Generate Quotation"><FileText size={18} /></button>}
                            {canEdit && call.status !== 'completed' && <button onClick={() => openModal({ ...call, status: 'completed' })} className="p-1.5 rounded-full hover:bg-green-50 text-green-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Complete this call">✅</button>}
                            {canEdit && <button onClick={() => openModal(call)} className="p-1.5 rounded-full hover:bg-brand-50 text-brand-600 hover:scale-110 active:scale-95 transition-all duration-150"><Edit size={18} /></button>}
                            {canDelete && <button onClick={() => deleteItem('serviceCalls', call.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600 hover:scale-110 active:scale-95 transition-all duration-150"><Trash2 size={18} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB: Client Asset Register */}
        {mainTab === 'inventory' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              {canEdit && (
                <button onClick={() => openAssetModal()} className="bg-brand-600 text-white px-4 py-2.5 md:py-2 rounded-lg flex items-center gap-2 justify-center md:justify-start hover:bg-brand-700">
                  <Plus size={18} /> Add Client Printer
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 md:gap-4">
              <div className="bg-brand-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">Total Assets</p>
                <p className="text-xl md:text-2xl font-bold text-brand-600">{clientAssets.length}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">At Clients</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {clientAssets.filter(a => getServiceStatus(a.serialNumber).label === 'At Client').length}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">In Service</p>
                <p className="text-xl md:text-2xl font-bold text-orange-600">
                  {clientAssets.filter(a => getServiceStatus(a.serialNumber).label === 'In Service').length}
                </p>
              </div>
            </div>

            <input
              type="text"
              placeholder="🔍 Search assets..."
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              className="w-full px-4 py-2.5 md:py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-base"
            />

            <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Serial</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Make/Model</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAssets.map(asset => {
                    const status = getServiceStatus(asset.serialNumber);
                    return (
                      <tr key={asset.id} className="hover:bg-slate-50 transition-colors duration-150">
                        <td className="px-6 py-4 font-medium">{asset.serialNumber}</td>
                        <td className="px-6 py-4">{asset.make} {asset.model}</td>
                        <td className="px-6 py-4">{asset.clientName}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${status.color}`}>{status.label}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            <button onClick={() => setServiceHistorySerial(asset.serialNumber)} className="p-1.5 rounded-full hover:bg-orange-50 text-orange-600 hover:scale-110 active:scale-95 transition-all duration-150" title="Service History">🕓</button>
                            {canEdit && <button onClick={() => openAssetModal(asset)} className="p-1.5 rounded-full hover:bg-brand-50 text-brand-600 hover:scale-110 active:scale-95 transition-all duration-150"><Edit size={18} /></button>}
                            {canDelete && <button onClick={() => handleDeleteAsset(asset.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600 hover:scale-110 active:scale-95 transition-all duration-150"><Trash2 size={18} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {filteredAssets.map(asset => {
                const status = getServiceStatus(asset.serialNumber);
                return (
                  <div key={asset.id} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-base">{asset.serialNumber}</p>
                        <p className="text-sm text-slate-600">{asset.make} {asset.model}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${status.color}`}>{status.label}</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-3">👤 {asset.clientName}</p>
                    <div className="flex gap-2">
                      <button onClick={() => setServiceHistorySerial(asset.serialNumber)} className="flex-1 bg-orange-50 text-orange-600 py-2 rounded-lg text-sm font-medium">🕓 History</button>
                      {canEdit && (
                        <button onClick={() => openAssetModal(asset)} className="flex-1 bg-brand-50 text-brand-600 py-2 rounded-lg text-sm font-medium">
                          <Edit size={16} className="inline mr-1" /> Edit
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDeleteAsset(asset.id)} className="flex-1 bg-red-50 text-red-600 py-2 rounded-lg text-sm font-medium">
                          <Trash2 size={16} className="inline mr-1" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredAssets.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Printer size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="font-medium">No client assets found</p>
                <p className="text-sm mt-1">Add client-owned printers above</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: Inward/Outward Register */}
        {mainTab === 'movements' && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              <div className="bg-green-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">At Clients</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {clientAssets.filter(a => getServiceStatus(a.serialNumber).label === 'At Client').length}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">In Service</p>
                <p className="text-xl md:text-2xl font-bold text-orange-600">
                  {clientAssets.filter(a => getServiceStatus(a.serialNumber).label === 'In Service').length}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-slate-600">Total Movements</p>
                <p className="text-xl md:text-2xl font-bold text-slate-700">{printerMovements.length}</p>
              </div>
            </div>

            {canEdit && (
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3 text-sm md:text-base">🔧 Record Service Movement</h3>
              <p className="text-xs text-slate-500 -mt-2 mb-3">💡 Creating a Service Call with pickup details already logs an IN movement automatically. Use this form only for movements not tied to a Service Call (e.g. logging a return, or a walk-in drop-off).</p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select value={movementType} onChange={(e) => setMovementType(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500">
                    <option value="">Movement Type *</option>
                    <option value="IN">📥 IN - Receive for Service</option>
                    <option value="OUT">📤 OUT - Return / Send Out</option>
                  </select>
                  <input type="date" value={movementDate} onChange={(e) => setMovementDate(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500" />
                </div>

                <div>
                  <label className="text-sm text-slate-600">Asset Source *</label>
                  <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={movementAssetSource === 'client'} onChange={() => { setMovementAssetSource('client'); setSelectedPrinter(''); }} />
                      👤 Client-Owned Printer
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={movementAssetSource === 'company'} onChange={() => { setMovementAssetSource('company'); setSelectedPrinter(''); }} />
                      🏢 Our Asset (Rental Inventory)
                    </label>
                  </div>
                </div>

                {movementAssetSource === 'client' ? (
                  <>
                    <select value={selectedPrinter} onChange={(e) => setSelectedPrinter(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500">
                      <option value="">Select Client Printer *</option>
                      {clientAssets.map(asset => {
                        const status = getServiceStatus(asset.serialNumber);
                        return (
                          <option key={asset.id} value={asset.serialNumber}>
                            {asset.serialNumber} - {asset.make} {asset.model} | {asset.clientName} [{status.label}]
                          </option>
                        );
                      })}
                    </select>
                    {clientAssets.length === 0 && (
                      <p className="text-sm text-orange-600">⚠️ No assets found. Add them in the Client Asset Register tab first.</p>
                    )}
                  </>
                ) : (
                  <>
                    <select value={selectedPrinter} onChange={(e) => setSelectedPrinter(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500">
                      <option value="">Select Our Asset *</option>
                      {printers.map(p => {
                        const rental = rentals.find(r => r.serialNumber === p.serialNumber && r.status !== 'returned');
                        return (
                          <option key={p.id} value={p.serialNumber}>
                            {p.assetNumber ? `${p.assetNumber} • ` : ''}S/N: {p.serialNumber} - {p.make} {p.model} {rental ? `| Rented: ${rental.clientName}` : '| Not rented'} [{p.status}]
                          </option>
                        );
                      })}
                    </select>
                    {printers.length === 0 && (
                      <p className="text-sm text-orange-600">⚠️ No assets found in Asset Management.</p>
                    )}
                  </>
                )}
                <select value={technicianName} onChange={(e) => setTechnicianName(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500">
                  <option value="">Select Technician</option>
                  {engineers.map(eng => (
                    <option key={eng.id} value={eng.name}>{eng.name}</option>
                  ))}
                </select>

                {selectedPrinter && (() => {
                  const readingReq = getAssetReadingRequirements(selectedPrinter, movementAssetSource === 'company');
                  return (
                    <div className="bg-cyan-50 border border-cyan-300 rounded-lg p-3">
                      <p className="text-sm font-medium text-cyan-900 mb-1">📊 Meter Reading (required)</p>
                      <p className="text-xs text-cyan-700 mb-2">
                        {(() => {
                          const last = getLastReadingFor(selectedPrinter);
                          return last ? `Last recorded: A4 ${last.a4Reading?.toLocaleString() ?? 0} on ${last.date}` : 'No previous reading on record for this printer yet.';
                        })()}
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-slate-600">A4 Reading *</label>
                          <input type="number" value={movementA4Reading} onChange={(e) => setMovementA4Reading(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-base mt-1" placeholder="Current A4 count" required />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">A3 Reading {readingReq.requireA3 ? '*' : '(if applicable)'}</label>
                          <input type="number" value={movementA3Reading} onChange={(e) => setMovementA3Reading(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-base mt-1" placeholder="Current A3 count" />
                        </div>
                        {readingReq.hasScanner && (
                          <div className="col-span-2">
                            <label className="text-xs text-slate-600">Scanner Count * <span className="text-cyan-700">(MFP)</span></label>
                            <input type="number" value={movementScannerReading} onChange={(e) => setMovementScannerReading(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-base mt-1" placeholder="Current scan count" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <textarea
                  value={serviceNotes}
                  onChange={(e) => setServiceNotes(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2.5 md:py-2 text-base focus:ring-2 focus:ring-brand-500"
                  rows="3"
                  placeholder={movementType === 'IN' ? "Describe the issue..." : "Describe work done..."}
                />
                <button
                  onClick={handleServiceMovement}
                  disabled={!movementType || !selectedPrinter || !movementA4Reading}
                  className="w-full bg-brand-600 text-white px-6 py-3 md:py-2 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {movementType === 'IN' ? '📥 Receive for Service' : movementType === 'OUT' ? '📤 Return / Send Out' : 'Record Movement'}
                </button>
              </div>
            </div>
            )}

            <div>
              <h3 className="font-semibold mb-3 text-sm md:text-base">Movement History</h3>

              <div className="hidden md:block bg-white rounded-lg border overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Printer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tech</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {printerMovements.map(movement => (
                      <tr key={movement.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{movement.date}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium font-medium ${
                            movement.type === 'IN' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {movement.type === 'IN' ? '📥 IN' : '📤 OUT'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${movement.assetSource === 'company' ? 'bg-brand-50 text-brand-700' : 'bg-purple-50 text-purple-700'}`}>
                            {movement.assetSource === 'company' ? '🏢 Our Asset' : '👤 Client-Owned'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium">{movement.assetNumber ? `${movement.assetNumber} • ` : ''}{movement.printerSerial}</div>
                          <div className="text-xs text-slate-500">{movement.printerModel}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">{movement.clientName}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{movement.serviceNotes || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{movement.technician}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {printerMovements.map(movement => (
                  <div key={movement.id} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-sm">{movement.assetNumber ? `${movement.assetNumber} • ` : ''}{movement.printerSerial}</p>
                        <p className="text-xs text-slate-500">{movement.printerModel}</p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium font-medium ${
                        movement.type === 'IN' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {movement.type === 'IN' ? '📥 IN' : '📤 OUT'}
                      </span>
                    </div>
                    <span className={`inline-block mb-2 px-2 py-0.5 rounded-full text-xs font-medium ${movement.assetSource === 'company' ? 'bg-brand-50 text-brand-700' : 'bg-purple-50 text-purple-700'}`}>
                      {movement.assetSource === 'company' ? '🏢 Our Asset' : '👤 Client-Owned'}
                    </span>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-600">Date:</span> {movement.date}</p>
                      <p><span className="text-slate-600">Client:</span> {movement.clientName}</p>
                      <p><span className="text-slate-600">Tech:</span> {movement.technician}</p>
                      {movement.serviceNotes && (
                        <p className="text-slate-700 mt-2 p-2 bg-slate-50 rounded text-xs">{movement.serviceNotes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {printerMovements.length === 0 && (
                <div className="text-center py-12 text-slate-500 bg-white rounded-lg border">
                  <Wrench size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="font-medium">No service records yet</p>
                  <p className="text-sm mt-1">Record movements above</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Preventive Maintenance */}
        {mainTab === 'maintenance' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Assets with a maintenance interval set (configure it on the asset's Edit form, under Purchase/Warranty dates), sorted by how soon they're due.</p>
            {(() => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const scheduled = printers
                .filter(p => p.pmIntervalDays > 0)
                .map(p => {
                  // Next due date counts from the last logged service, or from
                  // the purchase date if it's never been serviced yet - so a
                  // brand-new asset with a 90-day interval shows up correctly
                  // even before its first maintenance visit.
                  const baseline = p.lastPmDate || p.purchaseDate;
                  const nextDue = baseline ? new Date(new Date(baseline).getTime() + p.pmIntervalDays * 86400000) : null;
                  const daysLeft = nextDue ? Math.ceil((nextDue - today) / 86400000) : null;
                  return { printer: p, daysLeft };
                })
                .sort((a, b) => (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity));

              if (scheduled.length === 0) {
                return (
                  <div className="text-center py-12 text-slate-500 bg-white rounded-lg border">
                    <Wrench size={48} className="mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">No maintenance schedules set</p>
                    <p className="text-sm mt-1">Set a "Maintenance Interval" on an asset's Edit form to start tracking it here.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {scheduled.map(({ printer, daysLeft }) => {
                    const overdue = daysLeft !== null && daysLeft < 0;
                    const dueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 14;
                    return (
                      <div key={printer.id} className={`flex items-center justify-between flex-wrap gap-2 bg-white rounded-lg border p-4 ${overdue ? 'border-red-300' : dueSoon ? 'border-amber-300' : 'border-slate-100'}`}>
                        <div>
                          <p className="font-medium text-slate-900">{printer.assetNumber || printer.serialNumber} — {printer.make} {printer.model}</p>
                          <p className="text-xs text-slate-500">
                            Every {printer.pmIntervalDays} days
                            {printer.lastPmDate ? ` · Last serviced ${printer.lastPmDate}` : ' · Never serviced (using purchase date as baseline)'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {daysLeft !== null ? (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${overdue ? 'bg-red-100 text-red-700' : dueSoon ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                              {overdue ? `⚠️ Overdue ${Math.abs(daysLeft)}d` : daysLeft === 0 ? 'Due today' : `Due in ${daysLeft}d`}
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 whitespace-nowrap">No baseline date set</span>
                          )}
                          {canEdit && (
                            <button
                              onClick={async () => {
                                const todayStr = new Date().toISOString().split('T')[0];
                                try {
                                  const updatedPrinter = { ...printer, lastPmDate: todayStr };
                                  await setDoc(doc(db, 'printers', printer.id), updatedPrinter);
                                  await logAudit('UPDATE', 'Asset', printer.id, `Preventive maintenance logged for ${printer.assetNumber || printer.serialNumber}`, updatedPrinter);
                                } catch (err) {
                                  alert('Error logging maintenance: ' + err.message);
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 whitespace-nowrap"
                            >
                              ✅ Log Service
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };
  // ENGINEERS VIEW
  const EngineersView = () => {
    const canEdit = hasPermission('canEditEngineers');
    const canDelete = hasPermission('canDelete');
    const canImport = hasPermission('canImport');
    const canExport = hasPermission('canExport');
    const [searchEngineers, setSearchEngineers] = useState('');

    const filteredEngineers = engineers.filter(e => matchesSearch(searchEngineers, e.name, e.phone, e.email, e.specialization));

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setEditingItem(item);
      setModalType('engineer');
      setShowModal(true);
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Engineers</h2>
          <div className="flex gap-2">
            {canImport && <button onClick={() => { setImportType('engineers'); setShowImportDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Upload size={18} /> Import</button>}
            {canExport && <button onClick={() => exportToExcel(filteredEngineers, 'engineers')} className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"><Download size={18} /> Export</button>}
            {canEdit && <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"><Plus size={18} /> Add</button>}
          </div>
        </div>
        <input
          type="text"
          placeholder="🔍 Search by name, phone, email, or specialization..."
          value={searchEngineers}
          onChange={(e) => setSearchEngineers(e.target.value)}
          className="w-full px-4 py-2.5 md:py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEngineers.map(engineer => (
            <div key={engineer.id} className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{engineer.name}</h3>
                  <p className="text-sm text-slate-500">{engineer.specialization}</p>
                </div>
                {(canEdit || canDelete) && (
                  <div className="flex gap-2">
                    {canEdit && <button onClick={() => openModal(engineer)} className="p-1.5 rounded-md hover:bg-brand-50 text-brand-600 transition-colors duration-150"><Edit size={18} /></button>}
                    {canDelete && <button onClick={() => deleteItem('engineers', engineer.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors duration-150"><Trash2 size={18} /></button>}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={16} className="text-slate-400" />
                  <span>{engineer.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-slate-400" />
                  <span>{engineer.email}</span>
                </div>
                <div className="mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs ${engineer.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                    {engineer.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // METER READINGS VIEW
  const MeterReadingsView = () => {
    const canDelete = hasPermission('canDelete');
    const [searchPrinter, setSearchPrinter] = useState('');
    const [expandedHistory, setExpandedHistory] = useState({});

    const deleteReading = async (reading) => {
      if (!window.confirm(`Delete this reading?\n\nClient: ${reading.clientName}\nDate: ${reading.date}\nA4: ${reading.a4Reading}${reading.a3Reading ? `, A3: ${reading.a3Reading}` : ''}\n\nThis cannot be undone. Use this to remove test entries or mistakes.`)) return;
      try {
        await logAudit('DELETE', 'MeterReading', reading.id, `${reading.clientName} - ${reading.serialNumber} (${reading.date})`, reading);
        await deleteDoc(doc(db, 'meterReadings', reading.id));
      } catch (err) {
        alert('Error deleting reading: ' + err.message);
      }
    };

    // Get all rented printers with client info
    const rentedPrinters = rentals
      .filter(r => r.status === 'active')
      .map(r => {
        const printer = printers.find(p => p.serialNumber === r.serialNumber);
        const latestReading = meterReadings
          .filter(mr => mr.serialNumber === r.serialNumber)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        
        return {
          ...r,
          printerType: printer?.type || r.printerType || 'A4',
          latestA4: latestReading?.a4Reading ?? r.openingA4Reading ?? 0,
          latestA3: latestReading?.a3Reading ?? r.openingA3Reading ?? 0,
          latestColourA4: latestReading?.colourA4Reading ?? r.openingColourA4Reading ?? 0,
          latestColourA3: latestReading?.colourA3Reading ?? r.openingColourA3Reading ?? 0,
          latestScanner: latestReading?.scannerReading || 0,
          readingDate: latestReading?.date
        };
      })
      .filter(r => matchesSearch(searchPrinter, r.clientName, r.serialNumber, r.assetNumber));

    const addReading = (printer) => {
      setEditingItem({
        clientName: printer.clientName,
        serialNumber: printer.serialNumber,
        assetNumber: printer.assetNumber,
        printerModel: printer.printerModel,
        printerType: printer.printerType,
        rentalType: printer.rentalType,
        cartridgeCost: printer.cartridgeCost,
        previousA4: printer.latestA4,
        previousA3: printer.latestA3,
        previousColourA4: printer.latestColourA4,
        previousColourA3: printer.latestColourA3,
        previousScanner: printer.latestScanner,
        a4Reading: printer.latestA4,
        a3Reading: printer.latestA3,
        colourA4Reading: printer.latestColourA4,
        colourA3Reading: printer.latestColourA3,
        scannerReading: printer.latestScanner
      });
      setModalType('reading');
      setShowModal(true);
    };

    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Meter Readings</h2>

        <input
          type="text"
          placeholder="🔍 Search client or printer..."
          value={searchPrinter}
          onChange={(e) => setSearchPrinter(e.target.value)}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-base"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rentedPrinters.map(printer => {
            const isMFP = printer.printerType === 'MFP';
            const isMFPColour = printer.printerType === 'MFP_Colour';
            const hasA3 = printer.printerType === 'A3' || isMFP || isMFPColour;
            const isStandby = printer.rentalType === 'standby';
            const statCount = 1 + (hasA3 ? 1 : 0) + (isMFP || isMFPColour ? 1 : 0) + (isMFPColour ? 2 : 0);
            
            return (
              <div key={printer.id} className={`rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md ${isStandby ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-slate-100'}`}>
                <div className="p-5">
                  {/* Header */}
                  <div className="flex justify-between items-start gap-3 mb-1">
                    <h3 className="font-semibold text-base text-slate-900 leading-snug">{printer.clientName}</h3>
                    <div className="flex gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isMFPColour ? 'bg-pink-50 text-pink-700' : isMFP ? 'bg-indigo-50 text-indigo-700' : 'bg-brand-50 text-brand-700'}`}>
                        {isMFPColour ? 'MFP Colour' : isMFP ? 'MFP B/W' : printer.printerType}
                      </span>
                      {isStandby && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">⚡ Standby</span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    {printer.department ? `${printer.department} • ` : ''}{printer.printerModel}
                    {printer.assetNumber && <span className="font-medium text-slate-700"> • {printer.assetNumber}</span>}
                    {' '}• S/N: {printer.serialNumber}
                  </p>

                  {/* Stat strip */}
                  <div className={`grid gap-px bg-slate-100 rounded-lg overflow-hidden mb-3`} style={{ gridTemplateColumns: `repeat(${statCount}, 1fr)` }}>
                    <div className="bg-white px-3 py-2.5 text-center">
                      <p className="text-[11px] text-slate-500 mb-0.5">📄 {isMFPColour ? 'B/W A4' : 'A4'}</p>
                      <p className="text-lg font-bold text-emerald-600">{printer.latestA4.toLocaleString()}</p>
                    </div>
                    {hasA3 && (
                      <div className="bg-white px-3 py-2.5 text-center">
                        <p className="text-[11px] text-slate-500 mb-0.5">📋 {isMFPColour ? 'B/W A3' : 'A3'}</p>
                        <p className="text-lg font-bold text-purple-600">{printer.latestA3.toLocaleString()}</p>
                      </div>
                    )}
                    {isMFPColour && (
                      <>
                        <div className="bg-white px-3 py-2.5 text-center">
                          <p className="text-[11px] text-slate-500 mb-0.5">🎨 Colour A4</p>
                          <p className="text-lg font-bold text-pink-600">{printer.latestColourA4.toLocaleString()}</p>
                        </div>
                        <div className="bg-white px-3 py-2.5 text-center">
                          <p className="text-[11px] text-slate-500 mb-0.5">🎨 Colour A3</p>
                          <p className="text-lg font-bold text-pink-600">{printer.latestColourA3.toLocaleString()}</p>
                        </div>
                      </>
                    )}
                    {(isMFP || isMFPColour) && (
                      <div className="bg-white px-3 py-2.5 text-center">
                        <p className="text-[11px] text-slate-500 mb-0.5">🖨️ Scan</p>
                        <p className="text-lg font-bold text-brand-600">{printer.latestScanner.toLocaleString()}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setExpandedHistory(prev => ({ ...prev, [printer.serialNumber]: !prev[printer.serialNumber] }))}
                      className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted"
                    >
                      {printer.readingDate ? `Last read: ${printer.readingDate}` : 'No readings yet'} {expandedHistory[printer.serialNumber] ? '▲ Hide history' : '▼ View history'}
                    </button>
                    <button
                      onClick={() => addReading(printer)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 flex items-center gap-1.5 ${isStandby ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow-md' : 'bg-brand-600 hover:bg-brand-700 text-white shadow-sm hover:shadow-md'}`}
                    >
                      📊 Add Reading
                    </button>
                  </div>

                  {expandedHistory[printer.serialNumber] && (() => {
                    const history = meterReadings
                      .filter(mr => mr.serialNumber === printer.serialNumber)
                      .sort((a, b) => new Date(b.date) - new Date(a.date));
                    return (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                        {history.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No readings recorded yet.</p>
                        ) : history.map(reading => (
                          <div key={reading.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-xs">
                            <div>
                              <span className="font-medium text-slate-700">{reading.date}</span>
                              <span className="text-slate-500 ml-2">A4: {reading.a4Reading?.toLocaleString() ?? '-'}</span>
                              {reading.a3Reading > 0 && <span className="text-slate-500 ml-2">A3: {reading.a3Reading.toLocaleString()}</span>}
                              {reading.colourA4Reading > 0 && <span className="text-pink-600 ml-2">🎨A4: {reading.colourA4Reading.toLocaleString()}</span>}
                              {reading.recordedBy && <span className="text-slate-400 ml-2">by {reading.recordedBy}</span>}
                            </div>
                            {canDelete && (
                              <button onClick={() => deleteReading(reading)} className="p-1 rounded hover:bg-red-50 text-red-500 transition-colors duration-150" title="Delete this reading">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>

        {rentedPrinters.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p className="text-lg">No printers found</p>
          </div>
        )}
      </div>
    );
  };

  // AUDIT LOG VIEW (Super Admin Only)
  const AuditLogView = () => {
    const [searchAudit, setSearchAudit] = useState('');
    const [filterAction, setFilterAction] = useState('ALL');
    const [filterUser, setFilterUser] = useState('ALL');
    const [restoringId, setRestoringId] = useState(null);

    // Maps an audit log's entityType to the actual Firestore collection + local state setter
    const ENTITY_RESTORE_MAP = {
      Asset: { collection: 'printers', setState: setPrinters, state: printers },
      Client: { collection: 'clients', setState: setClients, state: clients },
      Rental: { collection: 'rentals', setState: setRentals, state: rentals },
      Engineer: { collection: 'engineers', setState: setEngineers, state: engineers },
      MeterReading: { collection: 'meterReadings', setState: setMeterReadings, state: meterReadings },
      ServiceCall: { collection: 'serviceCalls', setState: setServiceCalls, state: serviceCalls }
    };

    const canRestore = (log) => {
      if (log.action !== 'DELETE') return false;
      const mapping = ENTITY_RESTORE_MAP[log.entityType];
      if (!mapping) return false; // e.g. 'Database' bulk actions aren't restorable
      if (!log.changes || Object.keys(log.changes).length === 0) return false; // nothing was captured to restore
      // Already restored (exists again with the same id)?
      if (mapping.state.some(item => item.id === log.entityId)) return false;
      return true;
    };

    const restoreRecord = async (log) => {
      const mapping = ENTITY_RESTORE_MAP[log.entityType];
      if (!mapping) return;
      if (!window.confirm(`Restore this ${log.entityType.toLowerCase()}?\n\n"${log.entityName}"\n\nThis will recreate the exact record as it was before deletion.`)) return;

      setRestoringId(log.id);
      try {
        const restoredRecord = { ...log.changes, id: log.entityId };
        await setDoc(doc(db, mapping.collection, log.entityId), restoredRecord);
        mapping.setState(prev => [...prev, restoredRecord]);
        await logAudit('CREATE', log.entityType, log.entityId, `${log.entityName} (restored from Audit Logs)`, restoredRecord);
        alert(`✅ Restored successfully! "${log.entityName}" is back.`);
      } catch (err) {
        alert('❌ Error restoring record: ' + err.message);
      }
      setRestoringId(null);
    };

    // Users who actually appear in the log, not the full systemUsers list -
    // avoids a dropdown full of accounts that have never done anything here.
    const logUsers = [...new Set(auditLogs.map(log => log.userEmail).filter(Boolean))].sort();

    // Filter logs
    const filteredLogs = auditLogs.filter(log => {
      const searchMatch = matchesSearch(searchAudit, log.userEmail, log.entityType, log.entityName, log.action);
      const actionMatch = filterAction === 'ALL' || log.action === filterAction;
      const userMatch = filterUser === 'ALL' || log.userEmail === filterUser;
      return searchMatch && actionMatch && userMatch;
    });

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">📋 Audit Logs</h2>
          <button 
            onClick={() => exportToExcel(filteredLogs, 'audit_logs')} 
            className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 px-4 py-2.5 rounded-lg border border-slate-200 transition-all duration-150 font-medium flex items-center gap-2"
          >
            <Download size={18} /> Export Logs
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-4 flex gap-4">
          <input
            type="text"
            placeholder="🔍 Search by user, action, entity type, or description..."
            value={searchAudit}
            onChange={(e) => setSearchAudit(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500"
          />
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500"
          >
            <option value="ALL">All Actions</option>
            <option value="LOGIN">Logins Only</option>
            <option value="CREATE">Create Only</option>
            <option value="UPDATE">Update Only</option>
            <option value="DELETE">Delete Only</option>
          </select>
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500"
          >
            <option value="ALL">All Users</option>
            {logUsers.map(email => <option key={email} value={email}>{email}</option>)}
          </select>
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date/Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entity Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                {userRole === 'super_admin' && <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Restore</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredLogs.map(log => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div>{log.date}</div>
                    <div className="text-slate-500 text-xs">{log.time}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{log.userEmail}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium font-medium ${
                      log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                      log.action === 'UPDATE' ? 'bg-brand-100 text-brand-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm capitalize">{log.entityType}</td>
                  <td className="px-6 py-4 text-sm">{log.entityName}</td>
                  {userRole === 'super_admin' && (
                    <td className="px-6 py-4">
                      {canRestore(log) && (
                        <button
                          onClick={() => restoreRecord(log)}
                          disabled={restoringId === log.id}
                          className="text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg font-medium transition-colors duration-150 disabled:opacity-50 flex items-center gap-1"
                        >
                          {restoringId === log.id ? '⏳ Restoring...' : '↩️ Restore'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLogs.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-12 text-center">
            <div className="text-slate-400 mb-4 flex justify-center">
              <FileText size={64} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No audit logs found</h3>
            <p className="text-slate-500">
              {searchAudit || filterAction !== 'ALL' 
                ? 'No logs match your search criteria' 
                : 'No activity recorded yet. Audit logs will appear here when you create, update, or delete items.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // SETTINGS VIEW
  const SettingsView = () => {
    const [cleanupConfirmText, setCleanupConfirmText] = useState('');
    const [cleaning, setCleaning] = useState(false);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [backingUp, setBackingUp] = useState(false);
    const [settingsTab, setSettingsTab] = useState('emailrules');

    const SETTINGS_TABS = [
      ...(userRole === 'super_admin' ? [
        { key: 'emailrules', label: '📧 Email Rules' },
        { key: 'backups', label: '💾 Backups' },
        { key: 'danger', label: '⚠️ Danger Zone' }
      ] : [])
    ];

    // Collections to wipe - EXCLUDES clients and users
    const CLEANUP_COLLECTIONS = [
      { key: 'printers', label: 'Assets / Printers' },
      { key: 'rentals', label: 'Rentals' },
      { key: 'serviceCalls', label: 'Service Calls' },
      { key: 'meterReadings', label: 'Meter Readings' },
      { key: 'printerMovements', label: 'Client Service Movements' },
      { key: 'clientAssets', label: 'Client-Owned Assets' },
      { key: 'engineers', label: 'Engineers Directory' },
      { key: 'auditLogs', label: 'Audit Logs' }
    ];

    // Kicks off an on-demand Firestore backup the same way the email check
    // triggers its background worker: write a request doc, let the
    // Firestore-triggered Cloud Function (backupWorker) react to it, then
    // poll that doc for the result. Deliberately NOT a direct fetch() to a
    // Cloud Function URL - that requires the function to be publicly
    // invokable, which needs an IAM policy change this project's Cloud
    // Functions service account role doesn't have permission to make.
    const triggerBackup = async () => {
      setBackingUp(true);
      try {
        const requestRef = doc(collection(db, 'backupTriggers'));
        await setDoc(requestRef, {
          status: 'pending',
          triggeredBy: currentUser.email,
          createdAt: new Date().toISOString()
        });

        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          const snap = await getDoc(requestRef);
          const data = snap.data();
          if (data?.status === 'success') {
            clearInterval(poll);
            // backupWorker now waits for the actual export to finish (not
            // just start) before marking this trigger doc 'success', so this
            // really is done, not just kicked off.
            alert(`✅ Backup completed successfully!\n\nDestination: ${data.destination}`);
            await logAudit('CREATE', 'Backup', 'manual-backup', 'Manual backup triggered from Settings', {});
            const backupSnapshot = await getDocs(collection(db, 'backupLogs'));
            setBackupLogs(backupSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)));
            setBackingUp(false);
          } else if (data?.status === 'error') {
            clearInterval(poll);
            alert(`❌ Backup failed: ${data.error}`);
            setBackingUp(false);
          } else if (attempts >= 36) {
            clearInterval(poll);
            alert('⏱️ Backup is taking longer than expected.\n\nCheck the backup history table in a minute - it may still complete.');
            setBackingUp(false);
          }
        }, 5000);
      } catch (err) {
        alert(`❌ Could not start backup: ${err.message}`);
        setBackingUp(false);
      }
    };

    const runDatabaseCleanup = async () => {
      if (cleanupConfirmText !== 'DELETE ALL DATA') {
        alert('Please type exactly: DELETE ALL DATA');
        return;
      }
      if (!window.confirm('FINAL WARNING: This will permanently delete Assets, Rentals, Service Calls, Meter Readings, Client Service records, Engineers, and Audit Logs.\n\nClients and Users will NOT be touched.\n\nThis cannot be undone. Continue?')) {
        return;
      }

      setCleaning(true);
      setCleanupResult(null);
      const results = {};

      try {
        for (const col of CLEANUP_COLLECTIONS) {
          const snapshot = await getDocs(collection(db, col.key));
          let batch = writeBatch(db);
          let count = 0;
          let opCount = 0;

          for (const docSnap of snapshot.docs) {
            batch.delete(doc(db, col.key, docSnap.id));
            count++;
            opCount++;
            // Firestore batch limit is 500
            if (opCount === 450) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }
          if (opCount > 0) {
            await batch.commit();
          }
          results[col.label] = count;
        }

        // Clear local state
        setAuditLogs([]);

        // Log this action itself (fresh audit log)
        await logAudit('DELETE', 'Database', 'bulk-cleanup', 'Full database cleanup (excluding clients & users)', results);

        setCleanupResult(results);
        setCleanupConfirmText('');
      } catch (error) {
        alert('❌ Error during cleanup: ' + error.message);
      }
      setCleaning(false);
    };

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Settings</h2>
          <p className="text-slate-600 mt-1">Email rules, database backups, and admin tools</p>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {SETTINGS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setSettingsTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
                settingsTab === tab.key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* DATABASE BACKUPS (Super Admin Only) */}
        {settingsTab === 'emailrules' && userRole === 'super_admin' && (() => {
          const FIELDS = [
            { key: 'orderWords', label: 'Words that make a mail an ORDER', hint: 'e.g. indent, booking confirmed, kindly book' },
            { key: 'enquiryWords', label: 'Words that suggest an ENQUIRY', hint: 'e.g. rate card, tender, estimate' },
            { key: 'productWords', label: 'Extra product words', hint: 'e.g. cabling, switch, projector (a product word + an enquiry word = enquiry)' },
            { key: 'serviceWords', label: 'Words that make a mail a SERVICE REQUEST', hint: 'e.g. not powering on, display blank' },
            { key: 'alwaysInboxSenders', label: 'Always show mail from these customers', hint: 'Sender address or domain, e.g. @kauveryhospital.com, store@nethradhama.org. Every mail from them lands in the Inbox, even if no rule matches.' },
            { key: 'ignoreSenders', label: 'Ignore mail from these senders', hint: 'Address or domain, e.g. noreply@bank.com, @newsletters.in' },
            { key: 'ignoreSubjectWords', label: 'Ignore mails whose subject contains', hint: 'e.g. webinar, newsletter, bid published' }
          ];
          const saveRules = async () => {
            setEmailRulesSaving(true);
            try {
              const clean = {};
              FIELDS.forEach(f => { clean[f.key] = String(emailRulesForm[f.key] || '').trim(); });
              await setDoc(doc(db, 'config', 'emailRules'), { ...clean, updatedBy: currentUser?.email || '', updatedAt: new Date().toISOString() });
              await logAudit('UPDATE', 'Settings', 'emailRules', 'Email rules updated', clean);
              alert('✅ Saved.\n\nThe rules apply from the next mailbox check (every 10 minutes). To re-apply them to recent mail now, open Email Inbox and click "↻ Rescan 3 days".');
            } catch (err) {
              alert('Could not save: ' + err.message);
            } finally {
              setEmailRulesSaving(false);
            }
          };
          return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold">📧 Email Rules</h3>
                <p className="text-sm text-slate-600">Teach the Email Inbox your own words and customers. One entry per line or separated by commas. These add to the built-in rules - they never switch them off.</p>
              </div>
              {FIELDS.map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                  <textarea
                    rows="2"
                    value={emailRulesForm[f.key] || ''}
                    onChange={(e) => setEmailRulesForm({ ...emailRulesForm, [f.key]: e.target.value })}
                    placeholder={f.hint}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none"
                  />
                  <p className="text-xs text-slate-400 mt-0.5">{f.hint}</p>
                </div>
              ))}
              <button onClick={saveRules} disabled={emailRulesSaving} className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-60">
                {emailRulesSaving ? 'Saving…' : 'Save rules'}
              </button>
            </div>
          );
        })()}

        {settingsTab === 'backups' && userRole === 'super_admin' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-50 p-3 rounded-lg">💾</div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Database Backups</h3>
                  <p className="text-sm text-slate-500">Runs automatically every day at 2:00 AM. Take an extra backup anytime below.</p>
                </div>
              </div>
              <button
                onClick={triggerBackup}
                disabled={backingUp}
                className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {backingUp ? '🔄 Starting Backup...' : '💾 Backup Now'}
              </button>
            </div>

            {backupLogs.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-4">No backups recorded yet. Click "Backup Now" to take your first one, or wait for tonight's automatic run.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date/Time</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Destination</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {backupLogs.slice(0, 20).map(log => (
                      <tr key={log.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.triggerType === 'scheduled' ? 'bg-brand-50 text-brand-700' : 'bg-purple-50 text-purple-700'}`}>
                            {log.triggerType === 'scheduled' ? '⏰ Scheduled' : '👆 Manual'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            log.status === 'completed' ? 'bg-green-50 text-green-700' :
                            log.status === 'started' ? 'bg-brand-50 text-brand-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {log.status === 'completed' ? '✅ Completed' : log.status === 'started' ? '⏳ In progress' : '❌ Failed'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 font-mono max-w-xs truncate" title={log.destination}>{log.destination || log.error || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {backupLogs.length > 20 && (
                  <p className="text-xs text-slate-400 mt-2">Showing most recent 20 of {backupLogs.length} backups.</p>
                )}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-3">💡 "Started" means the backup successfully kicked off - for a database this size it typically finishes within a minute or two. Full backup files live in Cloud Storage and can be restored via <code className="bg-slate-100 px-1 rounded">gcloud firestore import</code>.</p>
          </div>
        )}

        {/* DANGER ZONE - Database Cleanup (Super Admin Only) */}
        {settingsTab === 'danger' && userRole === 'super_admin' && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-lg">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-900">Danger Zone: Clean Database</h3>
                <p className="text-sm text-red-700">Permanently delete operational data. Clients and Users are always preserved.</p>
              </div>
            </div>

            <div className="bg-white rounded p-4 mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">This will delete ALL records from:</p>
              <div className="grid grid-cols-2 gap-2">
                {CLEANUP_COLLECTIONS.map(col => (
                  <div key={col.key} className="text-sm text-slate-600 flex items-center gap-2">
                    <span className="text-red-500">✕</span> {col.label}
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium text-green-700 mt-3 flex items-center gap-2">
                <span className="text-green-600">✓</span> Clients — PRESERVED
              </p>
              <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                <span className="text-green-600">✓</span> Users / Logins — PRESERVED
              </p>
            </div>

            {cleanupResult ? (
              <div className="bg-green-50 border border-green-300 rounded p-4">
                <p className="font-semibold text-green-800 mb-2">✅ Cleanup Complete!</p>
                {Object.entries(cleanupResult).map(([label, count]) => (
                  <p key={label} className="text-sm text-green-700">{label}: {count} deleted</p>
                ))}
                <button onClick={() => setCleanupResult(null)} className="mt-3 text-sm text-green-800 underline">Dismiss</button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type <span className="font-mono font-bold">DELETE ALL DATA</span> to confirm:
                </label>
                <input
                  type="text"
                  value={cleanupConfirmText}
                  onChange={(e) => setCleanupConfirmText(e.target.value)}
                  placeholder="DELETE ALL DATA"
                  className="w-full border-2 border-red-300 rounded px-3 py-2.5 text-base mb-3 font-mono"
                />
                <button
                  onClick={runDatabaseCleanup}
                  disabled={cleaning || cleanupConfirmText !== 'DELETE ALL DATA'}
                  className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                >
                  {cleaning ? '🔄 Deleting... Please wait' : '🗑️ Permanently Clean Database'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // REPORTS VIEW - Comprehensive Business Reports
  const ReportsView = () => {
    const [activeReport, setActiveReport] = useState('revenue');
    const [dateRange, setDateRange] = useState({
      start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    });

    // Revenue Report
    const getRevenueReport = () => {
      const filtered = rentals.filter(r => {
        if (!r.startDate) return false;
        const rentDate = new Date(r.startDate);
        return rentDate >= new Date(dateRange.start) && rentDate <= new Date(dateRange.end);
      });

      const totalRevenue = filtered.reduce((sum, r) => sum + (parseFloat(r.monthlyRate) || 0), 0);
      const avgRevenue = filtered.length > 0 ? totalRevenue / filtered.length : 0;

      // Clean, report-friendly export rows (raw rental objects have dozens of
      // internal fields - this keeps only what's meaningful for a revenue report)
      const exportRows = filtered.map(r => ({
        Client: r.clientName || '',
        Department: r.department || '',
        AssetNumber: r.assetNumber || '',
        SerialNumber: r.serialNumber || '',
        PrinterModel: r.printerModel || '',
        RentalType: r.rentalType || 'regular',
        StartDate: r.startDate || '',
        MonthlyRate: r.monthlyRate || 0,
        Status: r.status || ''
      }));

      return { rentals: filtered, exportRows, totalRevenue, avgRevenue, count: filtered.length };
    };

    // Asset Utilization Report
    const getAssetUtilization = () => {
      const total = printers.length;
      const rented = printers.filter(p => p.status === 'rented').length;
      const available = printers.filter(p => p.status === 'available').length;
      const maintenance = printers.filter(p => p.status === 'maintenance').length;
      const utilization = total > 0 ? ((rented / total) * 100).toFixed(1) : 0;

      // Group by category
      const byCategory = printers.reduce((acc, p) => {
        const cat = p.category || 'Printer';
        if (!acc[cat]) acc[cat] = { total: 0, rented: 0, available: 0 };
        acc[cat].total++;
        if (p.status === 'rented') acc[cat].rented++;
        if (p.status === 'available') acc[cat].available++;
        return acc;
      }, {});

      return { total, rented, available, maintenance, utilization, byCategory };
    };

    // Client Report
    const getClientReport = () => {
      const clientData = clients.map(client => {
        const clientRentals = rentals.filter(r => r.clientName === client.name);
        const activeRentals = clientRentals.filter(r => r.status === 'active');
        const revenue = activeRentals.reduce((sum, r) => sum + (parseFloat(r.monthlyRate) || 0), 0);
        const clientServiceCalls = serviceCalls.filter(s => s.clientName === client.name).length;
        
        return {
          name: client.name,
          email: client.email,
          phone: client.phone,
          rentals: activeRentals.length,
          revenue,
          serviceCalls: clientServiceCalls
        };
      }).sort((a, b) => b.revenue - a.revenue);

      return clientData;
    };

    // Service Report
    const getServiceReport = () => {
      const filtered = printerMovements.filter(m => {
        if (!m.date) return false;
        const serviceDate = new Date(m.date);
        return serviceDate >= new Date(dateRange.start) && serviceDate <= new Date(dateRange.end);
      });

      const inServices = filtered.filter(m => m.type === 'IN');
      const outServices = filtered.filter(m => m.type === 'OUT');

      return { total: filtered.length, inServices: inServices.length, outServices: outServices.length };
    };

    // Meter Reading Report
    const getMeterReadingReport = () => {
      const filtered = meterReadings.filter(r => {
        if (!r.date) return false;
        const readingDate = new Date(r.date);
        return readingDate >= new Date(dateRange.start) && readingDate <= new Date(dateRange.end);
      });

      // Combine B/W (A4 + A3) and Colour (A4 + A3) pages for a total page count per reading
      const bwPagesForReading = (r) => ((r.a4Reading || 0) - (r.previousA4 || 0)) + ((r.a3Reading || 0) - (r.previousA3 || 0));
      const colourPagesForReading = (r) => ((r.colourA4Reading || 0) - (r.previousColourA4 || 0)) + ((r.colourA3Reading || 0) - (r.previousColourA3 || 0));
      const pagesForReading = (r) => bwPagesForReading(r) + colourPagesForReading(r);

      // Billable calculation: pages beyond the rental's free-page allowance, per tier,
      // priced at that tier's extra-page cost. Pay Per Page rentals have no free
      // allowance (everything is billable); Standby rentals aren't page-priced at all.
      const billableForReading = (r) => {
        const rental = rentals.find(rent => rent.serialNumber === r.serialNumber);
        if (!rental || rental.rentalType === 'standby') {
          return { billableA4: 0, billableA3: 0, billableColourA4: 0, billableColourA3: 0, amount: 0 };
        }
        const isPerPage = rental.rentalType === 'per_page';
        const a4Used = (r.a4Reading || 0) - (r.previousA4 || 0);
        const a3Used = (r.a3Reading || 0) - (r.previousA3 || 0);
        const colourA4Used = (r.colourA4Reading || 0) - (r.previousColourA4 || 0);
        const colourA3Used = (r.colourA3Reading || 0) - (r.previousColourA3 || 0);

        const billableA4 = isPerPage ? a4Used : Math.max(0, a4Used - (rental.a4FreePages || 0));
        const billableA3 = isPerPage ? a3Used : Math.max(0, a3Used - (rental.a3FreePages || 0));
        const billableColourA4 = isPerPage ? colourA4Used : Math.max(0, colourA4Used - (rental.colourA4FreePages || 0));
        const billableColourA3 = isPerPage ? colourA3Used : Math.max(0, colourA3Used - (rental.colourA3FreePages || 0));

        const amount = (billableA4 * (rental.a4PageCost || 0)) +
                        (billableA3 * (rental.a3PageCost || 0)) +
                        (billableColourA4 * (rental.colourA4PageCost || 0)) +
                        (billableColourA3 * (rental.colourA3PageCost || 0));

        return { billableA4, billableA3, billableColourA4, billableColourA3, amount };
      };

      const totalPages = filtered.reduce((sum, r) => sum + pagesForReading(r), 0);
      const totalBWPages = filtered.reduce((sum, r) => sum + bwPagesForReading(r), 0);
      const totalColourPages = filtered.reduce((sum, r) => sum + colourPagesForReading(r), 0);
      const totalBillableAmount = filtered.reduce((sum, r) => sum + billableForReading(r).amount, 0);
      const avgPages = filtered.length > 0 ? totalPages / filtered.length : 0;

      // Group by client
      const byClient = filtered.reduce((acc, r) => {
        const client = r.clientName || 'Unknown';
        if (!acc[client]) acc[client] = { pages: 0, bwPages: 0, colourPages: 0, readings: 0, billableAmount: 0 };
        acc[client].pages += pagesForReading(r);
        acc[client].bwPages += bwPagesForReading(r);
        acc[client].colourPages += colourPagesForReading(r);
        acc[client].billableAmount += billableForReading(r).amount;
        acc[client].readings++;
        return acc;
      }, {});

      // Exportable row-level data
      const exportRows = filtered.map(r => {
        const billable = billableForReading(r);
        const matchedPrinter = printers.find(p => p.serialNumber === r.serialNumber);
        return {
          Date: r.date,
          Client: r.clientName || '',
          AssetNumber: r.assetNumber || matchedPrinter?.assetNumber || '',
          SerialNumber: r.serialNumber || '',
          PrinterModel: r.printerModel || '',
          A4_Previous: r.previousA4 || 0,
          A4_Current: r.a4Reading || 0,
          A4_Pages: (r.a4Reading || 0) - (r.previousA4 || 0),
          A4_Billable: billable.billableA4,
          A3_Previous: r.previousA3 || 0,
          A3_Current: r.a3Reading || 0,
          A3_Pages: (r.a3Reading || 0) - (r.previousA3 || 0),
          A3_Billable: billable.billableA3,
          ColourA4_Pages: (r.colourA4Reading || 0) - (r.previousColourA4 || 0),
          ColourA4_Billable: billable.billableColourA4,
          ColourA3_Pages: (r.colourA3Reading || 0) - (r.previousColourA3 || 0),
          ColourA3_Billable: billable.billableColourA3,
          TotalPages: pagesForReading(r),
          BillableAmount: billable.amount,
          RecordedBy: r.recordedBy || ''
        };
      });

      return { total: filtered.length, totalPages, totalBWPages, totalColourPages, totalBillableAmount, avgPages, byClient, exportRows };
    };

    // Export to Excel
    const exportReport = (reportData, filename) => {
      const XLSX = require('xlsx');
      const ws = XLSX.utils.json_to_sheet(reportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const reports = [
      { id: 'revenue', icon: '💰', label: 'Revenue Report', desc: 'Monthly revenue, rentals, and trends' },
      { id: 'assets', icon: '🖨️', label: 'Asset Utilization', desc: 'Asset usage, availability, and performance' },
      { id: 'clients', icon: '👥', label: 'Client Analysis', desc: 'Top clients, revenue by client, engagement' },
      { id: 'service', icon: '🔧', label: 'Service Report', desc: 'Service calls, response time, resolution' },
      { id: 'meter', icon: '📊', label: 'Meter Readings', desc: 'Page usage, billing, trends' },
      { id: 'inventory', icon: '📦', label: 'Inventory Report', desc: 'Stock levels, purchases, depreciation' }
    ];

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Business Reports & Analytics</h2>
          <p className="text-slate-600">Comprehensive insights into your printer management business</p>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" />
            </div>
            <button className="bg-brand-600 text-white px-6 py-2 rounded hover:bg-brand-700">
              <Download size={18} className="inline mr-2" />
              Export All
            </button>
          </div>
        </div>

        {/* Report Type Selector - Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map(report => (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                activeReport === report.id
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-slate-200 bg-white hover:border-brand-300'
              }`}
            >
              <div className="text-3xl mb-2">{report.icon}</div>
              <h3 className="font-semibold text-lg mb-1">{report.label}</h3>
              <p className="text-sm text-slate-600">{report.desc}</p>
            </button>
          ))}
        </div>

        {/* Report Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
          {activeReport === 'revenue' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Revenue Report</h3>
              {(() => {
                const data = getRevenueReport();
                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-green-600">₹{data.totalRevenue.toLocaleString()}</p>
                      </div>
                      <div className="bg-brand-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Active Rentals</p>
                        <p className="text-2xl font-bold text-brand-600">{data.count}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Avg Revenue/Rental</p>
                        <p className="text-2xl font-bold text-purple-600">₹{data.avgRevenue.toFixed(0)}</p>
                      </div>
                    </div>
                    <button onClick={() => exportReport(data.exportRows, 'revenue_report')} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium">
                      <Download size={18} className="inline mr-2" />
                      Export Revenue Data
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {activeReport === 'assets' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Asset Utilization Report</h3>
              {(() => {
                const data = getAssetUtilization();
                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-brand-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Total Assets</p>
                        <p className="text-2xl font-bold text-brand-600">{data.total}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Rented</p>
                        <p className="text-2xl font-bold text-green-600">{data.rented}</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Available</p>
                        <p className="text-2xl font-bold text-yellow-600">{data.available}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Utilization</p>
                        <p className="text-2xl font-bold text-purple-600">{data.utilization}%</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">By Category</h4>
                      <div className="space-y-2">
                        {Object.entries(data.byCategory).map(([cat, stats]) => (
                          <div key={cat} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                            <span className="font-medium">{cat}</span>
                            <div className="flex gap-4 text-sm">
                              <span>Total: {stats.total}</span>
                              <span className="text-green-600">Rented: {stats.rented}</span>
                              <span className="text-slate-600">Available: {stats.available}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {activeReport === 'clients' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Client Analysis</h3>
              {(() => {
                const data = getClientReport();
                return (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Top 10 Clients by Revenue</p>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-slate-500">Client</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-slate-500">Rentals</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-slate-500">Revenue</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-slate-500">Service Calls</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {data.slice(0, 10).map((client, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="px-4 py-3">{client.name}</td>
                                <td className="px-4 py-3">{client.rentals}</td>
                                <td className="px-4 py-3 font-semibold text-green-600">₹{client.revenue.toLocaleString()}</td>
                                <td className="px-4 py-3">{client.serviceCalls}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <button onClick={() => exportReport(data, 'client_analysis')} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium">
                      <Download size={18} className="inline mr-2" />
                      Export Client Data
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {activeReport === 'service' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Service Report</h3>
              {(() => {
                const data = getServiceReport();
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-brand-50 rounded-lg p-4">
                      <p className="text-sm text-slate-600">Total Services</p>
                      <p className="text-2xl font-bold text-brand-600">{data.total}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-sm text-slate-600">IN (Received)</p>
                      <p className="text-2xl font-bold text-orange-600">{data.inServices}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-slate-600">OUT (Returned)</p>
                      <p className="text-2xl font-bold text-green-600">{data.outServices}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeReport === 'meter' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Meter Reading Report</h3>
              {(() => {
                const data = getMeterReadingReport();
                const clientRows = Object.entries(data.byClient).sort((a, b) => b[1].pages - a[1].pages);
                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-brand-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Total Readings</p>
                        <p className="text-2xl font-bold text-brand-600">{data.total}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Total Pages</p>
                        <p className="text-2xl font-bold text-purple-600">{data.totalPages.toLocaleString()}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">Avg Pages/Reading</p>
                        <p className="text-2xl font-bold text-green-600">{data.avgPages.toFixed(0)}</p>
                      </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-slate-600">💰 Billable Amount (pages beyond free allowance)</p>
                      <p className="text-2xl font-bold text-orange-700">₹{data.totalBillableAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    </div>

                    {data.totalColourPages > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p className="text-sm text-slate-600">B/W Pages</p>
                          <p className="text-xl font-bold text-slate-700">{data.totalBWPages.toLocaleString()}</p>
                        </div>
                        <div className="bg-pink-50 rounded-lg p-4">
                          <p className="text-sm text-slate-600">🎨 Colour Pages</p>
                          <p className="text-xl font-bold text-pink-600">{data.totalColourPages.toLocaleString()}</p>
                        </div>
                      </div>
                    )}

                    {clientRows.length > 0 && (
                      <div className="mb-6">
                        <p className="text-sm font-medium text-slate-700 mb-2">Usage by Client</p>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Client</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Readings</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">B/W Pages</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Colour Pages</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Total Pages</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Billable (₹)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {clientRows.map(([client, stats]) => (
                                <tr key={client} className="hover:bg-slate-50">
                                  <td className="px-4 py-3">{client}</td>
                                  <td className="px-4 py-3">{stats.readings}</td>
                                  <td className="px-4 py-3">{stats.bwPages.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-pink-600">{stats.colourPages > 0 ? stats.colourPages.toLocaleString() : '-'}</td>
                                  <td className="px-4 py-3 font-semibold">{stats.pages.toLocaleString()}</td>
                                  <td className="px-4 py-3 font-semibold text-orange-700">{stats.billableAmount > 0 ? `₹${stats.billableAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '✓ Covered'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {data.exportRows.length > 0 ? (
                      <button onClick={() => exportReport(data.exportRows, 'meter_reading_report')} className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium">
                        <Download size={18} className="inline mr-2" />
                        Export Meter Readings
                      </button>
                    ) : (
                      <p className="text-sm text-slate-400 italic">No readings recorded in this date range to export.</p>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {activeReport === 'inventory' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Inventory Report</h3>
              <div className="text-center py-8 text-slate-500">
                <p>Inventory depreciation and purchase reports coming soon</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // MODAL COMPONENT - Handles all add/edit forms
  const Modal = () => {
    const [formData, setFormData] = useState(editingItem || {});
    // Rental rate lock: fill the customer's locked rates into the rental form (non-super-admins cannot change them)
    useEffect(() => {
      if (modalType !== 'rental' || userRole === 'super_admin') return;
      const e = rentalLockEntry(formData);
      if (!e) return;
      const patch = {};
      RENTAL_RATE_FIELDS.forEach(f => { if (e[f] !== undefined && e[f] !== '' && e[f] !== null && String(formData[f] ?? '') !== String(e[f])) patch[f] = String(e[f]); });
      if (Object.keys(patch).length) setFormData(prev => ({ ...prev, ...patch }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalType, formData.clientName, formData.printerType, formData.category, formData.rentalType]);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [uploadingClientDocType, setUploadingClientDocType] = useState(null);
    const [showWalkinForm, setShowWalkinForm] = useState(false);
    const [rentalAssetTypeFilter, setRentalAssetTypeFilter] = useState('all');
    const [walkinData, setWalkinData] = useState({ name: '', phone: '', email: '', company: '' });
    const [savingWalkin, setSavingWalkin] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const saveWalkinClient = async () => {
      if (!walkinData.name.trim()) {
        alert('❌ Client name is required.');
        return;
      }
      setSavingWalkin(true);
      try {
        const newClient = {
          id: `CLT${Date.now()}`,
          name: walkinData.name.trim(),
          phone: walkinData.phone.trim(),
          email: walkinData.email.trim(),
          company: walkinData.company.trim(),
          companyId: currentCompany,
          isWalkin: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'clients', newClient.id), newClient);
        await logAudit('CREATE', 'Client', newClient.id, `${newClient.name} (walk-in)`, newClient);
        autoSyncClient(newClient);

        // Select this new client right away in the current form
        setFormData({...formData, clientName: newClient.name, serialNumber: '', printerModel: '', assetNumber: '', printerMake: '', printerModelOnly: ''});
        setShowWalkinForm(false);
        setWalkinData({ name: '', phone: '', email: '', company: '' });

      } catch (err) {
        alert('❌ Error saving client: ' + err.message);
      }
      setSavingWalkin(false);
    };
    
    // editingItem IS genuine, correct state from the enclosing component (not
    // a stale outer scope value) and SHOULD re-run this effect whenever it
    // changes, to re-sync the form when switching between "add new" and
    // "edit existing". ESLint's exhaustive-deps rule doesn't reliably
    // recognize state from an enclosing component's closure as valid here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      setFormData(editingItem || {});
    }, [editingItem]);

    const handleAgreementUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        alert('File too large. Please upload a file under 10MB.');
        return;
      }

      setUploadingDoc(true);
      try {
        const rentalId = formData.id || `RNT${Date.now()}`;
        const filePath = `agreements/${rentalId}/${file.name}`;
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        setFormData(prev => ({
          ...prev,
          id: prev.id || rentalId,
          agreementDocUrl: downloadUrl,
          agreementDocName: file.name,
          agreementDocPath: filePath
        }));
      } catch (err) {
        alert('❌ Error uploading document: ' + err.message);
      }
      setUploadingDoc(false);
    };

    const handleRemoveAgreementDoc = async () => {
      if (!window.confirm('Remove the uploaded agreement document?')) return;
      try {
        if (formData.agreementDocPath) {
          await deleteObject(ref(storage, formData.agreementDocPath)).catch(() => {});
        }
      } catch (err) {
        console.error('Error deleting old file:', err);
      }
      setFormData(prev => ({ ...prev, agreementDocUrl: '', agreementDocName: '', agreementDocPath: '' }));
    };

    // Client Documents: unlike the single-file agreement upload above, a
    // client can have any number of files per type (agreement, GST
    // certificate) added over time, so these accumulate into a
    // formData.documents array instead of overwriting flat fields. Uploads
    // happen immediately on file select (same as the agreement pattern
    // above) rather than being deferred to Save, so a cancelled edit can
    // leave an orphaned Storage file - an accepted tradeoff already present
    // in the existing agreement-upload flow.
    const handleClientDocUpload = async (e, docType) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      const oversized = files.find(f => f.size > 10 * 1024 * 1024);
      if (oversized) {
        alert(`"${oversized.name}" is over 10MB. Please upload files under 10MB.`);
        e.target.value = '';
        return;
      }

      setUploadingClientDocType(docType);
      try {
        const clientId = formData.id || `CLT${Date.now()}`;
        const uploaded = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const filePath = `clientDocuments/${clientId}/${docType}/${Date.now()}_${i}_${file.name}`;
          const storageRef = ref(storage, filePath);
          await uploadBytes(storageRef, file);
          const downloadUrl = await getDownloadURL(storageRef);
          uploaded.push({ name: file.name, url: downloadUrl, path: filePath, type: docType, uploadedAt: new Date().toISOString() });
        }
        setFormData(prev => ({
          ...prev,
          id: prev.id || clientId,
          documents: [...(prev.documents || []), ...uploaded]
        }));
      } catch (err) {
        alert('❌ Error uploading document(s): ' + err.message);
      }
      setUploadingClientDocType(null);
      e.target.value = '';
    };

    const handleRemoveClientDoc = async (docToRemove) => {
      if (!window.confirm(`Remove "${docToRemove.name}"?`)) return;
      try {
        if (docToRemove.path) {
          await deleteObject(ref(storage, docToRemove.path)).catch(() => {});
        }
      } catch (err) {
        console.error('Error deleting client document:', err);
      }
      setFormData(prev => ({
        ...prev,
        documents: (prev.documents || []).filter(d => d.path !== docToRemove.path)
      }));
    };

    if (!showModal) return null;

    const handleSubmit = async (e) => {
      e.preventDefault();
      // Guards against double-submit creating duplicate records (e.g. a
      // double-click, or a slow save + impatient re-click) - this was the
      // direct cause of the duplicate quotation number bug: two saves fired
      // before the first had written its doc and refreshed local state, so
      // both computed the same "next" quote number independently.
      if (submitting) return;
      setSubmitting(true);

      try {
        if (modalType === 'printer') {
          // Use a FRESH snapshot (not local state) to avoid race conditions where
          // two people add assets around the same time and both compute the same "next number"
          const freshSnapshot = await getDocs(collection(db, 'printers'));
          const freshPrinters = freshSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

          // Auto-generate asset number based on category
          let assetNumber = formData.assetNumber;
          if (!assetNumber && !editingItem) {
            const prefixMap = getAssetPrefixMap(currentCompany);
            const prefix = prefixMap[formData.category || 'Printer'];
            assetNumber = getNextAssetNumberForPrefix(prefix, freshPrinters);
          }

          // If an EXISTING asset's category was just corrected (e.g. it was
          // mistakenly added as Laptop and should be Desktop), its number
          // still carries the old category's prefix. Offer to fix it: the
          // old number is retired (asset numbers are never reused), and this
          // asset gets a correct one for its real category.
          if (editingItem && formData.category && editingItem.category && formData.category !== editingItem.category && assetNumber) {
            const prefixMap = getAssetPrefixMap(editingItem.companyId || currentCompany);
            const expectedPrefix = prefixMap[formData.category];
            if (expectedPrefix && !assetNumber.startsWith(expectedPrefix)) {
              // Driven by the Yes/No choice rendered right in the form (see the
              // Asset Number preview box) instead of window.confirm() - native
              // browser confirm dialogs silently auto-decline with no visible
              // popup once a page has triggered Chrome's "prevent this page
              // from creating additional dialogs" state, which this app's
              // heavy use of alert()/confirm() elsewhere can easily trip.
              if (formData.renumberOnCategoryChange === undefined) {
                alert(`❌ This asset's category changed to ${formData.category}, but its number (${assetNumber}) still uses the ${editingItem.category} prefix.\n\nScroll up and choose "Yes, renumber" or "No, keep ${assetNumber}" before saving.`);
                return;
              }
              if (formData.renumberOnCategoryChange === true) {
                assetNumber = getNextAssetNumberForPrefix(expectedPrefix, freshPrinters.filter(p => p.id !== (formData.id || '')));
              }
            }
          }

          // Validation: block duplicate asset numbers (excluding this same record when editing)
          const currentId = formData.id || `PRT${Date.now()}`;
          const duplicate = freshPrinters.find(p => p.assetNumber === assetNumber && p.id !== currentId);
          if (assetNumber && duplicate) {
            alert(`❌ Asset number "${assetNumber}" is already used by another printer:\n\n${duplicate.make} ${duplicate.model} (Serial: ${duplicate.serialNumber})\n\nPlease choose a different asset number, or leave it blank to auto-generate the next available one.`);
            return;
          }

          // Validation: block duplicate Serial Numbers too. Two different physical
          // printers must never share a serial - if they do, every feature that
          // joins rentals/readings to a printer BY serial number (search, asset
          // number sync, billing, etc.) gets silently confused between them.
          const duplicateSerial = freshPrinters.find(p => p.serialNumber && formData.serialNumber && p.serialNumber.trim().toLowerCase() === formData.serialNumber.trim().toLowerCase() && p.id !== currentId);
          if (duplicateSerial) {
            alert(`❌ Serial number "${formData.serialNumber}" is already used by another printer:\n\n${duplicateSerial.assetNumber || 'No asset number'} - ${duplicateSerial.make} ${duplicateSerial.model}\n\nEach physical printer must have a unique serial number. Please double-check the serial you entered.`);
            return;
          }
          
          // eslint-disable-next-line no-unused-vars
          const { renumberOnCategoryChange, ...formDataToSave } = formData;
          const newPrinter = {
            ...formDataToSave,
            id: currentId,
            assetNumber: assetNumber,
            type: formData.type || 'A4',
            status: formData.status || 'available',
            companyId: editingItem?.companyId || currentCompany
          };
          await setDoc(doc(db, 'printers', newPrinter.id), newPrinter);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'Asset', newPrinter.id, `${newPrinter.assetNumber} - ${newPrinter.make} ${newPrinter.model}`, newPrinter);

          // If this was an edit and the asset number actually changed, keep any
          // rentals referencing this printer's serial number in sync - otherwise
          // the Rentals page would keep showing the old asset number.
          if (editingItem && editingItem.assetNumber !== newPrinter.assetNumber) {
            const linkedRentals = rentals.filter(r => r.serialNumber === newPrinter.serialNumber);
            if (linkedRentals.length > 0) {
              for (const r of linkedRentals) {
                await setDoc(doc(db, 'rentals', r.id), { ...r, assetNumber: newPrinter.assetNumber });
              }
            }
          }
        } else if (modalType === 'client') {
          const oldName = editingItem?.name;
          const nameChanged = editingItem && oldName && formData.name && formData.name !== oldName;
          const newClient = {
            ...formData,
            id: formData.id || `CLT${Date.now()}`,
            companyId: editingItem?.companyId || currentCompany,
            createdBy: editingItem?.createdBy || currentUser?.email || 'Unknown',
            // Keep a record of past names - both so a rename doesn't erase the
            // trail, and so searching the OLD name still finds this client.
            ...(nameChanged ? { previousNames: Array.from(new Set([...(editingItem.previousNames || []), oldName])) } : {})
          };
          await setDoc(doc(db, 'clients', newClient.id), newClient);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'Client', newClient.id, newClient.name, newClient);
          autoSyncClient(newClient, editingItem?.name);

          // Every collection below caches the client's name as a plain string
          // at the time each record was created - there's no clientId link
          // anywhere in this app, so a rename otherwise leaves every existing
          // rental/quotation/service call/etc. silently pointing at a name
          // that no longer exists on the Client record. Cascade the rename
          // across all of them, scoped to this same company so a same-named
          // client elsewhere is never touched.
          if (nameChanged) {
            const scopeCompany = newClient.companyId;
            const CLIENT_NAME_COLLECTIONS = ['rentals', 'quotations', 'serviceCalls', 'meterReadings', 'printerMovements', 'clientAssets'];
            let renamedCount = 0;
            for (const collName of CLIENT_NAME_COLLECTIONS) {
              const snap = await getDocs(collection(db, collName));
              const matches = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => r.clientName === oldName && (r.companyId || 'rb_infotech') === scopeCompany);
              if (matches.length === 0) continue;
              let batch = writeBatch(db);
              let opCount = 0;
              for (const r of matches) {
                batch.update(doc(db, collName, r.id), { clientName: newClient.name });
                opCount++;
                renamedCount++;
                if (opCount === 450) { await batch.commit(); batch = writeBatch(db); opCount = 0; }
              }
              if (opCount > 0) await batch.commit();
            }
            if (renamedCount > 0) {
              await logAudit('UPDATE', 'Client', newClient.id, `Renamed "${oldName}" → "${newClient.name}" - updated ${renamedCount} linked record(s)`, {});
              // Local state for the collections above is only refreshed by
              // their own onSnapshot listeners picking up the batch writes,
              // which happens automatically - no manual refetch needed here.
            }
          }
        } else if (modalType === 'lead') {
          const newLead = {
            ...formData,
            id: formData.id || `LEAD${Date.now()}`,
            companyId: editingItem?.companyId || currentCompany,
            stage: formData.stage || 'new',
            createdAt: editingItem?.createdAt || new Date().toISOString(),
            createdBy: editingItem?.createdBy || currentUser?.email || 'Unknown'
          };
          await setDoc(doc(db, 'leads', newLead.id), newLead);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'Lead', newLead.id, newLead.name, newLead);
        } else if (modalType === 'quotation') {
          if (!formData.clientId) {
            alert('❌ Please select a client.');
            return;
          }
          const lockIssues = priceLockIssues(formData.clientId, formData.clientName, formData.items);
          if (lockIssues.length) { alert('🔒 Price lock:\n\n' + lockIssues.join('\n') + '\n\nOnly the super admin can change locked prices or add other items.'); return; }
          const validItems = (formData.items || []).filter(item => item.description && item.rate);
          if (validItems.length === 0) {
            alert('❌ Please add at least one line item with a description and rate.');
            return;
          }
          const isNew = !editingItem?.id;
          const newQuotation = {
            ...formData,
            items: validItems,
            id: editingItem?.id || `QUO${Date.now()}`,
            companyId: editingItem?.companyId || currentCompany,
            quoteNumber: editingItem?.quoteNumber || await generateQuoteNumber(),
            createdAt: editingItem?.createdAt || new Date().toISOString(),
            createdBy: editingItem?.createdBy || currentUser?.email || 'Unknown'
          };
          await setDoc(doc(db, 'quotations', newQuotation.id), newQuotation);
          await logAudit(isNew ? 'CREATE' : 'UPDATE', 'Quotation', newQuotation.id, `${newQuotation.quoteNumber} - ${newQuotation.clientName}`, newQuotation);
          if (isNew && newQuotation.inboxMailId) {
            // Created from an Email Inbox row: link the mail to this quotation. A quoted enquiry is handled, so close it;
            // orders stay open (a quotation alone doesn't complete an order).
            try {
              const srcMail = inboxMails.find(x => x.id === newQuotation.inboxMailId);
              await setDoc(doc(db, 'inboxMails', newQuotation.inboxMailId), {
                quotationId: newQuotation.id,
                quotationNumber: newQuotation.quoteNumber,
                quotedAt: new Date().toISOString(),
                ...(srcMail?.type === 'enquiry' ? { status: 'done' } : {})
              }, { merge: true });
            } catch (linkErr) { console.error('Could not link quotation to email:', linkErr); }
          }
          if (isNew) autoSyncQuotation(newQuotation);
          setShowModal(false);
          setEditingItem(null);
          // Generate the PDF immediately so the engineer can hand it over / send it right away
          generateQuotationPDF(newQuotation);
          return;
        } else if (modalType === 'engineer') {
          const newEngineer = { ...formData, id: formData.id || `ENG${Date.now()}` };
          await setDoc(doc(db, 'engineers', newEngineer.id), newEngineer);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'Engineer', newEngineer.id, newEngineer.name, newEngineer);
        } else if (modalType === 'clientAsset') {
          const isNewAsset = !editingItem?.id;
          const newAsset = {
            ...formData,
            id: formData.id || `CA${Date.now()}`,
            companyId: editingItem?.companyId || currentCompany,
            addedDate: formData.addedDate || new Date().toISOString().split('T')[0]
          };
          await setDoc(doc(db, 'clientAssets', newAsset.id), newAsset);
          await logAudit(isNewAsset ? 'CREATE' : 'UPDATE', 'ClientAsset', newAsset.id, `${newAsset.serialNumber || newAsset.id} - ${newAsset.clientName || 'Unknown client'}`, newAsset);
        } else if (modalType === 'reading') {
          const parsedA4 = parseInt(formData.a4Reading) || 0;
          const parsedA3 = parseInt(formData.a3Reading) || 0;
          const parsedColourA4 = parseInt(formData.colourA4Reading) || 0;
          const parsedColourA3 = parseInt(formData.colourA3Reading) || 0;
          const parsedScanner = parseInt(formData.scannerReading) || 0;
          const prevA4 = parseInt(formData.previousA4) || 0;
          const prevA3 = parseInt(formData.previousA3) || 0;
          const prevColourA4 = parseInt(formData.previousColourA4) || 0;
          const prevColourA3 = parseInt(formData.previousColourA3) || 0;
          const prevScanner = parseInt(formData.previousScanner) || 0;

          // Validation: a meter counter can only go up, never down. Catches typos
          // (e.g. missing a digit) and prevents impossible negative page counts.
          if (parsedA4 < prevA4) {
            alert(`❌ A4 Current Reading (${parsedA4}) cannot be less than the Previous Reading (${prevA4}).\n\nPrinter meter counters only increase - please check the value entered.`);
            return;
          }
          if (parsedA3 < prevA3) {
            alert(`❌ A3 Current Reading (${parsedA3}) cannot be less than the Previous Reading (${prevA3}).\n\nPrinter meter counters only increase - please check the value entered.`);
            return;
          }
          if (parsedColourA4 < prevColourA4) {
            alert(`❌ Colour A4 Current Reading (${parsedColourA4}) cannot be less than the Previous Reading (${prevColourA4}).`);
            return;
          }
          if (parsedColourA3 < prevColourA3) {
            alert(`❌ Colour A3 Current Reading (${parsedColourA3}) cannot be less than the Previous Reading (${prevColourA3}).`);
            return;
          }
          if (parsedScanner < prevScanner) {
            alert(`❌ Scanner Current Reading (${parsedScanner}) cannot be less than the Previous Reading (${prevScanner}).`);
            return;
          }

          const newReading = { 
            ...formData, 
            id: formData.id || `RDG${Date.now()}`, 
            companyId: editingItem?.companyId || currentCompany,
            a4Reading: parsedA4,
            a3Reading: parsedA3,
            colourA4Reading: parsedColourA4,
            colourA3Reading: parsedColourA3,
            scannerReading: parsedScanner,
            previousA4: prevA4,
            previousA3: prevA3,
            previousColourA4: prevColourA4,
            previousColourA3: prevColourA3,
            previousScanner: prevScanner,
            cartridgesUsed: parseInt(formData.cartridgesUsed) || 0,
            date: formData.date || new Date().toISOString().split('T')[0],
            recordedBy: currentUser?.email || 'Unknown'
          };
          await setDoc(doc(db, 'meterReadings', newReading.id), newReading);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'MeterReading', newReading.id, `${newReading.clientName} - ${newReading.serialNumber}`, {a4: newReading.a4Reading, a3: newReading.a3Reading, colourA4: newReading.colourA4Reading, colourA3: newReading.colourA3Reading, scanner: newReading.scannerReading});
        } else if (modalType === 'rental') {
          const isStandby = formData.rentalType === 'standby';
          const isPerPage = formData.rentalType === 'per_page';
          // Desktops and laptops don't print pages, so none of the
          // page-cost/meter-reading billing model applies to them - they're
          // always a flat monthly rate, same shape as how standby skips the
          // page-billing fields for its own reason (cartridge-only).
          const isDeviceRental = formData.category === 'Desktop' || formData.category === 'Laptop';
          const currentRentalId = formData.id || `RNT${Date.now()}`;

          // Validation: Agreement Start/End Date are mandatory
          if (!formData.agreementStartDate || !formData.agreementEndDate) {
            alert('❌ Agreement Start Date and Agreement End Date are required.\n\nThese drive the renewal reminder alerts, so every rental must have both dates set.');
            return;
          }
          if (new Date(formData.agreementEndDate) < new Date(formData.agreementStartDate)) {
            alert('❌ Agreement End Date cannot be before the Agreement Start Date. Please check the dates.');
            return;
          }

          // Validation: Opening Meter Reading is mandatory for Regular and Pay Per Page rentals
          // (Standby is billed by cartridge, not by page count, so it doesn't need one;
          // desktops/laptops don't have a page meter at all, so they don't either)
          if (!isStandby && !isDeviceRental) {
            if (formData.openingA4Reading === undefined || formData.openingA4Reading === '') {
              alert('❌ A4 Opening Reading is required.\n\nThis is the baseline reading used to calculate billable pages for this rental going forward.');
              return;
            }
            if ((formData.printerType === 'A3' || formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (formData.openingA3Reading === undefined || formData.openingA3Reading === '')) {
              alert('❌ A3 Opening Reading is required for A3/MFP printers.\n\nThis is the baseline reading used to calculate billable pages for this rental going forward.');
              return;
            }
            if (formData.printerType === 'MFP_Colour') {
              if (formData.openingColourA4Reading === undefined || formData.openingColourA4Reading === '') {
                alert('❌ Colour A4 Opening Reading is required for MFP Colour printers.');
                return;
              }
              if (formData.openingColourA3Reading === undefined || formData.openingColourA3Reading === '') {
                alert('❌ Colour A3 Opening Reading is required for MFP Colour printers.');
                return;
              }
            }
          }

          const rentLockIssues = rentalLockIssues(formData);
          if (rentLockIssues.length) { alert('🔒 Rental rate lock:\n\n' + rentLockIssues.join('\n') + '\n\nOnly the super admin can change locked rental rates.'); return; }

          // Validation: block a second ACTIVE rental for a printer that already has one
          // (only relevant when creating new, or reactivating - not when editing an
          // already-returned rental record)
          if (formData.status !== 'returned') {
            const freshRentalsSnapshot = await getDocs(collection(db, 'rentals'));
            const duplicateActiveRental = freshRentalsSnapshot.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .find(r => r.serialNumber === formData.serialNumber && r.status !== 'returned' && r.id !== currentRentalId);
            if (duplicateActiveRental) {
              alert(`❌ This printer already has an active rental:\n\n${duplicateActiveRental.clientName} (started ${duplicateActiveRental.startDate})\n\nCheck in / return that rental first before assigning this printer again.`);
              return;
            }
          }

          // If the agreement end date has changed (renewal), reset reminder flags
          // so the Cloud Function sends fresh 30/7/expiry/overdue reminders for the new date.
          const agreementDateChanged = editingItem && editingItem.agreementEndDate !== formData.agreementEndDate;
          const isMFPColour = formData.printerType === 'MFP_Colour';
          const newRental = {
            ...formData,
            id: currentRentalId,
            companyId: editingItem?.companyId || currentCompany,
            // Rentals saved before Desktop/Laptop categories existed have no
            // category field at all - formData.category would be undefined
            // when editing one of those without touching the asset dropdown,
            // and Firestore's setDoc() rejects the entire write over a single
            // undefined field. Same fallback as the asset-select handler.
            category: formData.category || 'Printer',
            rentalType: formData.rentalType || 'regular',
            monthlyRate: (isStandby || isPerPage) ? 0 : parseFloat(formData.monthlyRate),
            a4FreePages: (isStandby || isPerPage || isDeviceRental) ? 0 : parseInt(formData.a4FreePages),
            a4PageCost: (isStandby || isDeviceRental) ? 0 : parseFloat(formData.a4PageCost),
            a3FreePages: (isStandby || isPerPage || isDeviceRental) ? 0 : parseInt(formData.a3FreePages) || 0,
            a3PageCost: (isStandby || isDeviceRental) ? 0 : parseFloat(formData.a3PageCost) || 0,
            colourA4FreePages: (isStandby || isPerPage || isDeviceRental || !isMFPColour) ? 0 : parseInt(formData.colourA4FreePages) || 0,
            colourA4PageCost: (isStandby || isDeviceRental || !isMFPColour) ? 0 : parseFloat(formData.colourA4PageCost) || 0,
            colourA3FreePages: (isStandby || isPerPage || isDeviceRental || !isMFPColour) ? 0 : parseInt(formData.colourA3FreePages) || 0,
            colourA3PageCost: (isStandby || isDeviceRental || !isMFPColour) ? 0 : parseFloat(formData.colourA3PageCost) || 0,
            cartridgeCost: isStandby ? parseFloat(formData.cartridgeCost) : 0,
            openingA4Reading: (isStandby || isDeviceRental) ? null : parseInt(formData.openingA4Reading) || 0,
            openingA3Reading: (isStandby || isDeviceRental) ? null : parseInt(formData.openingA3Reading) || 0,
            openingColourA4Reading: (isStandby || !isMFPColour) ? null : parseInt(formData.openingColourA4Reading) || 0,
            openingColourA3Reading: (isStandby || !isMFPColour) ? null : parseInt(formData.openingColourA3Reading) || 0,
            department: formData.department || '',
            status: formData.status || 'active',
            createdBy: editingItem?.createdBy || currentUser?.email || 'Unknown',
            ...(agreementDateChanged ? {
              reminder30Sent: false,
              reminder7Sent: false,
              reminderExpirySent: false,
              reminderOverdueSent: false
            } : {})
          };
          await setDoc(doc(db, 'rentals', newRental.id), newRental);
          await logAudit(editingItem ? 'UPDATE' : 'CREATE', 'Rental', newRental.id, `${newRental.clientName} - ${newRental.serialNumber}`, newRental);
          
          // Update printer status
          const affectedPrinter = printers.find(p => p.serialNumber === newRental.serialNumber);
          if (affectedPrinter) {
            const updatedPrinter = { ...affectedPrinter, status: 'rented' };
            await setDoc(doc(db, 'printers', affectedPrinter.id), updatedPrinter);
          }
        } else if (modalType === 'serviceCall') {
          if (!formData.clientName) {
            alert('❌ Please select a Client.');
            return;
          }
          if (!formData.serialNumber || (formData.serialNumber === '__new__' && !formData.newSerialNumber)) {
            alert('❌ Please select a Printer for this service call.\n\nIf no printer shows in the dropdown, this client may not have any assets assigned yet - check Rentals (for our assets) or the Client Asset Register (for their own devices).');
            return;
          }
          if (!formData.issue) {
            alert('❌ Please enter an Issue Description.');
            return;
          }

          // Every printer handover needs a meter reading, whether it's arriving
          // for service or being handed back - this is how usage/billing stays
          // accurate for both our own assets and client-owned printers. Required
          // unconditionally for every new call (not just when other pickup
          // fields happen to be filled in).
          const pickupPrinter = formData.assetSource === 'client' ? null : printers.find(p => p.serialNumber === formData.serialNumber);
          const pickupType = pickupPrinter?.type;
          const pickupHasA3 = pickupType === 'A3' || pickupType === 'MFP' || pickupType === 'MFP_Colour';
          const pickupHasScanner = pickupType === 'MFP' || pickupType === 'MFP_Colour';

          if (!editingItem?.id && !formData.pickupA4Reading) {
            alert('⚠️ A4 Meter Reading on Pickup is required for every new Service Call.');
            return;
          }
          if (!editingItem?.id && pickupHasA3 && !formData.pickupA3Reading) {
            alert('⚠️ This printer supports A3 - an A3 Meter Reading on Pickup is required too.');
            return;
          }
          if (!editingItem?.id && pickupHasScanner && !formData.pickupScannerReading) {
            alert('⚠️ This is an MFP - a Scanner Count on Pickup is required too.');
            return;
          }
          if (formData.status === 'in-progress' && formData.engineer) {
            const busy = serviceCalls.find(c => c.id !== editingItem?.id && c.engineer === formData.engineer && c.status === 'in-progress');
            if (busy) {
              alert(`⛔ ${formData.engineer} has not completed the current call (${busy.clientName || 'client'} - ${busy.issue || busy.serialNumber || busy.id}).\n\nComplete that call first, with its completion details, before taking the next one.`);
              return;
            }
          }
          if (formData.status === 'completed' && !String(formData.workDone || '').trim()) {
            alert('⚠️ Please describe the work done (Completion Details) to mark this Service Call as Completed.');
            return;
          }
          if (formData.status === 'completed') {
            if (!formData.returnA4Reading) {
              alert('⚠️ A4 Meter Reading on Return is required to mark this Service Call as Completed.');
              return;
            }
            if (pickupHasA3 && !formData.returnA3Reading) {
              alert('⚠️ This printer supports A3 - an A3 Meter Reading on Return is required too.');
              return;
            }
            if (pickupHasScanner && !formData.returnScannerReading) {
              alert('⚠️ This is an MFP - a Scanner Count on Return is required too.');
              return;
            }
          }

          // Stock used on this call: check availability before anything is written
          if (formData.status === 'completed') {
            const need = {};
            for (const r of (formData.stockUsed || []).filter(x => !x.moveId && x.itemId && Number(x.qty) > 0)) {
              const it = stockItems.find(i => i.id === r.itemId);
              if (!it) continue;
              const fromEng = Math.min(Number(r.qty), Math.max(0, engineerHolding(formData.engineer || '', it.id) - (need[`e${it.id}`] || 0)));
              need[`e${it.id}`] = (need[`e${it.id}`] || 0) + fromEng;
              need[`s${it.id}`] = (need[`s${it.id}`] || 0) + (Number(r.qty) - fromEng);
              if (need[`s${it.id}`] > stockOnHand(it.id)) { alert(`⚠️ Not enough ${it.name} in stock: need ${need[`s${it.id}`]} from stores, only ${stockOnHand(it.id)} ${it.unit} on hand.`); return; }
            }
          }

          let finalSerialNumber = formData.serialNumber;
          let finalPrinterModel = formData.printerModel;

          // If a brand-new (not yet registered) client-owned device was entered
          // inline, validate it and add it to the Client Asset register too -
          // so the register stays the single source of truth, and this same
          // device is properly selectable next time instead of being re-typed.
          if (formData.assetSource === 'client' && formData.serialNumber === '__new__') {
            if (!formData.printerMake || !formData.printerModelOnly || !formData.newSerialNumber) {
              alert('❌ Please fill in Make, Model, and Serial Number for the new device.');
              return;
            }
            const existingBySerial = clientAssets.find(a => a.serialNumber.trim().toLowerCase() === formData.newSerialNumber.trim().toLowerCase());
            if (existingBySerial) {
              alert(`❌ Serial number "${formData.newSerialNumber}" is already registered to ${existingBySerial.clientName}. Please select it from the dropdown instead, or double-check the serial.`);
              return;
            }
            const newAsset = {
              id: `CAS${Date.now()}`,
              serialNumber: formData.newSerialNumber,
              make: formData.printerMake,
              model: formData.printerModelOnly,
              clientName: formData.clientName,
              companyId: currentCompany
            };
            await setDoc(doc(db, 'clientAssets', newAsset.id), newAsset);
            await logAudit('CREATE', 'ClientAsset', newAsset.id, `${newAsset.serialNumber} - ${newAsset.make} ${newAsset.model} (${newAsset.clientName})`, newAsset);
            finalSerialNumber = newAsset.serialNumber;
            finalPrinterModel = `${newAsset.make} ${newAsset.model}`;
          }

          const isNewCall = !editingItem?.id;
          const newCall = { 
            ...formData, 
            serialNumber: finalSerialNumber,
            printerModel: finalPrinterModel,
            id: editingItem?.id || `SVC${Date.now()}`,
            companyId: editingItem?.companyId || currentCompany,
            status: formData.status || 'open',
            createdAt: editingItem?.createdAt || new Date().toISOString()
          };
          delete newCall.newSerialNumber;

          // Assignment trail: record every change of engineer (who, when, by whom)
          if ((newCall.engineer || '') !== (editingItem?.engineer || '')) {
            newCall.assignmentHistory = [...(editingItem?.assignmentHistory || []), { engineer: newCall.engineer || '', at: new Date().toISOString(), by: currentUser?.email || 'Unknown' }];
            newCall.assignedAt = new Date().toISOString();
          }
          // Completion trail: stamped when the call is first marked completed, cleared if it is reopened
          if (newCall.status === 'completed') {
            newCall.completedAt = editingItem?.completedAt || new Date().toISOString();
            newCall.completedBy = editingItem?.completedBy || currentUser?.email || 'Unknown';
          } else {
            newCall.completedAt = '';
            newCall.completedBy = '';
          }
          // Stock used: take it from the engineer's carried stock first, then from stores; each row is recorded once
          if (newCall.status === 'completed' && (newCall.stockUsed || []).some(x => !x.moveId && x.itemId && Number(x.qty) > 0)) {
            const rows = [];
            for (const r of newCall.stockUsed) {
              const it = stockItems.find(i => i.id === r.itemId);
              if (r.moveId || !it || !(Number(r.qty) > 0)) { if (r.moveId) rows.push(r); continue; }
              const qty = Number(r.qty);
              const fromEng = Math.min(qty, Math.max(0, engineerHolding(newCall.engineer || '', it.id)));
              const base = { engineer: newCall.engineer || '', clientName: newCall.clientName, callId: newCall.id, consumed: true, date: newCall.completedAt ? newCall.completedAt.slice(0, 10) : undefined };
              let mv = null;
              if (fromEng > 0) mv = await recordStockMove(it, 'use', fromEng, { ...base, ref: 'Service call - used at client' });
              if (qty - fromEng > 0) mv = await recordStockMove(it, 'issue', qty - fromEng, { ...base, ref: 'Service call - from stores' });
              rows.push({ itemId: it.id, itemName: it.name, unit: it.unit || 'Nos', qty, rate: it.rate || 0, moveId: mv ? mv.id : '' });
            }
            newCall.stockUsed = rows;
            if (!String(newCall.partsUsed || '').trim()) newCall.partsUsed = rows.map(r => `${r.itemName} x${r.qty}`).join(', ');
          } else if (newCall.status !== 'completed') { newCall.stockUsed = (newCall.stockUsed || []).filter(x => x.moveId); }
          await setDoc(doc(db, 'serviceCalls', newCall.id), newCall);
          await logAudit(isNewCall ? 'CREATE' : 'UPDATE', 'ServiceCall', newCall.id, `${newCall.clientName} - ${newCall.serialNumber}`, newCall);

          // Client Service page and Service Calls page were both being used to
          // log the same physical event (a printer arriving for service),
          // causing duplicate manual entry. Fix: creating a Service Call with
          // pickup details filled in now automatically logs the IN movement
          // too, so Client Service's Inward/Outward register and Service
          // Calls stay in sync without double data entry.
          if (isNewCall && (formData.pickedUpBy || formData.pickupDate)) {
            const pickedUpByName = formData.pickedUpBy === '__other__' ? formData.pickedUpByOther : formData.pickedUpBy;
            const movement = {
              id: `SVC${Date.now()}_auto`,
              printerSerial: newCall.serialNumber,
              printerModel: newCall.printerModel,
              assetSource: newCall.assetSource || 'company',
              assetNumber: newCall.assetNumber || '',
              companyId: currentCompany,
              clientName: newCall.clientName,
              type: 'IN',
              date: formData.pickupDate || newCall.date,
              serviceNotes: `Auto-logged from Service Call: ${newCall.issue}`,
              technician: pickedUpByName || currentUser?.email || 'Unknown',
              recordedBy: currentUser?.email || 'Unknown',
              serviceCallId: newCall.id,
              timestamp: new Date().toISOString()
            };
            await setDoc(doc(db, 'printerMovements', movement.id), movement);

            // Mandatory meter reading captured at pickup.
            if (formData.pickupA4Reading) {
              const lastReading = meterReadings
                .filter(r => r.serialNumber === newCall.serialNumber)
                .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
              const readingDoc = {
                id: `RDG${Date.now()}_pickup`,
                date: formData.pickupDate || newCall.date,
                clientName: newCall.clientName,
                serialNumber: newCall.serialNumber,
                assetNumber: newCall.assetNumber || '',
                printerModel: newCall.printerModel,
                a4Reading: parseInt(formData.pickupA4Reading) || 0,
                previousA4: lastReading?.a4Reading ?? 0,
                a3Reading: formData.pickupA3Reading ? parseInt(formData.pickupA3Reading) : (lastReading?.a3Reading ?? 0),
                previousA3: lastReading?.a3Reading ?? 0,
                scannerReading: formData.pickupScannerReading ? parseInt(formData.pickupScannerReading) : (lastReading?.scannerReading ?? 0),
                previousScanner: lastReading?.scannerReading ?? 0,
                recordedBy: currentUser?.email || 'Unknown',
                serviceCallId: newCall.id,
                notes: `Captured on pickup for Service Call: ${newCall.issue}`
              };
              await setDoc(doc(db, 'meterReadings', readingDoc.id), readingDoc);
            }

            if (newCall.assetSource === 'company') {
              const affectedPrinter = printers.find(p => p.serialNumber === newCall.serialNumber);
              if (affectedPrinter) {
                const updatedPrinter = { ...affectedPrinter, status: 'maintenance' };
                await setDoc(doc(db, 'printers', affectedPrinter.id), updatedPrinter);
              }
            }
          }

          // Symmetric close-out: when an EXISTING service call is marked
          // "completed" (and wasn't already completed), automatically log the
          // OUT movement too - closing the loop that the IN movement opened,
          // without requiring a second manual trip to the Movement tab.
          if (!isNewCall && formData.status === 'completed' && editingItem?.status !== 'completed') {
            const returnedByName = formData.returnedBy === '__other__' ? formData.returnedByOther : formData.returnedBy;
            const movement = {
              id: `SVC${Date.now()}_autoout`,
              printerSerial: newCall.serialNumber,
              printerModel: newCall.printerModel,
              assetSource: newCall.assetSource || 'company',
              assetNumber: newCall.assetNumber || '',
              companyId: currentCompany,
              clientName: newCall.clientName,
              type: 'OUT',
              date: formData.returnDate || new Date().toISOString().split('T')[0],
              serviceNotes: `Auto-logged: Service Call marked completed (${newCall.issue})`,
              technician: returnedByName || newCall.engineer || currentUser?.email || 'Unknown',
              recordedBy: currentUser?.email || 'Unknown',
              serviceCallId: newCall.id,
              timestamp: new Date().toISOString()
            };
            await setDoc(doc(db, 'printerMovements', movement.id), movement);

            // Mandatory meter reading captured at return.
            if (formData.returnA4Reading) {
              const lastReading = meterReadings
                .filter(r => r.serialNumber === newCall.serialNumber)
                .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
              const readingDoc = {
                id: `RDG${Date.now()}_return`,
                date: formData.returnDate || new Date().toISOString().split('T')[0],
                clientName: newCall.clientName,
                serialNumber: newCall.serialNumber,
                assetNumber: newCall.assetNumber || '',
                printerModel: newCall.printerModel,
                a4Reading: parseInt(formData.returnA4Reading) || 0,
                previousA4: lastReading?.a4Reading ?? 0,
                a3Reading: formData.returnA3Reading ? parseInt(formData.returnA3Reading) : (lastReading?.a3Reading ?? 0),
                previousA3: lastReading?.a3Reading ?? 0,
                scannerReading: formData.returnScannerReading ? parseInt(formData.returnScannerReading) : (lastReading?.scannerReading ?? 0),
                previousScanner: lastReading?.scannerReading ?? 0,
                recordedBy: currentUser?.email || 'Unknown',
                serviceCallId: newCall.id,
                notes: `Captured on return for Service Call: ${newCall.issue}`
              };
              await setDoc(doc(db, 'meterReadings', readingDoc.id), readingDoc);
            }

            if (newCall.assetSource === 'company') {
              const affectedPrinter = printers.find(p => p.serialNumber === newCall.serialNumber);
              if (affectedPrinter) {
                // If this printer is still under an active rental, it should go
                // back to "rented" (it's just returning from a repair trip),
                // not "available" for a brand new assignment.
                const stillRented = rentals.some(r => r.serialNumber === newCall.serialNumber && r.status !== 'returned');
                const updatedPrinter = { ...affectedPrinter, status: stillRented ? 'rented' : 'available' };
                await setDoc(doc(db, 'printers', affectedPrinter.id), updatedPrinter);
              }
            }
          }
        }
        
        setShowModal(false);
        setEditingItem(null);
        alert('✅ Saved successfully!');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
            <h3 className="text-xl font-semibold text-slate-900">{editingItem?.id ? 'Edit' : 'Add'} {
              modalType === 'printer' ? 'Printer' : 
              modalType === 'client' ? 'Client' :
              modalType === 'engineer' ? 'Engineer' :
              modalType === 'clientAsset' ? 'Client Asset' :
              modalType === 'reading' ? 'Meter Reading' :
              modalType === 'rental' ? 'Rental' :
              modalType === 'serviceCall' ? 'Service Call' :
              modalType === 'lead' ? 'Lead' : ''
            }</h3>
            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-colors duration-150">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Printer Form */}
            {modalType === 'printer' && (
              <>
                {(() => {
                  const prefixMap = getAssetPrefixMap(currentCompany);
                  const prefix = prefixMap[formData.category || 'Printer'];
                  const willRenumber = editingItem?.assetNumber && editingItem.category && formData.category
                    && formData.category !== editingItem.category
                    && !editingItem.assetNumber.startsWith(prefix);
                  const previewAssetNumber = editingItem?.assetNumber || getNextAssetNumberForPrefix(prefix, printers);
                  return (
                    <div className="bg-brand-50 border-2 border-brand-300 rounded p-3">
                      <p className="text-sm text-slate-600">Asset Number {editingItem ? '' : '(Auto-generated)'}</p>
                      <p className="text-2xl font-bold text-brand-700">{previewAssetNumber}</p>
                      {willRenumber && (
                        <div className="mt-2 pt-2 border-t border-brand-200">
                          <p className="text-xs text-amber-700 font-medium mb-2">
                            ⚠️ Category changed to {formData.category} — {editingItem.assetNumber} still uses the {editingItem.category} prefix. Renumber to {prefix}#### when you save?
                          </p>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setFormData({...formData, renumberOnCategoryChange: true})} className={`text-xs px-3 py-1.5 rounded-lg font-medium border-2 transition-all duration-150 ${formData.renumberOnCategoryChange === true ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-300 text-slate-600'}`}>Yes, renumber</button>
                            <button type="button" onClick={() => setFormData({...formData, renumberOnCategoryChange: false})} className={`text-xs px-3 py-1.5 rounded-lg font-medium border-2 transition-all duration-150 ${formData.renumberOnCategoryChange === false ? 'bg-slate-600 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-600'}`}>No, keep {editingItem.assetNumber}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <input type="text" placeholder="Serial Number *" value={formData.serialNumber || ''} onChange={(e) => setFormData({...formData, serialNumber: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <select value={formData.category || 'Printer'} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                  <option value="Printer">Printer</option>
                  <option value="Desktop">Desktop</option>
                  <option value="Laptop">Laptop</option>
                  <option value="Scanner">Scanner</option>
                </select>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    // Combine the static seed catalog with every make/model already present in
                    // your live Assets, so the suggestion list grows automatically as you add
                    // new printers - no need to manually update the catalog for every model.
                    const learnedModels = printers
                      .filter(p => p.make && p.model)
                      .map(p => ({ make: p.make.trim(), model: p.model.trim() }));
                    const combinedModels = [...KNOWN_PRINTER_MODELS, ...learnedModels];
                    const uniqueModels = Array.from(
                      new Map(combinedModels.map(m => [`${m.make.toLowerCase()}|${m.model.toLowerCase()}`, m])).values()
                    );
                    const uniqueMakes = [...new Set(uniqueModels.map(m => m.make))].sort();
                    return (
                      <>
                        <div>
                          <input 
                            type="text" 
                            list="makeList" 
                            placeholder="Make * (e.g. HP, Canon, Kyocera)" 
                            value={formData.make || ''} 
                            onChange={(e) => setFormData({...formData, make: e.target.value})} 
                            className="w-full border rounded px-3 py-2.5 md:py-2 text-base" 
                            required 
                          />
                          <datalist id="makeList">
                            {uniqueMakes.map(make => (
                              <option key={make} value={make} />
                            ))}
                          </datalist>
                        </div>
                        <div>
                          <input 
                            type="text" 
                            list="modelList" 
                            placeholder="Model * (start typing to see suggestions)" 
                            value={formData.model || ''} 
                            onChange={(e) => {
                              const modelValue = e.target.value;
                              const matched = uniqueModels.find(m => m.model.toLowerCase() === modelValue.toLowerCase());
                              if (matched) {
                                // Auto-populate Make + detect printer Type from model name
                                const upperModel = matched.model.toUpperCase();
                                let detectedType = formData.type;
                                if (upperModel.includes('MFP')) detectedType = 'MFP';
                                setFormData({...formData, model: matched.model, make: matched.make, type: detectedType});
                              } else {
                                setFormData({...formData, model: modelValue});
                              }
                            }} 
                            className="w-full border rounded px-3 py-2.5 md:py-2 text-base" 
                            required 
                          />
                          <datalist id="modelList">
                            {uniqueModels
                              .filter(m => !formData.make || m.make.toLowerCase() === formData.make.toLowerCase())
                              .map(m => (
                                <option key={`${m.make}-${m.model}`} value={m.model} />
                              ))}
                          </datalist>
                        </div>
                      </>
                    );
                  })()}
                </div>
                {formData.make && formData.model && printers.some(p => p.model?.toLowerCase() === (formData.model || '').toLowerCase()) && (
                  <p className="text-xs text-green-600 -mt-2">✓ Matches a model already in your Assets</p>
                )}
                {formData.make && formData.model && !printers.some(p => p.model?.toLowerCase() === (formData.model || '').toLowerCase()) && KNOWN_PRINTER_MODELS.some(m => m.model.toLowerCase() === (formData.model || '').toLowerCase()) && (
                  <p className="text-xs text-green-600 -mt-2">✓ Matched known model from catalog</p>
                )}
                {(formData.category === 'Printer' || !formData.category) && (
                  <select value={formData.type || 'A4'} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                    <option value="A4">A4 Printer</option>
                    <option value="A3">A3 Printer</option>
                    <option value="MFP">MFP B/W (Multi-Function, Black &amp; White only)</option>
                    <option value="MFP_Colour">MFP Colour (Multi-Function, Colour + B/W)</option>
                    <option value="Colour">Colour Printer (single-function)</option>
                  </select>
                )}
                {(formData.category === 'Desktop' || formData.category === 'Laptop') && (
                  <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Configuration</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input type="text" placeholder="Processor (e.g. Intel i5 12th Gen)" value={formData.processor || ''} onChange={(e) => setFormData({...formData, processor: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                      <input type="text" placeholder="RAM (e.g. 8GB)" value={formData.ram || ''} onChange={(e) => setFormData({...formData, ram: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                      <input type="text" placeholder="Storage (e.g. 512GB SSD)" value={formData.storage || ''} onChange={(e) => setFormData({...formData, storage: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                      <input type="text" placeholder="Operating System (e.g. Windows 11 Pro)" value={formData.os || ''} onChange={(e) => setFormData({...formData, os: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                      <input type="text" placeholder="BIOS Serial Number" value={formData.biosSerial || ''} onChange={(e) => setFormData({...formData, biosSerial: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">Purchase Date</label>
                    <input type="date" value={formData.purchaseDate || ''} onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Warranty Until</label>
                    <input type="date" value={formData.warrantyDate || ''} onChange={(e) => setFormData({...formData, warrantyDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Maintenance Interval (days)</label>
                    <input type="number" min="0" placeholder="e.g. 90" value={formData.pmIntervalDays || ''} onChange={(e) => setFormData({...formData, pmIntervalDays: parseInt(e.target.value) || ''})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Last Serviced</label>
                    <input type="date" value={formData.lastPmDate || ''} onChange={(e) => setFormData({...formData, lastPmDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                </div>
              </>
            )}

            {/* Client Form */}
            {modalType === 'client' && (
              <>
                <input type="text" placeholder="Client Name *" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <input type="email" placeholder="Email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                <input type="tel" placeholder="Phone *" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <textarea placeholder="Address" value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" rows="2" />
                <input type="text" placeholder="GSTIN (for quotations/invoices)" value={formData.gstin || ''} onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base font-mono" />
                <div>
                  <label className="text-sm text-slate-600">Allocated Engineer</label>
                  <select value={formData.allocatedEngineer || ''} onChange={(e) => setFormData({...formData, allocatedEngineer: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1">
                    <option value="">Not allocated</option>
                    {engineers.map(eng => <option key={eng.id} value={eng.name}>{eng.name}</option>)}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">New service calls for this client are assigned to this engineer automatically.</p>
                </div>

                {/* Locked price list: the super admin or anyone given 'Set client price locks' edits it; everyone else sees it */}
                <div className="border rounded-lg p-3 space-y-2 bg-amber-50/50">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">🔒 Price list & rental rates (locked prices)</p>
                    {canSetLocks() && (
                      <label className="flex items-center gap-1.5 text-xs text-slate-700"><input type="checkbox" checked={!!formData.priceLock} onChange={(e) => setFormData({ ...formData, priceLock: e.target.checked })} /> Lock prices for this customer</label>
                    )}
                  </div>
                  {(formData.priceList || []).length === 0 && <p className="text-xs text-slate-400 italic">No price list. When one is added and locked, quotations, invoices and proformas for this customer can only use these items at these rates.</p>}
                  {(formData.priceList || []).map((e, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" placeholder="Item / model / service" value={e.item || ''} readOnly={!canSetLocks()} onChange={(ev) => { const l = [...formData.priceList]; l[idx] = { ...l[idx], item: ev.target.value }; setFormData({ ...formData, priceList: l }); }} className="col-span-7 border rounded px-2 py-1.5 text-sm read-only:bg-slate-100" />
                      <input type="number" step="0.01" placeholder="Rate ₹" value={e.rate ?? ''} readOnly={!canSetLocks()} onChange={(ev) => { const l = [...formData.priceList]; l[idx] = { ...l[idx], rate: ev.target.value }; setFormData({ ...formData, priceList: l }); }} className="col-span-4 border rounded px-2 py-1.5 text-sm read-only:bg-slate-100" />
                      {canSetLocks() && <button type="button" onClick={() => setFormData({ ...formData, priceList: formData.priceList.filter((_, i) => i !== idx) })} className="col-span-1 text-red-500 hover:bg-red-50 rounded p-1 flex justify-center"><Trash2 size={14} /></button>}
                    </div>
                  ))}
                  {canSetLocks() && (
                    <div className="flex gap-2 flex-wrap">
                      <button type="button" onClick={() => setFormData({ ...formData, priceList: [...(formData.priceList || []), { item: '', rate: '' }] })} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2 py-1 rounded-lg font-medium">+ Add item</button>
                      <button type="button" data-fill-prices onClick={() => {
                        const seen = new Map((formData.priceList || []).map(x => [normItem(x.item), x]));
                        const docs = [...invoices, ...quotations].filter(d => (formData.id && d.clientId === formData.id) || (!formData.id && false) || normItem(d.clientName) === normItem(formData.name)).sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
                        docs.forEach(d => (d.items || []).forEach(i => { const k = normItem(i.description); if (k && parseFloat(i.rate) > 0) seen.set(k, { item: String(i.description).trim(), rate: parseFloat(i.rate) }); }));
                        setFormData({ ...formData, priceList: [...seen.values()] });
                      }} className="text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 px-2 py-1 rounded-lg font-medium">↻ Fill from past quotations &amp; invoices (latest rate)</button>
                    </div>
                  )}
                  <div className="border-t border-amber-200 pt-2 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Locked rental rates (per asset type)</p>
                    {(formData.rentalRates || []).length === 0 && <p className="text-xs text-slate-400 italic">None. With the lock on and rates added here, a rental for this customer must use exactly these rates.</p>}
                    {(formData.rentalRates || []).map((e, idx) => {
                      const ro = !canSetLocks();
                      const upd = (patch) => { const l = [...formData.rentalRates]; l[idx] = { ...l[idx], ...patch }; setFormData({ ...formData, rentalRates: l }); };
                      const num = (f, ph) => <input key={f} type="number" step="0.01" placeholder={ph} title={ph} value={e[f] ?? ''} readOnly={ro} onChange={(ev) => upd({ [f]: ev.target.value })} className="border rounded px-2 py-1.5 text-sm read-only:bg-slate-100 min-w-0" />;
                      const isDev = ['Desktop', 'Laptop', 'Scanner'].includes(e.typeKey);
                      return (
                        <div key={idx} className="bg-white border rounded-lg p-2 space-y-1.5">
                          <div className="flex gap-2 items-center flex-wrap">
                            <select value={e.typeKey || 'A4'} disabled={ro} onChange={(ev) => upd({ typeKey: ev.target.value })} className="border rounded px-2 py-1.5 text-sm">{RENTAL_TYPE_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                            {!isDev && <select value={e.rentalType || 'regular'} disabled={ro} onChange={(ev) => upd({ rentalType: ev.target.value })} className="border rounded px-2 py-1.5 text-sm"><option value="regular">Regular</option><option value="per_page">Per page</option><option value="standby">Standby</option></select>}
                            {!ro && <button type="button" onClick={() => setFormData({ ...formData, rentalRates: formData.rentalRates.filter((_, i) => i !== idx) })} className="ml-auto text-red-500 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                            {(e.rentalType || 'regular') === 'standby' && !isDev ? num('cartridgeCost', 'Cartridge cost ₹') : <>
                              {((e.rentalType || 'regular') !== 'per_page' || isDev) && num('monthlyRate', 'Monthly rate ₹')}
                              {!isDev && <>
                                {(e.rentalType || 'regular') !== 'per_page' && num('a4FreePages', 'A4 free pages')}{num('a4PageCost', 'A4 extra ₹/page')}
                                {['A3', 'MFP', 'MFP_Colour'].includes(e.typeKey) && <>{(e.rentalType || 'regular') !== 'per_page' && num('a3FreePages', 'A3 free pages')}{num('a3PageCost', 'A3 extra ₹/page')}</>}
                                {e.typeKey === 'MFP_Colour' && <>{(e.rentalType || 'regular') !== 'per_page' && num('colourA4FreePages', 'Colour A4 free')}{num('colourA4PageCost', 'Colour A4 ₹/page')}{(e.rentalType || 'regular') !== 'per_page' && num('colourA3FreePages', 'Colour A3 free')}{num('colourA3PageCost', 'Colour A3 ₹/page')}</>}
                              </>}
                            </>}
                          </div>
                        </div>
                      );
                    })}
                    {canSetLocks() && (
                      <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={() => setFormData({ ...formData, rentalRates: [...(formData.rentalRates || []), { typeKey: 'A4', rentalType: 'regular' }] })} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2 py-1 rounded-lg font-medium">+ Add rental rate</button>
                        <button type="button" onClick={() => {
                          const mine = rentals.filter(r => r.status !== 'returned' && normItem(r.clientName) === normItem(formData.name));
                          const seen = new Map((formData.rentalRates || []).map(x => [`${x.typeKey}|${x.rentalType || 'regular'}`, x]));
                          mine.forEach(r => { const k = `${rentalTypeKeyOf(r)}|${r.rentalType || 'regular'}`; if (!seen.has(k)) { const e = { typeKey: rentalTypeKeyOf(r), rentalType: r.rentalType || 'regular' }; RENTAL_RATE_FIELDS.forEach(f => { if (r[f] !== undefined && r[f] !== null && r[f] !== '' && Number(r[f]) !== 0) e[f] = r[f]; }); seen.set(k, e); } });
                          setFormData({ ...formData, rentalRates: [...seen.values()] });
                        }} className="text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 px-2 py-1 rounded-lg font-medium">↻ Fill from this customer's current rentals</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* The name/phone/email above stay as the PRIMARY contact -
                    every existing quotation, rental, and service call
                    references a client by that single name/phone, so keeping
                    it as-is avoids touching all of that. This section is for
                    the other real people at a client company (accounts,
                    procurement, etc.) who aren't captured by one contact. */}
                <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Additional Contacts</p>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, contacts: [...(formData.contacts || []), { name: '', role: '', phone: '', email: '' }]})}
                      className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2 py-1 rounded-lg font-medium transition-colors duration-150"
                    >
                      + Add Contact
                    </button>
                  </div>
                  {(formData.contacts || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic">No additional contacts yet.</p>
                  )}
                  {(formData.contacts || []).map((contact, idx) => (
                    <div key={idx} className="bg-white rounded-lg border p-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text" placeholder="Name" value={contact.name || ''}
                          onChange={(e) => { const updated = [...formData.contacts]; updated[idx] = {...updated[idx], name: e.target.value}; setFormData({...formData, contacts: updated}); }}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                        <input
                          type="text" placeholder="Role (e.g. Accounts)" value={contact.role || ''}
                          onChange={(e) => { const updated = [...formData.contacts]; updated[idx] = {...updated[idx], role: e.target.value}; setFormData({...formData, contacts: updated}); }}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="tel" placeholder="Phone" value={contact.phone || ''}
                          onChange={(e) => { const updated = [...formData.contacts]; updated[idx] = {...updated[idx], phone: e.target.value}; setFormData({...formData, contacts: updated}); }}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                        <input
                          type="email" placeholder="Email" value={contact.email || ''}
                          onChange={(e) => { const updated = [...formData.contacts]; updated[idx] = {...updated[idx], email: e.target.value}; setFormData({...formData, contacts: updated}); }}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, contacts: formData.contacts.filter((_, i) => i !== idx)})}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-up Reminder</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="date" value={formData.followUpDate || ''} onChange={(e) => setFormData({...formData, followUpDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                    <input type="text" placeholder="What to follow up about" value={formData.followUpNote || ''} onChange={(e) => setFormData({...formData, followUpNote: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                </div>

                <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documents</p>
                  {[
                    { key: 'agreement', label: 'Agreement' },
                    { key: 'gst_certificate', label: 'GST Certificate' }
                  ].map(({ key, label }) => {
                    const docsOfType = (formData.documents || []).filter(d => d.type === key);
                    const isUploading = uploadingClientDocType === key;
                    return (
                      <div key={key} className="bg-white rounded-lg border p-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-700">{label}</p>
                          <label className={`text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-2 py-1 rounded-lg font-medium transition-colors duration-150 cursor-pointer flex items-center gap-1 ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                            {isUploading ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                            {isUploading ? 'Uploading...' : '+ Add Files'}
                            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleClientDocUpload(e, key)} className="hidden" disabled={isUploading} />
                          </label>
                        </div>
                        {docsOfType.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No {label.toLowerCase()} uploaded yet.</p>
                        ) : (
                          <div className="space-y-1">
                            {docsOfType.map((d) => (
                              <div key={d.path} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1.5 gap-2">
                                <span className="flex items-center gap-1.5 text-xs text-slate-700 truncate min-w-0">
                                  <Paperclip size={12} className="shrink-0 text-slate-400" />
                                  <span className="truncate">{d.name}</span>
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  <a href={d.url} target="_blank" rel="noreferrer" className="p-1 text-slate-400 hover:text-brand-600" title="View"><Eye size={14} /></a>
                                  <button type="button" onClick={() => handleRemoveClientDoc(d)} className="p-1 text-slate-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {editingItem?.createdBy && (
                  <p className="text-xs text-slate-400">Created by {editingItem.createdBy}{editingItem.createdAt ? ` on ${new Date(editingItem.createdAt).toLocaleDateString()}` : ''}</p>
                )}
              </>
            )}

            {/* Lead Form */}
            {modalType === 'lead' && (
              <>
                <input type="text" placeholder="Company / Lead Name *" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <input type="text" placeholder="Contact Person" value={formData.contactName || ''} onChange={(e) => setFormData({...formData, contactName: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  <input type="email" placeholder="Email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                </div>
                <textarea placeholder="Address" value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" rows="2" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select value={formData.stage || 'new'} onChange={(e) => setFormData({...formData, stage: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="quoted">Quoted</option>
                    <option value="negotiating">Negotiating</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                  <input type="number" placeholder="Estimated Value (₹)" value={formData.estimatedValue || ''} onChange={(e) => setFormData({...formData, estimatedValue: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" min="0" />
                </div>
                <select value={formData.source || ''} onChange={(e) => setFormData({...formData, source: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                  <option value="">Source (optional)</option>
                  <option value="referral">Referral</option>
                  <option value="website">Website</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="walk_in">Walk-in</option>
                  <option value="email">Email</option>
                  <option value="indiamart">IndiaMART</option>
                  <option value="other">Other</option>
                </select>
                <textarea placeholder="Notes" value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" rows="3" />
                <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-up Reminder</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="date" value={formData.followUpDate || ''} onChange={(e) => setFormData({...formData, followUpDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                    <input type="text" placeholder="What to follow up about" value={formData.followUpNote || ''} onChange={(e) => setFormData({...formData, followUpNote: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                  </div>
                </div>
              </>
            )}

            {/* Quotation Form */}
            {modalType === 'quotation' && (
              <>
                <div>
                  <label className="text-sm text-slate-600">Quotation Type</label>
                  <select
                    value={formData.quotationType || 'service'}
                    onChange={(e) => {
                      const qt = e.target.value;
                      // Server / email / licence quotations: keep the equipment type in step so the subject, tag and "load items" match
                      const eq = EQUIPMENT_FOR_SUPPLY_QUOTE_TYPE[qt] || ((SUPPLY_QUOTE_TYPE_FOR_EQUIPMENT[formData.printerType] && !EQUIPMENT_FOR_SUPPLY_QUOTE_TYPE[qt] && qt !== 'cartridge') ? 'laserjet_printer' : (formData.printerType || 'laserjet_printer'));
                      setFormData({...formData, quotationType: qt, printerType: eq, terms: getTermsForQuotation(qt, eq)});
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1"
                  >
                    {QUOTATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Sets the Terms &amp; Conditions below to match - edit freely after.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Client *</label>
                    <select
                      value={formData.clientId || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setFormData({
                          ...formData,
                          clientId: e.target.value,
                          clientName: client?.name || '',
                          // Different data sources (manual entry vs import) may have
                          // used slightly different field names for these - check a few
                          // common variants so nothing gets missed if the sync used e.g.
                          // "billingAddress" instead of "address".
                          clientAddress: client?.address || client?.billingAddress || client?.zohoAddress || '',
                          clientGSTIN: client?.gstin || client?.gstNumber || client?.taxId || '',
                          clientEmail: client?.email || client?.emailAddress || client?.contactEmail || ''
                        });
                      }}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1"
                      required
                    >
                      <option value="">Select Client *</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Equipment Type *</label>
                    <select
                      value={formData.printerType || 'laserjet_printer'}
                      onChange={(e) => setFormData({
                        ...formData,
                        printerType: e.target.value,
                        // Rental T&C varies by equipment (printer vs barcode printer vs
                        // general) - keep it in sync if Rental is the selected quotation type.
                        ...((formData.quotationType || 'service') === 'rental' ? { terms: getRentalTerms(e.target.value) } : {}),
                        // Picking a cartridge while still on the default Service type
                        // means a consumable sale, not a repair - switch to its terms.
                        ...(isSupplyType(e.target.value) && (formData.quotationType || 'service') === 'service'
                          ? { quotationType: SUPPLY_QUOTE_TYPE_FOR_EQUIPMENT[e.target.value], terms: getTermsForQuotation(SUPPLY_QUOTE_TYPE_FOR_EQUIPMENT[e.target.value], e.target.value) } : {})
                      })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1"
                    >
                      {QUOTATION_EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                {formData.clientId && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-700 text-sm">Bill To</p>
                      {(!formData.clientAddress || !formData.clientEmail) && (
                        <span className="text-xs text-amber-600">⚠️ Missing from Client record - fill in below</span>
                      )}
                    </div>
                    <textarea
                      placeholder="Client address (not on file - enter here)"
                      value={formData.clientAddress || ''}
                      onChange={(e) => setFormData({...formData, clientAddress: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      rows="2"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="email"
                        placeholder="Client email (not on file - enter here)"
                        value={formData.clientEmail || ''}
                        onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="GSTIN (optional)"
                        value={formData.clientGSTIN || ''}
                        onChange={(e) => setFormData({...formData, clientGSTIN: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const client = clients.find(c => c.id === formData.clientId);
                        if (!client) return;
                        const updatedClient = { ...client, address: formData.clientAddress || '', email: formData.clientEmail || client.email || '', gstin: formData.clientGSTIN || client.gstin || '' };
                        try {
                          await setDoc(doc(db, 'clients', client.id), updatedClient);
                          alert('✅ Saved to Client record - won\'t need to re-enter this next time.');
                        } catch (err) {
                          alert('❌ Error saving: ' + err.message);
                        }
                      }}
                      className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150"
                    >
                      💾 Save these details to {formData.clientName}'s Client record
                    </button>
                  </div>
                )}

                {formData.clientId && (() => {
                  // Quotations are only ever for the CLIENT's own equipment -
                  // servicing our own rented-out printers is covered under the
                  // rental agreement itself, not billed via a separate
                  // quotation. So only Client-Owned devices are listed here,
                  // never printers from our rental inventory.
                  const ownedByClient = clientAssets
                    .filter(a => a.clientName === formData.clientName)
                    .map(a => ({
                      key: `owned_${a.serialNumber}`,
                      serial: a.serialNumber,
                      model: `${a.make} ${a.model}`,
                      source: 'Client-Owned'
                    }));
                  const options = ownedByClient;

                  if (options.length === 0) {
                    return (
                      <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">⚠️ No client-owned printers found on record for {formData.clientName} yet - enter Model/Serial manually below, or register the device via the Client Asset Register first.</p>
                    );
                  }
                  return (
                    <div>
                      <label className="text-sm text-slate-600">Select {formData.clientName}'s Printer</label>
                      <select
                        value={options.find(o => o.serial === formData.printerSerial)?.key || ''}
                        onChange={(e) => {
                          const selected = options.find(o => o.key === e.target.value);
                          if (selected) {
                            setFormData({...formData, printerModel: selected.model, printerSerial: selected.serial});
                          }
                        }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1"
                      >
                        <option value="">Choose a registered printer to auto-fill Model &amp; Serial...</option>
                        {options.map(o => (
                          <option key={o.key} value={o.key}>{o.model} - S/N: {o.serial} ({o.source})</option>
                        ))}
                      </select>
                      {formData.printerSerial && (
                        <p className="text-xs text-green-700 mt-1">✓ Selected: {formData.printerModel} - S/N: {formData.printerSerial}</p>
                      )}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Quote Date</label>
                    <input type="date" value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Model</label>
                    <input type="text" value={formData.printerModel || ''} onChange={(e) => setFormData({...formData, printerModel: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1" placeholder="Auto-filled, or type manually" />
                  </div>
                </div>
                <input type="text" placeholder="Serial Number (auto-filled, or type manually)" value={formData.printerSerial || ''} onChange={(e) => setFormData({...formData, printerSerial: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />

                <button
                  type="button"
                  onClick={() => {
                    const preset = QUOTATION_ITEM_PRESETS[formData.printerType || 'laserjet_printer'] || [];
                    setFormData({...formData, items: preset.map(i => ({...i}))});
                  }}
                  className="text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-3 py-2 rounded-lg font-medium transition-colors duration-150"
                >
                  ✨ Load common {(QUOTATION_EQUIPMENT_TYPES.find(t => t.value === (formData.printerType || 'laserjet_printer'))?.label || '').replace(/^\S+\s/, '')} {isSupplyType(formData.printerType) ? 'items' : 'service items'}
                </button>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-medium text-slate-500">
                    <span className="col-span-6">Description</span>
                    <span className="col-span-2">HSN/SAC</span>
                    <span className="col-span-1">Qty</span>
                    <span className="col-span-2">Rate (₹)</span>
                    <span className="col-span-1"></span>
                  </div>
                  {(() => { const lc = priceLockOf(formData.clientId, formData.clientName); return lc ? (
                    <div className="px-3 py-2 bg-amber-50 text-xs text-amber-800 border-t border-amber-100">🔒 {lc.name} has a locked price list ({lc.priceList.filter(e => e.item).length} items). Pick items from the list; rates fill in automatically{userRole === 'super_admin' ? ' (you can override as super admin).' : ' and cannot be changed.'}
                      <datalist id="plist-q">{lc.priceList.filter(e => e.item).map(e => <option key={e.item} value={e.item}>{`₹${e.rate}`}</option>)}</datalist></div>) : null; })()}
                  {(formData.items || []).map((item, idx) => (
                    <div key={idx} className="px-3 py-2 grid grid-cols-12 gap-2 border-t border-slate-100 items-center">
                      <input
                        type="text"
                        list={priceLockOf(formData.clientId, formData.clientName) ? 'plist-q' : undefined}
                        value={item.description}
                        onChange={(e) => {
                          const items = [...formData.items];
                          const lc = priceLockOf(formData.clientId, formData.clientName);
                          const hit = lc ? priceMatch(lc, e.target.value) : null;
                          items[idx] = {...items[idx], description: e.target.value, ...(hit ? { rate: String(hit.rate) } : {})};
                          setFormData({...formData, items});
                        }}
                        placeholder="Service description *"
                        className="col-span-6 border rounded px-2 py-1.5 text-sm"
                        required
                      />
                      <input
                        type="text"
                        value={item.hsn}
                        onChange={(e) => {
                          const items = [...formData.items];
                          items[idx] = {...items[idx], hsn: e.target.value};
                          setFormData({...formData, items});
                        }}
                        className="col-span-2 border rounded px-2 py-1.5 text-sm"
                      />
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => {
                          const items = [...formData.items];
                          items[idx] = {...items[idx], qty: e.target.value};
                          setFormData({...formData, items});
                        }}
                        className="col-span-1 border rounded px-2 py-1.5 text-sm"
                        min="1"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => {
                          const items = [...formData.items];
                          items[idx] = {...items[idx], rate: e.target.value};
                          setFormData({...formData, items});
                        }}
                        placeholder="Rate *"
                        readOnly={userRole !== 'super_admin' && !!priceLockOf(formData.clientId, formData.clientName) && !!priceMatch(priceLockOf(formData.clientId, formData.clientName), item.description)}
                        title={priceLockOf(formData.clientId, formData.clientName) ? 'Locked price' : undefined}
                        className="col-span-2 border rounded px-2 py-1.5 text-sm read-only:bg-slate-100 read-only:text-slate-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, items: formData.items.filter((_, i) => i !== idx)})}
                        className="col-span-1 text-red-500 hover:bg-red-50 rounded p-1 flex justify-center"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, items: [...(formData.items || []), { description: '', hsn: '998713', qty: 1, rate: '' }]})}
                    className="w-full px-3 py-2 text-sm text-brand-600 hover:bg-brand-50 border-t border-slate-100 flex items-center justify-center gap-1"
                  >
                    <Plus size={14} /> Add Line Item
                  </button>
                  {formData.clientId && canSeeStock() && stockMoveLines(formData.clientName).length > 0 && (
                    <select value="" onChange={(e) => { const x = stockMoveLines(formData.clientName).find(y => y.move.id === e.target.value); if (!x) return; const { stockMoveId, ...line } = x.line; const cur = formData.items || []; const blank = cur.length === 1 && !cur[0].description && !cur[0].rate; setFormData({...formData, items: [...(blank ? [] : cur), { ...line, hsn: line.hsn || '998713' }]}); }} className="w-full border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
                      <option value="">📦 Add from stock supplied to this client…</option>
                      {stockMoveLines(formData.clientName).map(y => <option key={y.move.id} value={y.move.id}>{y.move.date} · {y.move.itemName} × {y.move.qty}</option>)}
                    </select>
                  )}
                </div>

                {(() => {
                  const subTotal = (formData.items || []).reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)), 0);
                  const gst = subTotal * 0.18;
                  return (
                    <div className="bg-brand-50 rounded-lg p-3 text-sm space-y-1">
                      <div className="flex justify-between"><span>Sub Total:</span><span>₹{subTotal.toFixed(2)}</span></div>
                      <div className="flex justify-between text-slate-600"><span>GST (18% = CGST 9% + SGST 9%):</span><span>₹{gst.toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold text-base border-t border-brand-200 pt-1 mt-1"><span>Grand Total:</span><span>₹{(subTotal + gst).toFixed(2)}</span></div>
                    </div>
                  );
                })()}

                <textarea placeholder="Notes" value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" rows="2" />
                <div>
                  <label className="text-sm text-slate-600">Terms &amp; Conditions (one per line)</label>
                  <textarea value={formData.terms || ''} onChange={(e) => setFormData({...formData, terms: e.target.value})} className="w-full border rounded px-3 py-2 text-sm mt-1" rows="5" />
                </div>
              </>
            )}

            {/* Engineer Form */}
            {modalType === 'engineer' && (
              <>
                <input type="text" placeholder="Name *" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <input type="text" placeholder="Specialization" value={formData.specialization || ''} onChange={(e) => setFormData({...formData, specialization: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone *" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                  <input type="email" placeholder="Email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                </div>
              </>
            )}

            {/* Client Asset Form */}
            {modalType === 'clientAsset' && (
              <>
                <input type="text" placeholder="Serial Number *" value={formData.serialNumber || ''} onChange={(e) => setFormData({...formData, serialNumber: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    // Same "learns from existing data" catalog as the Printer form, but
                    // learns from both company Assets AND client-owned assets already recorded.
                    const learnedModels = [...printers, ...clientAssets]
                      .filter(p => p.make && p.model)
                      .map(p => ({ make: p.make.trim(), model: p.model.trim() }));
                    const combinedModels = [...KNOWN_PRINTER_MODELS, ...learnedModels];
                    const uniqueModels = Array.from(
                      new Map(combinedModels.map(m => [`${m.make.toLowerCase()}|${m.model.toLowerCase()}`, m])).values()
                    );
                    const uniqueMakes = [...new Set(uniqueModels.map(m => m.make))].sort();
                    return (
                      <>
                        <div>
                          <input type="text" list="makeListClient" placeholder="Make * (e.g. HP, Canon, Kyocera)" value={formData.make || ''} onChange={(e) => setFormData({...formData, make: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                          <datalist id="makeListClient">
                            {uniqueMakes.map(make => (
                              <option key={make} value={make} />
                            ))}
                          </datalist>
                        </div>
                        <div>
                          <input 
                            type="text" 
                            list="modelListClient" 
                            placeholder="Model * (start typing to see suggestions)" 
                            value={formData.model || ''} 
                            onChange={(e) => {
                              const modelValue = e.target.value;
                              const matched = uniqueModels.find(m => m.model.toLowerCase() === modelValue.toLowerCase());
                              setFormData(matched ? {...formData, model: matched.model, make: matched.make} : {...formData, model: modelValue});
                            }} 
                            className="w-full border rounded px-3 py-2.5 md:py-2 text-base" 
                            required 
                          />
                          <datalist id="modelListClient">
                            {uniqueModels
                              .filter(m => !formData.make || m.make.toLowerCase() === formData.make.toLowerCase())
                              .map(m => (
                                <option key={`${m.make}-${m.model}`} value={m.model} />
                              ))}
                          </datalist>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <select value={formData.clientName || ''} onChange={(e) => setFormData({...formData, clientName: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required>
                  <option value="">Select Client (Owner) *</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowWalkinForm(!showWalkinForm)}
                  style={{width: '100%', display: 'block', backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0'}}
                  className="px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium"
                >
                  ➕ New Walk-in Client (not in the list above)
                </button>

                {showWalkinForm && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-emerald-900">New Walk-in Client</p>
                    <p className="text-xs text-emerald-700 -mt-1">This creates the client in your Clients list.</p>
                    <input type="text" placeholder="Client Name *" value={walkinData.name} onChange={(e) => setWalkinData({...walkinData, name: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input type="text" placeholder="Phone" value={walkinData.phone} onChange={(e) => setWalkinData({...walkinData, phone: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                      <input type="email" placeholder="Email" value={walkinData.email} onChange={(e) => setWalkinData({...walkinData, email: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    </div>
                    <input type="text" placeholder="Company (optional)" value={walkinData.company} onChange={(e) => setWalkinData({...walkinData, company: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!walkinData.name.trim()) {
                            alert('❌ Client name is required.');
                            return;
                          }
                          setSavingWalkin(true);
                          try {
                            const newClient = {
                              id: `CLT${Date.now()}`,
                              name: walkinData.name.trim(),
                              phone: walkinData.phone.trim(),
                              email: walkinData.email.trim(),
                              company: walkinData.company.trim(),
                              companyId: currentCompany,
                              isWalkin: true,
                              createdAt: new Date().toISOString(),
                              createdBy: currentUser?.email || 'Unknown'
                            };
                            await setDoc(doc(db, 'clients', newClient.id), newClient);
                            await logAudit('CREATE', 'Client', newClient.id, `${newClient.name} (walk-in)`, newClient);
                            autoSyncClient(newClient);
                            setFormData({...formData, clientName: newClient.name});
                            setShowWalkinForm(false);
                            setWalkinData({ name: '', phone: '', email: '', company: '' });
                          } catch (err) {
                            alert('❌ Error saving client: ' + err.message);
                          }
                          setSavingWalkin(false);
                        }}
                        disabled={savingWalkin}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {savingWalkin ? '⏳ Saving...' : '✅ Add & Select Client'}
                      </button>
                      <button type="button" onClick={() => setShowWalkinForm(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Rental Form */}
            {modalType === 'rental' && (() => {
              const isDeviceRental = formData.category === 'Desktop' || formData.category === 'Laptop';
              return (
              <>
                <input
                  type="text"
                  placeholder="🔍 Search client..."
                  value={formData.clientSearch || ''} 
                  onChange={(e) => {
                    const search = e.target.value;
                    setFormData({...formData, clientSearch: search});
                  }} 
                  className="w-full border rounded px-3 py-2.5 text-base mb-2" 
                />
                <select value={formData.clientName || ''} onChange={(e) => setFormData({...formData, clientName: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required>
                  <option value="">Select Client *</option>
                  {clients
                    .filter(c => !formData.clientSearch || c.name.toLowerCase().includes(formData.clientSearch.toLowerCase()))
                    .map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                </select>
                <input type="text" placeholder="Department" value={formData.department || ''} onChange={(e) => setFormData({...formData, department: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" />
                {(() => {
                  const availableAssets = printers.filter(p => p.status === 'available' || p.serialNumber === formData.serialNumber);
                  const countByCategory = {};
                  availableAssets.forEach(p => { const c = p.category || 'Printer'; countByCategory[c] = (countByCategory[c] || 0) + 1; });
                  const ASSET_TYPES = ['Printer', 'Desktop', 'Laptop', 'Scanner'];
                  return (
                    <select value={rentalAssetTypeFilter} onChange={(e) => setRentalAssetTypeFilter(e.target.value)} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                      <option value="all">Filter by Asset Type — All ({availableAssets.length} available)</option>
                      {ASSET_TYPES.map(t => (
                        <option key={t} value={t}>{t} ({countByCategory[t] || 0} available)</option>
                      ))}
                    </select>
                  );
                })()}
                <select value={formData.serialNumber || ''} onChange={(e) => {
                  const printer = printers.find(p => p.serialNumber === e.target.value);
                  // Older printer records from before Desktop/Laptop categories
                  // existed never got a category field stored at all - printer?.category
                  // would then be undefined, and Firestore's setDoc() rejects any
                  // undefined field value outright, failing the whole save. Fall back
                  // to 'Printer', same default used everywhere else category is read.
                  setFormData({...formData, serialNumber: e.target.value, printerModel: printer ? `${printer.make} ${printer.model}` : '', printerType: printer?.type, category: printer?.category || 'Printer', assetNumber: printer?.assetNumber});
                }} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required>
                  <option value="">Select Asset *</option>
                  {printers
                    .filter(p => p.status === 'available' || p.serialNumber === formData.serialNumber)
                    .filter(p => rentalAssetTypeFilter === 'all' || (p.category || 'Printer') === rentalAssetTypeFilter)
                    .map(p => (
                      <option key={p.id} value={p.serialNumber}>{p.assetNumber ? `${p.assetNumber} • ` : ''}S/N: {p.serialNumber} - {p.make} {p.model} ({p.type}){p.serialNumber === formData.serialNumber && p.status !== 'available' ? ' — currently assigned' : ''}</option>
                    ))}
                  {/* Fallback: if this rental's printer doesn't exist as an Asset record at all
                      (e.g. deleted, or created via an older import), still show it using the
                      rental's own saved details, so the field is never left blank when editing. */}
                  {formData.serialNumber && !printers.some(p => p.serialNumber === formData.serialNumber) && (
                    <option value={formData.serialNumber}>
                      {formData.assetNumber || formData.serialNumber} - {formData.printerModel || 'Unknown model'} — currently assigned (no Asset record found)
                    </option>
                  )}
                </select>
                
                {!isDeviceRental && (
                  <select value={formData.rentalType || 'regular'} onChange={(e) => setFormData({...formData, rentalType: e.target.value})} className="w-full border-2 border-brand-500 rounded px-3 py-2.5 text-base font-medium">
                    <option value="regular">Regular Rental (Monthly Cost + Free Pages)</option>
                    <option value="per_page">Pay Per Page (No Monthly Cost, No Free Pages)</option>
                    <option value="standby">Standby (Free Deployment, Cartridge Only)</option>
                  </select>
                )}

                {(() => { const lc = rentalLockClient(formData.clientName); if (!lc) return null; const e = rentalLockEntry(formData); return (
                  <div className={`px-3 py-2 rounded-lg text-xs ${e ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>🔒 {e ? `${lc.name}: rental rates are locked${userRole === 'super_admin' ? ' (you can override as super admin).' : ' and fill in automatically.'}` : `${lc.name} has no locked rental rate for this asset type${userRole === 'super_admin' ? ' – add one on the client record.' : ' – ask the super admin to add it before this rental can be saved.'}`}</div>); })()}
                <fieldset disabled={userRole !== 'super_admin' && !!rentalLockEntry(formData)} className="space-y-3 border-0 p-0 m-0 min-w-0 disabled:opacity-80">
                {isDeviceRental ? (
                  <div className="bg-indigo-50 border-2 border-indigo-300 rounded p-4">
                    <p className="font-bold text-indigo-900 mb-2">💻 {formData.category} Rental</p>
                    <p className="text-sm text-indigo-800 mb-3">Flat monthly rate — no page costs or meter readings apply to a {(formData.category || 'device').toLowerCase()}.</p>
                    <input type="number" placeholder="Monthly Rate (₹) *" value={formData.monthlyRate || ''} onChange={(e) => setFormData({...formData, monthlyRate: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                  </div>
                ) : formData.rentalType === 'standby' ? (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded p-4">
                    <p className="font-bold text-yellow-900 mb-2">⚡ Standby Model</p>
                    <p className="text-sm text-yellow-800 mb-3">Free deployment - Charged only for cartridges</p>
                    <input type="number" step="0.01" placeholder="Cartridge Cost (₹) *" value={formData.cartridgeCost || ''} onChange={(e) => setFormData({...formData, cartridgeCost: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                  </div>
                ) : formData.rentalType === 'per_page' ? (
                  <div className="bg-orange-50 border-2 border-orange-400 rounded p-4 space-y-3">
                    <p className="font-bold text-orange-900 mb-1">📄 Pay Per Page Model</p>
                    <p className="text-sm text-orange-800 mb-2">No monthly rent, no free pages — every page is charged from page 1</p>
                    <div>
                      <label className="text-sm font-medium text-orange-900">A4 Cost per Page (₹) *</label>
                      <input type="number" step="0.01" placeholder="e.g. 0.60" value={formData.a4PageCost || ''} onChange={(e) => setFormData({...formData, a4PageCost: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                    </div>
                    {(formData.printerType === 'A3' || formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (
                      <div>
                        <label className="text-sm font-medium text-orange-900">A3 Cost per Page (₹) *</label>
                        <input type="number" step="0.01" placeholder="e.g. 2.50" value={formData.a3PageCost || ''} onChange={(e) => setFormData({...formData, a3PageCost: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                      </div>
                    )}
                    {formData.printerType === 'MFP_Colour' && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-orange-900">Colour A4 Cost per Page (₹) *</label>
                          <input type="number" step="0.01" placeholder="e.g. 4.00" value={formData.colourA4PageCost || ''} onChange={(e) => setFormData({...formData, colourA4PageCost: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-orange-900">Colour A3 Cost per Page (₹) *</label>
                          <input type="number" step="0.01" placeholder="e.g. 8.00" value={formData.colourA3PageCost || ''} onChange={(e) => setFormData({...formData, colourA3PageCost: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <input type="number" placeholder="Monthly Rate *" value={formData.monthlyRate || ''} onChange={(e) => setFormData({...formData, monthlyRate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" required />
                    
                    {(formData.printerType === 'A3' || formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (
                      <div className="bg-purple-50 p-4 rounded space-y-3">
                        <p className="font-bold text-purple-900">{formData.printerType === 'MFP_Colour' ? 'B/W A3 Pricing' : 'A3 Pricing'}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <input type="number" placeholder="A3 Free Pages *" value={formData.a3FreePages || ''} onChange={(e) => setFormData({...formData, a3FreePages: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                          <input type="number" step="0.01" placeholder="A3 Extra Cost (₹) *" value={formData.a3PageCost || ''} onChange={(e) => setFormData({...formData, a3PageCost: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-green-50 p-4 rounded space-y-3">
                      <p className="font-bold text-green-900">{formData.printerType === 'MFP_Colour' ? 'B/W A4 Pricing' : 'A4 Pricing'}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <input type="number" placeholder="A4 Free Pages *" value={formData.a4FreePages || formData.freePages || ''} onChange={(e) => setFormData({...formData, a4FreePages: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                        <input type="number" step="0.01" placeholder="A4 Extra Cost (₹) *" value={formData.a4PageCost || formData.pageCost || ''} onChange={(e) => setFormData({...formData, a4PageCost: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                      </div>
                    </div>

                    {formData.printerType === 'MFP_Colour' && (
                      <div className="bg-pink-50 border border-pink-300 p-4 rounded space-y-3">
                        <p className="font-bold text-pink-900">🎨 Colour Pricing</p>
                        <div className="grid grid-cols-2 gap-3">
                          <input type="number" placeholder="Colour A4 Free Pages *" value={formData.colourA4FreePages || ''} onChange={(e) => setFormData({...formData, colourA4FreePages: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                          <input type="number" step="0.01" placeholder="Colour A4 Extra Cost (₹) *" value={formData.colourA4PageCost || ''} onChange={(e) => setFormData({...formData, colourA4PageCost: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                          <input type="number" placeholder="Colour A3 Free Pages *" value={formData.colourA3FreePages || ''} onChange={(e) => setFormData({...formData, colourA3FreePages: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                          <input type="number" step="0.01" placeholder="Colour A3 Extra Cost (₹) *" value={formData.colourA3PageCost || ''} onChange={(e) => setFormData({...formData, colourA3PageCost: e.target.value})} className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" required />
                        </div>
                      </div>
                    )}
                  </>
                )}
                </fieldset>

                {!isDeviceRental && (formData.rentalType === 'regular' || formData.rentalType === 'per_page' || !formData.rentalType) && (
                  <div className="bg-cyan-50 border border-cyan-300 rounded p-4 space-y-3">
                    <p className="font-bold text-cyan-900">📊 Opening Meter Reading</p>
                    <p className="text-xs text-cyan-700 -mt-2">Required — this is the baseline reading at the time the printer is assigned, used to calculate billable pages going forward.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-cyan-900">{formData.printerType === 'MFP_Colour' ? 'B/W A4 Opening Reading *' : 'A4 Opening Reading *'}</label>
                        <input type="number" placeholder="e.g. 5000" value={formData.openingA4Reading ?? ''} onChange={(e) => setFormData({...formData, openingA4Reading: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                      </div>
                      {(formData.printerType === 'A3' || formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (
                        <div>
                          <label className="text-sm font-medium text-cyan-900">{formData.printerType === 'MFP_Colour' ? 'B/W A3 Opening Reading *' : 'A3 Opening Reading *'}</label>
                          <input type="number" placeholder="e.g. 1000" value={formData.openingA3Reading ?? ''} onChange={(e) => setFormData({...formData, openingA3Reading: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                        </div>
                      )}
                      {formData.printerType === 'MFP_Colour' && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-pink-800">Colour A4 Opening Reading *</label>
                            <input type="number" placeholder="e.g. 500" value={formData.openingColourA4Reading ?? ''} onChange={(e) => setFormData({...formData, openingColourA4Reading: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-pink-800">Colour A3 Opening Reading *</label>
                            <input type="number" placeholder="e.g. 100" value={formData.openingColourA3Reading ?? ''} onChange={(e) => setFormData({...formData, openingColourA3Reading: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Start Date * <span className="text-slate-400">(Billing Start Date)</span></label>
                    <input type="date" value={formData.startDate || ''} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1" required />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">PO Number</label>
                    <input type="text" value={formData.poNumber || ''} onChange={(e) => setFormData({...formData, poNumber: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1" />
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-300 rounded p-4 space-y-3">
                  <p className="font-bold text-slate-800">📋 Rental Agreement Period</p>
                  <p className="text-xs text-slate-500 -mt-2">Used to send renewal reminders 1 month before expiry</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600">Agreement Start Date *</label>
                      <input type="date" value={formData.agreementStartDate || ''} onChange={(e) => setFormData({...formData, agreementStartDate: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Agreement End Date *</label>
                      <input type="date" value={formData.agreementEndDate || ''} onChange={(e) => setFormData({...formData, agreementEndDate: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 mt-1" required />
                    </div>
                  </div>

                  <div className="pt-1">
                    <label className="text-sm text-slate-600 mb-1 block">Agreement Document (PDF/Image, max 10MB)</label>
                    {formData.agreementDocUrl ? (
                      <div className="flex items-center justify-between bg-white border border-slate-300 rounded-lg px-3 py-2.5">
                        <a href={formData.agreementDocUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-600 hover:text-brand-800 min-w-0">
                          <Paperclip size={16} className="shrink-0" />
                          <span className="truncate text-sm">{formData.agreementDocName || 'View Document'}</span>
                        </a>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <a href={formData.agreementDocUrl} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-brand-600 p-1"><Eye size={16} /></a>
                          <button type="button" onClick={handleRemoveAgreementDoc} className="text-slate-500 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-3 py-4 cursor-pointer hover:border-brand-400 hover:bg-brand-50/50 transition-all duration-150">
                        {uploadingDoc ? (
                          <>
                            <Loader size={18} className="animate-spin text-brand-600" />
                            <span className="text-sm text-brand-600">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload size={18} className="text-slate-400" />
                            <span className="text-sm text-slate-500">Click to upload agreement document</span>
                          </>
                        )}
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAgreementUpload} className="hidden" disabled={uploadingDoc} />
                      </label>
                    )}
                  </div>
                </div>
              </>
              );
            })()}

            {/* Meter Reading Form */}
            {modalType === 'reading' && (
              <>
                <div className="bg-brand-50 p-4 rounded mb-4">
                  <p className="font-medium text-slate-900">{formData.clientName}</p>
                  <p className="text-sm text-slate-600">{formData.printerModel}</p>
                  <p className="text-xs text-slate-500">{formData.assetNumber && <span className="font-medium">{formData.assetNumber} • </span>}{formData.serialNumber} • {formData.printerType}</p>
                  {formData.rentalType === 'standby' && (
                    <span className="inline-block mt-2 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold">⚡ STANDBY</span>
                  )}
                </div>
                
                <input type="date" value={formData.date || new Date().toISOString().split('T')[0]} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full border rounded px-3 py-2.5 text-base" />
                
                <div className="bg-slate-50 p-4 rounded">
                  <p className="font-medium mb-3">📄 {formData.printerType === 'MFP_Colour' ? 'B/W A4 Reading' : 'A4 Reading'}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600">Previous</label>
                      <input type="number" value={formData.previousA4 || 0} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 bg-slate-100" readOnly />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Current *</label>
                      <input type="number" value={formData.a4Reading || ''} onChange={(e) => setFormData({...formData, a4Reading: e.target.value})} className="w-full border-2 border-green-500 rounded px-3 py-2 text-base font-bold" required />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    Pages: <span className="font-bold">{((formData.a4Reading || 0) - (formData.previousA4 || 0)).toLocaleString()}</span>
                  </p>
                </div>

                {(formData.printerType === 'A3' || formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (
                  <div className="bg-purple-50 p-4 rounded">
                    <p className="font-medium mb-3">📋 {formData.printerType === 'MFP_Colour' ? 'B/W A3 Reading' : 'A3 Reading'}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-slate-600">Previous</label>
                        <input type="number" value={formData.previousA3 || 0} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 bg-slate-100" readOnly />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Current *</label>
                        <input type="number" value={formData.a3Reading || ''} onChange={(e) => setFormData({...formData, a3Reading: e.target.value})} className="w-full border-2 border-purple-500 rounded px-3 py-2 text-base font-bold" required />
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Pages: <span className="font-bold">{((formData.a3Reading || 0) - (formData.previousA3 || 0)).toLocaleString()}</span>
                    </p>
                  </div>
                )}

                {formData.printerType === 'MFP_Colour' && (
                  <>
                    <div className="bg-pink-50 border border-pink-300 p-4 rounded">
                      <p className="font-medium mb-3 text-pink-900">🎨 Colour A4 Reading</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-slate-600">Previous</label>
                          <input type="number" value={formData.previousColourA4 || 0} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 bg-slate-100" readOnly />
                        </div>
                        <div>
                          <label className="text-sm text-slate-600">Current *</label>
                          <input type="number" value={formData.colourA4Reading || ''} onChange={(e) => setFormData({...formData, colourA4Reading: e.target.value})} className="w-full border-2 border-pink-500 rounded px-3 py-2 text-base font-bold" required />
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        Pages: <span className="font-bold">{((formData.colourA4Reading || 0) - (formData.previousColourA4 || 0)).toLocaleString()}</span>
                      </p>
                    </div>
                    <div className="bg-pink-50 border border-pink-300 p-4 rounded">
                      <p className="font-medium mb-3 text-pink-900">🎨 Colour A3 Reading</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-slate-600">Previous</label>
                          <input type="number" value={formData.previousColourA3 || 0} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 bg-slate-100" readOnly />
                        </div>
                        <div>
                          <label className="text-sm text-slate-600">Current *</label>
                          <input type="number" value={formData.colourA3Reading || ''} onChange={(e) => setFormData({...formData, colourA3Reading: e.target.value})} className="w-full border-2 border-pink-500 rounded px-3 py-2 text-base font-bold" required />
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        Pages: <span className="font-bold">{((formData.colourA3Reading || 0) - (formData.previousColourA3 || 0)).toLocaleString()}</span>
                      </p>
                    </div>
                  </>
                )}

                {(formData.printerType === 'MFP' || formData.printerType === 'MFP_Colour') && (
                  <div className="bg-brand-50 p-4 rounded">
                    <p className="font-medium mb-3">🖨️ Scanner Reading</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-slate-600">Previous</label>
                        <input type="number" value={formData.previousScanner || 0} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 bg-slate-100" readOnly />
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Current *</label>
                        <input type="number" value={formData.scannerReading || ''} onChange={(e) => setFormData({...formData, scannerReading: e.target.value})} className="w-full border-2 border-brand-500 rounded px-3 py-2 text-base font-bold" required />
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Scans: <span className="font-bold">{((formData.scannerReading || 0) - (formData.previousScanner || 0)).toLocaleString()}</span>
                    </p>
                  </div>
                )}

                {formData.rentalType === 'standby' && (
                  <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded">
                    <p className="font-medium mb-3">🔋 Cartridge Usage</p>
                    <input type="number" placeholder="Cartridges Used *" value={formData.cartridgesUsed || ''} onChange={(e) => setFormData({...formData, cartridgesUsed: e.target.value})} className="w-full border-2 border-yellow-500 rounded px-3 py-2 text-base font-bold" required />
                    <p className="text-sm text-yellow-800 mt-2">
                      Cost: ₹{((formData.cartridgesUsed || 0) * (formData.cartridgeCost || 0)).toLocaleString()}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Service Call Form */}
            {modalType === 'serviceCall' && (
              <>
                <select value={formData.clientName || ''} onChange={(e) => {
                  const picked = clients.find(c => c.name === e.target.value);
                  // A new call defaults to the client's allocated engineer (can still be changed below)
                  setFormData({...formData, clientName: e.target.value, serialNumber: '', printerModel: '', assetNumber: '', printerMake: '', printerModelOnly: '', engineer: editingItem?.id ? formData.engineer : (picked?.allocatedEngineer || formData.engineer || '')});
                }} className="w-full border rounded px-3 py-2.5 md:py-2 text-base">
                  <option value="">Select Client *</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowWalkinForm(!showWalkinForm)}
                  style={{width: '100%', display: 'block', backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0'}}
                  className="px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium"
                >
                  ➕ New Walk-in Client (not in the list above)
                  </button>

                {showWalkinForm && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-emerald-900">New Walk-in Client</p>
                    <p className="text-xs text-emerald-700 -mt-1">This creates the client in your Clients list.</p>
                    <input type="text" placeholder="Client Name *" value={walkinData.name} onChange={(e) => setWalkinData({...walkinData, name: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input type="text" placeholder="Phone" value={walkinData.phone} onChange={(e) => setWalkinData({...walkinData, phone: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                      <input type="email" placeholder="Email" value={walkinData.email} onChange={(e) => setWalkinData({...walkinData, email: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    </div>
                    <input type="text" placeholder="Company (optional)" value={walkinData.company} onChange={(e) => setWalkinData({...walkinData, company: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveWalkinClient}
                        disabled={savingWalkin}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {savingWalkin ? '⏳ Saving...' : '✅ Add & Select Client'}
                      </button>
                      <button type="button" onClick={() => setShowWalkinForm(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-slate-600">Equipment Type</label>
                  <select value={formData.equipmentType || 'laserjet_printer'} onChange={(e) => setFormData({...formData, equipmentType: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1">
                    {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-slate-600">Printer Source *</label>
                  <div className="flex gap-3 mt-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={(formData.assetSource || 'company') === 'company'} onChange={() => setFormData({...formData, assetSource: 'company', serialNumber: '', printerModel: '', assetNumber: ''})} />
                      🏢 Our Asset (Rental Inventory)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={formData.assetSource === 'client'} onChange={() => setFormData({...formData, assetSource: 'client', serialNumber: '', printerModel: '', assetNumber: '', printerMake: '', printerModelOnly: ''})} />
                      👤 Client-Owned Printer
                    </label>
                  </div>
                </div>

                {(formData.assetSource || 'company') === 'company' ? (
                  <>
                    {!formData.clientName ? (
                      <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">⚠️ Select a client first to see which of our printers are assigned to them.</p>
                    ) : (() => {
                      const assignedSerials = new Set(
                        rentals.filter(r => r.clientName === formData.clientName && r.status !== 'returned').map(r => r.serialNumber)
                      );
                      const assignedPrinters = printers.filter(p => assignedSerials.has(p.serialNumber));
                      if (assignedPrinters.length === 0) {
                        return <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">⚠️ No printers from our rental inventory are currently assigned to {formData.clientName}.</p>;
                      }
                      return (
                        <select
                          value={formData.serialNumber || ''}
                          onChange={(e) => {
                            const printer = printers.find(p => p.serialNumber === e.target.value);
                            setFormData({...formData, serialNumber: e.target.value, printerModel: printer ? `${printer.make} ${printer.model}` : '', assetNumber: printer?.assetNumber || ''});
                          }}
                          className="w-full border rounded px-3 py-2.5 md:py-2 text-base"
                        >
                          <option value="">Select Printer Assigned to {formData.clientName} *</option>
                          {assignedPrinters.map(p => (
                            <option key={p.id} value={p.serialNumber}>
                              {p.assetNumber ? `${p.assetNumber} • ` : ''}S/N: {p.serialNumber} - {p.make} {p.model}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                    {formData.serialNumber && (
                      <div className="grid grid-cols-3 gap-3 bg-slate-50 rounded-lg p-3 text-sm">
                        <div><p className="text-xs text-slate-500">Asset No.</p><p className="font-medium">{formData.assetNumber || '-'}</p></div>
                        <div><p className="text-xs text-slate-500">Model</p><p className="font-medium">{formData.printerModel || '-'}</p></div>
                        <div><p className="text-xs text-slate-500">Serial No.</p><p className="font-medium">{formData.serialNumber}</p></div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!formData.clientName ? (
                      <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">⚠️ Select a client first to see their registered printers.</p>
                    ) : (
                      <select
                        value={formData.serialNumber || ''}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setFormData({...formData, serialNumber: '__new__', printerMake: '', printerModelOnly: ''});
                            return;
                          }
                          const asset = clientAssets.find(a => a.serialNumber === e.target.value);
                          setFormData({...formData, serialNumber: e.target.value, printerModel: asset ? `${asset.make} ${asset.model}` : '', printerMake: asset?.make || '', printerModelOnly: asset?.model || '', assetNumber: ''});
                        }}
                        className="w-full border rounded px-3 py-2.5 md:py-2 text-base"
                      >
                        <option value="">Select {formData.clientName}'s Printer *</option>
                        {clientAssets.filter(a => a.clientName === formData.clientName).map(a => (
                          <option key={a.id} value={a.serialNumber}>{a.serialNumber} - {a.make} {a.model}</option>
                        ))}
                        <option value="__new__">➕ Not listed - add new device</option>
                      </select>
                    )}

                    {formData.serialNumber === '__new__' ? (
                      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-cyan-800">New device details - this will also be added to {formData.clientName}'s asset register</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <input type="text" placeholder="Make *" value={formData.printerMake || ''} onChange={(e) => setFormData({...formData, printerMake: e.target.value})} className="border rounded px-3 py-2 text-sm" required />
                          <input type="text" placeholder="Model *" value={formData.printerModelOnly || ''} onChange={(e) => setFormData({...formData, printerModelOnly: e.target.value})} className="border rounded px-3 py-2 text-sm" required />
                          <input type="text" placeholder="Serial Number *" value={formData.newSerialNumber || ''} onChange={(e) => setFormData({...formData, newSerialNumber: e.target.value})} className="border rounded px-3 py-2 text-sm" required />
                        </div>
                      </div>
                    ) : formData.serialNumber && (
                      <div className="grid grid-cols-3 gap-3 bg-slate-50 rounded-lg p-3 text-sm">
                        <div><p className="text-xs text-slate-500">Make</p><p className="font-medium">{formData.printerMake || '-'}</p></div>
                        <div><p className="text-xs text-slate-500">Model</p><p className="font-medium">{formData.printerModelOnly || '-'}</p></div>
                        <div><p className="text-xs text-slate-500">Serial No.</p><p className="font-medium">{formData.serialNumber}</p></div>
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Date</label>
                    <input type="date" value={formData.date || new Date().toISOString().split('T')[0]} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Assign Service Engineer</label>
                    <select value={formData.engineer || ''} onChange={(e) => setFormData({...formData, engineer: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1">
                      <option value="">Unassigned</option>
                      {engineers.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                    </select>
                    {(() => {
                      const allocated = clients.find(c => c.name === formData.clientName)?.allocatedEngineer;
                      if (allocated && formData.engineer && formData.engineer !== allocated) return <p className="text-xs text-amber-600 mt-1">⚠️ {formData.clientName} is allocated to {allocated}.</p>;
                      if (allocated && !formData.engineer) return <p className="text-xs text-slate-500 mt-1">Allocated engineer for this client: {allocated}</p>;
                      return null;
                    })()}
                    {(formData.assignmentHistory || []).length > 0 && (
                      <p className="text-xs text-slate-400 mt-1" title={(formData.assignmentHistory || []).map(h => `${(h.at || '').slice(0, 16).replace('T', ' ')} - ${h.engineer || 'Unassigned'} (by ${h.by})`).join('\n')}>
                        Assigned {(formData.assignmentHistory || []).length} time{(formData.assignmentHistory || []).length !== 1 ? 's' : ''}, last on {((formData.assignmentHistory || []).slice(-1)[0].at || '').slice(0, 10)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-brand-900 mb-2">📦 Pickup Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600">Picked Up By</label>
                      <select value={formData.pickedUpBy || ''} onChange={(e) => setFormData({...formData, pickedUpBy: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1">
                        <option value="">Select...</option>
                        {engineers.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                        <option value="__other__">Other (type below)</option>
                      </select>
                      {formData.pickedUpBy === '__other__' && (
                        <input type="text" placeholder="Name" value={formData.pickedUpByOther || ''} onChange={(e) => setFormData({...formData, pickedUpByOther: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-2" />
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Date of Pickup</label>
                      <input type="date" value={formData.pickupDate || ''} onChange={(e) => setFormData({...formData, pickupDate: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" />
                    </div>
                  </div>
                  {(() => {
                    const printer = formData.assetSource === 'client' ? null : printers.find(p => p.serialNumber === formData.serialNumber);
                    const type = printer?.type;
                    const hasA3 = type === 'A3' || type === 'MFP' || type === 'MFP_Colour';
                    const hasScanner = type === 'MFP' || type === 'MFP_Colour';
                    return (
                      <div className="mt-3 pt-3 border-t border-brand-200 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-slate-600">A4 Meter Reading on Pickup * <span className="text-xs text-brand-600">(required)</span></label>
                          <input type="number" value={formData.pickupA4Reading || ''} onChange={(e) => setFormData({...formData, pickupA4Reading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current A4 count on the printer" />
                        </div>
                        {hasA3 && (
                          <div>
                            <label className="text-sm text-slate-600">A3 Meter Reading on Pickup * <span className="text-xs text-brand-600">(required)</span></label>
                            <input type="number" value={formData.pickupA3Reading || ''} onChange={(e) => setFormData({...formData, pickupA3Reading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current A3 count on the printer" />
                          </div>
                        )}
                        {hasScanner && (
                          <div className="md:col-span-2">
                            <label className="text-sm text-slate-600">Scanner Count on Pickup * <span className="text-xs text-brand-600">(MFP)</span></label>
                            <input type="number" value={formData.pickupScannerReading || ''} onChange={(e) => setFormData({...formData, pickupScannerReading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current scan count" />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-emerald-900 mb-2">📤 Return Details</p>
                  <p className="text-xs text-emerald-700 -mt-1 mb-2">Fill this in when the printer is handed back to the client. Marking Status as "Completed" above uses these details for the Inward/Outward register automatically.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600">Returned By</label>
                      <select value={formData.returnedBy || ''} onChange={(e) => setFormData({...formData, returnedBy: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1">
                        <option value="">Select...</option>
                        {engineers.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                        <option value="__other__">Other (type below)</option>
                      </select>
                      {formData.returnedBy === '__other__' && (
                        <input type="text" placeholder="Name" value={formData.returnedByOther || ''} onChange={(e) => setFormData({...formData, returnedByOther: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-2" />
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Date of Return</label>
                      <input type="date" value={formData.returnDate || ''} onChange={(e) => setFormData({...formData, returnDate: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" />
                    </div>
                  </div>
                  {formData.status === 'completed' && (() => {
                    const printer = formData.assetSource === 'client' ? null : printers.find(p => p.serialNumber === formData.serialNumber);
                    const type = printer?.type;
                    const hasA3 = type === 'A3' || type === 'MFP' || type === 'MFP_Colour';
                    const hasScanner = type === 'MFP' || type === 'MFP_Colour';
                    return (
                      <div className="mt-3 pt-3 border-t border-emerald-200 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-slate-600">A4 Meter Reading on Return * <span className="text-xs text-emerald-600">(required to complete)</span></label>
                          <input type="number" value={formData.returnA4Reading || ''} onChange={(e) => setFormData({...formData, returnA4Reading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current A4 count on the printer" />
                        </div>
                        {hasA3 && (
                          <div>
                            <label className="text-sm text-slate-600">A3 Meter Reading on Return * <span className="text-xs text-emerald-600">(required to complete)</span></label>
                            <input type="number" value={formData.returnA3Reading || ''} onChange={(e) => setFormData({...formData, returnA3Reading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current A3 count on the printer" />
                          </div>
                        )}
                        {hasScanner && (
                          <div className="md:col-span-2">
                            <label className="text-sm text-slate-600">Scanner Count on Return * <span className="text-xs text-emerald-600">(MFP)</span></label>
                            <input type="number" value={formData.returnScannerReading || ''} onChange={(e) => setFormData({...formData, returnScannerReading: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" placeholder="Current scan count" />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <textarea placeholder="Issue Description *" value={formData.issue || ''} onChange={(e) => setFormData({...formData, issue: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base" rows="3" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Priority</label>
                    <select value={formData.priority || 'medium'} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Status</label>
                    <select value={formData.status || 'open'} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full border rounded px-3 py-2.5 md:py-2 text-base mt-1">
                      <option value="open">🟡 Open</option>
                      <option value="in-progress">🔵 In Progress</option>
                      <option value="completed">🟢 Completed</option>
                    </select>
                    {formData.status === 'completed' && !formData.returnA4Reading && (
                      <p className="text-xs text-red-600 mt-1">⚠️ Fill in the A4 Meter Reading on Return above before this can be saved as Completed.</p>
                    )}
                  </div>
                </div>
                {formData.status === 'completed' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-3">
                    <p className="text-sm font-medium text-green-900">✅ Completion Details</p>
                    <div>
                      <label className="text-sm text-slate-600">Work done *</label>
                      <textarea placeholder="What was done to resolve the issue" value={formData.workDone || ''} onChange={(e) => setFormData({...formData, workDone: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" rows="2" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-slate-600">Parts / consumables used</label>
                        <input type="text" placeholder="e.g. Pickup roller, 85A toner" value={formData.partsUsed || ''} onChange={(e) => setFormData({...formData, partsUsed: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" />
                      </div>
                      <div className="md:col-span-2 space-y-1.5">
                        <label className="text-sm text-slate-600">Stock used (from Stores) – deducted automatically when you save</label>
                        {(formData.stockUsed || []).map((r, idx) => r.moveId ? (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-white border rounded px-3 py-1.5"><span className="flex-1">✓ {r.itemName} × {r.qty} {r.unit}</span><span className="text-xs text-slate-400">recorded – correct it in Stock</span></div>
                        ) : (
                          <div key={idx} className="flex gap-2">
                            <select value={r.itemId || ''} onChange={(e) => { const rows = [...formData.stockUsed]; rows[idx] = { ...rows[idx], itemId: e.target.value }; setFormData({...formData, stockUsed: rows}); }} className="flex-1 border rounded px-2 py-2 text-sm min-w-0"><option value="">Select stock item…</option>{stockItems.filter(i => i.active !== false).map(i => <option key={i.id} value={i.id}>{i.name} (stores: {stockOnHand(i.id)}{formData.engineer && engineerHolding(formData.engineer, i.id) > 0 ? `, with ${formData.engineer}: ${engineerHolding(formData.engineer, i.id)}` : ''})</option>)}</select>
                            <input type="number" min="1" placeholder="Qty" value={r.qty || ''} onChange={(e) => { const rows = [...formData.stockUsed]; rows[idx] = { ...rows[idx], qty: e.target.value }; setFormData({...formData, stockUsed: rows}); }} className="w-20 border rounded px-2 py-2 text-sm" />
                            <button type="button" onClick={() => setFormData({...formData, stockUsed: formData.stockUsed.filter((_, i) => i !== idx)})} className="text-red-500 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
                          </div>
                        ))}
                        {stockItems.length > 0 && <button type="button" onClick={() => setFormData({...formData, stockUsed: [...(formData.stockUsed || []), { itemId: '', qty: 1 }]})} className="text-xs text-brand-600 hover:underline">+ Add stock item</button>}
                      </div>
                      <div>
                        <label className="text-sm text-slate-600">Time spent (minutes)</label>
                        <input type="number" min="0" value={formData.timeSpentMins || ''} onChange={(e) => setFormData({...formData, timeSpentMins: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Accepted by (client contact)</label>
                      <input type="text" placeholder="Name of the person who confirmed the work" value={formData.signedOffBy || ''} onChange={(e) => setFormData({...formData, signedOffBy: e.target.value})} className="w-full border rounded px-3 py-2 text-base mt-1" />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex gap-3 pt-5 mt-2 border-t border-slate-100">
              <button type="submit" disabled={submitting} className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white py-3 md:py-2.5 rounded-lg shadow-sm hover:shadow-md font-medium transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Saving...' : (editingItem?.id ? 'Update' : 'Create')}
              </button>
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-white text-slate-700 border border-slate-300 py-3 md:py-2.5 rounded-lg hover:bg-slate-50 font-medium transition-all duration-150">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // QUOTATIONS - Generate and manage service quotations for clients
  const QuotationsView = () => {
    const canCreate = hasPermission('canCreateQuotations');
    const canDelete = hasPermission('canDelete');
    const [searchQuote, setSearchQuote] = useState('');
    const [quoteDupePreview, setQuoteDupePreview] = useState(null);

    const openModal = (item = null) => {
      if (!canCreate) return;
      setEditingItem(item || {
        date: new Date().toISOString().split('T')[0],
        printerType: 'laserjet_printer',
        items: [{ description: '', hsn: '998713', qty: 1, rate: '' }],
        notes: 'Looking forward for your business.',
        quotationType: 'service',
        terms: QUOTATION_TERMS_PRESETS.service
      });
      setModalType('quotation');
      setShowModal(true);
    };

    const deleteQuotation = async (q) => {
      if (!window.confirm(`Delete quotation ${q.quoteNumber} for ${q.clientName}?`)) return;
      try {
        await logAudit('DELETE', 'Quotation', q.id, `${q.quoteNumber} - ${q.clientName}`, q);
        await deleteDoc(doc(db, 'quotations', q.id));
      } catch (err) {
        alert('Error deleting quotation: ' + err.message);
      }
    };

    const filteredQuotations = quotations.filter(q => matchesSearch(searchQuote, q.quoteNumber, q.clientName, q.printerModel, q.printerSerial));

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-bold">Quotations</h2>
          <div className="flex gap-2">
            {(() => {
              const seen = {};
              quotations.forEach(q => {
                if (!q.quoteNumber) return;
                seen[q.quoteNumber] = (seen[q.quoteNumber] || 0) + 1;
              });
              const dupeCount = Object.values(seen).filter(c => c > 1).reduce((sum, c) => sum + (c - 1), 0);
              if (dupeCount === 0 || userRole !== 'super_admin') return null;
              return (
                <button
                  onClick={() => {
                    // Same class of bug as the duplicate asset numbers on the
                    // Assets page: build a preview from current data before
                    // touching anything. Within each colliding quote number,
                    // the earliest-created one is kept as-is. Each other one
                    // is either an EXACT duplicate (same client, printer
                    // serial and line items - a true accidental double-save,
                    // e.g. from a double-click) and gets deleted, or a
                    // genuinely different quotation that just happened to
                    // collide on the number, and gets renumbered instead.
                    const groups = {};
                    quotations.forEach(q => {
                      if (!q.quoteNumber) return;
                      if (!groups[q.quoteNumber]) groups[q.quoteNumber] = [];
                      groups[q.quoteNumber].push(q);
                    });
                    const preview = Object.entries(groups)
                      .filter(([, group]) => group.length > 1)
                      .map(([quoteNumber, group]) => {
                        const sorted = [...group].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
                        const keep = sorted[0];
                        const rest = sorted.slice(1).map(q => {
                          // Compare by clientNAME, not clientId - two quotations for
                          // what is obviously the same client can end up with
                          // different clientIds if there happen to be two separate
                          // Client records with the same name (its own, separate
                          // data-quality issue), and clientId alone missed exactly
                          // that case here. Name + printer + items is what a human
                          // reviewing the list actually judges "the same" by anyway.
                          const isExactDuplicate = (q.clientName || '').trim().toLowerCase() === (keep.clientName || '').trim().toLowerCase() &&
                            (q.printerSerial || '') === (keep.printerSerial || '') &&
                            (q.printerModel || '') === (keep.printerModel || '') &&
                            JSON.stringify(q.items || []) === JSON.stringify(keep.items || []);
                          return { ...q, _action: isExactDuplicate ? 'delete' : 'renumber' };
                        });
                        return { quoteNumber, keep, rest };
                      });
                    setQuoteDupePreview(preview);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2"
                >
                  ⚠️ Fix Duplicate Numbers ({dupeCount})
                </button>
              );
            })()}
            {canCreate && (
              <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2">
                <Plus size={18} /> New Quotation
              </button>
            )}
          </div>
        </div>

        {quoteDupePreview && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-amber-900 text-lg">⚠️ Duplicate Quote Numbers Found</h3>
                <p className="text-sm text-amber-700">Exact duplicates (same client, printer &amp; items) will be deleted. Genuinely different quotations that collided on the same number will be renumbered instead. Review before confirming.</p>
              </div>
              <button onClick={() => setQuoteDupePreview(null)} className="text-amber-600 hover:text-amber-800 p-1"><X size={20} /></button>
            </div>

            <div className="space-y-3">
              {quoteDupePreview.map(group => (
                <div key={group.quoteNumber} className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Duplicated Number: {group.quoteNumber}</p>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ KEEPS {group.quoteNumber}</span>
                      <span className="font-medium text-sm">{group.keep.clientName}</span>
                    </div>
                    <p className="text-xs text-slate-500">{group.keep.printerModel || ''} {group.keep.printerSerial ? `• S/N: ${group.keep.printerSerial}` : ''}</p>
                  </div>

                  {group.rest.map(q => (
                    <div key={q.id} className={`border rounded-lg p-3 mb-2 last:mb-0 ${q._action === 'delete' ? 'bg-red-50 border-red-200' : 'bg-brand-50 border-brand-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${q._action === 'delete' ? 'text-red-700 bg-red-100' : 'text-brand-700 bg-brand-100'}`}>
                          {q._action === 'delete' ? '✕ EXACT DUPLICATE — DELETE' : '↻ WILL BE RENUMBERED'}
                        </span>
                        <span className="font-medium text-sm">{q.clientName}</span>
                      </div>
                      <p className="text-xs text-slate-500">{q.printerModel || ''} {q.printerSerial ? `• S/N: ${q.printerSerial}` : ''}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  const freshSnapshot = await getDocs(collection(db, 'quotations'));
                  const freshQuotations = freshSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                  const existingNumbers = new Set(freshQuotations.map(q => q.quoteNumber));

                  let deletedCount = 0;
                  let renumberedCount = 0;
                  for (const group of quoteDupePreview) {
                    for (const q of group.rest) {
                      try {
                        if (q._action === 'delete') {
                          await logAudit('DELETE', 'Quotation', q.id, `Duplicate deleted (${q.quoteNumber} - ${q.clientName})`, q);
                          await deleteDoc(doc(db, 'quotations', q.id));
                          deletedCount++;
                        } else {
                          // Find the next free number sharing this quote's own prefix
                          // (e.g. "RBQT2026-27") rather than today's FY, since a
                          // colliding quote may have been created in a different
                          // financial year's numbering than "now".
                          const lastSlash = q.quoteNumber.lastIndexOf('\\');
                          const fyPrefix = q.quoteNumber.slice(0, lastSlash);
                          let n = freshQuotations.filter(fq => (fq.quoteNumber || '').startsWith(fyPrefix + '\\')).length + 1;
                          let candidate = `${fyPrefix}\\${n}`;
                          while (existingNumbers.has(candidate)) {
                            n++;
                            candidate = `${fyPrefix}\\${n}`;
                          }
                          existingNumbers.add(candidate);
                          const updated = { ...q, quoteNumber: candidate };
                          await setDoc(doc(db, 'quotations', q.id), updated);
                          await logAudit('UPDATE', 'Quotation', q.id, `Renumbered ${q.quoteNumber} → ${candidate} (duplicate fix)`, updated);
                          renumberedCount++;
                        }
                      } catch (e) {
                        console.error('Error fixing duplicate quotation', q.id, e);
                      }
                    }
                  }

                  setQuoteDupePreview(null);
                  alert(`✅ Fixed! Deleted ${deletedCount} exact duplicate(s) and renumbered ${renumberedCount} quotation(s). Full details are in Audit Logs.`);
                }}
                className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium"
              >
                Confirm &amp; Fix Duplicates
              </button>
              <button
                onClick={() => setQuoteDupePreview(null)}
                className="bg-white text-slate-700 border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 transition-all duration-150 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <input
          type="text"
          placeholder="🔍 Search by quote number, client, or printer..."
          value={searchQuote}
          onChange={(e) => setSearchQuote(e.target.value)}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-base"
        />

        {filteredQuotations.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 flex flex-col items-center justify-center">
            <div className="text-slate-400 mb-4"><FileText size={64} /></div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No quotations yet</h3>
            <p className="text-slate-500 text-center max-w-md">Click "New Quotation" to create your first service quotation for a client.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredQuotations.map(q => {
              const subTotal = (q.items || []).reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0)), 0);
              const grandTotal = subTotal * 1.18;
              return (
                <div key={q.id} className="bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200 p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-mono text-sm font-semibold text-brand-700">{q.quoteNumber}</p>
                      <h3 className="font-semibold text-slate-900">{q.clientName}</h3>
                      {q.inboxMailId && <p className="text-xs text-slate-400 truncate max-w-[220px]" title={q.emailSubject}>📧 From email: {q.emailSubject || q.emailFrom}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      ['laptop', 'desktop'].includes(q.printerType) ? 'bg-slate-50 text-slate-700' :
                      q.printerType === 'scanner' ? 'bg-teal-50 text-teal-700' :
                      q.printerType === 'barcode_printer' ? 'bg-amber-50 text-amber-700' :
                      q.printerType === 'inktank_printer' ? 'bg-cyan-50 text-cyan-700' : 'bg-purple-50 text-purple-700'
                    }`}>
                      {(QUOTATION_EQUIPMENT_TYPES.find(t => t.value === q.printerType)?.label || 'LaserJet Printer').replace(/^\S+\s/, '')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    {q.date ? new Date(q.date + 'T00:00:00').toLocaleDateString('en-GB') : ''} {q.printerModel ? `• ${q.printerModel}` : ''} {q.printerSerial ? `• S/N: ${q.printerSerial}` : ''}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-slate-900">₹{grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const client = clients.find(c => c.id === q.clientId || c.name === q.clientName);
                          const suggested = client?.email || q.clientEmail || '';
                          const recipient = window.prompt(`Send quotation ${q.quoteNumber} to which email address?`, suggested);
                          if (recipient) sendQuotationEmail(q, recipient);
                        }}
                        className="text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150 flex items-center gap-1"
                      >
                        📧 Email
                      </button>
                      {canCreateIn('invoices') && !isRentalQuotation(q) && <button onClick={() => openInvoiceFromQuotation(q)} className="text-xs bg-brand-50 text-brand-700 hover:bg-brand-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150" title="Sales: create the invoice directly from this quotation">🧾 Invoice</button>}
                      {canCreateIn('invoices') && !isRentalQuotation(q) && <button onClick={() => openProformaFromQuotation(q)} className="text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150" title="Create a proforma invoice (not a tax invoice) from this quotation">📝 Proforma</button>}
                      {canCreateIn('invoices') && isRentalQuotation(q) && <button onClick={() => openChallanFromQuotation(q)} className="text-xs bg-sky-50 text-sky-700 hover:bg-sky-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150" title="Rental: create the delivery challan from this quotation">🚚 Delivery challan</button>}
                      <button onClick={() => generateQuotationPDF(q)} className="text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 rounded-lg font-medium transition-colors duration-150 flex items-center gap-1">
                        <Download size={13} /> PDF
                      </button>
                      {canCreate && (
                        <button onClick={() => openModal(q)} className="p-1.5 rounded-md hover:bg-brand-50 text-brand-600 transition-colors duration-150"><Edit size={16} /></button>
                      )}
                      {canDelete && (
                        <button onClick={() => deleteQuotation(q)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors duration-150"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const fmtDMY = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB') : '');

  // ---------------------------------------------------------------- INVOICES & DELIVERY CHALLANS
  // Numbered tax invoices (PDF, GST split by client state) and delivery challans (PDF). New invoices are sent to Tally
  // automatically as Sales vouchers (Tally > Settings > "send automatically"). Challans stay in the portal: Tally's
  // Delivery Note needs stock items, which this portal does not keep.
  // (Form drafts live at App level because this component is re-created on every data update.)
  const pdfDeps = { jsPDF, autoTable, COMPANIES, COMPANY_LOGOS, COMPANY_SEALS, numberToWordsINR, gstTotals, stateFromGstin };
  const LEDGER_KIND_FOR_QUOTATION = { service: 'service', email_service: 'service', rental: 'rental', laptop_rental: 'rental' };
  const todayYmd = () => new Date().toISOString().split('T')[0];
  const addDaysYmd = (ymd, n) => { const d = new Date(`${ymd}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

  // Next number in a yearly series, e.g. RBINV2026-27\12 (same style as quotation numbers). Deleted numbers are reused.
  const generateDocNumber = async (collName, field, series) => {
    const code = currentCompany === 'canopus' ? 'CN' : 'RB';
    const today = new Date();
    const fyStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const prefix = `${code}${series}${fyStart}-${String((fyStart + 1) % 100).padStart(2, '0')}`;
    // invoices: also steer clear of numbers accounts has already used directly in Tally
    return allocateNumber(collName, field, prefix, collName === 'invoices' ? tallyInvoices.map(r => r.number) : []);
  };

  // ---- Tally auto-sync: called right after a record is saved. Never blocks or fails the save.
  const tallyAutoReady = () => !!String(tallyCfg.tallyCompany || '').trim();
  const queueTallyDoc = async ({ kind, title, xml, refs }) => {
    const id = `TQ${Date.now()}${Math.floor(Math.random() * 1000)}`;
    await setDoc(doc(db, 'tallyQueue', id), {
      id, companyId: currentCompany, tallyCompany: tallyCfg.tallyCompany, kind, title, xml, refs: refs || [],
      status: 'pending', createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown', auto: true
    });
  };
  const autoSyncClient = async (client, oldName) => {
    try {
      if (!tallyAutoReady() || !tallyCfg.autoSync.clients || !String(client.name || '').trim()) return;
      const synced = tallyRefStatus[`client:${client.id}`] === 'done';
      const led = clientToLedger(client, tallyCfg);
      if (synced && oldName && oldName.trim() && oldName.trim() !== client.name.trim()) led.oldName = oldName.trim();   // rename in Tally
      await queueTallyDoc({ kind: 'ledger', title: `Client ledger: ${client.name}`, xml: mastersXml(tallyCfg.tallyCompany, [led], synced ? 'Alter' : 'Create'), refs: [`client:${client.id}`] });
    } catch (e) { console.error('Tally auto-sync (client) failed:', e); }
  };
  const autoSyncInvoice = async (inv) => {
    try {
      if (!tallyAutoReady() || !tallyCfg.autoSync.invoices) return;
      const client = clients.find(c => c.id === inv.clientId) || findClientByName(clients, inv.clientName);
      const v = invoiceToVoucher(inv, client, tallyCfg);
      if (!v.lines.length) return;
      await queueTallyDoc({ kind: 'voucher', title: `Invoice ${inv.invoiceNumber}`, xml: vouchersXml(tallyCfg.tallyCompany, [v], tallyCfg), refs: [`invoice:${inv.id}`] });
    } catch (e) { console.error('Tally auto-sync (invoice) failed:', e); }
  };
  const autoSyncQuotation = async (q) => {
    try {
      if (!tallyAutoReady() || !tallyCfg.autoSync.quotations) return;
      const client = clients.find(c => c.id === q.clientId) || findClientByName(clients, q.clientName);
      const v = quotationToVoucher(q, client, tallyCfg);
      if (!v.lines.length) return;
      await queueTallyDoc({ kind: 'voucher', title: `Quotation ${q.quoteNumber}`, xml: vouchersXml(tallyCfg.tallyCompany, [v], tallyCfg), refs: [`quotation:${q.id}`] });
    } catch (e) { console.error('Tally auto-sync (quotation) failed:', e); }
  };

  const autoSyncProforma = async (p) => {
    try {
      if (!tallyAutoReady() || !tallyCfg.autoSync.proformas) return;
      const client = clients.find(c => c.id === p.clientId) || findClientByName(clients, p.clientName);
      const v = proformaToVoucher(p, client, tallyCfg);
      if (!v.lines.length) return;
      await queueTallyDoc({ kind: 'voucher', title: `Proforma ${p.invoiceNumber}`, xml: vouchersXml(tallyCfg.tallyCompany, [v], tallyCfg), refs: [`proforma:${p.id}`] });
    } catch (e) { console.error('Tally auto-sync (proforma) failed:', e); }
  };
  const autoSyncChallan = async (ch) => {
    try {
      if (!tallyAutoReady() || !tallyCfg.autoSync.challans) return false;
      const client = clients.find(c => c.id === ch.clientId) || findClientByName(clients, ch.clientName);
      const q = ch.quotationId ? quotations.find(x => x.id === ch.quotationId) : null;
      const v = challanToVoucher(ch, client, tallyCfg, q);
      if (!v.lines.length) return false;   // no value known (no quotation, no value typed) -> nothing to post
      await queueTallyDoc({ kind: 'voucher', title: `Delivery challan ${ch.challanNumber}`, xml: vouchersXml(tallyCfg.tallyCompany, [v], tallyCfg), refs: [`challan:${ch.id}`] });
      return true;
    } catch (e) { console.error('Tally auto-sync (challan) failed:', e); return false; }
  };

  const buildInvoiceDoc = (inv) => ((invCfg.invoiceStyle || 'tally') === 'tally'
    ? buildTallyInvoicePDFDoc({ ...inv, declaration: inv.declaration || invCfg.declaration }, pdfDeps)
    : buildInvoicePDFDoc(inv, pdfDeps));
  const downloadInvoicePDF = (inv) => buildInvoiceDoc(inv).save(`${String(inv.invoiceNumber).replace(/\\/g, '-')}_${(inv.clientName || 'invoice').replace(/[^a-z0-9]/gi, '_')}.pdf`);
  const downloadChallanPDF = (ch) => buildChallanPDFDoc(ch, pdfDeps).save(`${String(ch.challanNumber).replace(/\\/g, '-')}_${(ch.clientName || 'challan').replace(/[^a-z0-9]/gi, '_')}.pdf`);

  const blankInvoice = (extra = {}) => ({
    clientId: '', date: todayYmd(), dueDate: addDaysYmd(todayYmd(), Number(invCfg.dueDays) || 15), gstRate: 18, ledgerKind: 'sales',
    items: [{ description: '', hsn: '998713', qty: 1, rate: '' }], notes: '', terms: invCfg.terms, bankDetails: invCfg.bankDetails, ...extra
  });
  const blankChallan = (extra = {}) => ({
    clientId: '', date: todayYmd(), purpose: 'Supply of goods', deliveryAddress: '', vehicleNo: '', transporter: '', invoiceId: '',
    items: [{ description: '', serialNo: '', qty: 1, unit: 'Nos' }], notes: '', subject: '', terms: challanTermsFor('Supply of goods'), ...extra
  });

  // House rule: SALES quotations go straight to an invoice; RENTAL quotations go to a delivery challan (rent is invoiced monthly).
  const isRentalQuotation = (qt) => ['rental', 'laptop_rental'].includes(qt.quotationType);
  const quotationSubjectText = (qt) => {
    const equipmentLabel = (QUOTATION_EQUIPMENT_TYPES.find(t => t.value === qt.printerType)?.label || 'Goods').replace(/^\S+\s/, '');
    const phrase = (QUOTATION_SUBJECT_PHRASE[qt.quotationType] || QUOTATION_SUBJECT_PHRASE.service)(equipmentLabel);
    return `${phrase}${qt.printerModel ? ' - ' + qt.printerModel : ''}${qt.printerSerial ? ' (S/N: ' + qt.printerSerial + ')' : ''}`;
  };
  const challanTermsFor = (purpose) => [
    'Goods delivered as listed above in good condition; please check them on receipt, then sign and return the duplicate copy.',
    'This challan is not an invoice.',
    ...(/rental/i.test(purpose || '') ? ['Goods supplied on rental remain the property of the company and must be returned on termination of the agreement.'] : [])
  ].join('\n');

  // Prefill an invoice / delivery challan from a quotation (any type: sale, service, licence, rental ...)
  const invoiceFromQuotation = (qt) => {
    const c = clients.find(x => x.id === qt.clientId) || findClientByName(clients, qt.clientName);
    return blankInvoice({
      clientId: c?.id || '', clientName: c?.name || qt.clientName, clientAddress: c?.address || qt.clientAddress || '', clientGSTIN: c?.gstin || qt.clientGSTIN || '', clientEmail: c?.email || '',
      quotationId: qt.id, source: 'quotation', subject: `Invoice for ${quotationSubjectText(qt)}`, notes: `Against quotation ${qt.quoteNumber}`, ledgerKind: LEDGER_KIND_FOR_QUOTATION[qt.quotationType] || 'sales',
      incomeLedger: suggestIncomeLedger(qt, (tallyLedgerLists?.sales || []).map(x => x.name)),
      items: (qt.items || []).map(i => ({ description: i.description, hsn: i.hsn || '998713', qty: i.qty, rate: i.rate }))
    });
  };
  const challanFromQuotation = (qt) => {
    const c = clients.find(x => x.id === qt.clientId) || findClientByName(clients, qt.clientName);
    return blankChallan({
      clientId: c?.id || '', clientName: c?.name || qt.clientName, clientAddress: c?.address || qt.clientAddress || '', clientGSTIN: c?.gstin || qt.clientGSTIN || '',
      deliveryAddress: c?.address || qt.clientAddress || '', quotationId: qt.id, quotationNumber: qt.quoteNumber, purpose: isRentalQuotation(qt) ? 'Supply on rental' : 'Supply of goods',
      subject: `Delivery Challan - ${isRentalQuotation(qt) ? 'Supply on rental' : 'Supply of goods'}: ${quotationSubjectText(qt)}`, terms: challanTermsFor(isRentalQuotation(qt) ? 'rental' : ''), notes: '',
      items: (qt.items || []).map(i => ({ description: i.description, serialNo: '', qty: i.qty || 1, unit: 'Nos' }))
    });
  };
  const openProformaFromQuotation = (qt) => { setInvForm({ ...invoiceFromQuotation(qt), docType: 'proforma', subject: `Proforma Invoice for ${quotationSubjectText(qt)}` }); setInvTab('proforma'); setTallyTab('proforma'); setCurrentView('invoices'); };
  const convertProformaToInvoice = (p) => {
    setInvForm(blankInvoice({
      clientId: p.clientId, clientName: p.clientName, clientAddress: p.clientAddress, clientGSTIN: p.clientGSTIN, clientEmail: p.clientEmail,
      items: (p.items || []).map(i => ({ ...i })), gstRate: p.gstRate, ledgerKind: p.ledgerKind || 'sales', quotationId: p.quotationId, proformaId: p.id, source: 'proforma',
      subject: String(p.subject || '').replace(/proforma\s+invoice/i, 'Invoice'), notes: p.notes || `Against proforma invoice ${p.invoiceNumber}`, bankDetails: p.bankDetails || invCfg.bankDetails, terms: p.terms || invCfg.terms
    }));
    setInvTab('invoices'); setTallyTab('invoices');
  };
  const openInvoiceFromQuotation = (qt) => { setInvForm(invoiceFromQuotation(qt)); setInvTab('invoices'); setTallyTab('invoices'); setCurrentView('invoices'); };
  const openChallanFromQuotation = (qt) => { setChallanForm(challanFromQuotation(qt)); setInvTab('challans'); setTallyTab('challans'); setCurrentView('invoices'); };

  const createInvoiceFromBilling = async (rows) => {
    const todo = rows.filter(r => r.voucher && !invoices.some(i => i.billingKey === r.key));
    if (todo.length === 0) { alert('Nothing to invoice (already invoiced or nothing billable).'); return; }
    if (!window.confirm(`Create ${todo.length} invoice(s) for ${tallyMonth}?${tallyAutoReady() && tallyCfg.autoSync.invoices ? '\n\nThey will be sent to Tally automatically.' : ''}`)) return;
    let made = 0;
    for (const r of todo) {
      const client = r.client;
      const inv = {
        id: `INV${Date.now()}${made}`, companyId: currentCompany, invoiceNumber: await generateDocNumber('invoices', 'invoiceNumber', 'INV'),
        date: todayYmd(), dueDate: addDaysYmd(todayYmd(), Number(invCfg.dueDays) || 15), clientId: client.id, clientName: client.name,
        clientAddress: client.address || '', clientGSTIN: client.gstin || '', clientEmail: client.email || '',
        items: r.lines.map(l => ({ description: l.description, hsn: '997319', qty: 1, rate: l.amount })), gstRate: 18, ledgerKind: 'rental',
        subject: `Invoice for Equipment Rental and usage charges - ${tallyMonth}`, notes: `Rental and usage charges - ${tallyMonth}`, terms: invCfg.terms, bankDetails: invCfg.bankDetails, status: 'unpaid',
        source: 'billing', billingKey: r.key, createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown'
      };
      if (invoiceNeedsApproval(inv)) {
        if (canApproveInvoices()) { inv.approvalStatus = 'approved'; inv.approvedBy = currentUser?.email || 'Unknown'; inv.approvedAt = new Date().toISOString(); }
        else { inv.approvalStatus = 'pending'; inv.submittedBy = currentUser?.email || 'Unknown'; inv.submittedAt = new Date().toISOString(); }
      }
      await setDoc(doc(db, 'invoices', inv.id), inv);
      await logAudit('CREATE', 'Invoice', inv.id, `${inv.invoiceNumber} - ${inv.clientName}${invApproval(inv) === 'pending' ? ' (sent for approval)' : ''}`, inv);
      if (invApproval(inv) === 'approved') await autoSyncInvoice(inv);
      made++;
    }
    alert(`✅ Created ${made} invoice(s). Find them under Invoices.`);
  };

  const InvoicesView = ({ embedded = false } = {}) => {
    // Inside the Tally page the documents stay read-only for anyone without Tally write access
    const canEdit = hasPermission('canManageInvoices') && (!embedded || canWriteTally());
    const canCreate = canCreateIn('invoices') && (!embedded || canWriteTally());
    const today = todayYmd();
    const q = invSearch.trim().toLowerCase();
    const money0 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const ownState = 'Karnataka';
    const tallyLocked = (inv) => ['done', 'processing'].includes(tallyRefStatus[`invoice:${inv.id}`]);

    const rowsInv = invoices.map(i => ({ i, t: invoiceTotals(i, ownState), overdue: i.status === 'unpaid' && i.dueDate && i.dueDate < today }))
      .filter(r => (invFilter === 'all') || (invFilter === 'overdue' ? r.overdue : invFilter === 'approval' ? invApproval(r.i) === 'pending' : r.i.status === invFilter))
      .filter(r => !q || [r.i.invoiceNumber, r.i.clientName, r.i.notes].some(v => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => String(b.i.date).localeCompare(String(a.i.date)) || String(b.i.invoiceNumber).localeCompare(String(a.i.invoiceNumber), undefined, { numeric: true }));
    const unpaidSum = invoices.filter(i => i.status === 'unpaid').reduce((s, i) => s + invoiceTotals(i, ownState).total, 0);
    const overdueSum = invoices.filter(i => i.status === 'unpaid' && i.dueDate && i.dueDate < today).reduce((s, i) => s + invoiceTotals(i, ownState).total, 0);
    const monthPrefix = today.slice(0, 7);
    const paidMonth = invoices.filter(i => i.status === 'paid' && String(i.paidDate || '').startsWith(monthPrefix)).reduce((s, i) => s + invoiceTotals(i, ownState).total, 0);

    const f = invForm;
    const setF = (patch) => setInvForm({ ...f, ...patch });
    const setItem = (idx, patch) => setF({ items: f.items.map((it, k) => (k === idx ? { ...it, ...patch } : it)) });
    const formTotals = f ? invoiceTotals({ items: f.items, gstRate: f.gstRate, clientGSTIN: (clients.find(c => c.id === f.clientId) || {}).gstin }, ownState) : null;

    const pickClient = (id) => {
      const c = clients.find(x => x.id === id);
      setF({ clientId: id, clientName: c?.name || '', clientAddress: c?.address || '', clientGSTIN: c?.gstin || '', clientEmail: c?.email || '' });
    };
    const saveInvoice = async () => {
      if (!f.clientId) { alert('❌ Please select a client.'); return; }
      const valid = f.items.filter(i => i.description && i.rate !== '' && parseFloat(i.rate) > 0);
      if (valid.length === 0) { alert('❌ Add at least one line with a description and a rate.'); return; }
      const lockIssues = priceLockIssues(f.clientId, f.clientName, f.items);
      if (lockIssues.length) { alert('🔒 Price lock:\n\n' + lockIssues.join('\n') + '\n\nOnly the super admin can change locked prices or add other items.'); return; }
      const isProforma = f.docType === 'proforma';
      const coll = isProforma ? 'proformas' : 'invoices';
      setInvSaving(true);
      try {
        const isNew = !f.id;
        const rec = {
          ...f, items: valid.map(i => ({ ...i, qty: parseFloat(i.qty) || 1, rate: parseFloat(i.rate) })),
          id: f.id || `${isProforma ? 'PRF' : 'INV'}${Date.now()}`, companyId: f.companyId || currentCompany,
          invoiceNumber: f.invoiceNumber || await generateDocNumber(coll, 'invoiceNumber', isProforma ? 'PI' : 'INV'),
          status: f.status || (isProforma ? 'open' : 'unpaid'), source: f.source || 'manual',
          createdAt: f.createdAt || new Date().toISOString(), createdBy: f.createdBy || currentUser?.email || 'Unknown'
        };
        // Approval step (tax invoices only). Approvers' own invoices are approved as they save; anyone else's
        // new or edited invoice waits for an approver. An invoice already waiting stays as it is.
        if (!isProforma && invoiceNeedsApproval(rec)) {
          const nowIso = new Date().toISOString();
          const prevApproval = f.id ? invApproval(f) : 'new';
          if (canApproveInvoices() && (prevApproval === 'new' || prevApproval === 'approved')) {
            rec.approvalStatus = 'approved'; rec.approvedBy = f.approvedBy || currentUser?.email || 'Unknown'; rec.approvedAt = f.approvedAt || nowIso; rec.rejectionReason = '';
          } else if (prevApproval !== 'pending') {
            rec.approvalStatus = 'pending'; rec.submittedBy = currentUser?.email || 'Unknown'; rec.submittedAt = nowIso;
            rec.approvedBy = ''; rec.approvedAt = ''; rec.rejectionReason = '';
          }
        }
        await setDoc(doc(db, coll, rec.id), rec);
        await logAudit(isNew ? 'CREATE' : 'UPDATE', isProforma ? 'Proforma' : 'Invoice', rec.id, `${rec.invoiceNumber} - ${rec.clientName}${!isProforma && invApproval(rec) === 'pending' ? ' (sent for approval)' : ''}`, rec);
        if (!isProforma && isNew) {
          for (const li of rec.items.filter(i => i.stockMoveId)) { try { await setDoc(doc(db, 'stockMoves', li.stockMoveId), { billedTo: rec.invoiceNumber, billedAt: new Date().toISOString() }, { merge: true }); } catch (e) { console.error(e); } }
        }
        if (isNew) { if (isProforma) await autoSyncProforma(rec); else if (invApproval(rec) === 'approved') await autoSyncInvoice(rec); }
        if (!isProforma && isNew && rec.proformaId) {
          await setDoc(doc(db, 'proformas', rec.proformaId), { status: 'converted', convertedInvoiceId: rec.id, convertedInvoiceNumber: rec.invoiceNumber }, { merge: true });
        }
        setInvForm(null);
        alert(`✅ ${isProforma ? 'Proforma invoice' : 'Invoice'} ${rec.invoiceNumber} saved.${!isProforma && invApproval(rec) === 'pending' ? '\n\n🕐 Sent for approval. It can go to Tally once an approver approves it.' : (isNew && tallyAutoReady() && (isProforma ? tallyCfg.autoSync.proformas : tallyCfg.autoSync.invoices) ? '\n\nSent to the Tally queue' + (isProforma ? ' (as a non-posting Memorandum).' : '.') : '')}`);
      } catch (err) { alert('Could not save: ' + err.message); }
      setInvSaving(false);
    };
    const setProformaStatus = async (p, status) => {
      try { await setDoc(doc(db, 'proformas', p.id), { status }, { merge: true }); await logAudit('UPDATE', 'Proforma', p.id, `${p.invoiceNumber} marked ${status}`, {}); }
      catch (err) { alert('Could not update: ' + err.message); }
    };
    const deleteProforma = async (p) => {
      if (!window.confirm(`Delete proforma invoice ${p.invoiceNumber}? Its number will be free for the next one.`)) return;
      try { await logAudit('DELETE', 'Proforma', p.id, `${p.invoiceNumber} - ${p.clientName}`, p); await deleteDoc(doc(db, 'proformas', p.id)); }
      catch (err) { alert('Could not delete: ' + err.message); }
    };
    const setStatus = async (inv, status) => {
      try { await setDoc(doc(db, 'invoices', inv.id), { status, paidDate: status === 'paid' ? today : '' }, { merge: true }); await logAudit('UPDATE', 'Invoice', inv.id, `${inv.invoiceNumber} marked ${status}`, {}); }
      catch (err) { alert('Could not update: ' + err.message); }
    };
    const approveInvoice = async (inv) => {
      if (!canApproveInvoices()) { alert('You do not have permission to approve invoices.'); return; }
      try {
        const patch = { approvalStatus: 'approved', approvedBy: currentUser?.email || 'Unknown', approvedAt: new Date().toISOString(), rejectionReason: '' };
        await setDoc(doc(db, 'invoices', inv.id), patch, { merge: true });
        await logAudit('UPDATE', 'Invoice', inv.id, `${inv.invoiceNumber} approved`, {});
        if (!tallyRefStatus[`invoice:${inv.id}`]) await autoSyncInvoice({ ...inv, ...patch });
      } catch (err) { alert('Could not approve: ' + err.message); }
    };
    const rejectInvoice = async (inv) => {
      if (!canApproveInvoices()) { alert('You do not have permission to reject invoices.'); return; }
      const reason = window.prompt(`Reject ${inv.invoiceNumber}?\n\nEnter the reason. The person who created it will see it.`, '');
      if (reason === null) return;
      try {
        const patch = { approvalStatus: 'rejected', rejectionReason: reason.trim() || 'No reason given', rejectedBy: currentUser?.email || 'Unknown', rejectedAt: new Date().toISOString(), approvedBy: '', approvedAt: '' };
        await setDoc(doc(db, 'invoices', inv.id), patch, { merge: true });
        await logAudit('UPDATE', 'Invoice', inv.id, `${inv.invoiceNumber} rejected: ${patch.rejectionReason}`, {});
      } catch (err) { alert('Could not reject: ' + err.message); }
    };
    const deleteInvoice = async (inv) => {
      if (tallyLocked(inv)) { alert('This invoice is already in Tally. Cancel it instead (and cancel the voucher in Tally).'); return; }
      if (!window.confirm(`Delete invoice ${inv.invoiceNumber}? Its number will be free for the next invoice.`)) return;
      try { await logAudit('DELETE', 'Invoice', inv.id, `${inv.invoiceNumber} - ${inv.clientName}`, inv); await deleteDoc(doc(db, 'invoices', inv.id)); }
      catch (err) { alert('Could not delete: ' + err.message); }
    };
    const sendProformaToTally = async (p) => { if (!tallyAutoReady()) { alert('Set the Tally company in Tally > Settings first.'); return; } await autoSyncProforma(p); alert('✅ Queued for Tally.'); };
    const sendChallanToTally = async (c) => { if (!tallyAutoReady()) { alert('Set the Tally company in Tally > Settings first.'); return; } alert((await autoSyncChallan(c)) ? '✅ Queued for Tally.' : 'This challan has no value to post. Fetch it from a quotation or edit it and type a value.'); };
    const sendToTally = async (inv) => {
      if (invApproval(inv) !== 'approved') { alert('This invoice has not been approved yet. An approver must approve it before it can go to Tally.'); return; }
      if (!tallyAutoReady()) { alert('Set the Tally company in Tally > Settings first.'); return; }
      await autoSyncInvoice(inv); alert('✅ Queued for Tally.');
    };

    const cf = challanForm;
    const setCf = (patch) => setChallanForm({ ...cf, ...patch });
    const setCItem = (idx, patch) => setCf({ items: cf.items.map((it, k) => (k === idx ? { ...it, ...patch } : it)) });
    const pickChallanClient = (id) => {
      const c = clients.find(x => x.id === id);
      setCf({ clientId: id, clientName: c?.name || '', clientAddress: c?.address || '', clientGSTIN: c?.gstin || '', deliveryAddress: cf.deliveryAddress || c?.address || '' });
    };
    const saveChallan = async () => {
      if (!cf.clientId) { alert('❌ Please select a client.'); return; }
      const valid = cf.items.filter(i => i.description && parseFloat(i.qty) > 0);
      if (valid.length === 0) { alert('❌ Add at least one item with a quantity.'); return; }
      setInvSaving(true);
      try {
        const isNew = !cf.id;
        const linked = invoices.find(i => i.id === cf.invoiceId);
        const rec = {
          ...cf, items: valid.map(i => ({ ...i, qty: parseFloat(i.qty) })), id: cf.id || `DC${Date.now()}`, companyId: cf.companyId || currentCompany,
          challanNumber: cf.challanNumber || await generateDocNumber('challans', 'challanNumber', 'DC'), invoiceNumber: linked?.invoiceNumber || '', quotationNumber: cf.quotationNumber || '',
          status: cf.status || 'issued', createdAt: cf.createdAt || new Date().toISOString(), createdBy: cf.createdBy || currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'challans', rec.id), rec);
        await logAudit(isNew ? 'CREATE' : 'UPDATE', 'Challan', rec.id, `${rec.challanNumber} - ${rec.clientName}`, rec);
        const sent = isNew ? await autoSyncChallan(rec) : false;
        setChallanForm(null);
        alert(`✅ Delivery challan ${rec.challanNumber} saved.${sent ? '\n\nSent to the Tally queue (as a non-posting Memorandum).' : isNew && tallyAutoReady() && tallyCfg.autoSync.challans ? '\n\nNot sent to Tally: it has no value (fetch it from a quotation or type a value).' : ''}`);
      } catch (err) { alert('Could not save: ' + err.message); }
      setInvSaving(false);
    };

    const rowsCh = challans.filter(c => !q || [c.challanNumber, c.clientName, c.vehicleNo, c.purpose].some(v => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.challanNumber).localeCompare(String(a.challanNumber), undefined, { numeric: true }));

    const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm';
    const Pill = ({ children, cls }) => <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          {embedded ? <p className="text-xs text-slate-500">Numbered PDFs; invoices go to Tally automatically{tallyAutoReady() && tallyCfg.autoSync.invoices ? '' : ' (switch on in Settings)'}. Delivery challans stay in the portal.</p> : (
          <div>
            <h2 className="text-2xl font-bold">Invoices &amp; Delivery Challans</h2>
            <p className="text-xs text-slate-500">{COMPANIES[currentCompany]?.name} · numbered PDFs; invoices go to Tally automatically{tallyAutoReady() && tallyCfg.autoSync.invoices ? '' : ' (switch on in Tally > Settings)'}</p>
          </div>)}
          {canCreate && invTab === 'proforma' && !f && <button onClick={() => setInvForm(blankInvoice({ docType: 'proforma', ledgerKind: 'sales' }))} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Plus size={18} /> New proforma invoice</button>}
          {canCreate && invTab === 'invoices' && !f && <button onClick={() => setInvForm(blankInvoice())} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Plus size={18} /> New invoice</button>}
          {canCreate && invTab === 'challans' && !cf && <button onClick={() => setChallanForm(blankChallan())} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Plus size={18} /> New challan</button>}
        </div>

        {!embedded && <div className="flex gap-1 border-b border-slate-200">
          {[['invoices', '🧾 Invoices'], ['proforma', '📝 Proforma invoices'], ['challans', '🚚 Delivery challans'], ...(userRole === 'super_admin' ? [['settings', '⚙️ Settings']] : [])].map(([k, label]) => (
            <button key={k} onClick={() => setInvTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${invTab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>}

        {invTab !== 'settings' && <input type="text" placeholder="🔍 Search number, client..." value={invSearch} onChange={(e) => setInvSearch(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-base" />}

        {(invTab === 'invoices' || invTab === 'proforma') && f && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between"><p className="font-semibold">{f.id ? `Edit ${f.invoiceNumber}` : (f.docType === 'proforma' ? 'New proforma invoice' : 'New invoice')}</p>
              {!f.id && quotations.length > 0 && (
                <select value="" onChange={(e) => { const qt = quotations.find(x => x.id === e.target.value); if (qt) setInvForm(invoiceFromQuotation(qt)); }}
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm max-w-[260px]"><option value="">↳ Fill from a quotation…</option>
                  <optgroup label="Sales / service / licence quotations">{quotations.filter(x => !isRentalQuotation(x)).slice(0, 80).map(x => <option key={x.id} value={x.id}>{x.quoteNumber} · {x.clientName}</option>)}</optgroup>
                  <optgroup label="Rental quotations (usually a delivery challan instead)">{quotations.filter(x => isRentalQuotation(x)).slice(0, 40).map(x => <option key={x.id} value={x.id}>{x.quoteNumber} · {x.clientName}</option>)}</optgroup></select>)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Client *</label>
                <select value={f.clientId || ''} onChange={(e) => pickClient(e.target.value)} className={inputCls}><option value="">Select client…</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div><label className="text-xs text-slate-500">Invoice date</label><input type="date" value={f.date} onChange={(e) => setF({ date: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-slate-500">{f.docType === 'proforma' ? 'Valid until' : 'Due date'}</label><input type="date" value={f.dueDate} onChange={(e) => setF({ dueDate: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-slate-500">GST %</label><input type="number" value={f.gstRate} onChange={(e) => setF({ gstRate: e.target.value })} className={inputCls} /></div>
              {f.docType !== 'proforma' && <div className="md:col-span-2"><label className="text-xs text-slate-500">Books under (Tally income ledger)</label>
                <select value={f.ledgerKind} onChange={(e) => setF({ ledgerKind: e.target.value })} className={inputCls}>{INVOICE_LEDGER_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}</select></div>}
              {f.docType !== 'proforma' && (tallyLedgerLists?.sales || []).length > 0 && <div className="md:col-span-4"><label className="text-xs text-slate-500">Exact Tally sales ledger (optional – overrides the above)</label>
                <select value={f.incomeLedger || ''} onChange={(e) => setF({ incomeLedger: e.target.value })} className={inputCls}><option value="">— use the ledger for the type above —</option>{tallyLedgerLists.sales.map(x => <option key={x.name} value={x.name}>{x.name}</option>)}</select></div>}
            </div>
            {f.clientId && <p className="text-xs text-slate-400">{f.clientAddress || 'No address on the client record'} · GSTIN {f.clientGSTIN || '-'} · {formTotals.interstate ? 'IGST (other state)' : 'CGST + SGST'}</p>}
            {(() => { const lc = priceLockOf(f.clientId, f.clientName); return lc ? (
              <div className="px-3 py-2 bg-amber-50 text-xs text-amber-800 rounded-lg">🔒 {lc.name} has a locked price list ({lc.priceList.filter(e => e.item).length} items). Pick items from the list; rates fill in automatically{userRole === 'super_admin' ? ' (you can override as super admin).' : ' and cannot be changed.'}
                <datalist id="plist-i">{lc.priceList.filter(e => e.item).map(e => <option key={e.item} value={e.item}>{`₹${e.rate}`}</option>)}</datalist></div>) : null; })()}
            <div className="space-y-2">
              {f.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input className={`${inputCls} col-span-12 md:col-span-6`} placeholder="Description *" list={priceLockOf(f.clientId, f.clientName) ? 'plist-i' : undefined} value={it.description} onChange={(e) => { const lc = priceLockOf(f.clientId, f.clientName); const hit = lc ? priceMatch(lc, e.target.value) : null; setItem(idx, { description: e.target.value, ...(hit ? { rate: String(hit.rate) } : {}) }); }} />
                  <input className={`${inputCls} col-span-4 md:col-span-2`} placeholder="HSN/SAC" value={it.hsn} onChange={(e) => setItem(idx, { hsn: e.target.value })} />
                  <input className={`${inputCls} col-span-3 md:col-span-1`} type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} />
                  <input className={`${inputCls} col-span-4 md:col-span-2 read-only:bg-slate-100 read-only:text-slate-500`} type="number" placeholder="Rate *" value={it.rate} readOnly={userRole !== 'super_admin' && !!priceLockOf(f.clientId, f.clientName) && !!priceMatch(priceLockOf(f.clientId, f.clientName), it.description)} onChange={(e) => setItem(idx, { rate: e.target.value })} />
                  <button onClick={() => setF({ items: f.items.filter((_, k) => k !== idx) })} disabled={f.items.length === 1} className="col-span-1 text-red-500 disabled:opacity-30"><X size={16} /></button>
                </div>
              ))}
              <button onClick={() => setF({ items: [...f.items, { description: '', hsn: '998713', qty: 1, rate: '' }] })} className="text-sm text-brand-600 hover:text-brand-700 font-medium">+ Add line</button>
              {f.clientId && canSeeStock() && stockMoveLines(f.clientName).length > 0 && (
                <select value="" onChange={(e) => { const x = stockMoveLines(f.clientName).find(y => y.move.id === e.target.value); if (!x) return; const blank = f.items.length === 1 && !f.items[0].description && !f.items[0].rate; setF({ items: [...(blank ? [] : f.items), { ...x.line }] }); }} className="ml-3 border border-slate-300 rounded-lg px-2 py-1 text-sm max-w-[280px]">
                  <option value="">📦 Add from stock supplied to this client…</option>
                  {stockMoveLines(f.clientName).filter(y => !f.items.some(i => i.stockMoveId === y.move.id)).map(y => <option key={y.move.id} value={y.move.id}>{y.move.date} · {y.move.itemName} × {y.move.qty}</option>)}
                </select>
              )}
            </div>
            <p className="text-sm text-right text-slate-600">Sub total {money0(formTotals.net)} · GST {money0(formTotals.cgst + formTotals.sgst + formTotals.igst)} · <strong>Total {money0(formTotals.total)}</strong></p>
            <input className={inputCls} placeholder="Subject (printed under Bill To, e.g. Invoice for Microsoft 365 licences)" value={f.subject || ''} onChange={(e) => setF({ subject: e.target.value })} />
            <details className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">More details printed on the invoice (order no., dispatch, ship-to) - optional</summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <input className={inputCls} placeholder="Mode / terms of payment (e.g. 15 Days)" value={f.modeOfPayment || ''} onChange={(e) => setF({ modeOfPayment: e.target.value })} />
                <input className={inputCls} placeholder="Delivery note no." value={f.deliveryNote || ''} onChange={(e) => setF({ deliveryNote: e.target.value })} />
                <input className={inputCls} placeholder="Buyer's order / PO no." value={f.buyerOrderNo || ''} onChange={(e) => setF({ buyerOrderNo: e.target.value })} />
                <input className={inputCls} type="date" title="Order date" value={f.buyerOrderDate || ''} onChange={(e) => setF({ buyerOrderDate: e.target.value })} />
                <input className={inputCls} placeholder="Dispatched through" value={f.dispatchedThrough || ''} onChange={(e) => setF({ dispatchedThrough: e.target.value })} />
                <input className={inputCls} placeholder="Destination" value={f.destination || ''} onChange={(e) => setF({ destination: e.target.value })} />
                <input className={`${inputCls} md:col-span-2`} placeholder="Terms of delivery" value={f.termsOfDelivery || ''} onChange={(e) => setF({ termsOfDelivery: e.target.value })} />
                <input className={inputCls} placeholder="Consignee (ship to) name - if different from the client" value={f.shipToName || ''} onChange={(e) => setF({ shipToName: e.target.value })} />
                <textarea rows="2" className={inputCls} placeholder="Consignee (ship to) address" value={f.shipToAddress || ''} onChange={(e) => setF({ shipToAddress: e.target.value })} />
              </div>
            </details>
            <textarea rows="2" className={inputCls} placeholder="Notes" value={f.notes || ''} onChange={(e) => setF({ notes: e.target.value })} />
            <textarea rows="3" className={inputCls} placeholder="Terms & conditions" value={f.terms || ''} onChange={(e) => setF({ terms: e.target.value })} />
            <textarea rows="2" className={inputCls} placeholder="Bank details (shown on the invoice)" value={f.bankDetails || ''} onChange={(e) => setF({ bankDetails: e.target.value })} />
            <div className="flex gap-2"><button onClick={saveInvoice} disabled={invSaving} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-60">{invSaving ? 'Saving…' : 'Save invoice'}</button>
              <button onClick={() => setInvForm(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button></div>
          </div>
        )}

        {invTab === 'invoices' && !f && (
          <>
            <div className="grid grid-cols-3 gap-3">
              {[['Outstanding', unpaidSum, ''], ['Overdue', overdueSum, overdueSum ? 'text-red-600' : ''], ['Paid this month', paidMonth, 'text-emerald-700']].map(([l, v, c]) => (
                <div key={l} className="bg-white rounded-xl border border-slate-100 p-3"><p className="text-xs text-slate-500">{l}</p><p className={`text-lg font-bold ${c || 'text-slate-900'}`}>{money0(v)}</p></div>))}
            </div>
            <div className="flex gap-2 flex-wrap">
              {[['all', 'All'], ['approval', `Awaiting approval (${invoices.filter(x => invApproval(x) === 'pending').length})`], ['unpaid', 'Unpaid'], ['overdue', 'Overdue'], ['paid', 'Paid'], ['cancelled', 'Cancelled']].map(([k, label]) => (
                <button key={k} onClick={() => setInvFilter(k)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${invFilter === k ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{label}</button>))}
            </div>
            {rowsInv.length === 0 ? <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500">{invoices.length === 0 ? 'No invoices yet. Click "New invoice", or create them from Tally > Rental billing.' : 'Nothing here.'}</div> : (
              <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
                {rowsInv.map(({ i, t, overdue }) => {
                  const ts = tallyRefStatus[`invoice:${i.id}`];
                  return (
                    <div key={i.id} className="p-3 space-y-2">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="min-w-0 flex-1 basis-56">
                          <p className="font-semibold text-slate-900 truncate"><span className="font-mono text-brand-700">{i.invoiceNumber}</span> · {i.clientName}</p>
                          <p className="text-xs text-slate-400 truncate">{fmtDMY(i.date)}{i.dueDate ? ` · due ${fmtDMY(i.dueDate)}` : ''}{i.notes ? ` · ${i.notes}` : ''}</p>
                          {invApproval(i) === 'pending' && i.submittedBy ? <p className="text-xs text-amber-700 truncate">Submitted by {i.submittedBy} for approval</p> : null}
                          {invApproval(i) === 'rejected' ? <p className="text-xs text-red-600">Rejected{i.rejectedBy ? ` by ${i.rejectedBy}` : ''}: {i.rejectionReason || 'no reason given'}. Edit and save to send it for approval again.</p> : null}
                          {invApproval(i) === 'approved' && i.approvedBy ? <p className="text-xs text-slate-400 truncate">Approved by {i.approvedBy}</p> : null}
                        </div>
                        <div className="text-right"><p className="font-semibold">{money0(t.total)}</p>
                          <div className="flex gap-1 justify-end mt-0.5">
                            {i.status === 'paid' ? <Pill cls="bg-emerald-100 text-emerald-700">Paid</Pill> : i.status === 'cancelled' ? <Pill cls="bg-slate-200 text-slate-600">Cancelled</Pill> : overdue ? <Pill cls="bg-red-100 text-red-700">Overdue</Pill> : <Pill cls="bg-amber-100 text-amber-700">Unpaid</Pill>}
                            {invApproval(i) === 'pending' ? <Pill cls="bg-amber-100 text-amber-800">Awaiting approval</Pill> : invApproval(i) === 'rejected' ? <Pill cls="bg-red-100 text-red-700">Rejected</Pill> : null}
                            {ts ? <Pill cls={TALLY_STATUS[ts]?.cls || ''}>{`Tally: ${TALLY_STATUS[ts]?.label || ts}`}</Pill> : null}
                          </div></div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <button onClick={() => downloadInvoicePDF(i)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">{invApproval(i) === 'approved' ? 'PDF' : 'PDF (draft)'}</button>
                        {canApproveInvoices() && invApproval(i) === 'pending' && <button onClick={() => approveInvoice(i)} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium">✓ Approve</button>}
                        {canApproveInvoices() && invApproval(i) === 'pending' && <button onClick={() => rejectInvoice(i)} className="text-xs px-2.5 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 font-medium">✕ Reject</button>}
                        {canCreate && <button onClick={() => { setChallanForm(blankChallan({ clientId: i.clientId, clientName: i.clientName, clientAddress: i.clientAddress, clientGSTIN: i.clientGSTIN, deliveryAddress: i.clientAddress, invoiceId: i.id, items: i.items.map(it => ({ description: it.description, serialNo: '', qty: it.qty, unit: 'Nos' })) })); setInvTab('challans'); setTallyTab('challans'); }} className="text-xs px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100">🚚 Challan</button>}
                        {canEdit && i.status === 'unpaid' && invApproval(i) === 'approved' && <button onClick={() => setStatus(i, 'paid')} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">✓ Mark paid</button>}
                        {canEdit && i.status === 'paid' && <button onClick={() => setStatus(i, 'unpaid')} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Mark unpaid</button>}
                        {canEdit && i.status !== 'cancelled' && !tallyLocked(i) && <button onClick={() => setInvForm({ ...i, items: i.items.map(x => ({ ...x })) })} className="text-xs px-2.5 py-1 rounded-lg text-slate-600 hover:bg-slate-100">Edit</button>}
                        {canCreate && tallyAutoReady() && invApproval(i) === 'approved' && (!ts || ts === 'error') && i.status !== 'cancelled' && <button onClick={() => sendToTally(i)} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100">{ts === 'error' ? 'Retry Tally' : 'Send to Tally'}</button>}
                        {canEdit && i.status !== 'cancelled' && <button onClick={() => { if (window.confirm(`Cancel ${i.invoiceNumber}?${ts === 'done' ? '\n\nIt is already in Tally - cancel the voucher there too.' : ''}`)) setStatus(i, 'cancelled'); }} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>}
                        {userRole === 'super_admin' && !tallyLocked(i) && <button onClick={() => deleteInvoice(i)} className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50" title="Delete"><Trash2 size={13} /></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {invTab === 'proforma' && !f && (
          (() => {
            const rowsPf = proformas.filter(p => !q || [p.invoiceNumber, p.clientName, p.notes].some(v => String(v || '').toLowerCase().includes(q)))
              .sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.invoiceNumber).localeCompare(String(a.invoiceNumber), undefined, { numeric: true }));
            return rowsPf.length === 0 ? <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500">No proforma invoices yet. Click "New proforma invoice", or use the 📝 Proforma button on a quotation.</div> : (
              <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
                {rowsPf.map(p => {
                  const t = invoiceTotals(p, ownState);
                  return (
                    <div key={p.id} className="p-3 space-y-2">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="min-w-0 flex-1 basis-56">
                          <p className="font-semibold text-slate-900 truncate"><span className="font-mono text-brand-700">{p.invoiceNumber}</span> · {p.clientName}</p>
                          <p className="text-xs text-slate-400 truncate">{fmtDMY(p.date)}{p.dueDate ? ` · valid until ${fmtDMY(p.dueDate)}` : ''}{p.notes ? ` · ${p.notes}` : ''}</p>
                        </div>
                        <div className="text-right"><p className="font-semibold">{money0(t.total)}</p>
                          <div className="flex gap-1 justify-end mt-0.5">
                            {p.status === 'converted' ? <Pill cls="bg-emerald-100 text-emerald-700">{`Invoiced${p.convertedInvoiceNumber ? ' · ' + p.convertedInvoiceNumber : ''}`}</Pill> : p.status === 'cancelled' ? <Pill cls="bg-slate-200 text-slate-600">Cancelled</Pill> : <Pill cls="bg-sky-100 text-sky-700">Open</Pill>}{tallyRefStatus[`proforma:${p.id}`] ? <Pill cls={TALLY_STATUS[tallyRefStatus[`proforma:${p.id}`]]?.cls || ''}>{`Tally: ${TALLY_STATUS[tallyRefStatus[`proforma:${p.id}`]]?.label || tallyRefStatus[`proforma:${p.id}`]}`}</Pill> : null}
                          </div></div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <button onClick={() => buildInvoiceDoc({ ...p, isProforma: true }).save(`${String(p.invoiceNumber).replace(/\\/g, '-')}_${(p.clientName || 'proforma').replace(/[^a-z0-9]/gi, '_')}.pdf`)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">PDF</button>
                        {canCreate && p.status === 'open' && <button onClick={() => convertProformaToInvoice(p)} className="text-xs px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 font-medium">🧾 Create tax invoice</button>}
                        {canEdit && p.status === 'open' && <button onClick={() => setInvForm({ ...p, docType: 'proforma', items: p.items.map(x => ({ ...x })) })} className="text-xs px-2.5 py-1 rounded-lg text-slate-600 hover:bg-slate-100">Edit</button>}
                        {canEdit && p.status === 'open' && <button onClick={() => { if (window.confirm(`Cancel ${p.invoiceNumber}?`)) setProformaStatus(p, 'cancelled'); }} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>}
                        {canEdit && p.status === 'cancelled' && <button onClick={() => setProformaStatus(p, 'open')} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Re-open</button>}
                        {canCreate && tallyAutoReady() && (!tallyRefStatus[`proforma:${p.id}`] || tallyRefStatus[`proforma:${p.id}`] === 'error') && p.status === 'open' && <button onClick={() => sendProformaToTally(p)} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium">Send to Tally</button>}
                        {userRole === 'super_admin' && p.status !== 'converted' && <button onClick={() => deleteProforma(p)} className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50" title="Delete"><Trash2 size={13} /></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}

        {invTab === 'challans' && cf && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2"><p className="font-semibold">{cf.id ? `Edit ${cf.challanNumber}` : 'New delivery challan'}</p>
              {!cf.id && quotations.length > 0 && (
                <select value="" onChange={(e) => { const qt = quotations.find(x => x.id === e.target.value); if (qt) setChallanForm(challanFromQuotation(qt)); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm max-w-[280px]">
                  <option value="">↳ Fetch from a quotation…</option>
                  <optgroup label="Rental quotations">{quotations.filter(x => isRentalQuotation(x)).slice(0, 60).map(x => <option key={x.id} value={x.id}>{x.quoteNumber} · {x.clientName}</option>)}</optgroup>
                  <optgroup label="Other quotations">{quotations.filter(x => !isRentalQuotation(x)).slice(0, 60).map(x => <option key={x.id} value={x.id}>{x.quoteNumber} · {x.clientName}</option>)}</optgroup>
                </select>)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Client *</label>
                <select value={cf.clientId || ''} onChange={(e) => pickChallanClient(e.target.value)} className={inputCls}><option value="">Select client…</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div><label className="text-xs text-slate-500">Date</label><input type="date" value={cf.date} onChange={(e) => setCf({ date: e.target.value })} className={inputCls} /></div>
              <div className="md:col-span-3"><label className="text-xs text-slate-500">Deliver to (address)</label><textarea rows="2" value={cf.deliveryAddress || ''} onChange={(e) => setCf({ deliveryAddress: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-slate-500">Reason</label>
                <select value={cf.purpose} onChange={(e) => setCf({ purpose: e.target.value, ...((!cf.terms || cf.terms === challanTermsFor(cf.purpose)) ? { terms: challanTermsFor(e.target.value) } : {}) })} className={inputCls}>{['Supply of goods', 'Supply on rental', 'Replacement', 'Return after repair', 'Demo / trial', 'Other'].map(p => <option key={p}>{p}</option>)}</select></div>
              <div><label className="text-xs text-slate-500">Vehicle no.</label><input value={cf.vehicleNo || ''} onChange={(e) => setCf({ vehicleNo: e.target.value })} className={inputCls} placeholder="KA 05 AB 1234" /></div>
              <div><label className="text-xs text-slate-500">Driver / transporter</label><input value={cf.transporter || ''} onChange={(e) => setCf({ transporter: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="space-y-2">
              {cf.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input className={`${inputCls} col-span-12 md:col-span-5`} placeholder="Item description *" value={it.description} onChange={(e) => setCItem(idx, { description: e.target.value })} />
                  <input className={`${inputCls} col-span-6 md:col-span-4`} placeholder="Serial / asset no." list="challan-assets" value={it.serialNo} onChange={(e) => setCItem(idx, { serialNo: e.target.value })} />
                  <input className={`${inputCls} col-span-3 md:col-span-1`} type="number" placeholder="Qty" value={it.qty} onChange={(e) => setCItem(idx, { qty: e.target.value })} />
                  <input className={`${inputCls} col-span-2 md:col-span-1`} placeholder="Unit" value={it.unit} onChange={(e) => setCItem(idx, { unit: e.target.value })} />
                  <button onClick={() => setCf({ items: cf.items.filter((_, k) => k !== idx) })} disabled={cf.items.length === 1} className="col-span-1 text-red-500 disabled:opacity-30"><X size={16} /></button>
                </div>
              ))}
              <datalist id="challan-assets">{printers.slice(0, 1500).map(p => <option key={p.id} value={p.assetNumber ? `${p.serialNumber} / ${p.assetNumber}` : p.serialNumber} />)}</datalist>
              <button onClick={() => setCf({ items: [...cf.items, { description: '', serialNo: '', qty: 1, unit: 'Nos' }] })} className="text-sm text-brand-600 hover:text-brand-700 font-medium">+ Add item</button>
            </div>
            <input className={inputCls} placeholder="Subject (printed under Deliver To)" value={cf.subject || ''} onChange={(e) => setCf({ subject: e.target.value })} />
            {!cf.quotationId && <div className="md:w-1/2"><label className="text-xs text-slate-500">Value for Tally (₹, before GST – optional)</label>
              <input type="number" className={inputCls} placeholder="Needed only to post this challan to Tally when it is not fetched from a quotation" value={cf.declaredValue || ''} onChange={(e) => setCf({ declaredValue: e.target.value })} /></div>}
            <textarea rows="2" className={inputCls} placeholder="Notes" value={cf.notes || ''} onChange={(e) => setCf({ notes: e.target.value })} />
            <textarea rows="3" className={inputCls} placeholder="Terms & conditions" value={cf.terms || ''} onChange={(e) => setCf({ terms: e.target.value })} />
            <p className="text-xs text-slate-400">Delivery challans stay in the portal - Tally's Delivery Note needs stock items, which this portal doesn't keep.</p>
            <div className="flex gap-2"><button onClick={saveChallan} disabled={invSaving} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-60">{invSaving ? 'Saving…' : 'Save challan'}</button>
              <button onClick={() => setChallanForm(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button></div>
          </div>
        )}

        {invTab === 'challans' && !cf && (
          rowsCh.length === 0 ? <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500">No delivery challans yet. Click "New challan", or use the 🚚 button on an invoice.</div> : (
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
              {rowsCh.map(c => (
                <div key={c.id} className="p-3 space-y-2">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1 basis-56">
                      <p className="font-semibold text-slate-900 truncate"><span className="font-mono text-brand-700">{c.challanNumber}</span> · {c.clientName}</p>
                      <p className="text-xs text-slate-400 truncate">{fmtDMY(c.date)} · {c.purpose}{c.vehicleNo ? ` · ${c.vehicleNo}` : ''}{c.invoiceNumber ? ` · invoice ${c.invoiceNumber}` : ''} · {(c.items || []).length} item(s)</p>
                    </div>
                    <div className="flex gap-1">{c.status === 'delivered' ? <Pill cls="bg-emerald-100 text-emerald-700">Delivered</Pill> : <Pill cls="bg-sky-100 text-sky-700">Issued</Pill>}{tallyRefStatus[`challan:${c.id}`] ? <Pill cls={TALLY_STATUS[tallyRefStatus[`challan:${c.id}`]]?.cls || ''}>{`Tally: ${TALLY_STATUS[tallyRefStatus[`challan:${c.id}`]]?.label || tallyRefStatus[`challan:${c.id}`]}`}</Pill> : null}</div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => downloadChallanPDF(c)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">PDF</button>
                    {canCreate && tallyAutoReady() && (!tallyRefStatus[`challan:${c.id}`] || tallyRefStatus[`challan:${c.id}`] === 'error') && <button onClick={() => sendChallanToTally(c)} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium">Send to Tally</button>}
                    {canEdit && c.status !== 'delivered' && <button onClick={async () => { await setDoc(doc(db, 'challans', c.id), { status: 'delivered', deliveredAt: new Date().toISOString() }, { merge: true }); }} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">✓ Mark delivered</button>}
                    {canEdit && <button onClick={() => setChallanForm({ ...c, items: c.items.map(x => ({ ...x })) })} className="text-xs px-2.5 py-1 rounded-lg text-slate-600 hover:bg-slate-100">Edit</button>}
                    {userRole === 'super_admin' && <button onClick={async () => { if (window.confirm(`Delete challan ${c.challanNumber}?`)) { await logAudit('DELETE', 'Challan', c.id, c.challanNumber, c); await deleteDoc(doc(db, 'challans', c.id)); } }} className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {invTab === 'settings' && userRole === 'super_admin' && (() => {
          const s = invSettingsForm || invCfg;
          const setS = (patch) => setInvSettingsForm({ ...s, ...patch });
          return (
            <div className="bg-white rounded-xl border border-slate-100 p-5 space-y-3 max-w-2xl">
              <p className="text-sm text-slate-600">Defaults for new invoices of <strong>{COMPANIES[currentCompany]?.name}</strong>.</p>
              <div><label className="text-sm font-medium text-slate-700">Bank details (printed on every invoice)</label>
                <textarea rows="3" className={inputCls} value={s.bankDetails || ''} onChange={(e) => setS({ bankDetails: e.target.value })} placeholder={'Bank: …\nA/c No: …  IFSC: …'} /></div>
              <div><label className="text-sm font-medium text-slate-700">Invoice print layout</label>
                <select className={inputCls} value={s.invoiceStyle || 'tally'} onChange={(e) => setS({ invoiceStyle: e.target.value })}>
                  <option value="tally">Tally style (boxed header, HSN tax summary, bank details, declaration)</option>
                  <option value="quotation">Quotation style (same as quotations)</option>
                </select></div>
              <div><label className="text-sm font-medium text-slate-700">Declaration (Tally-style invoices)</label>
                <textarea rows="2" className={inputCls} value={s.declaration || ''} onChange={(e) => setS({ declaration: e.target.value })} /></div>
              <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3"><input type="checkbox" className="mt-1" checked={s.requireApproval === true} onChange={(e) => setS({ requireApproval: e.target.checked })} /><span><b>Accounts approval for invoices whose prices are not locked.</b> An invoice where every line is on the customer's locked price list (or a locked-rental-rate monthly bill) is approved automatically. Any other invoice waits for someone with "Approve / reject invoices" (accounts) before it can go to Tally. Invoices made before this was switched on count as approved.</span></label>
              <div><label className="text-sm font-medium text-slate-700">Payment due after (days)</label>
                <input type="number" className={inputCls} value={s.dueDays} onChange={(e) => setS({ dueDays: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-700">Default terms &amp; conditions</label>
                <textarea rows="4" className={inputCls} value={s.terms || ''} onChange={(e) => setS({ terms: e.target.value })} /></div>
              <button onClick={async () => {
                try { await setDoc(doc(db, 'config', 'invoice'), { companies: { ...((invCfgRaw || {}).companies || {}), [currentCompany]: { bankDetails: s.bankDetails || '', dueDays: Number(s.dueDays) || 15, terms: s.terms || '', invoiceStyle: s.invoiceStyle || 'tally', declaration: s.declaration || '', requireApproval: s.requireApproval === true } } }, { merge: true }); setInvSettingsForm(null); alert('✅ Saved.'); }
                catch (err) { alert('Could not save: ' + err.message); }
              }} className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg font-medium">Save</button>
            </div>
          );
        })()}
      </div>
    );
  };

  // TALLY - sends clients, quotations (as Sales Orders) and monthly rental billing (as Sales vouchers) to Tally Prime,
  // and brings each client's outstanding + recent receipts back. Two ways to get data into Tally:
  //   1. "Download XML"  -> import it in Tally yourself (Gateway > Import > Data ... or Alt+O).
  //   2. "Send to Tally" -> queues it (collection tallyQueue); the helper app on the Tally server (tools/tally-bridge)
  //      posts it to Tally and records the result here.
  // Filters / selections live at App level because this component is re-created on every data update.
  const TALLY_STATUS = {
    done: { label: 'In Tally', cls: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'Queued', cls: 'bg-amber-100 text-amber-700' },
    processing: { label: 'Sending…', cls: 'bg-sky-100 text-sky-700' },
    error: { label: 'Failed', cls: 'bg-red-100 text-red-700' }
  };

  const downloadTextFile = (filename, text) => {
    const blob = new Blob([text], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const TallyView = () => {
    const cfg = tallyCfg;
    const companyCode = currentCompany === 'canopus' ? 'CN' : 'RB';
    const today = new Date().toISOString().split('T')[0];
    const ready = !!String(cfg.tallyCompany || '').trim();
    const agentSeen = tallyAgent?.lastSeen ? new Date(tallyAgent.lastSeen) : null;
    const agentAgeMin = agentSeen ? Math.round((Date.now() - agentSeen.getTime()) / 60000) : null;
    const agentOnline = agentAgeMin !== null && agentAgeMin <= 5;

    const statusOf = (ref) => tallyRefStatus[ref];
    const Badge = ({ st }) => st ? <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TALLY_STATUS[st]?.cls || 'bg-slate-100 text-slate-600'}`}>{TALLY_STATUS[st]?.label || st}</span> : <span className="text-[11px] text-slate-400">not sent</span>;

    const queueItems = async ({ kind, title, xml, refs, xml2 }) => {
      const id = `TQ${Date.now()}${Math.floor(Math.random() * 1000)}`;
      await setDoc(doc(db, 'tallyQueue', id), {
        id, companyId: currentCompany, tallyCompany: cfg.tallyCompany, kind, title, xml, ...(xml2 ? { xml2 } : {}), refs: refs || [],
        status: 'pending', createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown'
      });
      await logAudit('CREATE', 'TallyQueue', id, title, { kind, refs: (refs || []).length });
      return id;
    };
    const guard = () => { if (!ready) { alert('Set the Tally company name first (Tally > Settings).'); setTallyTab('settings'); return false; } return true; };
    const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
    const selectedKeys = (keys) => keys.filter(k => tallySel[k]);
    const toggle = (k) => setTallySel({ ...tallySel, [k]: !tallySel[k] });
    const setMany = (keys, v) => { const n = { ...tallySel }; keys.forEach(k => { n[k] = v; }); setTallySel(n); };

    // ---------- clients
    const nameCount = clients.reduce((m, c) => { const k = String(c.name || '').trim().toLowerCase(); m[k] = (m[k] || 0) + 1; return m; }, {});
    const clientRows = clients.filter(c => c.name && String(c.name).trim()).map(c => ({
      c, key: `client:${c.id}`, dup: nameCount[String(c.name).trim().toLowerCase()] > 1,
      badGst: !!c.gstin && !isGstin(c.gstin)
    })).filter(r => matchesSearch(tallySearch, r.c.name, r.c.gstin, r.c.email, r.c.phone));

    const sendClients = async (rows, mode) => {
      if (!guard()) return;
      const usable = rows.filter(r => !r.dup);
      if (usable.length === 0) { alert('Nothing to send (duplicate names are skipped - rename them first).'); return; }
      if (mode === 'download') {
        downloadTextFile(`tally_clients_${currentCompany}_${today}.xml`, mastersXml(cfg.tallyCompany, usable.map(r => clientToLedger(r.c, cfg))));
        return;
      }
      if (!window.confirm(`Queue ${usable.length} client ledger(s) for Tally company "${cfg.tallyCompany}"?`)) return;
      setTallyBusy(true);
      try {
        for (const part of chunk(usable, 100)) {
          await queueItems({ kind: 'ledger', title: `${part.length} client ledger(s)`, xml: mastersXml(cfg.tallyCompany, part.map(r => clientToLedger(r.c, cfg))), refs: part.map(r => r.key) });
        }
        alert(`✅ Queued ${usable.length} client ledger(s). The Tally helper will send them shortly.`);
      } catch (err) { alert('Could not queue: ' + err.message); }
      setTallyBusy(false);
    };

    // ---------- quotations
    const quoteRows = quotations.filter(q => matchesSearch(tallySearch, q.quoteNumber, q.clientName)).map(q => {
      const client = findClientByName(clients, q.clientName) || clients.find(c => c.id === q.clientId);
      const v = quotationToVoucher(q, client, cfg);
      const totals = v.lines.length ? voucherMessage(v, cfg).totals : null;
      return { q, key: `quotation:${q.id}`, client, v, totals, problem: !client ? 'Client not found' : (!v.lines.length ? 'No priced lines' : '') };
    });

    const sendVouchers = async (rows, mode, label) => {
      if (!guard()) return;
      const usable = rows.filter(r => !r.problem);
      if (usable.length === 0) { alert('Nothing to send.'); return; }
      const xml = (part) => vouchersXml(cfg.tallyCompany, part.map(r => r.v), cfg);
      if (mode === 'download') { downloadTextFile(`tally_${label}_${currentCompany}_${today}.xml`, xml(usable)); return; }
      if (!window.confirm(`Queue ${usable.length} ${label} voucher(s) for Tally company "${cfg.tallyCompany}"?\n\nVoucher type: ${rows[0]?.v?.voucherType || ''}. Check that every client already exists as a ledger in Tally.`)) return;
      setTallyBusy(true);
      try {
        for (const part of chunk(usable, 50)) {
          await queueItems({ kind: 'voucher', title: `${part.length} ${label} voucher(s)`, xml: xml(part), refs: part.map(r => r.key) });
        }
        alert(`✅ Queued ${usable.length} voucher(s).`);
      } catch (err) { alert('Could not queue: ' + err.message); }
      setTallyBusy(false);
    };

    // ---------- billing
    const billingRows = computeBilling({ rentals, meterReadings, clients, month: tallyMonth, companyCode, companyId: currentCompany, cfg })
      .map(b => ({ ...b, v: b.voucher, problem: b.problem }));

    // ---------- receivables
    const pullNow = async () => {
      if (!guard()) return;
      const from = new Date(Date.now() - 60 * 86400000).toISOString().split('T')[0];
      setTallyBusy(true);
      try {
        await queueItems({ kind: 'pull', title: 'Refresh outstanding + receipts from Tally', xml: pullDebtorsXml(cfg.tallyCompany, cfg.debtorsGroup), xml2: pullReceiptsXml(cfg.tallyCompany, from, today), refs: [] });
        alert('✅ Requested. The Tally helper will fetch the latest balances (give it a minute).');
      } catch (err) { alert('Could not request: ' + err.message); }
      setTallyBusy(false);
    };

    // Ask the helper to read the real ledger names from Tally (Duties & Taxes and Sales Accounts)
    const fetchLedgerNames = async () => {
      if (!guard()) return;
      setTallyBusy(true);
      try {
        const id = `TQ${Date.now()}${Math.floor(Math.random() * 1000)}`;
        await setDoc(doc(db, 'tallyQueue', id), {
          id, companyId: currentCompany, tallyCompany: cfg.tallyCompany, kind: 'pull', purpose: 'ledgers', title: 'Read ledger names from Tally',
          xml: pullLedgerListXml(cfg.tallyCompany, 'Duties & Taxes'), xml2: pullLedgerListXml(cfg.tallyCompany, 'Sales Accounts'), refs: [],
          status: 'pending', createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown'
        });
        alert('✅ Asked Tally for its ledger names. Give the helper a few seconds, then they appear in the boxes below.');
      } catch (err) { alert('Could not request: ' + err.message); }
      setTallyBusy(false);
    };
    const bestLedger = (names, mustRe, preferRe) => {
      const hits = (names || []).filter(x => mustRe.test(x));
      return (hits.find(x => preferRe && preferRe.test(x)) || [...hits].sort((a, b) => a.length - b.length)[0]) || '';
    };
    const autoMatchLedgers = () => {
      const duties = (tallyLedgerLists?.duties || []).map(x => x.name);
      const sales = (tallyLedgerLists?.sales || []).map(x => x.name);
      const pick = { ...f.ledgers };
      const rateful = (name, r) => String(name).replace(new RegExp(`(?<=[@\\s])${r}(\\.0+)?(?=\\s*%)`), '{rate}');   // 'Output CGST@9%' -> 'Output CGST@{rate}%' so it follows the invoice's GST rate
      const c = bestLedger(duties, /cgst|central/i, /output|out\b|payable/i); if (c) pick.cgst = rateful(c, 9);
      const g = bestLedger(duties, /sgst|state\s*tax|utgst/i, /output|out\b|payable/i); if (g) pick.sgst = rateful(g, 9);
      const i = bestLedger(duties, /igst|integrated/i, /output|out\b|payable/i); if (i) pick.igst = rateful(i, 18);
      const r = bestLedger(sales, /rent|lease|hire/i); if (r) pick.rental = r;
      const sv = bestLedger(sales, /service|labour|labor|maintenance|support|amc/i); if (sv) pick.service = sv;
      const sa = bestLedger(sales, /^sales?$|sale/i, /^sales$/i); if (sa) pick.sales = sa;
      setF({ ledgers: pick });
    };

    const saveSettings = async () => {
      const f = tallyForm || cfg;
      if (!String(f.tallyCompany || '').trim()) { alert('Enter the exact Tally company name.'); return; }
      try {
        const all = { ...(tallyCfgRaw?.companies || {}), [currentCompany]: f };
        await setDoc(doc(db, 'config', 'tally'), { companies: all, updatedBy: currentUser?.email || '', updatedAt: new Date().toISOString() }, { merge: true });
        await logAudit('UPDATE', 'Settings', 'tally', `Tally settings updated (${currentCompany})`, {});
        setTallyForm(null);
        alert('✅ Tally settings saved.');
      } catch (err) { alert('Could not save: ' + err.message); }
    };

    const TABS = [
      ['status', '📡 Status'], ['invoices', '🧾 Invoices'], ['proforma', '📝 Proforma'], ['challans', '🚚 Delivery challans'], ['clients', '👥 Clients'], ['quotations', '📄 Quotations'], ['billing', '🔁 Rental billing'],
      ['receivables', '💰 Receivables'], ['tallyinv', '📑 From Tally'], ['queue', '📬 Queue'],
      ...(userRole === 'super_admin' ? [['invsettings', '🏦 Invoice settings'], ['settings', '⚙️ Settings']] : [])
    ];

    const SelectBar = ({ keys, children }) => (
      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => setMany(keys, true)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Select all</button>
        <button onClick={() => setMany(keys, false)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Clear</button>
        <span className="text-xs text-slate-400">{selectedKeys(keys).length} selected</span>
        <div className="ml-auto flex gap-2 flex-wrap">{children}</div>
      </div>
    );
    const tallyWrite = canWriteTally();
    const Btn = ({ onClick, children, kind = 'primary', disabled }) => (
      <button onClick={onClick} disabled={disabled || tallyBusy || !tallyWrite} title={tallyWrite ? undefined : 'Read only'} className={`text-sm px-3.5 py-2 rounded-lg font-medium disabled:opacity-50 ${kind === 'primary' ? 'bg-brand-600 hover:bg-brand-700 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{children}</button>
    );
    const money0 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const f = tallyForm || cfg;
    const setF = (patch) => setTallyForm({ ...f, ...patch });

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold">Tally</h2>
            <p className="text-xs text-slate-500">{COMPANIES[currentCompany]?.name} → Tally company: <strong>{cfg.tallyCompany || 'not set'}</strong></p>
          </div>
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${agentOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {agentOnline ? '● Tally helper online' : agentSeen ? `○ Tally helper last seen ${agentAgeMin} min ago` : '○ Tally helper not connected'}
          </span>
        </div>

        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => { setTallyTab(k); if (k === 'invoices') setInvTab('invoices'); if (k === 'proforma') setInvTab('proforma'); if (k === 'challans') setInvTab('challans'); if (k === 'invsettings') setInvTab('settings'); }} className={`px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tallyTab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>

        {(tallyTab === 'invoices' || tallyTab === 'proforma' || tallyTab === 'challans' || tallyTab === 'invsettings') && <InvoicesView embedded />}

        {tallyTab === 'status' && (
          <div className="space-y-3">
            {!ready && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">Start in <button className="underline font-medium" onClick={() => setTallyTab('settings')}>Settings</button>: enter the exact Tally company name and your ledger names.</div>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[['Queued', tallyQueue.filter(q => q.status === 'pending').length], ['Sending', tallyQueue.filter(q => q.status === 'processing').length], ['In Tally', tallyQueue.filter(q => q.status === 'done').length], ['Failed', tallyQueue.filter(q => q.status === 'error').length]].map(([l, n]) => (
                <div key={l} className="bg-white rounded-xl border border-slate-100 p-4"><p className="text-xs text-slate-500">{l}</p><p className={`text-2xl font-bold ${l === 'Failed' && n ? 'text-red-600' : 'text-slate-900'}`}>{n}</p></div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4 text-sm text-slate-600 space-y-2">
              <p className="font-semibold text-slate-800">How it works</p>
              <p><strong>1. Clients</strong> become Sundry Debtor ledgers. <strong>2. Quotations</strong> become Sales Orders. <strong>3. Rental billing</strong> (monthly rent + extra pages) becomes one GST Sales voucher per client. <strong>4. Receivables</strong> brings each client's outstanding and recent receipts back.</p>
              <p>Use <em>Download XML</em> and import it in Tally yourself, or <em>Send to Tally</em> and let the helper app on the Tally server do it. The helper app lives in <code>tools/tally-bridge</code>.</p>
              {tallyAgent && <p className="text-xs text-slate-400">Helper: {tallyAgent.host || '?'} · Tally {tallyAgent.tallyReachable ? 'XML gateway working' : 'XML gateway NOT working'}{tallyAgent.tallyUrl ? ` at ${tallyAgent.tallyUrl}` : ''}{tallyAgent.tallyCompanies ? ` · companies open: ${tallyAgent.tallyCompanies}` : ''} · v{tallyAgent.version || '?'}</p>}
              {tallyAgent && !tallyAgent.tallyReachable && tallyAgent.tallyNote && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">{tallyAgent.tallyNote}</div>}
            </div>
          </div>
        )}

        {tallyTab === 'clients' && (
          <div className="space-y-3">
            <input type="text" placeholder="🔍 Search clients..." value={tallySearch} onChange={(e) => setTallySearch(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-base" />
            <SelectBar keys={clientRows.map(r => r.key)}>
              <Btn kind="secondary" onClick={() => sendClients(clientRows.filter(r => tallySel[r.key]), 'download')}>⬇ Download XML</Btn>
              <Btn onClick={() => sendClients(clientRows.filter(r => tallySel[r.key]), 'queue')}>Send selected to Tally</Btn>
              <Btn onClick={() => sendClients(clientRows.filter(r => !statusOf(r.key) || statusOf(r.key) === 'error'), 'queue')}>Send all not yet sent</Btn>
            </SelectBar>
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {clientRows.slice(0, 400).map(r => (
                <label key={r.key} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={!!tallySel[r.key]} onChange={() => toggle(r.key)} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{r.c.name}</p>
                    <p className="text-xs text-slate-400 truncate">{r.c.gstin || 'no GSTIN'}{r.c.gstin ? ` · ${stateFromGstin(r.c.gstin) || '?'}` : ''}{r.c.phone ? ` · ${r.c.phone}` : ''}</p>
                  </div>
                  {r.dup && <span className="text-[11px] text-red-600">duplicate name</span>}
                  {r.badGst && <span className="text-[11px] text-amber-600">GSTIN looks wrong</span>}
                  <Badge st={statusOf(r.key)} />
                </label>
              ))}
              {clientRows.length > 400 && <p className="p-3 text-xs text-slate-400">Showing first 400 of {clientRows.length} - use the search box.</p>}
            </div>
          </div>
        )}

        {tallyTab === 'quotations' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Each quotation is sent as a <strong>{cfg.vouchers.order}</strong> voucher (accounting mode, no stock items) with CGST+SGST or IGST by the client's state. Send the client ledger first.</p>
            <input type="text" placeholder="🔍 Search quotations..." value={tallySearch} onChange={(e) => setTallySearch(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-base" />
            <SelectBar keys={quoteRows.map(r => r.key)}>
              <Btn kind="secondary" onClick={() => sendVouchers(quoteRows.filter(r => tallySel[r.key]), 'download', 'quotations')}>⬇ Download XML</Btn>
              <Btn onClick={() => sendVouchers(quoteRows.filter(r => tallySel[r.key]), 'queue', 'quotation')}>Send selected to Tally</Btn>
            </SelectBar>
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {quoteRows.slice(0, 300).map(r => (
                <label key={r.key} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={!!tallySel[r.key]} disabled={!!r.problem} onChange={() => toggle(r.key)} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate"><span className="font-mono text-brand-700">{r.q.quoteNumber}</span> · {r.q.clientName}</p>
                    <p className="text-xs text-slate-400">{r.q.date}{r.totals ? ` · ${money0(r.totals.total)}${r.totals.interstate ? ' (IGST)' : ''}` : ''}</p>
                  </div>
                  {r.problem && <span className="text-[11px] text-red-600">{r.problem}</span>}
                  <Badge st={statusOf(r.key)} />
                </label>
              ))}
            </div>
          </div>
        )}

        {tallyTab === 'billing' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm text-slate-600">Billing month</label>
              <input type="month" value={tallyMonth} onChange={(e) => { setTallyMonth(e.target.value); setTallySel({}); }} className="border border-slate-300 rounded-lg px-3 py-2" />
              <p className="text-xs text-slate-500">Monthly rent + extra pages from meter readings in the month. "Create invoices" makes a numbered invoice per client (under Invoices), which goes to Tally automatically. Standby (per cartridge) rentals are listed but not billed here.</p>
            </div>
            <SelectBar keys={billingRows.filter(r => !r.problem).map(r => r.key)}>
              <Btn kind="secondary" onClick={() => sendVouchers(billingRows.filter(r => tallySel[r.key]), 'download', `billing_${tallyMonth}`)}>⬇ Download XML</Btn>
              <Btn onClick={() => createInvoiceFromBilling(billingRows.filter(r => tallySel[r.key]))}>🧾 Create invoices from selected</Btn>
            </SelectBar>
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {billingRows.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">No active rentals to bill for this month.</p>}
              {billingRows.map(r => (
                <label key={r.key} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" className="mt-1" checked={!!tallySel[r.key]} disabled={!!r.problem} onChange={() => toggle(r.key)} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{r.clientName} <span className="text-xs font-normal text-slate-400">{r.reference}</span></p>
                    {r.lines.map((l, i) => <p key={i} className="text-xs text-slate-500 truncate">{l.description} - {money0(l.amount)}</p>)}
                    {r.skipped.map((t, i) => <p key={i} className="text-xs text-amber-600 truncate">{t}</p>)}
                  </div>
                  <div className="text-right text-xs shrink-0">
                    {r.voucher ? <><p className="font-semibold text-slate-800">{money0(r.totals.total)}</p><p className="text-slate-400">{money0(r.totals.net)} + GST{r.totals.interstate ? ' (IGST)' : ''}</p></> : null}
                    {r.problem && <p className="text-red-600">{r.problem}</p>}
                  </div>
                  {(() => { const inv = invoices.find(x => x.billingKey === r.key); return inv ? <span className="text-[11px] font-mono text-emerald-700">{inv.invoiceNumber}</span> : <span className="text-[11px] text-slate-400">not invoiced</span>; })()}
                </label>
              ))}
            </div>
          </div>
        )}

        {tallyTab === 'receivables' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-slate-500">Closing balances of {cfg.debtorsGroup} and recent receipts, fetched from Tally by the helper app.</p>
              <Btn onClick={pullNow}>↻ Refresh from Tally</Btn>
            </div>
            {tallyOutstanding.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500">Nothing yet. Click "Refresh from Tally" (the helper app must be running).</div>
            ) : (
              <>
                <p className="text-sm text-slate-700">Total outstanding: <strong>{money0(tallyOutstanding.reduce((s, o) => s + (o.outstanding > 0 ? o.outstanding : 0), 0))}</strong> across {tallyOutstanding.filter(o => o.outstanding > 0).length} clients · updated {tallyOutstanding[0]?.updatedAt ? new Date(tallyOutstanding[0].updatedAt).toLocaleString('en-IN') : ''}</p>
                <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
                  {[...tallyOutstanding].filter(o => o.outstanding !== 0).sort((a, b) => b.outstanding - a.outstanding).map(o => (
                    <div key={o.id} className="p-3 flex items-center gap-3">
                      <p className="flex-1 min-w-0 truncate text-slate-900">{o.name}</p>
                      {!findClientByName(clients, o.name) && <span className="text-[11px] text-slate-400">not in portal</span>}
                      <p className={`font-semibold ${o.outstanding > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{money0(Math.abs(o.outstanding))}{o.outstanding < 0 ? ' Cr' : ''}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
            {tallyReceipts.length > 0 && (
              <>
                <p className="text-sm font-semibold text-slate-800">Recent receipts</p>
                <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-[35vh] overflow-y-auto">
                  {[...tallyReceipts].sort((a, b) => String(b.date).localeCompare(String(a.date))).slice(0, 100).map(r => (
                    <div key={r.id} className="p-3 flex items-center gap-3 text-sm">
                      <span className="text-slate-400 w-24 shrink-0">{String(r.date).replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</span>
                      <span className="flex-1 min-w-0 truncate text-slate-800">{r.party}{r.number ? ` · ${r.number}` : ''}</span>
                      <span className="font-semibold text-emerald-700">{money0(r.amount)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tallyTab === 'tallyinv' && (() => {
          const ym = (tallyInvMonth || '').replace('-', '');
          const rows = tallyInvoices.filter(r => String(r.date).startsWith(ym)).sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.number).localeCompare(String(a.number)));
          const known = new Set(invoices.map(i => String(i.invoiceNumber || '').trim()));
          const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
          return (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2"><label className="text-xs text-slate-500">Month</label>
                  <input type="month" value={tallyInvMonth} onChange={(e) => setTallyInvMonth(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" /></div>
                <Btn onClick={pullNow}>↻ Fetch from Tally</Btn>
              </div>
              <p className="text-xs text-slate-500">Sales vouchers raised in Tally (last 60 days are fetched). "Not in portal" marks vouchers that were made directly in Tally.</p>
              {rows.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500">{tallyInvoices.length === 0 ? 'Nothing fetched yet. Click "Fetch from Tally" (the helper app must be running).' : 'No Tally sales vouchers in this month.'}</div>
              ) : (
                <>
                  <p className="text-sm text-slate-700"><strong>{rows.length}</strong> invoices · total <strong>{money0(total)}</strong>{tallyInvoices[0]?.updatedAt ? ` · fetched ${new Date(tallyInvoices[0].updatedAt).toLocaleString('en-IN')}` : ''}</p>
                  <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
                    {rows.map(r => (
                      <div key={r.id} className="p-3 flex items-center gap-3 text-sm">
                        <span className="text-slate-400 w-24 shrink-0">{String(r.date).replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</span>
                        <span className="flex-1 min-w-0 truncate text-slate-800">{r.party}{r.number ? ` · ${r.number}` : ''}</span>
                        {!known.has(String(r.number || '').trim()) && <span className="text-[11px] text-amber-600">Not in portal</span>}
                        <span className="font-semibold text-slate-900">{money0(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {tallyTab === 'queue' && (
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {tallyQueue.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">Nothing has been queued yet.</p>}
            {tallyQueue.slice(0, 80).map(q => (
              <div key={q.id} className="p-3 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge st={q.status} />
                  <span className="font-medium text-slate-900">{q.title}</span>
                  <span className="text-xs text-slate-400 ml-auto">{q.createdAt ? new Date(q.createdAt).toLocaleString('en-IN') : ''} · {q.createdBy}</span>
                </div>
                {q.response && <p className="text-xs text-slate-500">Tally: created {q.response.created || 0}, altered {q.response.altered || 0}, errors {q.response.errors || 0}{q.response.alreadyExists ? ' (already existed)' : ''}</p>}
                {q.error && <p className="text-xs text-red-600 break-words">{q.error}</p>}
                {(/does not exist/i.test(q.error || '') || (q.response?.lineErrors || []).some(e => /does not exist/i.test(e))) && <p className="text-xs text-amber-700">A ledger or voucher type named here is not in Tally. In Settings use "Fetch ledger names from Tally" and "Auto-match", save, then use "Retry Tally" on the invoice (it rebuilds the voucher with the corrected names).</p>}
                {(q.response?.lineErrors || []).slice(0, 3).map((e, i) => <p key={i} className="text-xs text-red-600 break-words">{e}</p>)}
                <div className="flex gap-2">
                  <button onClick={() => downloadTextFile(`${q.id}.xml`, q.xml || '')} className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">XML</button>
                  {tallyWrite && (q.status === 'error' || q.status === 'processing') && <button onClick={() => setDoc(doc(db, 'tallyQueue', q.id), { status: 'pending', error: '' }, { merge: true })} className="text-xs px-2 py-1 rounded bg-brand-50 text-brand-700 hover:bg-brand-100">Retry</button>}
                  {tallyWrite && <button onClick={async () => { if (window.confirm('Remove this entry from the queue list?')) await deleteDoc(doc(db, 'tallyQueue', q.id)); }} className="text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50">Remove</button>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tallyTab === 'settings' && userRole === 'super_admin' && (
          <div className="bg-white rounded-xl border border-slate-100 p-5 space-y-4 max-w-2xl">
            <p className="text-sm text-slate-600">Settings for <strong>{COMPANIES[currentCompany]?.name}</strong>. Names must match Tally <em>exactly</em> (spelling, spaces, &amp;).</p>
            {[
              ['tallyCompany', 'Tally company name *', 'As shown in Tally, e.g. RB Infotech Solutions (2026-27)'],
              ['stateName', 'State of the Tally company', 'Decides CGST+SGST (same state) or IGST'],
              ['debtorsGroup', 'Ledger group for clients', 'Usually Sundry Debtors']
            ].map(([k, label, hint]) => (
              <div key={k}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                <input type="text" value={f[k] || ''} onChange={(e) => setF({ [k]: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
                <p className="text-xs text-slate-400 mt-0.5">{hint}</p>
                {k === 'tallyCompany' && (() => {
                  const open = String(tallyAgent?.tallyCompanies || '').split(' | ').map(x => x.trim()).filter(Boolean);
                  return open.length > 0 ? (
                    <div className="mt-1.5 flex gap-1.5 flex-wrap items-center">
                      <span className="text-xs text-slate-500">Open in Tally right now (click to use):</span>
                      {open.map(c => <button key={c} type="button" onClick={() => setF({ tallyCompany: c })} className={`text-xs px-2 py-1 rounded-full border ${c === f.tallyCompany ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{c}</button>)}
                    </div>
                  ) : (tallyAgent ? <p className="text-xs text-amber-600 mt-1">The helper can't see any open company in Tally. Open the company in TallyPrime (Gateway of Tally &gt; Select Company).</p> : null);
                })()}
              </div>
            ))}
            <div>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="text-sm font-medium text-slate-700">Ledger names in Tally</p>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={fetchLedgerNames} disabled={tallyBusy} className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">↻ Fetch ledger names from Tally</button>
                  {tallyLedgerLists && <button type="button" onClick={autoMatchLedgers} className="text-xs px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 font-medium">✨ Auto-match from Tally</button>}
                </div>
              </div>
              {tallyLedgerLists
                ? <p className="text-xs text-slate-400 mb-2">Read from Tally {new Date(tallyLedgerLists.updatedAt).toLocaleString('en-IN')}: {(tallyLedgerLists.duties || []).length} duty/tax ledgers, {(tallyLedgerLists.sales || []).length} sales ledgers. Start typing to pick one; a red note means the name is not in Tally.{' You can write {rate} in a GST name (e.g. Output CGST@{rate}%) so it follows each invoice\'s GST rate.'}</p>
                : <p className="text-xs text-amber-600 mb-2">Click "Fetch ledger names from Tally" (the helper must be running) so these can be checked against your real Tally ledgers.</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[['rental', 'Rental income', 'sales'], ['sales', 'Sales (equipment, licences, cartridges)', 'sales'], ['service', 'Service income', 'sales'], ['cgst', 'Output CGST', 'duties'], ['sgst', 'Output SGST', 'duties'], ['igst', 'Output IGST', 'duties']].map(([k, label, listKey]) => {
                  const names = (tallyLedgerLists?.[listKey] || []).map(x => x.name);
                  const probe = String(f.ledgers[k] || '').trim().toLowerCase().replace(/\{rate\}/g, k === 'igst' ? '18' : '9');   // {rate} is checked at the usual rate (9 for CGST/SGST, 18 for IGST)
                  const missing = tallyLedgerLists && f.ledgers[k] && !names.some(x => x.toLowerCase() === probe);
                  return (
                    <div key={k}><label className="block text-xs text-slate-500 mb-0.5">{label}</label>
                      <input type="text" list={`tl-${k}`} value={f.ledgers[k] || ''} onChange={(e) => setF({ ledgers: { ...f.ledgers, [k]: e.target.value } })} className={`w-full border rounded-lg px-3 py-2 ${missing ? 'border-red-400' : 'border-slate-300'}`} />
                      <datalist id={`tl-${k}`}>{names.map(x => <option key={x} value={x} />)}</datalist>
                      {missing && <p className="text-[11px] text-red-600 mt-0.5">Not found in Tally under {listKey === 'duties' ? 'Duties & Taxes' : 'Sales Accounts'}.</p>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="block text-xs text-slate-500 mb-0.5">Voucher type for billing</label>
                <input type="text" value={f.vouchers.sales || ''} onChange={(e) => setF({ vouchers: { ...f.vouchers, sales: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-2" /></div>
              <div><label className="block text-xs text-slate-500 mb-0.5">Voucher type for proformas &amp; delivery challans</label>
                <input type="text" value={f.vouchers.memo || ''} onChange={(e) => setF({ vouchers: { ...f.vouchers, memo: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-2" /></div>
              <div><label className="block text-xs text-slate-500 mb-0.5">Voucher type for quotations</label>
                <input type="text" value={f.vouchers.order || ''} onChange={(e) => setF({ vouchers: { ...f.vouchers, order: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-2" /></div>
            </div>
            <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
              <p className="text-sm font-medium text-slate-700">Send to Tally automatically (as soon as saved)</p>
              {[['clients', 'New and changed clients (ledgers)'], ['invoices', 'New invoices (Sales vouchers)'], ['quotations', 'New quotations (Memorandum / order vouchers)'], ['proformas', 'New proforma invoices (Memorandum vouchers)'], ['challans', 'New delivery challans (Memorandum vouchers, needs a value)']].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!(f.autoSync || {})[k]} onChange={(e) => setF({ autoSync: { ...(f.autoSync || {}), [k]: e.target.checked } })} /> {label}</label>
              ))}
              <p className="text-xs text-slate-400">Items wait in the queue while the Tally helper or Tally is offline, then go through automatically. Balances come back from Tally every few minutes.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!f.supplyVoucherNumber} onChange={(e) => setF({ supplyVoucherNumber: e.target.checked })} /> Use our reference as the voucher number (otherwise Tally numbers the vouchers itself)</label>
            <div className="flex gap-2 flex-wrap">
              <Btn onClick={saveSettings}>Save settings</Btn>
              <Btn kind="secondary" onClick={() => { if (!ready) { alert('Save the Tally company name first.'); return; } downloadTextFile(`tally_standard_ledgers_${currentCompany}.xml`, standardMastersXml(cfg)); }}>⬇ XML: income + GST ledgers</Btn>
              <Btn kind="secondary" onClick={async () => { if (!guard()) return; if (!window.confirm('Queue creation of the income and GST ledgers in Tally? (Ledgers that already exist are left alone.)')) return; await queueItems({ kind: 'ledger', title: 'Income + GST ledgers', xml: standardMastersXml(cfg), refs: [] }); alert('✅ Queued.'); }}>Create income + GST ledgers in Tally</Btn>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Preventive-maintenance and agreement-renewal due lists (shown on the Reminders page, not the Dashboard).
  const getPmDueItems = () => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return printers
      .filter(p => p.pmIntervalDays > 0)
      .map(p => {
        const baseline = p.lastPmDate || p.purchaseDate;
        const nextDue = baseline ? new Date(new Date(baseline).getTime() + p.pmIntervalDays * 86400000) : null;
        return { printer: p, nextDue, days: nextDue ? Math.ceil((nextDue - t) / 86400000) : null };
      })
      .filter(x => x.days !== null && x.days <= 14);
  };
  const getRenewalItems = () => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return rentals
      .filter(r => r.status === 'active' && r.agreementEndDate)
      .map(r => ({ rental: r, days: Math.ceil((new Date(r.agreementEndDate) - t) / 86400000) }))
      .filter(x => x.days <= 30);
  };

  // REMINDERS - one place for everything with a due date: Lead and Client follow-ups, reminders that came
  // with Email Inbox mails, and manual reminders typed in here. Deliberately NOT shown on the Dashboard.
  // (Filters / form state live at App level because this component is re-created on every data update.)
  const REMINDER_SOURCES = {
    lead: { label: 'Lead', chip: 'bg-indigo-100 text-indigo-700', view: 'leads' },
    client: { label: 'Client', chip: 'bg-purple-100 text-purple-700', view: 'clients' },
    mail: { label: 'Email', chip: 'bg-sky-100 text-sky-700', view: 'inbox' },
    manual: { label: 'Reminder', chip: 'bg-amber-100 text-amber-700', view: null },
    pm: { label: 'Maintenance', chip: 'bg-teal-100 text-teal-700', view: 'service' },
    renewal: { label: 'Renewal', chip: 'bg-orange-100 text-orange-700', view: 'rentals' },
    stock: { label: 'Low stock', chip: 'bg-rose-100 text-rose-700', view: 'stock' },
    po: { label: 'Purchase order', chip: 'bg-violet-100 text-violet-700', view: 'purchase' }
  };

  const RemindersView = () => {
    const canEdit = hasPermission('canEditReminders');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const addDays = (ymd, n) => { const d = new Date(`${ymd}T00:00:00`); d.setDate(d.getDate() + n); return toYmd(d); };
    const daysFrom = (ymd) => Math.round((new Date(`${ymd}T00:00:00`) - today) / 86400000);

    const items = [
      ...leads.filter(l => l.followUpDate && l.stage !== 'won' && l.stage !== 'lost').map(l => ({
        key: `lead-${l.id}`, source: 'lead', id: l.id, title: String(l.name || '').replace(/^["'\s]+|["'\s]+$/g, ''), note: l.followUpNote || 'Follow up', date: l.followUpDate, raw: l, sub: l.phone || l.email || '' })),
      ...clients.filter(c => c.followUpDate).map(c => ({
        key: `client-${c.id}`, source: 'client', id: c.id, title: c.name, note: c.followUpNote || 'Follow up', date: c.followUpDate, raw: c, sub: c.phone || c.email || '' })),
      ...inboxMails.filter(m => (m.status || 'new') === 'new' && m.followUpDate).map(m => ({
        key: `mail-${m.id}`, source: 'mail', id: m.id, title: String(m.name || m.email || '').replace(/^["'\s]+|["'\s]+$/g, ''), note: m.followUpNote || m.subject || '', date: m.followUpDate, raw: m, sub: m.subject || '' })),
      ...reminders.filter(r => (r.status || 'open') === 'open' && r.dueDate).map(r => ({
        key: `manual-${r.id}`, source: 'manual', id: r.id, title: r.title, note: r.note || '', date: r.dueDate, raw: r, sub: r.clientName || '' })),
      ...getPmDueItems().map(({ printer: p, nextDue }) => ({
        key: `pm-${p.id}`, source: 'pm', id: p.id, title: `${p.assetNumber || p.serialNumber} - ${p.make || ''} ${p.model || ''}`.trim(),
        note: `Preventive maintenance every ${p.pmIntervalDays} days${p.lastPmDate ? ` · last serviced ${p.lastPmDate}` : ''}`, date: toYmd(nextDue), raw: p, sub: '' })),
      ...getRenewalItems().map(({ rental: r }) => ({
        key: `renewal-${r.id}`, source: 'renewal', id: r.id, title: r.clientName || 'Rental', note: `${r.assetNumber || r.serialNumber || ''} - agreement ends ${r.agreementEndDate}`,
        date: String(r.agreementEndDate).slice(0, 10), raw: r, sub: '' })),
      ...(canSeePurchase() ? purchaseOrders.filter(p => ['open', 'partial'].includes(p.status) && p.deliveryDate).map(p => ({
        key: `po-${p.id}`, source: 'po', id: p.id, title: `${p.poNumber} - ${p.vendorName}`, note: `Delivery due ${p.deliveryDate}${p.status === 'partial' ? ' - part received' : ' - not received yet'}`, date: p.deliveryDate, raw: p, sub: '' })) : []),
      ...(canSeeStock() ? lowStockItems().map(i => ({
        key: `stock-${i.id}`, source: 'stock', id: i.id, title: i.name,
        note: `${stockOnHand(i.id) <= 0 ? 'OUT OF STOCK' : `${stockOnHand(i.id)} ${i.unit || ''} left`} · minimum ${i.minQty}${i.vendor ? ` · buy from ${i.vendor}` : ''}`, date: toYmd(today), raw: i, sub: i.location ? `Rack: ${i.location}` : '' })) : [])
    ].map(i => ({ ...i, days: daysFrom(i.date) }))
      .filter(i => reminderFilter === 'all' || i.source === reminderFilter)
      .sort((a, b) => a.days - b.days);

    const groups = [
      { key: 'overdue', label: '⚠️ Overdue', cls: 'text-red-700', rows: items.filter(i => i.days < 0) },
      { key: 'today', label: 'Today', cls: 'text-amber-700', rows: items.filter(i => i.days === 0) },
      { key: 'week', label: 'Next 7 days', cls: 'text-slate-700', rows: items.filter(i => i.days > 0 && i.days <= 7) },
      { key: 'later', label: 'Later', cls: 'text-slate-500', rows: items.filter(i => i.days > 7) }
    ];

    const writeDate = async (item, ymd) => {
      try {
        if (item.source === 'lead') { await setDoc(doc(db, 'leads', item.id), { followUpDate: ymd, ...(ymd ? {} : { followUpNote: '' }) }, { merge: true }); }
        else if (item.source === 'client') { await setDoc(doc(db, 'clients', item.id), { followUpDate: ymd, ...(ymd ? {} : { followUpNote: '' }) }, { merge: true }); }
        else if (item.source === 'mail') { await setDoc(doc(db, 'inboxMails', item.id), { followUpDate: ymd }, { merge: true }); }
        else if (item.source === 'pm') { await setDoc(doc(db, 'printers', item.id), { lastPmDate: toYmd(today) }, { merge: true }); }   // "Log service": restarts the interval
        else { await setDoc(doc(db, 'reminders', item.id), ymd ? { dueDate: ymd } : { status: 'done', doneAt: new Date().toISOString() }, { merge: true }); }
      } catch (err) { alert('Could not update reminder: ' + err.message); }
    };
    const markDone = async (item) => { await writeDate(item, ''); await logAudit('UPDATE', item.source === 'pm' ? 'Asset' : 'Reminder', item.source === 'pm' ? item.id : item.key, item.source === 'pm' ? `Preventive maintenance logged for ${item.title}` : `Done: ${item.title} - ${item.note}`.slice(0, 120), {}); };
    const snooze = (item, n) => writeDate(item, addDays(item.days < 0 ? toYmd(today) : item.date, n));
    const openSource = (item) => {
      const src = REMINDER_SOURCES[item.source];
      if (!src.view) return;
      if (item.source === 'mail') { setInboxType('all'); setInboxStatus('new'); setInboxOpenId(item.id); }
      setCurrentView(src.view);
    };

    const saveReminder = async () => {
      const f = reminderForm;
      if (!String(f.title || '').trim() || !f.dueDate) { alert('❌ Please enter what to be reminded about and a date.'); return; }
      try {
        const r = {
          id: `REM${Date.now()}`, title: f.title.trim(), note: (f.note || '').trim(), dueDate: f.dueDate, clientName: f.clientName || '',
          status: 'open', companyId: currentCompany, createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'reminders', r.id), r);
        await logAudit('CREATE', 'Reminder', r.id, `${r.title} (${r.dueDate})`, r);
        setReminderForm({ title: '', note: '', dueDate: '', clientName: '' });
        setShowReminderForm(false);
      } catch (err) { alert('Could not save reminder: ' + err.message); }
    };
    const deleteReminder = async (item) => {
      if (!window.confirm(`Delete reminder "${item.title}"?`)) return;
      try { await deleteDoc(doc(db, 'reminders', item.id)); } catch (err) { alert('Could not delete: ' + err.message); }
    };

    const all = [...groups.flatMap(g => g.rows)];
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold">Reminders</h2>
            <p className="text-xs text-slate-500">Follow-ups, email reminders, preventive maintenance, agreement renewals and your own reminders - all in one list.</p>
          </div>
          {canEdit && (
            <button onClick={() => setShowReminderForm(!showReminderForm)} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2">
              <Plus size={18} /> Add reminder
            </button>
          )}
        </div>

        {showReminderForm && canEdit && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <input type="text" placeholder="What do you need to be reminded about? *" value={reminderForm.title} onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-base" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="date" value={reminderForm.dueDate} onChange={(e) => setReminderForm({ ...reminderForm, dueDate: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-base" />
              <select value={reminderForm.clientName} onChange={(e) => setReminderForm({ ...reminderForm, clientName: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-base">
                <option value="">Client (optional)</option>
                {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <textarea placeholder="Note (optional)" rows="2" value={reminderForm.note} onChange={(e) => setReminderForm({ ...reminderForm, note: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-base" />
            <div className="flex gap-2">
              <button onClick={saveReminder} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium">Save reminder</button>
              <button onClick={() => setShowReminderForm(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {groups.map(g => (
            <div key={g.key} className="bg-white rounded-xl border border-slate-100 p-4">
              <p className="text-xs text-slate-500">{g.label.replace('⚠️ ', '')}</p>
              <p className={`text-2xl font-bold ${g.rows.length && g.key === 'overdue' ? 'text-red-600' : 'text-slate-900'}`}>{g.rows.length}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap">
          {[['all', 'All'], ['lead', 'Leads'], ['client', 'Clients'], ['mail', 'Email'], ['pm', 'Maintenance'], ['renewal', 'Renewals'], ['stock', 'Low stock'], ['po', 'Purchase'], ['manual', 'My reminders']].map(([k, label]) => (
            <button key={k} onClick={() => setReminderFilter(k)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${reminderFilter === k ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
          ))}
        </div>

        {all.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-lg font-semibold text-slate-700 mb-1">No reminders 🎉</p>
            <p className="text-slate-500 text-sm">Follow-up dates, maintenance due dates, agreement renewals and reminders from Email Inbox mails will show up here.</p>
          </div>
        ) : groups.filter(g => g.rows.length > 0).map(g => (
          <div key={g.key}>
            <h3 className={`font-semibold mb-2 ${g.cls}`}>{g.label} ({g.rows.length})</h3>
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
              {g.rows.map(item => {
                const src = REMINDER_SOURCES[item.source];
                return (
                  <div key={item.key} className="p-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="min-w-0 flex-1 basis-60">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${src.chip}`}>{src.label}</span>
                        <span className="font-semibold text-slate-900 truncate">{item.title}</span>
                      </div>
                      <p className="text-sm text-slate-600 truncate">{item.note}</p>
                      {item.sub && item.sub !== item.note && <p className="text-xs text-slate-400 truncate">{item.sub}</p>}
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <p className={item.days < 0 ? 'text-red-600 font-semibold' : item.days === 0 ? 'text-amber-600 font-semibold' : 'text-slate-500'}>
                        {item.days < 0 ? `Overdue ${Math.abs(item.days)}d` : item.days === 0 ? 'Today' : `In ${item.days}d`}
                      </p>
                      <p className="text-slate-400">{new Date(`${item.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {src.view && <button onClick={() => openSource(item)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Open</button>}
                      {item.source === 'stock' && canCreateIn('purchase') && <button onClick={() => poFromLowStock([item.raw])} className="text-xs px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 font-medium">🛒 Create PO</button>}
                      {canEdit && item.source === 'pm' && (
                        <button onClick={() => markDone(item)} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">✅ Log service</button>
                      )}
                      {canEdit && item.source !== 'renewal' && item.source !== 'pm' && item.source !== 'stock' && item.source !== 'po' && (
                        <>
                          <button onClick={() => markDone(item)} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">✓ Done</button>
                          <button onClick={() => snooze(item, 1)} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100" title="Move to tomorrow">+1d</button>
                          <button onClick={() => snooze(item, 3)} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">+3d</button>
                          <button onClick={() => snooze(item, 7)} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">+1w</button>
                          {item.source === 'manual' && <button onClick={() => deleteReminder(item)} className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50" title="Delete"><Trash2 size={13} /></button>}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // EMAIL INBOX - enquiry / order / service-request mails read from the business mailbox by the
  // emailLeadsSync Cloud Function (functions/emailLeads.js). Each mail can become a Lead, a
  // Quotation or a Service Call, be marked done, or be dismissed. The reminder date worked out
  // from the mail carries over to the Lead. (Filters/expanded row live in App-level state because
  // this component is re-created whenever any collection updates.)
  const INBOX_TYPES = {
    order: { label: 'Order', icon: '🛒', chip: 'bg-orange-100 text-orange-700', rank: 0 },
    service: { label: 'Service', icon: '🔧', chip: 'bg-rose-100 text-rose-700', rank: 1 },
    enquiry: { label: 'Enquiry', icon: '📧', chip: 'bg-sky-100 text-sky-700', rank: 2 }
  };

  const checkEmailNow = async (days = 0) => {
    setEmailChecking(true);
    try {
      // Request-document mechanism: a Cloud Function (emailSyncWorker) picks it up.
      const requestRef = doc(collection(db, 'syncRequests'));
      await setDoc(requestRef, { type: 'email', days, status: 'pending', triggeredBy: currentUser?.email || 'unknown', createdAt: new Date().toISOString() });
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const data = (await getDoc(requestRef)).data();
        if (data?.status === 'success') {
          const d = data.result || {};
          alert(`✅ Email checked: ${d.scanned || 0} mail(s) read.\n${d.created || 0} new in the Inbox, ${d.updated || 0} updated, ${d.skipped || 0} ignored (newsletters, own mail, not enquiries/orders).`);
          return;
        }
        if (data?.status === 'error') { alert('❌ Could not check email:\n\n' + data.error); return; }
      }
      alert('⏱️ The email check is taking longer than expected. Refresh the page in a minute.');
    } catch (err) {
      alert('Could not check email: ' + (err.message || err));
    } finally {
      setEmailChecking(false);
    }
  };

  const InboxView = () => {
    const canEdit = hasPermission('canEditInbox');
    const todayStr = new Date().toISOString().split('T')[0];
    const q = inboxSearch.trim().toLowerCase();

    const open = inboxMails.filter(m => (m.status || 'new') === 'new');
    const counts = {
      order: open.filter(m => m.type === 'order').length,
      service: open.filter(m => m.type === 'service').length,
      enquiry: open.filter(m => m.type === 'enquiry').length,
      overdue: open.filter(m => m.followUpDate && m.followUpDate < todayStr).length
    };

    const rows = inboxMails
      .filter(m => (m.status || 'new') === inboxStatus)
      .filter(m => inboxType === 'all' || m.type === inboxType)
      .filter(m => !q || [m.name, m.email, m.phone, m.subject, m.body].some(v => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => ((INBOX_TYPES[a.type]?.rank ?? 9) - (INBOX_TYPES[b.type]?.rank ?? 9))
        || String(a.followUpDate || '9999').localeCompare(String(b.followUpDate || '9999'))
        || new Date(b.lastReceivedAt || 0) - new Date(a.lastReceivedAt || 0));

    const patchMail = (m, patch) => setDoc(doc(db, 'inboxMails', m.id), patch, { merge: true });
    const findClient = (m) => {
      const em = String(m.email || '').toLowerCase();
      const nm = String(m.name || '').toLowerCase();
      return clients.find(c => em && String(c.email || '').toLowerCase() === em)
        || clients.find(c => nm && String(c.name || '').toLowerCase() === nm);
    };
    const shortBody = (m) => String(m.body || '').slice(0, 500);

    const addToLeads = async (m) => {
      if (!canEdit) return;
      try {
        const lead = {
          id: `LEAD${Date.now()}`,
          name: String(m.name || m.email || 'Email enquiry').replace(/^["'\s]+|["'\s]+$/g, ''),
          contactName: String(m.contactName || '').replace(/^["'\s]+|["'\s]+$/g, ''),
          phone: m.phone || '',
          email: m.email || '',
          address: '',
          stage: 'new',
          source: 'email',
          emailType: m.type || 'enquiry',
          emailSubject: m.subject || '',
          notes: `${(INBOX_TYPES[m.type]?.label || 'Enquiry')} by email - ${m.subject || ''}\n\n${m.body || ''}`.slice(0, 6000),
          followUpDate: m.followUpDate || '',
          followUpNote: m.followUpNote || '',
          companyId: m.companyId || currentCompany,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'leads', lead.id), lead);
        await patchMail(m, { status: 'added', leadId: lead.id });
        await logAudit('CREATE', 'Lead', lead.id, `${lead.name} (from email inbox)`, lead);
      } catch (err) { alert('Could not add to Leads: ' + err.message); }
    };

    const createQuotation = (m) => {
      if (!hasPermission('canCreateQuotations')) { alert('❌ You do not have permission to create quotations.'); return; }
      const client = findClient(m);
      const isCartridge = /cartridge|toner|\bink\b|refill/i.test(`${m.subject} ${m.body}`);
      const quotationType = isCartridge ? 'cartridge' : 'service';
      setEditingItem({
        clientId: client?.id || '',
        clientName: client?.name || '',
        clientAddress: client?.address || '',
        clientGSTIN: client?.gstin || '',
        printerType: isCartridge ? 'toner_cartridge' : 'laserjet_printer',
        date: new Date().toISOString().split('T')[0],
        items: [{ description: m.subject || '', hsn: '998713', qty: 1, rate: '' }],
        notes: 'Looking forward for your business.',
        quotationType,
        terms: getTermsForQuotation(quotationType, isCartridge ? 'toner_cartridge' : 'laserjet_printer'),
        inboxMailId: m.id,                       // links the saved quotation back to this email
        emailSubject: m.subject || '',
        emailFrom: m.email || ''
      });
      setModalType('quotation');
      setShowModal(true);
      if (!client) alert(`ℹ️ "${m.name}" is not in your Clients yet - pick or add the client in the quotation form.\n\nMail subject was put in as the first item.`);
    };

    const createServiceCall = (m) => {
      if (!(hasPermission('canEditRentals') || hasPermission('canEditServiceCalls'))) { alert('❌ You do not have permission to create service calls.'); return; }
      const client = findClient(m);
      setEditingItem({
        clientName: client?.name || '',
        issue: `${m.subject || ''}\n${shortBody(m)}`.trim().slice(0, 600)
      });
      setModalType('serviceCall');
      setShowModal(true);
      if (!client) alert(`ℹ️ "${m.name}" is not in your Clients yet - add them first, then pick them in the service call form.\n\nThe mail text was put in as the issue.`);
    };

    const markMail = async (m, status) => {
      try { await patchMail(m, { status }); } catch (err) { alert('Could not update: ' + err.message); }
    };

    const dueInfo = (m) => {
      if (!m.followUpDate) return null;
      const days = Math.round((new Date(m.followUpDate) - new Date(todayStr)) / 86400000);
      const cls = days < 0 ? 'text-red-600 font-semibold' : days === 0 ? 'text-amber-600 font-semibold' : 'text-slate-500';
      return { cls, text: days < 0 ? `Overdue ${Math.abs(days)}d` : days === 0 ? 'Due today' : `Due in ${days}d` };
    };

    const run = emailRunStatus;
    const lastRun = run?.lastRunAt?.toDate ? run.lastRunAt.toDate().toLocaleString('en-IN') : '';

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold">Email Inbox</h2>
            <p className="text-xs text-slate-500">Enquiries, orders and service requests read from the business mailbox every 10 minutes.</p>
          </div>
          {canEdit && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => checkEmailNow(0)} disabled={emailChecking} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium disabled:opacity-60">
                {emailChecking ? '⏳ Checking…' : '📧 Check email now'}
              </button>
              <button onClick={() => checkEmailNow(3)} disabled={emailChecking} className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-lg shadow-sm font-medium disabled:opacity-60" title="Re-read the last 3 days of mail (nothing gets duplicated)">
                ↻ Rescan 3 days
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: 'order', label: 'Orders waiting', value: counts.order, tint: 'text-orange-700' },
            { key: 'enquiry', label: 'Enquiries waiting', value: counts.enquiry, tint: 'text-sky-700' },
            { key: 'service', label: 'Service requests', value: counts.service, tint: 'text-rose-700' },
            { key: 'all', label: 'Reminders overdue', value: counts.overdue, tint: counts.overdue ? 'text-red-600' : 'text-slate-900' }
          ].map(c => (
            <button key={c.key} onClick={() => { setInboxType(c.key); setInboxStatus('new'); }} className="text-left bg-white rounded-xl border border-slate-100 hover:shadow-md transition-shadow p-4">
              <p className="text-xs text-slate-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.tint}`}>{c.value}</p>
            </button>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          {[['all', 'All'], ['order', '🛒 Orders'], ['enquiry', '📧 Enquiries'], ['service', '🔧 Service']].map(([k, label]) => (
            <button key={k} onClick={() => setInboxType(k)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${inboxType === k ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
          ))}
          <select value={inboxStatus} onChange={(e) => setInboxStatus(e.target.value)} className="ml-auto border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value="new">Open ({open.length})</option>
            <option value="added">Added to Leads ({inboxMails.filter(m => m.status === 'added').length})</option>
            <option value="done">Done ({inboxMails.filter(m => m.status === 'done').length})</option>
            <option value="dismissed">Dismissed ({inboxMails.filter(m => m.status === 'dismissed').length})</option>
          </select>
        </div>
        <input type="text" placeholder="🔍 Search customer, email, phone, subject or mail text..." value={inboxSearch} onChange={(e) => setInboxSearch(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none text-base" />

        {run && (run.lastRunAt || run.lastError) && (
          <p className={`text-xs ${run.lastError ? 'text-red-600' : 'text-slate-400'}`}>
            {run.lastError ? `Last check failed - ${run.lastError}` : `Last checked ${lastRun}`}
            {run.lastRunSummary && !run.lastError && ` · read ${run.lastRunSummary.scanned || 0}, new ${run.lastRunSummary.created || 0}, ignored ${run.lastRunSummary.skipped || 0}`}
          </p>
        )}

        {Array.isArray(run?.recentMails) && run.recentMails.length > 0 && (
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer hover:text-slate-700">What the last check read ({run.recentMails.length}) - see why a mail was ignored</summary>
            <ul className="mt-1 space-y-0.5 max-h-56 overflow-y-auto bg-white border border-slate-100 rounded-lg p-2">
              {run.recentMails.map((x, i) => (
                <li key={i} className="truncate">
                  <span className={x.outcome === 'ignored' ? 'text-slate-400' : 'text-emerald-700 font-medium'}>{x.outcome === 'ignored' ? '— ignored' : `✓ ${x.outcome}`}</span>{' '}
                  <span className="text-slate-700">{x.subject || '(no subject)'}</span>
                  <span className="text-slate-400"> · {x.from} · {x.why}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {rows.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-lg font-semibold text-slate-700 mb-1">{inboxMails.length === 0 ? 'No mails yet' : 'Nothing here'}</p>
            <p className="text-slate-500 text-sm">{inboxMails.length === 0 ? 'Click "Rescan 3 days" to read the last few days of mail.' : 'Try another filter, or search.'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {rows.map(m => {
              const t = INBOX_TYPES[m.type] || INBOX_TYPES.enquiry;
              const due = dueInfo(m);
              const expanded = inboxOpenId === m.id;
              const isNew = (m.status || 'new') === 'new';
              return (
                <div key={m.id} className="p-4">
                  <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                    <div className="min-w-0 flex-1 basis-64">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.chip}`}>{t.icon} {t.label}</span>
                        <span className="font-semibold text-slate-900 truncate">{String(m.name || m.email || '').replace(/^["'\s]+|["'\s]+$/g, '')}</span>
                        {(m.mailCount || 1) > 1 && <span className="text-[11px] text-slate-400">{m.mailCount} mails</span>}
                      </div>
                      <p className="text-sm text-slate-700 truncate">{m.subject || '(no subject)'}</p>
                      {m.po && (m.po.number || m.po.amount) && (
                        <p className="text-xs mt-0.5"><span className="bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">📄 {m.po.number ? `PO ${m.po.number}` : 'PO'}{m.po.amount ? ` · ₹${Number(m.po.amount).toLocaleString('en-IN')}` : ''}{(m.po.items || []).length ? ` · ${m.po.items.length} item${m.po.items.length > 1 ? 's' : ''}` : ''}</span></p>
                      )}
                      <p className="text-xs text-slate-400 truncate">{m.email}{m.phone ? ` · ${m.phone}` : ''}</p>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <p className="text-slate-400">{m.lastReceivedAt ? new Date(m.lastReceivedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                      {due && <p className={due.cls}>⏰ {due.text}</p>}
                      {m.followUpNote && <p className="text-slate-400 max-w-[220px] truncate" title={m.followUpNote}>{m.followUpNote}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap mt-3">
                    <button onClick={() => setInboxOpenId(expanded ? null : m.id)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">{expanded ? 'Hide mail' : 'View mail'}</button>
                    {canEdit && isNew && (
                      <>
                        <button onClick={() => addToLeads(m)} className="text-xs px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 font-medium">+ Add to Leads</button>
                        <button onClick={() => createQuotation(m)} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium">📄 Quotation</button>
                        <button onClick={() => createServiceCall(m)} className="text-xs px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 font-medium">🔧 Service call</button>
                        <button onClick={() => markMail(m, 'done')} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">✓ Done</button>
                        <button onClick={() => markMail(m, 'dismissed')} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Dismiss</button>
                      </>
                    )}
                    {canEdit && !isNew && (
                      <button onClick={() => markMail(m, 'new')} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">↩ Re-open</button>
                    )}
                    {m.status === 'added' && <span className="text-xs text-emerald-600 self-center">✓ In Leads</span>}
                    {m.quotationId && (() => {
                      const linked = quotations.find(x => x.id === m.quotationId);
                      return (
                        <>
                          <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-1 self-center font-mono">📄 {m.quotationNumber || 'Quotation'}</span>
                          {linked && <button onClick={() => generateQuotationPDF(linked)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">View PDF</button>}
                          {linked && canEdit && m.email && (
                            <button onClick={() => { if (window.confirm(`Email quotation ${linked.quoteNumber} to ${m.email}?`)) sendQuotationEmail(linked, m.email); }} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium">✉️ Email to {m.email.split('@')[0].slice(0, 14)}</button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {expanded && (
                    <pre className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 whitespace-pre-wrap break-words max-h-72 overflow-y-auto font-sans">{m.body}</pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // LEADS VIEW - sales pipeline for prospective clients, before they become
  // an actual Client record. Kanban-style columns by stage; "Won" leads get
  // a one-click "Convert to Client" that creates the real Client record from
  // the lead's info, so the lead flows straight into the existing
  // Quotations/Rentals workflow rather than being a separate silo.
  const LeadsView = () => {
    const canEdit = hasPermission('canEditLeads');
    const canDelete = hasPermission('canDelete');
    const [searchLeads, setSearchLeads] = useState('');

    const LEAD_STAGES = [
      { id: 'new', label: 'New', color: 'bg-gray-100 text-gray-700', border: 'border-gray-300' },
      { id: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700', border: 'border-blue-300' },
      { id: 'quoted', label: 'Quoted', color: 'bg-indigo-100 text-indigo-700', border: 'border-indigo-300' },
      { id: 'negotiating', label: 'Negotiating', color: 'bg-amber-100 text-amber-700', border: 'border-amber-300' },
      { id: 'won', label: 'Won', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-300' },
      { id: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700', border: 'border-red-300' }
    ];

    const openModal = (item = null) => {
      if (!canEdit && !item) return;
      setEditingItem(item || { stage: 'new' });
      setModalType('lead');
      setShowModal(true);
    };

    const advanceStage = async (lead, newStage) => {
      try {
        const updated = { ...lead, stage: newStage };
        await setDoc(doc(db, 'leads', lead.id), updated);
        await logAudit('UPDATE', 'Lead', lead.id, `${lead.name} moved to ${newStage}`, updated);
      } catch (err) {
        alert('Error updating lead: ' + err.message);
      }
    };

    const convertToClient = async (lead) => {
      if (!window.confirm(`Convert "${lead.name}" to a Client?\n\nThis creates a new Client record from this lead's info.`)) return;
      try {
        const newClient = {
          id: `CLT${Date.now()}`,
          name: lead.name,
          phone: lead.phone || '',
          email: lead.email || '',
          address: lead.address || '',
          companyId: currentCompany,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'Unknown'
        };
        await setDoc(doc(db, 'clients', newClient.id), newClient);
        const updatedLead = { ...lead, stage: 'won', convertedClientId: newClient.id };
        await setDoc(doc(db, 'leads', lead.id), updatedLead);
        await logAudit('CREATE', 'Client', newClient.id, `${newClient.name} (converted from lead)`, newClient);
        autoSyncClient(newClient);
        await logAudit('UPDATE', 'Lead', lead.id, `${lead.name} converted to Client`, updatedLead);
        alert(`✅ "${lead.name}" is now a Client! Find them on the Clients page.`);
      } catch (err) {
        alert('Error converting lead: ' + err.message);
      }
    };

    const deleteLead = async (lead) => {
      if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
      try {
        await logAudit('DELETE', 'Lead', lead.id, lead.name, lead);
        await deleteDoc(doc(db, 'leads', lead.id));
      } catch (err) {
        alert('Error deleting lead: ' + err.message);
      }
    };

    const avatarColors = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500'];
    const getAvatarColor = (name) => {
      const hash = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      return avatarColors[hash % avatarColors.length];
    };
    const getInitials = (name) => {
      if (!name) return '?';
      const words = name.trim().split(/\s+/).filter(Boolean);
      return words.length === 1 ? words[0].slice(0, 2).toUpperCase() : (words[0][0] + words[1][0]).toUpperCase();
    };

    const filteredLeads = leads.filter(l => matchesSearch(searchLeads, l.name, l.contactName, l.phone, l.email));

    // Sales metrics - pipeline value counts only leads still in play (not
    // won/lost, since those are settled); win rate is only meaningful once a
    // lead has actually been decided one way or the other, so the
    // denominator is won+lost, not the whole pipeline.
    const activeLeads = leads.filter(l => l.stage !== 'won' && l.stage !== 'lost');
    const wonLeads = leads.filter(l => l.stage === 'won');
    const lostLeads = leads.filter(l => l.stage === 'lost');
    const pipelineValue = activeLeads.reduce((sum, l) => sum + (Number(l.estimatedValue) || 0), 0);
    const decidedCount = wonLeads.length + lostLeads.length;
    const winRate = decidedCount > 0 ? Math.round((wonLeads.length / decidedCount) * 100) : null;
    const avgDealSize = wonLeads.length > 0 ? Math.round(wonLeads.reduce((sum, l) => sum + (Number(l.estimatedValue) || 0), 0) / wonLeads.length) : null;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-bold">Leads</h2>
          {canEdit && (
            <button onClick={() => openModal()} className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 font-medium flex items-center gap-2">
              <Plus size={18} /> Add Lead
            </button>
          )}
        </div>

        {leads.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500">Pipeline Value</p>
              <p className="text-xl font-bold text-brand-700">₹{pipelineValue.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{activeLeads.length} active lead{activeLeads.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500">Win Rate</p>
              <p className="text-xl font-bold text-emerald-700">{winRate !== null ? `${winRate}%` : '—'}</p>
              <p className="text-xs text-gray-400">{wonLeads.length} won / {lostLeads.length} lost</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500">Avg Deal Size</p>
              <p className="text-xl font-bold text-gray-900">{avgDealSize !== null ? `₹${avgDealSize.toLocaleString()}` : '—'}</p>
              <p className="text-xs text-gray-400">Based on won leads</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500">Total Leads</p>
              <p className="text-xl font-bold text-gray-900">{leads.length}</p>
              <p className="text-xs text-gray-400">All time, this company</p>
            </div>
          </div>
        )}

        <input
          type="text"
          placeholder="🔍 Search by lead name, contact, phone, or email..."
          value={searchLeads}
          onChange={(e) => setSearchLeads(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150 text-base"
        />

        {leads.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 flex flex-col items-center justify-center">
            <div className="text-gray-400 mb-4"><TrendingUp size={64} /></div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No leads yet</h3>
            <p className="text-gray-500 text-center max-w-md">Click "Add Lead" to start tracking a prospective client through your sales pipeline.</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {LEAD_STAGES.map(stage => {
              const stageLeads = filteredLeads.filter(l => (l.stage || 'new') === stage.id);
              return (
                <div key={stage.id} className="shrink-0 w-72">
                  <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl ${stage.color} font-semibold text-sm`}>
                    <span>{stage.label}</span>
                    <span className="bg-white/60 px-2 py-0.5 rounded-full text-xs">{stageLeads.length}</span>
                  </div>
                  <div className="bg-gray-50 rounded-b-xl p-2 space-y-2 min-h-[120px]">
                    {stageLeads.map(lead => {
                      const currentIdx = LEAD_STAGES.findIndex(s => s.id === (lead.stage || 'new'));
                      const nextStage = LEAD_STAGES[currentIdx + 1] && LEAD_STAGES[currentIdx + 1].id !== 'lost' ? LEAD_STAGES[currentIdx + 1] : null;
                      return (
                        <div key={lead.id} className={`bg-white rounded-xl border-2 ${stage.border} p-3 shadow-sm hover:shadow-md transition-all duration-150`}>
                          <div className="flex items-start gap-2 mb-2">
                            <div className={`shrink-0 w-8 h-8 rounded-full ${getAvatarColor(lead.name)} text-white flex items-center justify-center font-bold text-xs`}>
                              {getInitials(lead.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm text-gray-900 truncate">{lead.name}</p>
                              {lead.contactName && <p className="text-xs text-gray-500 truncate">{lead.contactName}</p>}
                            </div>
                          </div>
                          {lead.source === 'indiamart' && (
                            <p className="mb-1"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">🏷️ IndiaMART lead</span></p>
                          )}
                          {lead.source === 'email' && (
                            <p className="mb-1">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${lead.emailType === 'order' ? 'bg-orange-100 text-orange-700' : lead.emailType === 'service' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>
                                {lead.emailType === 'order' ? '🛒 Order' : lead.emailType === 'service' ? '🔧 Service request' : '📧 Enquiry'} · via email
                              </span>
                            </p>
                          )}
                          {(lead.phone || lead.email) && (
                            <p className="text-xs text-gray-500 mb-1 truncate">{lead.phone}{lead.phone && lead.email ? ' · ' : ''}{lead.email}</p>
                          )}
                          {lead.estimatedValue > 0 && (
                            <p className="text-xs font-semibold text-emerald-700 mb-1">₹{Number(lead.estimatedValue).toLocaleString()}</p>
                          )}
                          {lead.notes && <p className="text-xs text-gray-400 mb-2 line-clamp-2">{lead.notes}</p>}
                          {lead.followUpDate && (() => {
                            const today = new Date(); today.setHours(0, 0, 0, 0);
                            const daysLeft = Math.ceil((new Date(lead.followUpDate) - today) / 86400000);
                            const overdue = daysLeft < 0;
                            return (
                              <p className={`text-xs mb-2 flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : daysLeft <= 2 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                                ⏰ {overdue ? `Overdue ${Math.abs(daysLeft)}d` : daysLeft === 0 ? 'Follow up today' : `Follow up in ${daysLeft}d`}
                                {lead.followUpNote ? ` — ${lead.followUpNote}` : ''}
                              </p>
                            );
                          })()}
                          <div className="flex items-center justify-between gap-1 mt-2 pt-2 border-t border-gray-100">
                            <div className="flex gap-1">
                              {canEdit && <button onClick={() => openModal(lead)} className="p-1 rounded-full hover:bg-blue-50 text-blue-600" title="Edit"><Edit size={14} /></button>}
                              {canDelete && <button onClick={() => deleteLead(lead)} className="p-1 rounded-full hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={14} /></button>}
                            </div>
                            <div className="flex gap-1 items-center">
                              {stage.id !== 'won' && stage.id !== 'lost' && canEdit && (
                                <button onClick={() => advanceStage(lead, 'lost')} title="Mark as lost" className="p-1 rounded-full hover:bg-red-50 text-red-400">
                                  <X size={12} />
                                </button>
                              )}
                              {stage.id === 'won' ? (
                                lead.convertedClientId ? (
                                  <span className="text-xs text-emerald-600 font-medium">✓ Converted</span>
                                ) : (
                                  <button onClick={() => convertToClient(lead)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-full font-medium transition-colors duration-150">→ Client</button>
                                )
                              ) : nextStage && canEdit ? (
                                <button onClick={() => advanceStage(lead, nextStage.id)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded-full font-medium transition-colors duration-150">{nextStage.label} →</button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ======================================================================
  // DAILY OPERATIONS - a separate dashboard + day-wise reports for the
  // operations head. "Live" numbers are as of right now; "on the day"
  // numbers follow the selected date (or date range on the report tab).
  // ======================================================================
  const ymdLocal = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const ymdShift = (ymd, days) => { const d = new Date(`${ymd}T00:00:00`); d.setDate(d.getDate() + days); return ymdLocal(d); };
  const ymdDay = (v) => String(v || '').slice(0, 10);
  const ymdDiffDays = (fromYmd, toYmd) => Math.max(0, Math.round((new Date(`${toYmd}T00:00:00`) - new Date(`${fromYmd}T00:00:00`)) / 86400000));
  // Invoices created before the approval step existed count as approved
  const invApproval = (i) => i.approvalStatus || 'approved';

  const buildOpsData = (from, to) => {
    const today = ymdLocal();
    const inRange = (d) => !!d && d >= from && d <= to;
    const loggedDate = (c) => ymdDay(c.date || c.createdAt);
    const doneDate = (c) => (c.status === 'completed' ? ymdDay(c.completedAt || c.returnDate || c.date) : '');
    const ageOf = (c) => ymdDiffDays(loggedDate(c) || today, today);
    const printerLabel = (c) => `${c.assetNumber ? c.assetNumber + ' / ' : ''}${c.serialNumber || ''}`;

    const openCalls = serviceCalls.filter(c => c.status !== 'completed');
    const loggedCalls = serviceCalls.filter(c => inRange(loggedDate(c)));
    const doneCalls = serviceCalls.filter(c => inRange(doneDate(c)));
    const assignedCalls = serviceCalls.filter(c => (c.assignmentHistory || []).some(h => h.engineer && inRange(ymdDay(h.at))));
    const touched = [...new Map([...loggedCalls, ...doneCalls, ...assignedCalls].map(c => [c.id, c])).values()]
      .sort((a, b) => (a.status === 'completed' ? 1 : 0) - (b.status === 'completed' ? 1 : 0) || String(loggedDate(b)).localeCompare(String(loggedDate(a))));
    const prioRank = { high: 0, medium: 1, low: 2 };
    const board = [...openCalls].sort((a, b) => (prioRank[a.priority || 'medium'] ?? 1) - (prioRank[b.priority || 'medium'] ?? 1) || ageOf(b) - ageOf(a));
    const agedCalls = openCalls.filter(c => ageOf(c) > 3);
    const unassignedOpen = openCalls.filter(c => !c.engineer);
    const highOpen = openCalls.filter(c => (c.priority || 'medium') === 'high');

    const engNames = engineers.map(e => e.name);
    const strayNames = [...new Set(serviceCalls.map(c => c.engineer).filter(n => n && !engNames.includes(n)))];
    const engRows = [...engNames, ...strayNames].map(n => {
      const mine = openCalls.filter(c => c.engineer === n);
      return {
        name: n,
        clients: clients.filter(c => c.allocatedEngineer === n).length,
        assigned: assignedCalls.filter(c => c.engineer === n).length,
        logged: loggedCalls.filter(c => c.engineer === n).length,
        completed: doneCalls.filter(c => c.engineer === n).length,
        open: mine.filter(c => c.status !== 'in-progress').length,
        inProgress: mine.filter(c => c.status === 'in-progress').length,
        oldest: mine.reduce((m, c) => Math.max(m, ageOf(c)), 0)
      };
    });
    engRows.push({
      name: 'Unassigned', clients: clients.filter(c => !c.allocatedEngineer).length, assigned: 0,
      logged: loggedCalls.filter(c => !c.engineer).length, completed: doneCalls.filter(c => !c.engineer).length,
      open: unassignedOpen.filter(c => c.status !== 'in-progress').length, inProgress: unassignedOpen.filter(c => c.status === 'in-progress').length,
      oldest: unassignedOpen.reduce((m, c) => Math.max(m, ageOf(c)), 0)
    });

    const moves = printerMovements.filter(m => inRange(ymdDay(m.date)));
    const latest = {};
    printerMovements.forEach(m => {
      const k = m.printerSerial; if (!k) return;
      const cur = latest[k];
      if (!cur || String(m.timestamp || m.date || '') > String(cur.timestamp || cur.date || '')) latest[k] = m;
    });
    const atWorkshop = Object.values(latest).filter(m => m.type === 'IN').map(m => ({ ...m, days: ymdDiffDays(ymdDay(m.date) || today, today) })).sort((a, b) => b.days - a.days);

    const invCreated = invoices.filter(i => inRange(ymdDay(i.createdAt || i.date)));
    const invApproved = invoices.filter(i => inRange(ymdDay(i.approvedAt)));
    const invPending = invoices.filter(i => invApproval(i) === 'pending');
    const readings = meterReadings.filter(r => inRange(ymdDay(r.date)));
    const remindersDue = reminders.filter(r => (r.status || 'open') === 'open' && r.dueDate && r.dueDate <= today).length + getPmDueItems().filter(x => x.days <= 0).length;
    const invAmount = (i) => { try { return Math.round(invoiceTotals(i, 'Karnataka').total * 100) / 100; } catch (e) { return 0; } };

    const summary = [
      ['Service calls logged', loggedCalls.length],
      ['Service calls completed', doneCalls.length],
      ['Service calls assigned / reassigned', assignedCalls.length],
      ['Printers received (IN)', moves.filter(m => m.type === 'IN').length],
      ['Printers returned (OUT)', moves.filter(m => m.type === 'OUT').length],
      ['Meter readings captured', readings.length],
      ['Invoices created', invCreated.length],
      ['Invoices approved', invApproved.length],
      ['Open service calls (live)', openCalls.length],
      ['Open more than 3 days (live)', agedCalls.length],
      ['Open and unassigned (live)', unassignedOpen.length],
      ['High priority open (live)', highOpen.length],
      ['Printers at our workshop (live)', atWorkshop.length],
      ['Invoices awaiting approval (live)', invPending.length],
      ['Reminders / preventive maintenance due (live)', remindersDue]
    ];
    const callRows = touched.map(c => ({
      Date: loggedDate(c), Client: c.clientName || '', Printer: printerLabel(c), Issue: c.issue || '', Engineer: c.engineer || 'Unassigned',
      Priority: c.priority || 'medium', Status: c.status || 'open', 'Age (days)': c.status === 'completed' ? '' : ageOf(c)
    }));
    const doneRows = doneCalls.map(c => ({
      'Completed on': doneDate(c), Client: c.clientName || '', Printer: printerLabel(c), Engineer: c.engineer || 'Unassigned',
      'Work done': c.workDone || '', 'Parts used': c.partsUsed || '', 'Time (mins)': c.timeSpentMins || '', 'Accepted by': c.signedOffBy || ''
    }));
    const moveRows = moves.map(m => ({
      Date: ymdDay(m.date), Type: m.type === 'IN' ? 'Received' : 'Returned', Printer: `${m.printerSerial || ''} ${m.printerModel || ''}`.trim(),
      Client: m.clientName || '', Technician: m.technician || '', Notes: m.serviceNotes || ''
    }));
    const invRows = [...new Map([...invCreated, ...invApproved].map(i => [i.id, i])).values()].map(i => ({
      Invoice: i.invoiceNumber || '', Client: i.clientName || '', 'Amount (Rs.)': invAmount(i), Created: ymdDay(i.createdAt || i.date),
      Approval: invApproval(i), 'Approved by': i.approvedBy || '', Status: i.status || ''
    }));
    return { today, summary, engRows, board, atWorkshop, callRows, doneRows, moveRows, invRows, ageOf, printerLabel,
      counts: { open: openCalls.length, aged: agedCalls.length, unassigned: unassignedOpen.length, high: highOpen.length, workshop: atWorkshop.length, invPending: invPending.length, remindersDue,
        logged: loggedCalls.length, done: doneCalls.length, assigned: assignedCalls.length, recvd: moves.filter(m => m.type === 'IN').length, returned: moves.filter(m => m.type === 'OUT').length, readings: readings.length } };
  };

  // ---------------- Stock / Stores ----------------
  // stockItems: master list. stockMoves: ledger; each row has a signed `delta` so on-hand = sum of deltas.
  const stockOnHand = (itemId) => stockMoves.reduce((n, m) => (m.itemId === itemId ? n + (Number(m.delta) || 0) : n), 0);
  const saveStockItem = async () => {
    const f = stockUi.item;
    if (!String(f.name || '').trim()) { alert('Item name is required.'); return; }
    const id = f.id || `STK${Date.now()}`;
    const rec = {
      id, companyId: currentCompany, name: f.name.trim(), sku: (f.sku || '').trim(), category: f.category || 'Spare part', unit: f.unit || 'Nos',
      minQty: Number(f.minQty) || 0, rate: Number(f.rate) || 0, location: (f.location || '').trim(), vendor: (f.vendor || '').trim(), active: f.active !== false,
      createdAt: f.createdAt || new Date().toISOString()
    };
    try {
      await setDoc(doc(db, 'stockItems', id), rec);
      await logAudit(f.id ? 'UPDATE' : 'CREATE', 'StockItem', id, rec.name, rec);
      if (!f.id && Number(f.openingQty) > 0) {
        await recordStockMove(rec, 'in', Number(f.openingQty), { ref: 'Opening stock', vendor: rec.vendor });
      }
      setStockUi(u => ({ ...u, item: null }));
    } catch (err) { alert('Could not save item: ' + err.message); }
  };
  const recordStockMove = async (item, type, qty, extra = {}) => {
    const sign = type === 'use' ? 0 : type === 'in' || type === 'return' ? 1 : type === 'issue' ? -1 : (extra.sign || 1);
    const id = `SMV${Date.now()}${Math.floor(Math.random() * 1000)}`;
    const rec = {
      id, companyId: currentCompany, itemId: item.id, itemName: item.name, unit: item.unit || 'Nos', type, qty, delta: sign * qty,
      date: extra.date || new Date().toISOString().slice(0, 10), engineer: extra.engineer || '', clientName: extra.clientName || '', callId: extra.callId || '',
      consumed: !!extra.consumed, ref: extra.ref || '', vendor: (extra.vendor || '').trim(), by: currentUser?.email || 'Unknown', createdAt: new Date().toISOString()
    };
    await setDoc(doc(db, 'stockMoves', id), rec);
    await logAudit('CREATE', 'StockMove', id, `${type.toUpperCase()} ${qty} ${rec.unit} ${item.name}`, rec);
    return rec;
  };
  const saveStockMove = async () => {
    const f = stockUi.move;
    const item = stockItems.find(i => i.id === f.itemId);
    const qty = Number(f.qty);
    if (!item) { alert('Pick an item.'); return; }
    if (!(qty > 0)) { alert('Enter a quantity greater than zero.'); return; }
    if (f.type === 'in' && !String(f.vendor || '').trim()) { alert('Enter the vendor / supplier this stock came from.'); return; }
    if (f.type === 'issue') {
      if (!f.engineer && !f.clientName) { alert('Issue to an engineer or a client.'); return; }
      if (qty > stockOnHand(item.id)) { alert(`Only ${stockOnHand(item.id)} ${item.unit} of ${item.name} in stock. You cannot issue ${qty}.`); return; }
    }
    if (f.type === 'adjust') {
      if (!(f.sign === 1 || f.sign === -1)) { alert('Choose whether to add or remove stock.'); return; }
      if (!String(f.ref || '').trim()) { alert('Give a reason for the adjustment.'); return; }
      if (f.sign === -1 && qty > stockOnHand(item.id)) { alert('That would take stock below zero.'); return; }
    }
    try {
      await recordStockMove(item, f.type, qty, f);
      if (f.type === 'in' && !String(item.vendor || '').trim()) { await setDoc(doc(db, 'stockItems', item.id), { vendor: f.vendor.trim() }, { merge: true }); }   // first vendor becomes the item's usual vendor
      setStockUi(u => ({ ...u, move: null }));
    } catch (err) { alert('Could not save: ' + err.message); }
  };

  const StockView = () => {
    const canEditStock = moduleOverride('stock') ? ['rw', 'create'].includes(moduleOverride('stock')) : ['admin', 'manager'].includes(userRole);
    const q = (stockUi.search || '').toLowerCase();
    const rows = stockItems
      .filter(i => i.active !== false)
      .map(i => ({ ...i, onHand: stockOnHand(i.id) }))
      .filter(i => !q || [i.name, i.sku, i.category, i.location].some(x => String(x || '').toLowerCase().includes(q)));
    const low = rows.filter(r => r.minQty > 0 && r.onHand <= r.minQty);
    const totalValue = rows.reduce((n, r) => n + r.onHand * (r.rate || 0), 0);
    const moves = [...stockMoves].sort((a, b) => String(b.createdAt || b.date).localeCompare(String(a.createdAt || a.date)))
      .filter(m => (!stockUi.fEng || m.engineer === stockUi.fEng) && (!q || [m.itemName, m.engineer, m.clientName, m.ref].some(x => String(x || '').toLowerCase().includes(q))));
    const holding = {};
    stockMoves.forEach(m => {
      if (!m.engineer) return;
      if (m.type === 'issue' && !m.consumed) holding[`${m.engineer}||${m.itemId}`] = { engineer: m.engineer, itemName: m.itemName, unit: m.unit, qty: (holding[`${m.engineer}||${m.itemId}`]?.qty || 0) + m.qty };
      if (m.type === 'return' || m.type === 'use') holding[`${m.engineer}||${m.itemId}`] = { engineer: m.engineer, itemName: m.itemName, unit: m.unit, qty: (holding[`${m.engineer}||${m.itemId}`]?.qty || 0) - m.qty };
    });
    const holdRows = Object.values(holding).filter(h => h.qty > 0).sort((a, b) => a.engineer.localeCompare(b.engineer));
    const tab = stockUi.tab || 'items';
    const openMove = (type, itemId = '') => setStockUi(u => ({ ...u, move: { type, itemId, qty: '', engineer: '', clientName: '', callId: '', consumed: false, ref: '', vendor: (stockItems.find(x => x.id === itemId) || {}).vendor || '', date: new Date().toISOString().slice(0, 10), sign: 0 } }));
    const typeLabel = { in: '📥 Received', issue: '📤 Issued', return: '↩️ Returned', adjust: '✏️ Adjusted', use: '🔧 Used at client' };
    const openCalls = serviceCalls.filter(c => c.status !== 'completed');
    const inp = 'w-full border rounded-lg px-3 py-2 text-base';
    const mv = stockUi.move, it = stockUi.item;

    const exportStock = () => {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map(r => ({ Item: r.name, Code: r.sku, Category: r.category, Unit: r.unit, 'On hand': r.onHand, 'Min level': r.minQty, Rate: r.rate, Value: r.onHand * (r.rate || 0), Location: r.location, Vendor: r.vendor || '' }))), 'Stock');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stockMoves.map(m => ({ Date: m.date, Type: m.type, Item: m.itemName, Qty: m.qty, Unit: m.unit, Engineer: m.engineer, Client: m.clientName, Call: m.callId, Consumed: m.consumed ? 'Yes' : '', Note: m.ref, By: m.by }))), 'Movements');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(holdRows.map(h => ({ Engineer: h.engineer, Item: h.itemName, Qty: h.qty, Unit: h.unit }))), 'With engineers');
      XLSX.writeFile(wb, `Stock_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h2 className="text-2xl font-bold text-slate-900">Stock / Stores</h2><p className="text-sm text-slate-500">Spares and consumables: receive, issue to engineers, returns, and what each engineer is carrying.</p></div>
          <div className="flex gap-2 flex-wrap">
            {canEditStock && <button onClick={() => setStockUi(u => ({ ...u, item: { name: '', sku: '', category: 'Spare part', unit: 'Nos', minQty: '', rate: '', location: '', openingQty: '' } }))} className="bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium">+ New item</button>}
            {canEditStock && <button onClick={() => openMove('in')} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium">📥 Receive</button>}
            {canEditStock && <button onClick={() => openMove('issue')} className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium">📤 Issue</button>}
            <button onClick={exportStock} className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm">Excel</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[['Items', rows.length], ['Low / out of stock', low.length], ['Stock value', `₹${Math.round(totalValue).toLocaleString('en-IN')}`], ['Items with engineers', holdRows.length]].map(([k, v]) => (
            <div key={k} className="bg-white rounded-xl border border-slate-100 p-4"><p className="text-xs text-slate-500">{k}</p><p className={`text-2xl font-bold ${k.startsWith('Low') && v > 0 ? 'text-red-600' : 'text-slate-900'}`}>{v}</p></div>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          {[['items', 'Stock'], ['moves', 'Movements'], ['hold', 'With engineers']].map(([k, l]) => (
            <button key={k} onClick={() => setStockUi(u => ({ ...u, tab: k }))} className={`px-3 py-1.5 rounded-full text-sm ${tab === k ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700'}`}>{l}</button>
          ))}
          <input value={stockUi.search || ''} onChange={(e) => setStockUi(u => ({ ...u, search: e.target.value }))} placeholder="Search…" className="border rounded-lg px-3 py-1.5 text-sm ml-auto" />
          {tab === 'moves' && <select value={stockUi.fEng || ''} onChange={(e) => setStockUi(u => ({ ...u, fEng: e.target.value }))} className="border rounded-lg px-2 py-1.5 text-sm"><option value="">All engineers</option>{engineers.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}</select>}
        </div>

        {tab === 'items' && (
          <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr>
              <th className="text-left p-3">Item</th><th className="text-left p-3">Category</th><th className="text-right p-3">On hand</th><th className="text-right p-3">Min</th><th className="text-right p-3">Value</th><th className="text-left p-3">Location</th><th className="p-3"></th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-400 italic">No stock items yet. Add one with “+ New item”.</td></tr>}
                {rows.map(r => {
                  const isLow = r.minQty > 0 && r.onHand <= r.minQty;
                  return (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="p-3"><p className="font-medium text-slate-800">{r.name}</p>{r.sku && <p className="text-xs text-slate-400">{r.sku}</p>}{r.vendor && <p className="text-xs text-slate-400">Vendor: {r.vendor}</p>}</td>
                      <td className="p-3 text-slate-600">{r.category}</td>
                      <td className={`p-3 text-right font-semibold ${r.onHand <= 0 ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-slate-800'}`}>{r.onHand} {r.unit}{isLow && <span className="ml-1 text-xs">{r.onHand <= 0 ? '⛔ out' : '⚠ low'}</span>}</td>
                      <td className="p-3 text-right text-slate-500">{r.minQty || '-'}</td>
                      <td className="p-3 text-right text-slate-600">₹{Math.round(r.onHand * (r.rate || 0)).toLocaleString('en-IN')}</td>
                      <td className="p-3 text-slate-600">{r.location}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {canEditStock && <><button onClick={() => openMove('in', r.id)} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 mr-1">Receive</button>
                          <button onClick={() => openMove('issue', r.id)} className="text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 mr-1">Issue</button>
                          <button onClick={() => openMove('adjust', r.id)} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 mr-1">Adjust</button>
                          <button onClick={() => setStockUi(u => ({ ...u, item: { ...r } }))} className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600">Edit</button></>}
                      </td>
                    </tr>
                  );
                })}
              </tbody></table>
          </div>
        )}

        {tab === 'moves' && (
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {moves.length === 0 && <p className="p-6 text-center text-slate-400 italic text-sm">No movements yet.</p>}
            {moves.slice(0, 300).map(m => (
              <div key={m.id} className="p-3 flex justify-between gap-3 flex-wrap">
                <div><p className="text-sm font-medium text-slate-800">{typeLabel[m.type] || m.type} · {m.itemName} · {m.delta > 0 ? '+' : ''}{m.delta} {m.unit}</p>
                  <p className="text-xs text-slate-500">{[m.vendor && `From: ${m.vendor}`, m.engineer && `Engineer: ${m.engineer}`, m.clientName && `Client: ${m.clientName}`, m.callId && `Call: ${m.callId}`, m.consumed && 'used up on call', m.ref].filter(Boolean).join(' · ')}</p></div>
                <p className="text-xs text-slate-400">{fmtDMY(m.date)} · {m.by}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'hold' && (
          <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr><th className="text-left p-3">Engineer</th><th className="text-left p-3">Item</th><th className="text-right p-3">Carrying</th><th className="p-3"></th></tr></thead>
              <tbody>
                {holdRows.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-slate-400 italic">No engineer is carrying stock.</td></tr>}
                {holdRows.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="p-3">{h.engineer}</td><td className="p-3">{h.itemName}</td><td className="p-3 text-right font-semibold">{h.qty} {h.unit}</td>
                    <td className="p-3 text-right">{canEditStock && <button onClick={() => { const itm = stockItems.find(x => x.name === h.itemName); setStockUi(u => ({ ...u, move: { type: 'return', itemId: itm?.id || '', qty: h.qty, engineer: h.engineer, clientName: '', callId: '', consumed: false, ref: '', date: new Date().toISOString().slice(0, 10), sign: 0 } })); }} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">Take back</button>}</td></tr>
                ))}
              </tbody></table>
          </div>
        )}

        {it && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setStockUi(u => ({ ...u, item: null }))}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold">{it.id ? 'Edit item' : 'New stock item'}</h3>
              <input className={inp} placeholder="Item name *" value={it.name || ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, name: e.target.value } }))} />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Code / part no." value={it.sku || ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, sku: e.target.value } }))} />
                <select className={inp} value={it.category || 'Spare part'} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, category: e.target.value } }))}>{['Spare part', 'Toner / cartridge', 'Consumable', 'Cable / accessory', 'Device', 'Other'].map(c => <option key={c}>{c}</option>)}</select>
                <input className={inp} placeholder="Unit (Nos, Kg, Set)" value={it.unit || ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, unit: e.target.value } }))} />
                <input className={inp} type="number" placeholder="Rate ₹" value={it.rate ?? ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, rate: e.target.value } }))} />
                <input className={inp} type="number" placeholder="Minimum level (alert)" value={it.minQty ?? ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, minQty: e.target.value } }))} />
                <input className={inp} placeholder="Rack / location" value={it.location || ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, location: e.target.value } }))} />
                <input className={`${inp} col-span-2`} list="stk-vendors" placeholder="Usual vendor / supplier" value={it.vendor || ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, vendor: e.target.value } }))} />
                <datalist id="stk-vendors">{stockVendors().map(v => <option key={v} value={v} />)}</datalist>
              </div>
              {!it.id && <input className={inp} type="number" placeholder="Opening quantity (optional)" value={it.openingQty ?? ''} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, openingQty: e.target.value } }))} />}
              {it.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={it.active !== false} onChange={(e) => setStockUi(u => ({ ...u, item: { ...u.item, active: e.target.checked } }))} /> Active (untick to hide from the list)</label>}
              <div className="flex justify-end gap-2 pt-2"><button onClick={() => setStockUi(u => ({ ...u, item: null }))} className="px-4 py-2 rounded-lg border text-sm">Cancel</button><button onClick={saveStockItem} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium">Save</button></div>
            </div>
          </div>
        )}

        {mv && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setStockUi(u => ({ ...u, move: null }))}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold">{{ in: 'Receive stock', issue: 'Issue stock', return: 'Take back from engineer', adjust: 'Adjust stock' }[mv.type]}</h3>
              <select className={inp} value={mv.itemId} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, itemId: e.target.value, vendor: u.move.vendor || (stockItems.find(i => i.id === e.target.value) || {}).vendor || '' } }))}><option value="">Select item…</option>{stockItems.filter(i => i.active !== false).map(i => <option key={i.id} value={i.id}>{i.name} (in stock: {stockOnHand(i.id)} {i.unit})</option>)}</select>
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} type="number" min="0" placeholder="Quantity" value={mv.qty} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, qty: e.target.value } }))} />
                <input className={inp} type="date" value={mv.date} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, date: e.target.value } }))} />
              </div>
              {(mv.type === 'issue' || mv.type === 'return') && (
                <select className={inp} value={mv.engineer} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, engineer: e.target.value } }))}><option value="">{mv.type === 'issue' ? 'Issue to engineer…' : 'Returned by engineer…'}</option>{engineers.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}</select>
              )}
              {mv.type === 'issue' && (<>
                <select className={inp} value={mv.callId} onChange={(e) => { const c = serviceCalls.find(x => x.id === e.target.value); setStockUi(u => ({ ...u, move: { ...u.move, callId: e.target.value, clientName: c ? c.clientName || '' : u.move.clientName, engineer: u.move.engineer || (c ? c.engineer || '' : '') } })); }}><option value="">Link to a service call (optional)…</option>{openCalls.map(c => <option key={c.id} value={c.id}>{c.clientName} · {c.issue || c.serialNumber || c.id}</option>)}</select>
                <input className={inp} placeholder="Client (if supplied to a client)" value={mv.clientName} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, clientName: e.target.value } }))} />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mv.consumed} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, consumed: e.target.checked } }))} /> Used up / fitted at the client (not carried by the engineer)</label>
              </>)}
              {mv.type === 'adjust' && (
                <div className="flex gap-2">{[[1, '➕ Add'], [-1, '➖ Remove']].map(([v, l]) => <button key={v} onClick={() => setStockUi(u => ({ ...u, move: { ...u.move, sign: v } }))} className={`flex-1 py-2 rounded-lg text-sm border ${mv.sign === v ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-300'}`}>{l}</button>)}</div>
              )}
              {mv.type === 'in' && <><input className={inp} list="stk-vendors" placeholder="Vendor / supplier (from whom) *" value={mv.vendor || ''} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, vendor: e.target.value } }))} /><datalist id="stk-vendors">{stockVendors().map(v => <option key={v} value={v} />)}</datalist></>}
              <input className={inp} placeholder={mv.type === 'in' ? 'Bill / invoice no.' : mv.type === 'adjust' ? 'Reason (required)' : 'Note'} value={mv.ref} onChange={(e) => setStockUi(u => ({ ...u, move: { ...u.move, ref: e.target.value } }))} />
              <div className="flex justify-end gap-2 pt-2"><button onClick={() => setStockUi(u => ({ ...u, move: null }))} className="px-4 py-2 rounded-lg border text-sm">Cancel</button><button onClick={saveStockMove} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium">Save</button></div>
            </div>
          </div>
        )}
      </div>
    );
  };


  // =====================================================================================================================
  // PURCHASE ORDERS: orders to vendors (PDF in the quotation style), receiving goods against an order (which books the
  // stock in, with the vendor), and a vendor list. Numbers come from the same allocator as every other document.
  // State lives in App (poUi) and the view is CALLED, not mounted, so typing never loses focus.
  // =====================================================================================================================
  const PO_STATUS = { open: ['Open', 'bg-sky-100 text-sky-700'], partial: ['Part received', 'bg-amber-100 text-amber-800'], received: ['Received', 'bg-emerald-100 text-emerald-700'], cancelled: ['Cancelled', 'bg-slate-200 text-slate-600'] };
  const DEFAULT_PO_TERMS = 'Please quote this purchase order number on your delivery challan and invoice.\nGoods to be delivered to the address above by the delivery date.\nPayment within 30 days of receiving the goods and a correct tax invoice.\nGoods that do not match the specification or are damaged may be returned.';
  const poOwnState = () => (COMPANIES[currentCompany] && COMPANIES[currentCompany].state) || 'Karnataka';
  const poTotals = (po) => {
    const net = (po.items || []).reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0), 0);
    const own = poOwnState();
    return gstTotals(net, po.gstRate === '' || po.gstRate === undefined ? 18 : Number(po.gstRate), stateFromGstin(po.vendorGSTIN) || own, own);
  };
  const vendorFields = (v) => ({ vendorId: v.id, vendorName: v.name, vendorAddress: v.address || '', vendorGSTIN: v.gstin || '', vendorEmail: v.email || '', vendorPhone: v.phone || '' });
  const blankPO = (extra = {}) => ({
    vendorId: '', vendorName: '', vendorAddress: '', vendorGSTIN: '', vendorEmail: '', vendorPhone: '', date: todayYmd(), deliveryDate: addDaysYmd(todayYmd(), 7), gstRate: 18,
    items: [{ stockItemId: '', description: '', hsn: '', qty: 1, unit: 'Nos', rate: '' }], terms: DEFAULT_PO_TERMS, notes: '', ...extra
  });
  // From the low-stock list: one order for the vendor that supplies most of them, quantity topping each item up to twice its minimum
  const poFromLowStock = (lowItems) => {
    const list = lowItems && lowItems.length ? lowItems : lowStockItems();
    if (!list.length) { alert('Nothing is below its minimum level.'); return; }
    const byVendor = {};
    list.forEach(i => { const v = i.vendor || ''; byVendor[v] = (byVendor[v] || 0) + 1; });
    const top = Object.entries(byVendor).sort((a, b) => b[1] - a[1])[0][0];
    const pick = list.filter(i => (i.vendor || '') === top);
    const v = vendors.find(x => normStockName(x.name) === normStockName(top));
    setPoUi(u => ({ ...u, tab: 'orders', form: blankPO({
      ...(v ? vendorFields(v) : { vendorName: top }),
      items: pick.map(i => ({ stockItemId: i.id, description: i.name, hsn: '', qty: Math.max(1, Number(i.minQty) * 2 - stockOnHand(i.id)), unit: i.unit || 'Nos', rate: i.rate || '' })),
      notes: list.length > pick.length ? `Still low, from other vendors: ${list.filter(i => !pick.includes(i)).map(i => i.name).join(', ')}` : ''
    }) }));
    setCurrentView('purchase');
  };
  const downloadPOPDF = (po) => buildPurchaseOrderPDFDoc(po, pdfDeps).save(`${String(po.poNumber).replace(/\\/g, '-')}_${(po.vendorName || 'vendor').replace(/[^a-z0-9]/gi, '_')}.pdf`);

  const savePO = async () => {
    const f = poUi.form;
    if (!String(f.vendorName || '').trim()) { alert('Enter the vendor.'); return; }
    const valid = f.items.filter(i => String(i.description || '').trim() && parseFloat(i.qty) > 0);
    if (!valid.length) { alert('Add at least one item with a quantity.'); return; }
    if (valid.some(i => i.rate === '' || isNaN(parseFloat(i.rate)))) { alert('Enter a rate for every item.'); return; }
    setPoUi(u => ({ ...u, saving: true }));
    try {
      const isNew = !f.id;
      let vendorId = f.vendorId;
      if (!vendorId) { const ex = vendors.find(v => normStockName(v.name) === normStockName(f.vendorName)); if (ex) vendorId = ex.id; }
      if (!vendorId) {   // a vendor typed for the first time joins the vendor list
        vendorId = `VND${Date.now()}`;
        await setDoc(doc(db, 'vendors', vendorId), { id: vendorId, companyId: currentCompany, name: f.vendorName.trim(), address: f.vendorAddress || '', gstin: (f.vendorGSTIN || '').toUpperCase(), email: f.vendorEmail || '', phone: f.vendorPhone || '', createdAt: new Date().toISOString(), createdBy: currentUser?.email || 'Unknown' });
      }
      const rec = {
        ...f, id: f.id || `PO${Date.now()}`, companyId: f.companyId || currentCompany, vendorId, vendorName: f.vendorName.trim(), vendorGSTIN: (f.vendorGSTIN || '').toUpperCase(),
        poNumber: f.poNumber || await generateDocNumber('purchaseOrders', 'poNumber', 'PO'),
        items: valid.map(i => ({ ...i, qty: parseFloat(i.qty), rate: parseFloat(i.rate), receivedQty: Number(i.receivedQty) || 0 })),
        status: f.status || 'open', receipts: f.receipts || [], createdAt: f.createdAt || new Date().toISOString(), createdBy: f.createdBy || currentUser?.email || 'Unknown'
      };
      await setDoc(doc(db, 'purchaseOrders', rec.id), rec);
      await logAudit(isNew ? 'CREATE' : 'UPDATE', 'PurchaseOrder', rec.id, `${rec.poNumber} - ${rec.vendorName}`, rec);
      setPoUi(u => ({ ...u, form: null, saving: false }));
      alert(`✅ Purchase order ${rec.poNumber} saved.`);
    } catch (err) { alert('Could not save: ' + err.message); setPoUi(u => ({ ...u, saving: false })); }
  };
  const openReceive = (po) => setPoUi(u => ({ ...u, recv: { poId: po.id, date: todayYmd(), billNo: '', qty: (po.items || []).map(i => String(Math.max(0, (i.qty || 0) - (i.receivedQty || 0)))) } }));
  const saveReceive = async () => {
    const r = poUi.recv;
    const po = purchaseOrders.find(p => p.id === r.poId);
    if (!po) return;
    const lines = po.items.map((i, idx) => ({ idx, qty: Number(r.qty[idx]) || 0 })).filter(l => l.qty > 0);
    if (!lines.length) { alert('Enter the quantity received for at least one item.'); return; }
    const over = lines.find(l => l.qty > (po.items[l.idx].qty - (po.items[l.idx].receivedQty || 0)) + 1e-9);
    if (over) { alert(`${po.items[over.idx].description}: only ${po.items[over.idx].qty - (po.items[over.idx].receivedQty || 0)} still to receive.`); return; }
    try {
      const items = po.items.map(i => ({ ...i }));
      for (const l of lines) {
        items[l.idx].receivedQty = (Number(items[l.idx].receivedQty) || 0) + l.qty;
        const it = items[l.idx].stockItemId ? stockItems.find(s => s.id === items[l.idx].stockItemId) : null;
        if (it) await recordStockMove(it, 'in', l.qty, { vendor: po.vendorName, ref: `PO ${po.poNumber}${r.billNo ? ' / bill ' + r.billNo : ''}`, date: r.date });   // stock book + vendor in one step
      }
      const done = items.every(i => (Number(i.receivedQty) || 0) >= i.qty - 1e-9);
      const upd = { items, status: done ? 'received' : 'partial', receipts: [...(po.receipts || []), { date: r.date, billNo: r.billNo || '', by: currentUser?.email || 'Unknown', lines }] };
      if (done) upd.receivedAt = new Date().toISOString();
      await setDoc(doc(db, 'purchaseOrders', po.id), upd, { merge: true });
      await logAudit('UPDATE', 'PurchaseOrder', po.id, `${po.poNumber} goods received${done ? ' (complete)' : ''}`, upd);
      setPoUi(u => ({ ...u, recv: null }));
    } catch (err) { alert('Could not save: ' + err.message); }
  };
  const setPoStatus = async (po, status, extra = {}) => {
    try { await setDoc(doc(db, 'purchaseOrders', po.id), { status, ...extra }, { merge: true }); await logAudit('UPDATE', 'PurchaseOrder', po.id, `${po.poNumber} ${status}`, { status, ...extra }); }
    catch (err) { alert('Could not update: ' + err.message); }
  };
  const saveVendor = async () => {
    const v = poUi.vendor;
    if (!String(v.name || '').trim()) { alert('Vendor name is required.'); return; }
    try {
      const id = v.id || `VND${Date.now()}`;
      const rec = { id, companyId: v.companyId || currentCompany, name: v.name.trim(), address: v.address || '', gstin: (v.gstin || '').toUpperCase(), email: v.email || '', phone: v.phone || '', createdAt: v.createdAt || new Date().toISOString(), createdBy: v.createdBy || currentUser?.email || 'Unknown' };
      await setDoc(doc(db, 'vendors', id), rec);
      await logAudit(v.id ? 'UPDATE' : 'CREATE', 'Vendor', id, rec.name, rec);
      setPoUi(u => ({ ...u, vendor: null }));
    } catch (err) { alert('Could not save vendor: ' + err.message); }
  };

  const PurchaseView = () => {
    const canMake = canCreateIn('purchase');
    const canEditPO = hasPermission('canManagePurchase');
    const today = todayYmd();
    const q = (poUi.search || '').toLowerCase();
    const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm';
    const f = poUi.form, rv = poUi.recv, vf = poUi.vendor;
    const setF = (patch) => setPoUi(u => ({ ...u, form: { ...u.form, ...patch } }));
    const setItem = (idx, patch) => setPoUi(u => ({ ...u, form: { ...u.form, items: u.form.items.map((it, k) => (k === idx ? { ...it, ...patch } : it)) } }));
    const isOverdue = (p) => ['open', 'partial'].includes(p.status) && p.deliveryDate && p.deliveryDate < today;
    const rows = purchaseOrders
      .filter(p => !q || [p.poNumber, p.vendorName, ...(p.items || []).map(i => i.description)].some(x => String(x || '').toLowerCase().includes(q)))
      .filter(p => !poUi.status || (poUi.status === 'overdue' ? isOverdue(p) : p.status === poUi.status));
    const exportPO = () => {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(purchaseOrders.map(p => { const t = poTotals(p); return { 'PO No': p.poNumber, Date: p.date, Vendor: p.vendorName, GSTIN: p.vendorGSTIN, 'Delivery by': p.deliveryDate, Items: (p.items || []).map(i => `${i.description} x${i.qty}`).join('; '), 'Net amount': t.net, Total: t.total, Status: p.status, 'Created by': p.createdBy }; })), 'Purchase orders');
      XLSX.writeFile(wb, `Purchase_orders_${today}.xlsx`);
    };
    const totals = f ? poTotals(f) : null;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold">Purchase Orders</h2>
            <p className="text-xs text-slate-500">{COMPANIES[currentCompany]?.name} · orders to vendors, goods received into Stock, vendor list. Numbers follow the same series as every other document.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canMake && !f && <button onClick={() => setPoUi(u => ({ ...u, tab: 'orders', form: blankPO() }))} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Plus size={18} /> New purchase order</button>}
            {canMake && !f && lowStockItems().length > 0 && <button onClick={() => poFromLowStock()} className="bg-rose-50 text-rose-700 hover:bg-rose-100 px-4 py-2.5 rounded-lg font-medium">🛒 From low stock ({lowStockItems().length})</button>}
            {!f && <button onClick={exportPO} className="border border-slate-300 text-slate-700 px-4 py-2.5 rounded-lg text-sm">Excel</button>}
          </div>
        </div>

        {!f && (
          <div className="flex gap-2 flex-wrap items-center">
            {[['orders', 'Orders'], ['vendors', 'Vendors']].map(([k, l]) => <button key={k} onClick={() => setPoUi(u => ({ ...u, tab: k }))} className={`px-3 py-1.5 rounded-full text-sm ${poUi.tab === k ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700'}`}>{l}</button>)}
            <input value={poUi.search || ''} onChange={(e) => setPoUi(u => ({ ...u, search: e.target.value }))} placeholder="Search…" className="border rounded-lg px-3 py-1.5 text-sm ml-auto" />
            {poUi.tab === 'orders' && <select value={poUi.status || ''} onChange={(e) => setPoUi(u => ({ ...u, status: e.target.value }))} className="border rounded-lg px-2 py-1.5 text-sm"><option value="">All statuses</option><option value="open">Open</option><option value="partial">Part received</option><option value="received">Received</option><option value="overdue">Delivery overdue</option><option value="cancelled">Cancelled</option></select>}
          </div>
        )}

        {/* ---------------- order form */}
        {f && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <h3 className="text-lg font-semibold">{f.id ? `Edit ${f.poNumber}` : 'New purchase order'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Vendor *</label>
                <input list="po-vendors" className={inp} placeholder="Type or pick a vendor" value={f.vendorName} onChange={(e) => { const v = vendors.find(x => x.name === e.target.value); setF(v ? vendorFields(v) : { vendorName: e.target.value, vendorId: '' }); }} />
                <datalist id="po-vendors">{[...new Set([...vendors.map(v => v.name), ...stockVendors()])].map(n => <option key={n} value={n} />)}</datalist></div>
              <div><label className="text-xs text-slate-500">Order date</label><input type="date" className={inp} value={f.date} onChange={(e) => setF({ date: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Delivery by</label><input type="date" className={inp} value={f.deliveryDate || ''} onChange={(e) => setF({ deliveryDate: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Vendor address</label><input className={inp} value={f.vendorAddress || ''} onChange={(e) => setF({ vendorAddress: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Vendor GSTIN</label><input className={inp} value={f.vendorGSTIN || ''} onChange={(e) => setF({ vendorGSTIN: e.target.value.toUpperCase() })} /></div>
              <div><label className="text-xs text-slate-500">GST %</label><input type="number" className={inp} value={f.gstRate} onChange={(e) => setF({ gstRate: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Vendor phone</label><input className={inp} value={f.vendorPhone || ''} onChange={(e) => setF({ vendorPhone: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Vendor email</label><input className={inp} value={f.vendorEmail || ''} onChange={(e) => setF({ vendorEmail: e.target.value })} /></div>
            </div>
            <datalist id="po-items">{stockItems.filter(s => s.active !== false).map(s => <option key={s.id} value={s.name} />)}</datalist>
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-12 gap-2 text-xs text-slate-500 px-1"><span className="col-span-5">Item (pick a stock item to receive it into Stock)</span><span className="col-span-2">HSN/SAC</span><span className="col-span-1">Qty</span><span className="col-span-1">Unit</span><span className="col-span-2">Rate ₹</span><span className="col-span-1"></span></div>
              {f.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input className={`${inp} col-span-12 md:col-span-5`} list="po-items" placeholder="Item description *" value={it.description} onChange={(e) => { const m = stockItems.find(s => s.name === e.target.value); setItem(idx, m ? { description: e.target.value, stockItemId: m.id, unit: m.unit || 'Nos', rate: it.rate !== '' ? it.rate : (m.rate || '') } : { description: e.target.value, stockItemId: '' }); }} />
                  <input className={`${inp} col-span-4 md:col-span-2`} placeholder="HSN/SAC" value={it.hsn || ''} onChange={(e) => setItem(idx, { hsn: e.target.value })} />
                  <input className={`${inp} col-span-3 md:col-span-1`} type="number" min="0" placeholder="Qty" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} />
                  <input className={`${inp} col-span-3 md:col-span-1`} placeholder="Unit" value={it.unit || ''} onChange={(e) => setItem(idx, { unit: e.target.value })} />
                  <input className={`${inp} col-span-5 md:col-span-2`} type="number" step="0.01" placeholder="Rate *" value={it.rate} onChange={(e) => setItem(idx, { rate: e.target.value })} />
                  <button type="button" onClick={() => setF({ items: f.items.length > 1 ? f.items.filter((_, k) => k !== idx) : f.items })} className="col-span-1 text-red-500 hover:bg-red-50 rounded p-1 flex justify-center"><Trash2 size={14} /></button>
                  {it.stockItemId && <p className="col-span-12 text-[11px] text-emerald-700 -mt-1 pl-1">Linked to stock item – receiving this order adds it to Stock with the vendor.</p>}
                </div>
              ))}
              <button type="button" onClick={() => setF({ items: [...f.items, { stockItemId: '', description: '', hsn: '', qty: 1, unit: 'Nos', rate: '' }] })} className="text-sm text-brand-600 hover:text-brand-700 font-medium">+ Add line</button>
            </div>
            <div className="text-sm text-slate-700 text-right space-y-0.5">
              <p>Sub total: <b>{money(totals.net)}</b></p>
              {totals.interstate ? <p>IGST ({totals.rate}%): {money(totals.igst)}</p> : <><p>CGST ({totals.rate / 2}%): {money(totals.cgst)}</p><p>SGST ({totals.rate / 2}%): {money(totals.sgst)}</p></>}
              <p className="text-base">Total: <b>{money(totals.total)}</b></p>
            </div>
            <textarea rows="2" className={inp} placeholder="Notes (printed on the order)" value={f.notes || ''} onChange={(e) => setF({ notes: e.target.value })} />
            <textarea rows="4" className={inp} placeholder="Terms & conditions" value={f.terms || ''} onChange={(e) => setF({ terms: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPoUi(u => ({ ...u, form: null }))} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
              <button onClick={savePO} disabled={poUi.saving} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-60">{poUi.saving ? 'Saving…' : 'Save purchase order'}</button>
            </div>
          </div>
        )}

        {/* ---------------- orders list */}
        {!f && poUi.tab === 'orders' && (
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {rows.length === 0 && <p className="p-8 text-center text-slate-400 italic text-sm">{purchaseOrders.length === 0 ? 'No purchase orders yet.' : 'No orders match.'}</p>}
            {rows.map(p => {
              const t = poTotals(p);
              const ordered = (p.items || []).reduce((s, i) => s + (i.qty || 0), 0), got = (p.items || []).reduce((s, i) => s + (Number(i.receivedQty) || 0), 0);
              const st = PO_STATUS[p.status] || PO_STATUS.open;
              return (
                <div key={p.id} className="p-3 space-y-2">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1 basis-56">
                      <p className="font-semibold text-slate-900 truncate"><span className="font-mono text-brand-700">{p.poNumber}</span> · {p.vendorName}</p>
                      <p className="text-xs text-slate-400 truncate">{fmtDMY(p.date)}{p.deliveryDate ? ` · delivery by ${fmtDMY(p.deliveryDate)}` : ''} · {(p.items || []).map(i => `${i.description} ×${i.qty}`).join(', ')}</p>
                    </div>
                    <div className="text-right"><p className="font-semibold">{money(t.total)}</p>
                      <div className="flex gap-1 justify-end mt-0.5">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st[1]}`}>{st[0]}{p.status === 'partial' ? ` ${got}/${ordered}` : ''}</span>
                        {isOverdue(p) && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Overdue</span>}
                      </div></div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => downloadPOPDF(p)} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">PDF</button>
                    {canEditPO && ['open', 'partial'].includes(p.status) && <button onClick={() => openReceive(p)} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium">📥 Receive goods</button>}
                    {canEditPO && p.status === 'open' && !(p.receipts || []).length && <button onClick={() => setPoUi(u => ({ ...u, form: { ...p, items: p.items.map(x => ({ ...x })) } }))} className="text-xs px-2.5 py-1 rounded-lg text-slate-600 hover:bg-slate-100">Edit</button>}
                    {canEditPO && p.status === 'open' && <button onClick={() => { const why = window.prompt(`Cancel ${p.poNumber}? Reason:`, ''); if (why !== null) setPoStatus(p, 'cancelled', { cancelReason: why.trim(), cancelledBy: currentUser?.email || '' }); }} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>}
                    {canEditPO && p.status === 'partial' && <button onClick={() => { if (window.confirm(`Close ${p.poNumber} as complete? What has not arrived will not be expected any more.`)) setPoStatus(p, 'received', { closedShort: true, receivedAt: new Date().toISOString() }); }} className="text-xs px-2.5 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Close short</button>}
                    {userRole === 'super_admin' && <button onClick={async () => { if (window.confirm(`Delete ${p.poNumber}? Its number becomes free again.`)) { await logAudit('DELETE', 'PurchaseOrder', p.id, p.poNumber, p); await deleteDoc(doc(db, 'purchaseOrders', p.id)); } }} className="text-xs px-2 py-1 rounded-lg text-red-500 hover:bg-red-50" title="Delete"><Trash2 size={13} /></button>}
                  </div>
                  {(p.receipts || []).length > 0 && <p className="text-xs text-slate-400">Received: {(p.receipts || []).map(r => `${fmtDMY(r.date)}${r.billNo ? ` (bill ${r.billNo})` : ''}`).join(' · ')}</p>}
                  {p.status === 'cancelled' && p.cancelReason && <p className="text-xs text-slate-400">Cancelled: {p.cancelReason}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* ---------------- vendors */}
        {!f && poUi.tab === 'vendors' && (
          <div className="space-y-3">
            {canEditPO && <button onClick={() => setPoUi(u => ({ ...u, vendor: { name: '', address: '', gstin: '', email: '', phone: '' } }))} className="bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"><Plus size={16} /> New vendor</button>}
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
              {vendors.filter(v => !q || [v.name, v.gstin, v.email, v.phone].some(x => String(x || '').toLowerCase().includes(q))).length === 0 && <p className="p-8 text-center text-slate-400 italic text-sm">No vendors yet. They are added when you raise an order, or here.</p>}
              {vendors.filter(v => !q || [v.name, v.gstin, v.email, v.phone].some(x => String(x || '').toLowerCase().includes(q))).map(v => {
                const mine = purchaseOrders.filter(p => p.vendorId === v.id || normStockName(p.vendorName) === normStockName(v.name));
                return (
                  <div key={v.id} className="p-3 flex items-center gap-3 flex-wrap">
                    <div className="min-w-0 flex-1 basis-56"><p className="font-semibold text-slate-900 truncate">{v.name}</p>
                      <p className="text-xs text-slate-400 truncate">{[v.gstin, v.phone, v.email, v.address].filter(Boolean).join(' · ') || 'No details'}</p></div>
                    <p className="text-xs text-slate-500">{mine.length} order(s) · {money(mine.filter(p => p.status !== 'cancelled').reduce((s, p) => s + poTotals(p).total, 0))}</p>
                    {canEditPO && <button onClick={() => setPoUi(u => ({ ...u, vendor: { ...v } }))} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600">Edit</button>}
                    {canMake && <button onClick={() => setPoUi(u => ({ ...u, tab: 'orders', form: blankPO(vendorFields(v)) }))} className="text-xs px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700 font-medium">New order</button>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---------------- receive goods */}
        {rv && (() => {
          const po = purchaseOrders.find(p => p.id === rv.poId);
          if (!po) return null;
          return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setPoUi(u => ({ ...u, recv: null }))}>
              <div className="bg-white rounded-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold">Receive goods – {po.poNumber}</h3>
                <p className="text-xs text-slate-500">{po.vendorName}. Items linked to a stock item are booked into Stock with this vendor.</p>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" className={inp} value={rv.date} onChange={(e) => setPoUi(u => ({ ...u, recv: { ...u.recv, date: e.target.value } }))} />
                  <input className={inp} placeholder="Vendor bill / challan no." value={rv.billNo} onChange={(e) => setPoUi(u => ({ ...u, recv: { ...u.recv, billNo: e.target.value } }))} />
                </div>
                {po.items.map((i, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <div className="flex-1 min-w-0"><p className="truncate font-medium">{i.description}</p><p className="text-xs text-slate-400">Ordered {i.qty} · received {i.receivedQty || 0} {i.unit}{i.stockItemId ? ' · stock item' : ''}</p></div>
                    <input type="number" min="0" className="w-24 border rounded-lg px-2 py-1.5 text-sm" value={rv.qty[idx]} onChange={(e) => setPoUi(u => ({ ...u, recv: { ...u.recv, qty: u.recv.qty.map((x, k) => (k === idx ? e.target.value : x)) } }))} />
                  </div>
                ))}
                <div className="flex justify-end gap-2 pt-2"><button onClick={() => setPoUi(u => ({ ...u, recv: null }))} className="px-4 py-2 rounded-lg border text-sm">Cancel</button><button onClick={saveReceive} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">Save receipt</button></div>
              </div>
            </div>
          );
        })()}

        {/* ---------------- vendor form */}
        {vf && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setPoUi(u => ({ ...u, vendor: null }))}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-3" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold">{vf.id ? 'Edit vendor' : 'New vendor'}</h3>
              <input className={inp} placeholder="Vendor name *" value={vf.name || ''} onChange={(e) => setPoUi(u => ({ ...u, vendor: { ...u.vendor, name: e.target.value } }))} />
              <input className={inp} placeholder="GSTIN" value={vf.gstin || ''} onChange={(e) => setPoUi(u => ({ ...u, vendor: { ...u.vendor, gstin: e.target.value.toUpperCase() } }))} />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Phone" value={vf.phone || ''} onChange={(e) => setPoUi(u => ({ ...u, vendor: { ...u.vendor, phone: e.target.value } }))} />
                <input className={inp} placeholder="Email" value={vf.email || ''} onChange={(e) => setPoUi(u => ({ ...u, vendor: { ...u.vendor, email: e.target.value } }))} />
              </div>
              <textarea rows="2" className={inp} placeholder="Address" value={vf.address || ''} onChange={(e) => setPoUi(u => ({ ...u, vendor: { ...u.vendor, address: e.target.value } }))} />
              <div className="flex justify-end gap-2"><button onClick={() => setPoUi(u => ({ ...u, vendor: null }))} className="px-4 py-2 rounded-lg border text-sm">Cancel</button><button onClick={saveVendor} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium">Save</button></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const OperationsView = () => {
    const today = ymdLocal();
    const from = opsFrom <= opsTo ? opsFrom : opsTo;
    const to = opsFrom <= opsTo ? opsTo : opsFrom;
    const single = from === to;
    const d = buildOpsData(from, to);
    const c = d.counts;
    const rangeLabel = single ? fmtDMY(from) : `${fmtDMY(from)} to ${fmtDMY(to)}`;
    const setRange = (a, b) => { setOpsFrom(a); setOpsTo(b); };
    const setDay = (ymd) => { if (ymd) setRange(ymd, ymd); };
    const dow = (new Date().getDay() + 6) % 7;

    const kpi = (label, value, { tone = '', hint = '', go } = {}) => (
      <button key={label} type="button" onClick={go} disabled={!go} className={`text-left bg-white rounded-xl border border-slate-100 shadow-sm p-4 transition ${go ? 'hover:shadow-md hover:border-brand-200 cursor-pointer' : 'cursor-default'}`}>
        <p className="text-xs text-slate-500">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${tone || 'text-slate-900'}`}>{value}</p>
        {hint ? <p className="text-xs text-slate-400 mt-0.5">{hint}</p> : null}
      </button>
    );
    const prioPill = (p) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p === 'high' ? 'bg-red-100 text-red-700' : p === 'low' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{p || 'medium'}</span>;
    const th = 'px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap';
    const td = 'px-3 py-2 text-sm';
    const red = (n) => (n > 0 ? 'text-red-600' : '');

    // Generic table for the report tab (rows are plain objects)
    const dataTable = (title, rows) => (
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center"><h3 className="font-semibold text-slate-900">{title}</h3><span className="text-xs text-slate-400">{rows.length} row{rows.length !== 1 ? 's' : ''}</span></div>
        {rows.length === 0 ? <p className="px-4 py-5 text-sm text-slate-400 italic">None in this period.</p> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr>{Object.keys(rows[0]).map(k => <th key={k} className={th}>{k}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">{rows.map((r, i) => <tr key={i}>{Object.values(r).map((v, j) => <td key={j} className={td}>{String(v ?? '')}</td>)}</tr>)}</tbody>
          </table></div>
        )}
      </div>
    );

    const engineerRows = d.engRows.map(r => ({
      Engineer: r.name, 'Clients allocated': r.clients, 'Assigned': r.assigned, 'Logged': r.logged, 'Completed': r.completed,
      'Open': r.open, 'In progress': r.inProgress, 'Oldest open (days)': r.oldest
    }));
    const fileStamp = single ? from : `${from}_to_${to}`;

    const downloadExcel = () => {
      const wb = XLSX.utils.book_new();
      const add = (name, rows) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'None in this period' }]), name);
      add('Summary', d.summary.map(([k, v]) => ({ Metric: k, Value: v })));
      add('Engineer-wise', engineerRows);
      add('Service calls', d.callRows);
      add('Completed work', d.doneRows);
      add('Printer movements', d.moveRows);
      add('Invoices', d.invRows);
      XLSX.writeFile(wb, `Daily_Operations_${fileStamp}.xlsx`);
    };
    const downloadPdf = () => {
      const pdf = new jsPDF({ orientation: 'landscape' });
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
      pdf.text(COMPANIES[currentCompany]?.name || 'Daily Operations', 14, 14);
      pdf.setFontSize(11); pdf.text('Daily Operations Report', 14, 21);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
      pdf.text(`Period: ${rangeLabel}   |   Generated: ${new Date().toLocaleString('en-GB')}   |   By: ${currentUser?.email || ''}`, 14, 27);
      let y = 33;
      const table = (title, rows) => {
        if (!rows.length) return;
        if (y > 170) { pdf.addPage(); y = 16; }
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
        pdf.text(title, 14, y);
        autoTable(pdf, { startY: y + 2, head: [Object.keys(rows[0])], body: rows.map(r => Object.values(r).map(v => String(v ?? ''))), theme: 'grid', styles: { fontSize: 8, cellPadding: 1.8 }, headStyles: { fillColor: [49, 46, 129] }, margin: { left: 14, right: 14 } });
        y = pdf.lastAutoTable.finalY + 8;
      };
      table('Summary', d.summary.map(([k, v]) => ({ Metric: k, Value: v })));
      table('Engineer-wise', engineerRows);
      table('Service calls', d.callRows);
      table('Completed work', d.doneRows);
      table('Printer movements', d.moveRows);
      table('Invoices', d.invRows);
      pdf.save(`Daily_Operations_${fileStamp}.pdf`);
    };

    return (
      <div className="space-y-5">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold">Daily Operations</h2>
            <p className="text-sm text-slate-500">{COMPANIES[currentCompany]?.name} · {rangeLabel}</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {opsTab === 'dashboard' ? (
              <>
                <button onClick={() => setDay(ymdShift(from, -1))} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50" title="Previous day">◀</button>
                <input type="date" value={from} max={today} onChange={(e) => setDay(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500" />
                <button onClick={() => setDay(ymdShift(from, 1))} disabled={from >= today} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40" title="Next day">▶</button>
                <button onClick={() => setDay(today)} className="px-3 py-2 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 text-sm font-medium">Today</button>
              </>
            ) : (
              <>
                <input type="date" value={opsFrom} max={today} onChange={(e) => e.target.value && setOpsFrom(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500" />
                <span className="text-slate-400 text-sm">to</span>
                <input type="date" value={opsTo} max={today} onChange={(e) => e.target.value && setOpsTo(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500" />
                <button onClick={() => setRange(today, today)} className="px-3 py-2 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 text-sm font-medium">Today</button>
                <button onClick={() => setRange(ymdShift(today, -1), ymdShift(today, -1))} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-medium">Yesterday</button>
                <button onClick={() => setRange(ymdShift(today, -dow), today)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-medium">This week</button>
                <button onClick={() => setRange(`${today.slice(0, 7)}-01`, today)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-medium">This month</button>
              </>
            )}
          </div>
        </div>

        <div className="flex border-b border-slate-200">
          {[['dashboard', '📊 Dashboard'], ['report', '📄 Daily report']].map(([k, label]) => (
            <button key={k} onClick={() => setOpsTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${opsTab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>

        {opsTab === 'dashboard' && (
          <>
            {/* Needs attention */}
            {(() => {
              const items = [
                [c.unassigned, 'open call(s) with no engineer', () => setCurrentView('service')],
                [c.aged, 'open call(s) older than 3 days', () => setCurrentView('service')],
                [c.high, 'high priority call(s) open', () => setCurrentView('service')],
                [d.atWorkshop.filter(m => m.days > 7).length, 'printer(s) at our workshop for over 7 days', () => setCurrentView('service')],
                [c.invPending, 'invoice(s) waiting for approval', () => setCurrentView('invoices')],
                [c.remindersDue, 'reminder(s) / maintenance due', () => setCurrentView('reminders')]
              ].filter(x => x[0] > 0);
              return (
                <div className={`rounded-xl border p-4 ${items.length ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <p className={`font-semibold mb-2 ${items.length ? 'text-amber-900' : 'text-emerald-800'}`}>{items.length ? '⚠️ Needs attention now' : '✅ All clear: nothing needs attention right now'}</p>
                  <div className="flex gap-2 flex-wrap">
                    {items.map(([n, label, go], i) => <button key={i} onClick={go} className="bg-white border border-amber-200 hover:border-amber-400 rounded-full px-3 py-1 text-sm text-slate-800"><b>{n}</b> {label}</button>)}
                  </div>
                </div>
              );
            })()}

            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">On {rangeLabel}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {kpi('Calls logged', c.logged)}
                {kpi('Calls completed', c.done, { tone: 'text-emerald-700' })}
                {kpi('Assigned / reassigned', c.assigned)}
                {kpi('Printers received', c.recvd, { hint: 'IN', go: () => setCurrentView('service') })}
                {kpi('Printers returned', c.returned, { hint: 'OUT', go: () => setCurrentView('service') })}
                {kpi('Meter readings', c.readings, { go: () => setCurrentView('readings') })}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Live, right now</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {kpi('Open calls', c.open, { go: () => setCurrentView('service') })}
                {kpi('Open over 3 days', c.aged, { tone: red(c.aged), go: () => setCurrentView('service') })}
                {kpi('Unassigned', c.unassigned, { tone: red(c.unassigned), go: () => setCurrentView('service') })}
                {kpi('High priority open', c.high, { tone: red(c.high), go: () => setCurrentView('service') })}
                {kpi('At our workshop', c.workshop, { go: () => setCurrentView('service') })}
                {kpi('Invoices to approve', c.invPending, { tone: c.invPending ? 'text-amber-600' : '', go: () => setCurrentView('invoices') })}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-900">Engineer-wise</h3><p className="text-xs text-slate-400">Assigned, logged and completed are for {rangeLabel}; open and in progress are live.</p></div>
              <div className="overflow-x-auto"><table className="w-full">
                <thead className="bg-slate-50"><tr>{['Engineer', 'Clients allocated', 'Assigned', 'Logged', 'Completed', 'Open', 'In progress', 'Oldest open'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {d.engRows.map(r => (
                    <tr key={r.name} className={r.name === 'Unassigned' && (r.open + r.inProgress) > 0 ? 'bg-red-50/50' : ''}>
                      <td className={`${td} font-medium text-slate-900`}>{r.name === 'Unassigned' ? '❓ Unassigned' : `👷 ${r.name}`}</td>
                      <td className={td}>{r.clients}{r.name === 'Unassigned' ? ' not allocated' : ''}</td>
                      <td className={td}>{r.name === 'Unassigned' ? '-' : r.assigned}</td>
                      <td className={td}>{r.logged}</td>
                      <td className={`${td} text-emerald-700 font-medium`}>{r.completed}</td>
                      <td className={td}>{r.open}</td>
                      <td className={td}>{r.inProgress}</td>
                      <td className={`${td} ${r.oldest > 3 ? 'text-red-600 font-semibold' : ''}`}>{r.open + r.inProgress > 0 ? `${r.oldest} day${r.oldest !== 1 ? 's' : ''}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center"><h3 className="font-semibold text-slate-900">Open call board</h3><button onClick={() => setCurrentView('service')} className="text-sm text-brand-600 hover:text-brand-700 font-medium">Open Service →</button></div>
              {d.board.length === 0 ? <p className="px-4 py-5 text-sm text-slate-400 italic">No open service calls.</p> : (
                <div className="overflow-x-auto"><table className="w-full">
                  <thead className="bg-slate-50"><tr>{['Age', 'Priority', 'Client', 'Printer', 'Issue', 'Engineer', 'Status'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.board.slice(0, 25).map(call => {
                      const age = d.ageOf(call);
                      return (
                        <tr key={call.id}>
                          <td className={`${td} whitespace-nowrap ${age > 3 ? 'text-red-600 font-semibold' : ''}`}>{age}d</td>
                          <td className={td}>{prioPill(call.priority)}</td>
                          <td className={`${td} font-medium text-slate-900`}>{call.clientName}</td>
                          <td className={td}>{d.printerLabel(call)}</td>
                          <td className={`${td} max-w-xs truncate`} title={call.issue}>{call.issue}</td>
                          <td className={`${td} ${call.engineer ? '' : 'text-red-600 font-medium'}`}>{call.engineer || 'Unassigned'}</td>
                          <td className={td}>{call.status === 'in-progress' ? '🔵 In progress' : '🟡 Open'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              )}
              {d.board.length > 25 && <p className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">Showing the top 25 of {d.board.length} open calls (high priority and oldest first).</p>}
            </div>

            {d.atWorkshop.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-900">Printers at our workshop</h3><p className="text-xs text-slate-400">Received (IN) and not yet returned (OUT).</p></div>
                <div className="overflow-x-auto"><table className="w-full">
                  <thead className="bg-slate-50"><tr>{['Days', 'Printer', 'Client', 'Received on', 'Received by'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.atWorkshop.slice(0, 15).map(m => (
                      <tr key={m.id}>
                        <td className={`${td} ${m.days > 7 ? 'text-red-600 font-semibold' : ''}`}>{m.days}d</td>
                        <td className={td}>{m.printerSerial} {m.printerModel}</td>
                        <td className={td}>{m.clientName}</td>
                        <td className={td}>{fmtDMY(ymdDay(m.date))}</td>
                        <td className={td}>{m.technician}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}
          </>
        )}

        {opsTab === 'report' && (
          <>
            <div className="flex gap-2 flex-wrap">
              <button onClick={downloadExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Download size={18} /> Download Excel</button>
              <button onClick={downloadPdf} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2.5 rounded-lg shadow-sm font-medium flex items-center gap-2"><Download size={18} /> Download PDF</button>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-900">Summary · {rangeLabel}</h3><p className="text-xs text-slate-400">Rows marked (live) show the position right now, not on the selected dates.</p></div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                {[d.summary.slice(0, 8), d.summary.slice(8)].map((half, i) => (
                  <table key={i} className="w-full"><tbody className="divide-y divide-slate-100">
                    {half.map(([k, v]) => <tr key={k}><td className={`${td} text-slate-600`}>{k}</td><td className={`${td} text-right font-semibold text-slate-900`}>{v}</td></tr>)}
                  </tbody></table>
                ))}
              </div>
            </div>
            {dataTable('Engineer-wise', engineerRows)}
            {dataTable('Service calls (logged, completed or assigned in this period)', d.callRows)}
            {dataTable('Completed work', d.doneRows)}
            {dataTable('Printer movements (inward / outward)', d.moveRows)}
            {dataTable('Invoices (created or approved in this period)', d.invRows)}
          </>
        )}
      </div>
    );
  };

  const renderView = () => {
    if (userRole !== 'client' && userRole !== 'super_admin' && moduleOfNav(currentView) && moduleOverride(moduleOfNav(currentView)) === 'none') return <Dashboard />;
    if (userRole === 'client') {
      switch (currentView) {
        case 'dashboard': return <ClientDashboard />;
        case 'readings': return <ClientMeterReadings />;
        case 'service': return <ClientServiceHistory />;
        case 'quotations': return <ClientQuotationsView />;
        default: return <ClientDashboard />;
      }
    } else {
      switch (currentView) {
        case 'dashboard': return <Dashboard />;
        case 'printers': return <PrintersView />;
        case 'clients': return <ClientsView />;
        case 'rentals': return <RentalsView />;
        case 'service': return <ServiceCallsView />;
        case 'engineers': return <EngineersView />;
        case 'readings': return <MeterReadingsView />;
        case 'users': return <UserManagementView />;
        case 'audit': return <AuditLogView />;
        case 'settings': return <SettingsView />;
        case 'reports': return <ReportsView />;
        case 'quotations': return <QuotationsView />;
        case 'leads': return <LeadsView />;
        case 'inbox': return <InboxView />;
        case 'reminders': return <RemindersView />;
        case 'tally': return canSeeTally() ? <TallyView /> : <Dashboard />;
        case 'invoices': return canSeeInvoices() ? <InvoicesView /> : <Dashboard />;
        case 'operations': return canSeeOperations() ? <OperationsView /> : <Dashboard />;
        case 'purchase': return canSeePurchase() ? PurchaseView() : <Dashboard />;
        case 'stock': return canSeeStock() ? StockView() : <Dashboard />;   // called, not mounted as <StockView />: its state lives in App, so a new component type every render would drop the cursor on each keystroke
        default: return <Dashboard />;
      }
    }
  };

  const getNavItemsRaw = () => {
    if (userRole === 'client') {
      return [
        { id: 'dashboard', icon: Home, label: 'My Dashboard' },
        { id: 'service', icon: Wrench, label: 'My Service Requests' },
        { id: 'quotations', icon: FileText, label: 'My Quotations' },
        { id: 'readings', icon: TrendingUp, label: 'My Readings' }
      ];
    } else if (userRole === 'field_engineer') {
      return [
        { id: 'dashboard', icon: Home, label: 'Dashboard' },
        { id: 'service', icon: Wrench, label: 'Service' },
        { id: 'quotations', icon: FileText, label: 'Quotations' },
        { id: 'readings', icon: TrendingUp, label: 'Meter Readings' },
        { id: 'clients', icon: Users, label: 'Clients' }
      ];
    } else {
      const items = [
        { id: 'dashboard', icon: Home, label: 'Dashboard' },
        { id: 'printers', icon: Printer, label: 'Assets' },
        { id: 'clients', icon: Users, label: 'Clients' },
        { id: 'rentals', icon: FileText, label: 'Rentals' },
        { id: 'service', icon: Wrench, label: 'Service' },
        { id: 'quotations', icon: FileText, label: 'Quotations' },
        { id: 'engineers', icon: Users, label: 'Engineers' },
        { id: 'readings', icon: TrendingUp, label: 'Readings' }
      ];
      if (userRole !== 'user') {
        items.splice(1, 0, { id: 'leads', icon: TrendingUp, label: 'Leads' });
        items.splice(5, 0, { id: 'inbox', icon: Mail, label: 'Email Inbox', badge: inboxMails.filter(m => (m.status || 'new') === 'new').length });
        items.splice(6, 0, { id: 'reminders', icon: Calendar, label: 'Reminders', badge: (() => {
          const t = new Date(); t.setHours(0, 0, 0, 0); const ymd = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
          return [...leads.filter(l => l.stage !== 'won' && l.stage !== 'lost').map(l => l.followUpDate), ...clients.map(c => c.followUpDate), ...inboxMails.filter(m => (m.status || 'new') === 'new').map(m => m.followUpDate), ...reminders.filter(r => (r.status || 'open') === 'open').map(r => r.dueDate)]
            .filter(d => d && d <= ymd).length
            + getPmDueItems().filter(x => x.days <= 0).length + getRenewalItems().filter(x => x.days <= 0).length + (canSeeStock() ? lowStockItems().length : 0);
        })() });
        if (canSeeTally()) items.splice(7, 0, { id: 'tally', icon: BookOpen, label: 'Tally', badge: tallyQueue.filter(q => q.status === 'error').length });
        if (canSeeInvoices()) items.splice(8, 0, { id: 'invoices', icon: Receipt, label: 'Invoices', badge: invoices.filter(x => x.status === 'unpaid' && x.dueDate && x.dueDate < new Date().toISOString().split('T')[0]).length });
        if (canSeeOperations()) items.splice(1, 0, { id: 'operations', icon: ClipboardList, label: 'Daily Operations' });
        if (canSeePurchase()) items.push({ id: 'purchase', icon: ShoppingCart, label: 'Purchase Orders', badge: purchaseOrders.filter(p => ['open', 'partial'].includes(p.status) && p.deliveryDate && p.deliveryDate < new Date().toISOString().slice(0, 10)).length });
        if (canSeeStock()) items.push({ id: 'stock', icon: Package, label: 'Stock / Stores', badge: stockItems.filter(i => i.active !== false && i.minQty > 0 && stockOnHand(i.id) <= i.minQty).length });
      }
      if (userRole === 'super_admin') {
        items.push({ id: 'reports', icon: BarChart3, label: 'Reports' });
        items.push({ id: 'users', icon: User, label: 'User Management' });
        items.push({ id: 'audit', icon: FileText, label: 'Audit Logs' });
        items.push({ id: 'settings', icon: Settings, label: 'Settings' });
      }
      return items;
    }
  };

  // Sidebar sections (admin-type users); a client or engineer has so few items that they stay in one list
  const NAV_GROUPS = [
    ['', ['dashboard']],
    ['Follow-up', ['reminders', 'inbox']],
    ['Sales', ['leads', 'clients', 'quotations']],
    ['Billing', ['invoices', 'tally']],
    ['Operations', ['operations', 'service', 'printers', 'rentals', 'readings', 'stock', 'purchase', 'engineers']],
    ['Admin', ['reports', 'users', 'audit', 'settings']]
  ];
  const navGroups = (items) => {
    if (userRole === 'client' || userRole === 'field_engineer') return [{ title: '', items }];
    const used = new Set();
    const out = NAV_GROUPS.map(([title, ids]) => ({ title, items: ids.map(id => items.find(i => i.id === id)).filter(Boolean) })).filter(g => g.items.length);
    out.forEach(g => g.items.forEach(i => used.add(i.id)));
    const rest = items.filter(i => !used.has(i.id));
    if (rest.length) out.push({ title: 'More', items: rest });
    return out;
  };
  const bottomNavItems = () => {
    const all = getNavItems();
    const picked = ['dashboard', 'service', 'clients', 'quotations', 'reminders'].map(id => all.find(i => i.id === id)).filter(Boolean);
    return [...picked, ...all.filter(i => !picked.includes(i))].slice(0, 5);
  };
  const getNavItems = () => {
    const raw = getNavItemsRaw();
    if (userRole === 'client' || userRole === 'super_admin') return raw;
    const out = raw.filter(i => { const m = moduleOfNav(i.id); return !(m && moduleOverride(m) === 'none'); });
    // a module explicitly given Read / Read & write shows up even if the role would not normally list it
    ACCESS_MODULES.forEach(m => {
      if (['r', 'rw'].includes(moduleOverride(m.key)) && !out.some(i => i.id === m.nav)) out.push({ id: m.nav, icon: m.icon, label: m.label.replace(' & Delivery Challans', '') });
    });
    return out;
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-screen bg-slate-100"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>);
  }

  if (!currentUser) {
    return (
      <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-brand-950 to-slate-900 p-4 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-brand-600/30 rounded-full blur-3xl"></div>
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[28rem] h-[28rem] bg-brand-500/20 rounded-full blur-3xl"></div>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] bg-[size:28px_28px]"></div>

        <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 md:p-8 w-full max-w-md">
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl shadow-lg shadow-brand-600/30 mb-4">
              <Printer className="text-white" size={28} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Copiq</h1>
            <p className="text-sm md:text-base text-slate-500 mt-1.5">Enterprise Print &amp; Fleet Management</p>
          </div>
          {authSetupError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 whitespace-pre-line">
              ⚠️ {authSetupError}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-3.5">
            <input 
              type="email" 
              value={loginEmail} 
              onChange={(e) => setLoginEmail(e.target.value)} 
              className="w-full px-4 py-3 md:py-2.5 border border-slate-300 rounded-lg text-base focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" 
              placeholder="Email" 
              required 
              autoComplete="email"
            />
            <input 
              type="password" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              className="w-full px-4 py-3 md:py-2.5 border border-slate-300 rounded-lg text-base focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 outline-none transition-all duration-150" 
              placeholder="Password" 
              required 
              autoComplete="current-password"
            />
            {loginError && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2.5 rounded-lg text-sm">{loginError}</div>}
            <button type="submit" className="w-full bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white py-3 md:py-2.5 rounded-lg shadow-sm hover:shadow-md font-medium text-base transition-all duration-150">
              Sign In
            </button>
            
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-400">Or continue with</span>
              </div>
            </div>
            
            <button 
              type="button" 
              onClick={handleGoogleLogin} 
              className="w-full bg-white border border-slate-300 text-slate-700 py-3 md:py-2.5 rounded-lg hover:bg-slate-50 hover:shadow-sm font-medium text-base flex items-center justify-center gap-3 transition-all duration-150"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </form>
          <p className="text-center text-xs text-slate-400 mt-6">© {new Date().getFullYear()} Copiq · Secure enterprise portal</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-200 flex-col hidden md:flex border-r border-slate-800`}>
        <div className={`flex items-center border-b border-slate-800/60 h-16 ${sidebarOpen ? 'px-4 justify-between' : 'px-0 justify-center'}`}>
          <div className={`flex items-center gap-2.5 ${sidebarOpen ? '' : 'justify-center'}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shrink-0 shadow-sm shadow-brand-900/40">
              <Printer size={16} className="text-white" />
            </div>
            {sidebarOpen && <h1 className="text-lg font-bold tracking-tight">Copiq</h1>}
          </div>
          {sidebarOpen && (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white hover:bg-white/5 p-1.5 rounded-md transition-colors duration-150"><Menu size={20} /></button>
          )}
        </div>
        {!sidebarOpen && (
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="mx-auto mt-2 text-slate-400 hover:text-white hover:bg-white/5 p-1.5 rounded-md transition-colors duration-150"><Menu size={18} /></button>
        )}
        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
          {navGroups(getNavItems()).map(g => (<React.Fragment key={g.title || 'top'}>
            {g.title && (sidebarOpen
              ? <p className="px-3.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.title}</p>
              : <div className="mx-3 my-2 border-t border-slate-800" />)}
            {g.items.map(item => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id)}
                className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-lg text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-brand-600/15 text-white border-l-[3px] border-brand-500 font-medium pl-3'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white border-l-[3px] border-transparent'
                }`}
              >
                <item.icon size={18} className={isActive ? 'text-brand-400' : 'text-slate-400'} />
                {sidebarOpen && <span className="flex-1 text-left">{item.label}</span>}
                {sidebarOpen && item.badge > 0 && <span className="ml-auto bg-orange-500 text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-full">{item.badge}</span>}
              </button>
            );
          })}
          </React.Fragment>))}
        </nav>
        <div className="p-3 border-t border-slate-800/60">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-150">
            <LogOut size={18} className="text-slate-400" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute inset-y-0 left-0 w-72 bg-slate-900 text-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 flex items-center justify-between border-b border-slate-800/60 h-16">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shrink-0">
                  <Printer size={16} className="text-white" />
                </div>
                <h1 className="text-lg font-bold tracking-tight">Copiq</h1>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400 hover:text-white hover:bg-white/5 p-1.5 rounded-md transition-colors duration-150"><X size={20} /></button>
            </div>
            <nav className="px-2 py-3 overflow-y-auto h-[calc(100vh-140px)] space-y-0.5">
              {navGroups(getNavItems()).map(g => (<React.Fragment key={g.title || 'top'}>
              {g.title && <p className="px-3.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.title}</p>}
              {g.items.map(item => {
                const isActive = currentView === item.id;
                return (
                  <button 
                    key={item.id} 
                    onClick={() => {
                      setCurrentView(item.id);
                      setIsMobileMenuOpen(false);
                    }} 
                    className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm transition-all duration-150 ${
                      isActive
                        ? 'bg-brand-600/15 text-white border-l-[3px] border-brand-500 font-medium pl-3'
                        : 'text-slate-300 hover:bg-white/5 hover:text-white border-l-[3px] border-transparent'
                    }`}
                  >
                    <item.icon size={18} className={isActive ? 'text-brand-400' : 'text-slate-400'} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && <span className="ml-auto bg-orange-500 text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-full">{item.badge}</span>}
                  </button>
                );
              })}
              </React.Fragment>))}
            </nav>
            <div className="p-3 border-t border-slate-800/60">
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-150">
                <LogOut size={18} className="text-slate-400" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 md:px-6 md:py-4 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden text-slate-600 hover:bg-slate-100 p-1.5 rounded-md transition-colors duration-150">
                <Menu size={22} />
              </button>
              <h2 className="text-lg md:text-2xl font-semibold text-slate-900 tracking-tight">
                {getNavItems().find(item => item.id === currentView)?.label}
              </h2>
            </div>
            <div className="flex items-center gap-2.5 text-xs md:text-sm">
              <button onClick={refreshApp} disabled={refreshing} title="Refresh this page (reloads the latest version and data)" className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-60">
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              </button>
              {userRole !== 'client' && (
                <select
                  value={currentCompany}
                  onChange={(e) => setCurrentCompany(e.target.value)}
                  className="bg-slate-900 text-white text-xs md:text-sm rounded-full px-3 py-1.5 border-0 outline-none cursor-pointer font-medium"
                  title="Switch company"
                >
                  {Object.values(COMPANIES).map(c => (
                    <option key={c.id} value={c.id}>🏢 {c.name}</option>
                  ))}
                </select>
              )}
              <div className="hidden sm:flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pl-1 pr-3 py-1">
                <div className="bg-brand-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-bold shrink-0">
                  {(currentUser.email || '?')[0].toUpperCase()}
                </div>
                <span className="text-slate-700">{currentUser.email}</span>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${userRole === 'super_admin' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-brand-50 text-brand-700 border border-brand-200'}`}>
                {ROLES[userRole]?.label || userRole}
              </span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">{renderView()}</main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 md:hidden z-30">
          <div className="flex justify-around items-center h-16">
            {bottomNavItems().map(item => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentView(item.id)}
                  className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors duration-150 ${isActive ? 'text-brand-600' : 'text-slate-500'}`}
                >
                  <div className={`relative flex items-center justify-center ${isActive ? '' : ''}`}>
                    {isActive && <span className="absolute -top-2 w-1 h-1 rounded-full bg-brand-600"></span>}
                    <item.icon size={20} className={isActive ? 'text-brand-600' : 'text-slate-500'} />
                  </div>
                  <span className={`text-[11px] ${isActive ? 'font-medium' : ''}`}>{item.label.split(' ')[0]}</span>
                </button>
              );
            })}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-slate-500 transition-colors duration-150"
            >
              <Menu size={20} />
              <span className="text-[11px]">More</span>
            </button>
          </div>
        </nav>
      </div>

      <Modal />
      <ImportDialog />
      <ServiceHistoryModal />
      <ClientActivityModal />
    </div>
  );
};

export default PrinterRentalApp;
